/* ==========================================================================
   PREWORN BROWSE ENGINE
   PREWORN MySQL catalogue search and collection browsing
   - 50 products per request
   - exact totals beyond Shopify's 1,000 / 25,000 result limits
   - metafield-backed catalogue filters
   - Size buttons / Colour swatches
   - numeric pagination across the complete catalogue
   - Shopify Storefront API and native results remain as safety fallbacks
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = {
    catalogApiEnabled: true,
    catalogApiUrl: 'https://preworn.co.uk/sandbox/shopify-catalog-index/shopify_catalog_api.php',
    catalogRequestTimeout: 12000,
    colourRequestTimeout: 20000,
    colourNetworkRetries: 1,
    colourBusyRetries: 2,
    colourServerRetries: 1,
    catalogFailureCooldown: 15000,
    shopDomain: 'preworn-clothing.myshopify.com',
    storefrontToken: '601dc93f3b5bff39a996e00c51b4276c',
    apiVersion: '2026-07',
    pageSize: 50,
    newProductWindowDays: 30,
    shopifyFallbackMaxResults: 25000,
    maxQuickFilters: 12,
    wishlistStorageKey: 'preworn:wishlist'
  };
  var SEARCH_ROUTE_TRAIL_KEY = 'preworn:search-route:trail-v1';
  var SEARCH_ROUTE_TRAIL_TTL = 5000;
  var RETURNS_PORTAL_SEARCH_ROUTE = 'https://returns.reveni.io/preworn-ltd';
  var OUTFIT_START_KEY = 'preworn:outfit-start:v1';
  var OUTFIT_URL = '/pages/build-an-outfit';

  var state = {
    mode: 'search',
    query: '',
    interpretedSearchText: '',
    interpretedSearchIntent: null,
    trending: false,
    brandQuery: '',
    tagQuery: '',
    tagQueries: [],
    tagLabel: '',
    similarSku: '',
    similarTitle: '',
    collectionHandle: '',
    collectionTitle: '',
    collectionImage: '',
    collectionCount: null,
    collectionHero: false,
    collectionStyleDirectoryReady: false,
    collectionStyleDirectoryRequest: null,
    collectionStyleDirectoryItems: [],
    collectionStyleDirectoryDimension: 'type',
    collectionStyleDirectorySignature: '',
    collectionStyleDirectoryGeneration: 0,
    collectionStyleDirectoryController: null,
    collectionStyleDirectoryCountsReady: false,
    collectionStyleDirectorySchedule: null,
    toolbarActivityObserver: null,
    currentPage: 1,
    pageCursors: { 1: null },
    selectedValues: {},
    priceMin: '',
    priceMax: '',
    exactColourHex: '',
    exactColourTolerance: 12,
    exactColourName: '',
    shadePickerHex: '#5278A7',
    shadePickerOpen: false,
    colourPage: false,
    wardrobePageBrowse: false,
    sortKey: 'RELEVANCE',
    reverse: false,
    quickFilterOrder: [],
    collectionQueryAttempted: false,
    collectionQueryMatched: false,
    collectionQueryFilterId: '',
    collectionKeywordSearch: false,
    collectionSearchQuery: '',
    collectionScopeFilters: [],
    wishlistProductIds: [],
    wishlistHandles: [],
    wishlistItemCount: 0,
    wishlistSignature: '',
    wishlistScopeLimitReached: false,
    wishlistGeneration: 0,
    searchAnalyticsEligible: false,
    searchAnalyticsPublished: false,
    searchAnalyticsSource: '',
    searchRouteNavigating: false,
    emptySearchLanding: false,
    catalogFilters: [],
    catalogFacetsDeferred: false,
    catalogFacetsLoading: false,
    catalogFacetsHydrated: false,
    catalogFacetsStale: false,
    catalogSecondaryFacetsLoading: false,
    catalogFacetGeneration: 0,
    catalogFacetRetryCount: 0,
    catalogFacetRetryTimer: null,
    colourFacetTimer: null,
    colourFacetsReady: false,
    activeColourController: null,
    colourRequestGeneration: 0,
    colourFirstPageSeed: [],
    catalogUnavailableUntil: 0,
    collectionCatalogStale: false,
    openFilterGroups: {},
    expandedFilterGroups: {},
    mobileActiveFilterGroup: '',
    usingCatalog: false,
    wardrobeActive: false,
    wardrobe: null,
    linkedWardrobeParameters: {},
    wardrobeAudienceSyncing: false,
    tagWardrobePreferenceContext: null,
    tagWardrobePreferenceLoading: false,
    tagWardrobePreferenceResolved: false,
    wardrobeSummary: null,
    wardrobeSummaryKey: '',
    wardrobeSummaryLoading: false,
    wardrobeSummaryGeneration: 0,
    wardrobeSizeFallbackKey: '',
    wardrobeSizeFallbackSizes: [],
    preferencePromptChoices: [],
    preferencePromptChoicesKey: '',
    preferencePromptChoicesLoading: false,
    preferencePromptChoicesScope: '',
    preferencePromptSaving: false,
    preferencePromptAppliedSizes: [],
    preferencePromptTastesKey: '',
    preferencePromptSelectedColours: [],
    preferencePromptSelectedPatterns: [],
    loading: false,
    pendingReload: false,
    lastResult: null
  };

  var app = null;
  var inlineShadePicker = null;
  var preferencePromptController = null;

  var WARDROBE_URL_PARAMETERS = [
    'wardrobe_audience', 'wardrobe_categories',
    'wardrobe_size_top', 'wardrobe_size_bottom', 'wardrobe_size_dress',
    'wardrobe_size_shoe', 'wardrobe_colours', 'wardrobe_category_sizes',
    'wardrobe_styles', 'wardrobe_patterns', 'wardrobe_brands',
    'wardrobe_favourite_brands'
  ];

  function normaliseExactColour(value) {
    value = String(value || '').trim();
    return /^#[0-9a-f]{6}$/i.test(value) ? value.toUpperCase() : '';
  }

  function normaliseBrowseQuery(value) {
    value = String(value || '').trim();
    return /^["'\s]*$/.test(value) ? '' : value;
  }

  function canonicalDressSearchInterpretation(value) {
    var original = normaliseBrowseQuery(value);
    var query = original.toLowerCase().replace(/[’`]/g, "'")
      .replace(/[^a-z0-9' -]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!query) return null;

    var dressMatch = query.match(/\bdress(?:es)?\b/);
    if (!dressMatch) return null;
    var dressTerm = dressMatch[0];
    if (query === dressTerm) {
      return {
        original: original,
        query: '',
        selections: [
          { parameter: 'type', value: 'Dress', label: 'Dress', term: dressTerm }
        ]
      };
    }

    var colourAliases = {
      'black': 'Black', 'white': 'White', 'grey': 'Grey', 'gray': 'Grey',
      'red': 'Red', 'green': 'Green', 'pink': 'Pink', 'purple': 'Purple',
      'yellow': 'Yellow', 'orange': 'Orange', 'brown': 'Brown',
      'beige': 'Beige', 'cream': 'Cream', 'khaki': 'Khaki',
      'blue': 'Blue', 'navy': 'Navy', 'multi': 'Multicoloured',
      'multicoloured': 'Multicoloured', 'multicolored': 'Multicoloured',
      'multi coloured': 'Multicoloured', 'multi colored': 'Multicoloured'
    };
    var aliases = Object.keys(colourAliases).sort(function (left, right) {
      return right.length - left.length;
    });
    for (var index = 0; index < aliases.length; index++) {
      var alias = aliases[index];
      if (query !== alias + ' ' + dressTerm &&
          query !== dressTerm + ' ' + alias) continue;
      return {
        original: original,
        query: '',
        selections: [
          {
            parameter: 'colour',
            value: colourAliases[alias],
            label: colourAliases[alias],
            term: alias
          },
          { parameter: 'type', value: 'Dress', label: 'Dress', term: dressTerm }
        ]
      };
    }
    return null;
  }

  function addInterpretedSearchSelection(selection) {
    selection = selection || {};
    var parameter = String(selection.parameter || '').trim().toLowerCase();
    var value = String(selection.value || '').trim();
    if (!parameter || !value) return;
    var key = 'catalog:' + parameter + ':' + value.toLowerCase();
    var selected = state.selectedValues[key] || {
      input: { catalogParameter: parameter, catalogValue: value },
      label: String(selection.label || value)
    };
    selected.interpretedSearch = true;
    selected.interpretedQueryTerm = String(selection.term || value).toLowerCase();
    state.selectedValues[key] = selected;
  }

  function clearInterpretedSearchSelections() {
    Object.keys(state.selectedValues).forEach(function (key) {
      if (state.selectedValues[key] && state.selectedValues[key].interpretedSearch) {
        delete state.selectedValues[key];
      }
    });
    state.interpretedSearchText = '';
    state.interpretedSearchIntent = null;
  }

  function applyCanonicalDressSearchIntent(value) {
    var intent = canonicalDressSearchInterpretation(value);
    if (!intent) return false;
    state.interpretedSearchText = intent.original;
    state.interpretedSearchIntent = intent;
    state.query = intent.query;
    intent.selections.forEach(addInterpretedSearchSelection);
    var nominalColour = selectedNominalColour();
    var nominalPickerHex = nominalColourPickerHex(nominalColour);
    if (nominalPickerHex) {
      state.shadePickerHex = nominalPickerHex;
      state.shadePickerOpen = true;
    }
    return true;
  }

  function currentProductSearchText() {
    return normaliseBrowseQuery(state.interpretedSearchText) ||
      activeProductSearchQuery();
  }

  function safeSearchRouteDestination(route) {
    if (!route || !route.routeId || !route.destination) return '';
    try {
      var target = new URL(String(route.destination), window.location.origin);
      var internalDestination = target.origin === window.location.origin &&
        /^\/(?:collections(?:\/|$)|pages(?:\/|$)|products(?:\/|$)|search(?:\/|$)|blogs(?:\/|$))/.test(target.pathname);
      var returnsPortalDestination = target.href === RETURNS_PORTAL_SEARCH_ROUTE;
      if (!internalDestination && !returnsPortalDestination) {
        return '';
      }
      var destination = returnsPortalDestination
        ? RETURNS_PORTAL_SEARCH_ROUTE
        : target.pathname + target.search + target.hash;
      var current = window.location.pathname + window.location.search + window.location.hash;
      if (destination === current) return '';

      var trail = null;
      try {
        trail = JSON.parse(window.sessionStorage.getItem(SEARCH_ROUTE_TRAIL_KEY) || 'null');
      } catch (error) {}
      if (!trail || Number(trail.expiresAt || 0) < Date.now()) {
        trail = { hops: 0, urls: [] };
      }
      if (Number(trail.hops || 0) >= 3 || trail.urls.indexOf(destination) !== -1) {
        return '';
      }
      return destination;
    } catch (error) {
      return '';
    }
  }

  function followSearchRoute(route) {
    if (state.searchRouteNavigating) return;
    var destination = safeSearchRouteDestination(route);
    if (!destination) return;
    state.searchRouteNavigating = true;

    try {
      var current = window.location.pathname + window.location.search + window.location.hash;
      var trail = null;
      try {
        trail = JSON.parse(window.sessionStorage.getItem(SEARCH_ROUTE_TRAIL_KEY) || 'null');
      } catch (error) {}
      if (!trail || Number(trail.expiresAt || 0) < Date.now()) {
        trail = { hops: 0, urls: [] };
      }
      [current, destination].forEach(function (url) {
        if (trail.urls.indexOf(url) === -1) trail.urls.push(url);
      });
      trail.hops = Number(trail.hops || 0) + 1;
      trail.expiresAt = Date.now() + SEARCH_ROUTE_TRAIL_TTL;
      window.sessionStorage.setItem(SEARCH_ROUTE_TRAIL_KEY, JSON.stringify(trail));
    } catch (error) {}

    // The analytics listener starts its keepalive request synchronously. Give
    // it one task boundary before replacing the search page.
    window.setTimeout(function () {
      window.location.replace(destination);
    }, 25);
  }

  function syncProductSearchInput() {
    var input = app && app.querySelector('[data-pw-colour-product-search-input]');
    if (!input) return;
    input.value = currentProductSearchText();
    var clear = app.querySelector('[data-pw-colour-product-search-clear]');
    if (clear) clear.hidden = !input.value;
  }

  function removeInterpretedSearchTerm(selected) {
    if (!selected || !selected.interpretedSearch) return;
    var term = String(selected.interpretedQueryTerm || '').trim();
    if (term && state.interpretedSearchText) {
      var escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      state.interpretedSearchText = normaliseBrowseQuery(
        state.interpretedSearchText.replace(new RegExp('\\b' + escaped + '\\b', 'i'), ' ')
          .replace(/\s+/g, ' ')
      );
    }
    state.interpretedSearchIntent = null;
    syncProductSearchInput();
  }

  function activeProductSearchQuery() {
    var query = normaliseBrowseQuery(state.query);
    if (!query || query === '*') return '';
    var normalised = query.toLowerCase();
    var tagDefault = normaliseBrowseQuery(state.tagLabel).toLowerCase();
    var brandDefault = normaliseBrowseQuery(state.brandQuery).toLowerCase();
    // Tag and brand labels define the page scope; they are not an additional
    // keyword. Only a shopper-entered term should become `q` within that scope.
    if ((state.tagQuery && tagDefault && normalised === tagDefault) ||
        (state.brandQuery && brandDefault && normalised === brandDefault)) {
      return '';
    }
    return query;
  }

  function hasNonQueryBrowseScope() {
    return !!state.colourPage || !!state.exactColourHex || !!state.similarSku ||
      !!state.trending || state.sortKey === 'CREATED' ||
      !!state.brandQuery || !!state.tagQuery ||
      state.tagQueries.length > 0 || !!state.wardrobePageBrowse || wardrobeScopeActive() ||
      state.mode === 'wishlist' ||
      Object.keys(state.selectedValues).length > 0 ||
      !!state.priceMin || !!state.priceMax;
  }

  function syncEmptySearchLanding() {
    state.emptySearchLanding = state.mode === 'search' &&
      !normaliseBrowseQuery(state.query) && !hasNonQueryBrowseScope();
    if (app) app.classList.toggle('is-empty-search', state.emptySearchLanding);
    return state.emptySearchLanding;
  }

  function exactColourName(hex) {
    hex = normaliseExactColour(hex);
    if (!hex) return '';
    var red = parseInt(hex.slice(1, 3), 16) / 255;
    var green = parseInt(hex.slice(3, 5), 16) / 255;
    var blue = parseInt(hex.slice(5, 7), 16) / 255;
    var maximum = Math.max(red, green, blue);
    var minimum = Math.min(red, green, blue);
    var delta = maximum - minimum;
    var lightness = (maximum + minimum) / 2;
    var saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    var hue = 0;
    if (delta) {
      if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
      else if (maximum === green) hue = 60 * (((blue - red) / delta) + 2);
      else hue = 60 * (((red - green) / delta) + 4);
      if (hue < 0) hue += 360;
    }
    if (lightness < .13) return 'Black';
    if (lightness > .92 && saturation < .12) return 'White';
    if (saturation < .1) return lightness < .35 ? 'Charcoal' : (lightness < .72 ? 'Grey' : 'Silver');
    if (hue < 15 || hue >= 345) return lightness < .38 ? 'Burgundy' : 'Red';
    if (hue < 42) return lightness > .72 ? 'Peach' : 'Orange';
    if (hue < 68) return lightness > .8 ? 'Cream' : 'Yellow';
    if (hue < 85) return 'Lime';
    if (hue < 165) return lightness < .32 ? 'Forest Green' : 'Green';
    if (hue < 195) return 'Turquoise';
    if (hue < 218) return lightness > .68 ? 'Sky Blue' : 'Denim Blue';
    if (hue < 252) return lightness < .32 ? 'Navy' : 'Royal Blue';
    if (hue < 285) return 'Purple';
    if (hue < 330) return lightness > .66 ? 'Pink' : 'Magenta';
    return 'Burgundy';
  }

  function clampColourNumber(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, Number(value) || 0));
  }

  function exactColourHsv(hex) {
    hex = normaliseExactColour(hex) || '#5278A7';
    var red = parseInt(hex.slice(1, 3), 16) / 255;
    var green = parseInt(hex.slice(3, 5), 16) / 255;
    var blue = parseInt(hex.slice(5, 7), 16) / 255;
    var maximum = Math.max(red, green, blue);
    var minimum = Math.min(red, green, blue);
    var delta = maximum - minimum;
    var hue = 0;
    if (delta) {
      if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
      else if (maximum === green) hue = 60 * (((blue - red) / delta) + 2);
      else hue = 60 * (((red - green) / delta) + 4);
      if (hue < 0) hue += 360;
    }
    return {
      h: hue,
      s: maximum ? delta / maximum * 100 : 0,
      v: maximum * 100
    };
  }

  function exactColourFromHsv(hue, saturation, value) {
    var h = ((Number(hue) || 0) % 360 + 360) % 360;
    var s = clampColourNumber(saturation, 0, 100) / 100;
    var v = clampColourNumber(value, 0, 100) / 100;
    var chroma = v * s;
    var segment = h / 60;
    var secondary = chroma * (1 - Math.abs(segment % 2 - 1));
    var red = 0;
    var green = 0;
    var blue = 0;
    if (segment < 1) { red = chroma; green = secondary; }
    else if (segment < 2) { red = secondary; green = chroma; }
    else if (segment < 3) { green = chroma; blue = secondary; }
    else if (segment < 4) { green = secondary; blue = chroma; }
    else if (segment < 5) { red = secondary; blue = chroma; }
    else { red = chroma; blue = secondary; }
    var match = v - chroma;
    function channel(component) {
      return Math.round((component + match) * 255).toString(16).padStart(2, '0');
    }
    return ('#' + channel(red) + channel(green) + channel(blue)).toUpperCase();
  }

  function clearNominalColourSelections() {
    var changed = false;
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var match = String(key).match(/^catalog:([^:]+):/i);
        parameter = match ? match[1].toLowerCase() : '';
      }
      if (parameter === 'colour') {
        removeInterpretedSearchTerm(selected);
        delete state.selectedValues[key];
        changed = true;
      }
    });
    return changed;
  }

  function selectedNominalColour() {
    var keys = Object.keys(state.selectedValues);
    for (var index = 0; index < keys.length; index++) {
      var selected = state.selectedValues[keys[index]] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(keys[index]).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (parameter === 'colour') {
        return String(selected.label || input.catalogValue || '').trim();
      }
    }
    return '';
  }

  function inlineShadePickerIsOpen() {
    return !!state.shadePickerOpen || !!state.exactColourHex ||
      !!selectedNominalColour();
  }

  function nominalColourPickerHex(colour) {
    var colours = {
      black: '#111314', white: '#FBFAF5', grey: '#8F9692', gray: '#8F9692',
      blue: '#496FAC', green: '#447757', red: '#B9353C', pink: '#D890AA',
      brown: '#74513D', beige: '#D9C3A5', purple: '#71518B', yellow: '#E3BF31',
      orange: '#DF722D', cream: '#EEECD9', navy: '#17243F', gold: '#C0A24C',
      silver: '#C6C8C7', charcoal: '#47534E', turquoise: '#38BFC5',
      burgundy: '#782C36', lime: '#9DAA71', khaki: '#9A8F68',
      multicoloured: '#7A6A91', multi: '#7A6A91'
    };
    return colours[String(colour || '').trim().toLowerCase()] || '';
  }

  function activeShadePickerHex() {
    return normaliseExactColour(state.exactColourHex) ||
      nominalColourPickerHex(selectedNominalColour()) ||
      normaliseExactColour(state.shadePickerHex) || '#5278A7';
  }

  function linkedWardrobeParameters(params) {
    var linked = {};
    WARDROBE_URL_PARAMETERS.forEach(function (parameter) {
      var value = String(params.get(parameter) || '').trim();
      if (value) linked[parameter] = value.slice(0, parameter === 'wardrobe_colours' ? 12000 : 5000);
    });
    if (!/^(?:woman|man|girl|boy|baby)$/.test(linked.wardrobe_audience || '') ||
        !linked.wardrobe_categories) return {};
    return linked;
  }

  function hasLinkedWardrobe() {
    return Object.keys(state.linkedWardrobeParameters).length > 0;
  }

  function wardrobeScopeActive() {
    return Object.keys(wardrobeParametersForCurrentScope()).length > 0;
  }

  function broadAudienceTagPage() {
    if (!app || app.getAttribute('data-tag-page') !== 'true' ||
        state.mode !== 'search') return false;
    var scope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var scopeKeys = Object.keys(scope);
    return scopeKeys.length === 1 && scopeKeys[0] === 'department' &&
      tagPageWardrobeAudiences().length === 1;
  }

  function wardrobeSearchUsesSoftScope() {
    if (!wardrobeScopeActive() || state.wardrobePageBrowse ||
        state.mode === 'wishlist') return false;
    // A shopper-entered query inside a collection is still explicit search
    // intent. Keep the collection and its visible size facets fixed, while
    // allowing saved Wardrobe tastes to rank rather than hide valid stock.
    if (state.mode === 'collection') return !!activeProductSearchQuery();
    if (state.mode !== 'search') return false;
    // A top-level audience landing page such as /pages/womens is the primary
    // Wardrobe browse itself. Running every saved collection/size combination
    // as soft ORDER BY signals across the complete department is needlessly
    // expensive and can exceed the catalogue database timeout. Keep the saved
    // Wardrobe as the hard scope here; narrower tag and search pages stay soft.
    if (broadAudienceTagPage() && !activeProductSearchQuery() &&
        !state.brandQuery && !state.exactColourHex && !state.colourPage &&
        !state.similarSku && !state.trending) return false;
    // A deliberate query, brand, tag, shade, similar-product or trending page
    // is the shopper's current intent. Wardrobe preferences should rank that
    // result set, never replace it with a narrow category allow-list.
    return !!(activeProductSearchQuery() || state.brandQuery ||
      state.tagQueries.length || state.tagQuery || state.exactColourHex ||
      state.colourPage || state.similarSku || state.trending);
  }

  function wardrobeSoftSearchMessage() {
    var subject = String(state.brandQuery || activeProductSearchQuery() ||
      state.tagLabel || state.exactColourName || state.similarTitle || '').trim();
    var department = wardrobeDepartmentLabel();
    var audience = department ? department + "'s " : '';
    return subject
      ? 'Your saved sizes and Wardrobe matches come first. All matching ' + audience + subject +
        ' pieces stay visible.'
      : 'Your saved sizes and Wardrobe matches come first without hiding other ' +
        (department ? department + ' results.' : 'results.');
  }

  function wardrobeIsReady() {
    return !!(state.wardrobe && Array.isArray(state.wardrobe.categories) &&
      state.wardrobe.categories.length);
  }

  function ensureWardrobePageActive() {
    if (!state.wardrobePageBrowse || !wardrobeIsReady() || state.wardrobeActive ||
        !window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.setActive !== 'function') return false;
    state.linkedWardrobeParameters = {};
    return window.PrewornWardrobe.setActive(true) === true;
  }

  function wardrobePageCanBrowse() {
    return !state.wardrobePageBrowse || wardrobeScopeActive();
  }

  function renderWardrobeSummary() {
    var shell = app && app.querySelector('[data-pw-wardrobe-metrics]');
    if (!shell) return;
    var visible = state.wardrobePageBrowse && wardrobeScopeActive();
    shell.hidden = !visible;
    if (!visible) return;
    var summary = state.wardrobeSummary;
    var values = [
      ['[data-pw-wardrobe-available-count-value]', summary && summary.availableCount],
      ['[data-pw-wardrobe-new-count-value]', summary && summary.newInCount],
      ['[data-pw-wardrobe-sold-count-value]', summary && summary.sold30Days]
    ];
    shell.classList.toggle('is-loading', state.wardrobeSummaryLoading);
    values.forEach(function (entry) {
      var value = shell.querySelector(entry[0]);
      if (value) value.textContent = summary === null
        ? '—' : Number(entry[1] || 0).toLocaleString('en-GB');
    });
  }

  function resetWardrobeSummary() {
    state.wardrobeSummaryGeneration += 1;
    state.wardrobeSummary = null;
    state.wardrobeSummaryKey = '';
    state.wardrobeSummaryLoading = false;
    renderWardrobeSummary();
  }

  function wardrobeSummaryUrl() {
    if (!state.wardrobePageBrowse || !wardrobeScopeActive() || !app) return null;
    var endpoint = app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl;
    if (!endpoint) return null;
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', 'wardrobe_metrics');
    url.searchParams.set('metrics_dimensions', 'none');
    url.searchParams.set('include_recent', '0');
    url.searchParams.set('summary_version', '1');
    appendWardrobeParameters(url.searchParams);
    return url;
  }

  function normaliseWardrobeSummary(summary) {
    if (!summary || !isFinite(Number(summary.availableCount)) ||
        !isFinite(Number(summary.newInCount)) ||
        !isFinite(Number(summary.sold30Days))) return null;
    return {
      availableCount: Math.max(0, Number(summary.availableCount)),
      newInCount: Math.max(0, Number(summary.newInCount)),
      sold30Days: Math.max(0, Number(summary.sold30Days))
    };
  }

  function readWardrobeSummaryCache(key) {
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(
        'preworn:wardrobe-summary:v1'
      ) || 'null');
      var summary = cached ? normaliseWardrobeSummary(cached.summary) : null;
      if (!cached || cached.key !== key || !summary ||
          Date.now() - Number(cached.savedAt || 0) > 5 * 60 * 1000) return null;
      return summary;
    } catch (error) {
      return null;
    }
  }

  function writeWardrobeSummaryCache(key, summary) {
    try {
      window.sessionStorage.setItem('preworn:wardrobe-summary:v1', JSON.stringify({
        key: key,
        summary: summary,
        savedAt: Date.now()
      }));
    } catch (error) {}
  }

  function loadWardrobeSummary() {
    var url = wardrobeSummaryUrl();
    if (!url) {
      renderWardrobeSummary();
      return;
    }
    var key = url.toString();
    if (state.wardrobeSummaryKey === key &&
        (state.wardrobeSummaryLoading || state.wardrobeSummary !== null)) {
      renderWardrobeSummary();
      return;
    }
    state.wardrobeSummaryKey = key;
    var cached = readWardrobeSummaryCache(key);
    if (cached !== null) {
      state.wardrobeSummary = cached;
      state.wardrobeSummaryLoading = false;
      renderWardrobeSummary();
      return;
    }

    var generation = ++state.wardrobeSummaryGeneration;
    state.wardrobeSummary = null;
    state.wardrobeSummaryLoading = true;
    renderWardrobeSummary();
    fetch(key, {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Wardrobe summary request failed.');
      return response.json();
    }).then(function (payload) {
      if (generation !== state.wardrobeSummaryGeneration ||
          key !== state.wardrobeSummaryKey) return;
      var summary = normaliseWardrobeSummary(payload && payload.summary);
      if (!summary) throw new Error('Wardrobe summary is unavailable.');
      state.wardrobeSummary = summary;
      writeWardrobeSummaryCache(key, summary);
    }).catch(function () {
      if (generation !== state.wardrobeSummaryGeneration ||
          key !== state.wardrobeSummaryKey) return;
      state.wardrobeSummary = null;
    }).then(function () {
      if (generation !== state.wardrobeSummaryGeneration ||
          key !== state.wardrobeSummaryKey) return;
      state.wardrobeSummaryLoading = false;
      renderWardrobeSummary();
    });
  }

  function scheduleWardrobeSummary() {
    if (!state.wardrobePageBrowse || state.wardrobeSummaryLoading ||
        state.wardrobeSummary !== null) return;
    var start = function () { loadWardrobeSummary(); };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 900 });
    } else {
      window.setTimeout(start, 0);
    }
  }

  function wardrobeDepartmentLabel() {
    var audience = state.wardrobe && state.wardrobe.audience
      ? state.wardrobe.audience
      : state.linkedWardrobeParameters.wardrobe_audience;
    return { woman: 'Women', man: 'Men', girl: 'Girls', boy: 'Boys', baby: 'Baby' }[audience] || '';
  }

  function selectedDepartmentLabel() {
    var departments = [];
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (parameter !== 'department') return;

      var value = String(selected.label || input.catalogValue || '').trim();
      var department = quickDepartmentLabel(departmentKey(value)) || value;
      if (department && departments.indexOf(department) === -1) {
        departments.push(department);
      }
    });

    if (departments.length === 2 && departments.indexOf('Girls') !== -1 &&
        departments.indexOf('Boys') !== -1) return 'Kids';
    return departments.join(' / ');
  }

  function hasExplicitDepartmentSelection() {
    return Object.keys(state.selectedValues).some(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      return parameter === 'department';
    });
  }

  function selectedDepartmentAudiences() {
    var audiences = [];
    var audienceByDepartment = {
      women: 'woman',
      men: 'man',
      girls: 'girl',
      boys: 'boy',
      baby: 'baby'
    };
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (parameter !== 'department') return;

      var value = String(input.catalogValue || selected.label || '').trim();
      var audience = audienceByDepartment[departmentKey(value)] || '';
      if (audience && audiences.indexOf(audience) === -1) audiences.push(audience);
    });
    return audiences;
  }

  function tagPageWardrobeAudiences() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return [];
    var scope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var audiences = [];
    var audienceByDepartment = {
      women: 'woman',
      men: 'man',
      girls: 'girl',
      boys: 'boy',
      baby: 'baby'
    };
    String(scope.department || '').split(',').forEach(function (value) {
      var audience = audienceByDepartment[departmentKey(value)] || '';
      if (audience && audiences.indexOf(audience) === -1) audiences.push(audience);
    });
    if (audiences.length) return audiences;

    function audienceFromTaxonomyValue(value) {
      value = String(value || '').toLowerCase().replace(/[’']/g, '')
        .replace(/[^a-z0-9]+/g, ' ').trim();
      if (/^(?:women|womens)\b/.test(value)) return 'woman';
      if (/^(?:men|mens)\b/.test(value)) return 'man';
      if (/^girls?\b/.test(value)) return 'girl';
      if (/^boys?\b/.test(value)) return 'boy';
      if (/^bab(?:y|ies)\b/.test(value)) return 'baby';
      return '';
    }
    ['product_type', 'category_scope', 'category'].forEach(function (parameter) {
      String(scope[parameter] || '').split(',').forEach(function (value) {
        var audience = audienceFromTaxonomyValue(value);
        if (audience && audiences.indexOf(audience) === -1) audiences.push(audience);
      });
    });
    if (audiences.length) return audiences;

    var handle = String(app.getAttribute('data-page-handle') || '').toLowerCase();
    var handleAudience = handle.indexOf('womens-') === 0 ? 'woman'
      : handle.indexOf('mens-') === 0 ? 'man'
      : handle.indexOf('girls-') === 0 ? 'girl'
      : handle.indexOf('boys-') === 0 ? 'boy'
      : handle.indexOf('baby-') === 0 ? 'baby' : '';
    if (handleAudience) audiences.push(handleAudience);
    return audiences;
  }

  function wardrobeAudiencesForCurrentScope() {
    var collectionAudience = collectionWardrobeAudience();
    if (collectionAudience) return [collectionAudience];
    var tagAudiences = tagPageWardrobeAudiences();
    return tagAudiences.length ? tagAudiences : selectedDepartmentAudiences();
  }

  function hasExplicitWardrobeAudienceScope() {
    return !!collectionWardrobeAudience() || tagPageWardrobeAudiences().length > 0 ||
      hasExplicitDepartmentSelection();
  }

  function wardrobeParametersForCurrentScope() {
    var activeParameters = state.wardrobeActive && window.PrewornWardrobe &&
      typeof window.PrewornWardrobe.getFilterParams === 'function'
        ? window.PrewornWardrobe.getFilterParams()
        : {};
    var linkedParameters = hasLinkedWardrobe() ? state.linkedWardrobeParameters : {};
    var parameters = Object.keys(activeParameters).length
      ? activeParameters : linkedParameters;
    var audiences = wardrobeAudiencesForCurrentScope();
    if (!hasExplicitWardrobeAudienceScope()) return parameters || {};
    if (!audiences.length) return {};
    if (activeParameters &&
        audiences.indexOf(String(activeParameters.wardrobe_audience || '')) !== -1) {
      return activeParameters;
    }
    if (linkedParameters &&
        audiences.indexOf(String(linkedParameters.wardrobe_audience || '')) !== -1) {
      return linkedParameters;
    }
    return {};
  }

  function syncLinkedWardrobeParameters() {
    if (!state.wardrobeActive || !window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.getScopeParams !== 'function') {
      // Logged-in wardrobes arrive asynchronously. Keep URL filters while the
      // selected account wardrobe is still loading, or search broadens to the
      // whole department before the wardrobe can be activated.
      if (!state.wardrobeActive && !hasLinkedWardrobe()) state.linkedWardrobeParameters = {};
      return;
    }
    var currentScope = window.PrewornWardrobe.getScopeParams();
    if (currentScope && Object.keys(currentScope).length) {
      state.linkedWardrobeParameters = currentScope;
    }
  }

  function activateLinkedWardrobeIfReady() {
    if (!hasLinkedWardrobe() || state.wardrobeActive || !window.PrewornWardrobe ||
        !state.wardrobe || !Array.isArray(state.wardrobe.categories) ||
        !state.wardrobe.categories.length) return false;
    return window.PrewornWardrobe.setActive(true) === true;
  }

  function syncWardrobeState() {
    if (!window.PrewornWardrobe) {
      state.wardrobeActive = false;
      state.wardrobe = null;
      return;
    }
    state.wardrobeActive = window.PrewornWardrobe.isActive();
    var selected = window.PrewornWardrobe.getWardrobe();
    var audiences = wardrobeAudiencesForCurrentScope();
    if (!hasExplicitWardrobeAudienceScope() ||
        (selected && audiences.indexOf(selected.audience) !== -1)) {
      state.wardrobe = selected;
      return;
    }
    var applicable = wardrobesForCurrentCollection();
    state.wardrobe = applicable[0] || (audiences.length &&
      typeof window.PrewornWardrobe.getWardrobeForAudience === 'function'
        ? window.PrewornWardrobe.getWardrobeForAudience(audiences[0])
        : selected);
  }

  function resetWardrobeFacetState() {
    state.catalogFacetGeneration += 1;
    state.wardrobeSizeFallbackKey = '';
    state.wardrobeSizeFallbackSizes = [];
    state.catalogFilters = [];
    state.catalogFacetsDeferred = true;
    state.catalogFacetsLoading = false;
    state.catalogFacetsHydrated = false;
    state.catalogFacetsStale = true;
    state.catalogSecondaryFacetsLoading = false;
    state.quickFilterOrder = [];
    state.catalogFacetRetryCount = 0;
    state.colourFacetsReady = false;
    if (state.catalogFacetRetryTimer) {
      window.clearTimeout(state.catalogFacetRetryTimer);
      state.catalogFacetRetryTimer = null;
    }
    if (state.colourFacetTimer) {
      window.clearTimeout(state.colourFacetTimer);
      state.colourFacetTimer = null;
    }
    var quickFilters = document.getElementById('PrewornQuickFilters');
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    if (quickFilters) quickFilters.innerHTML = '';
    if (filterBody) filterBody.innerHTML = '<p class="pw-search__muted">Loading wardrobe filters…</p>';

    // A recently viewed wardrobe/scope can paint its filters immediately.
    // The normal deferred request still refreshes the counts in parallel.
    if (state.exactColourHex) hydrateInstantColourFacets();
    else hydrateCatalogFacetsFromSession();
  }

  function abortActiveColourRequest() {
    if (state.activeColourController) {
      try {
        state.activeColourController.abort();
      } catch (error) {
        // An already completed request needs no further handling.
      }
      state.activeColourController = null;
    }
  }

  function colourFacetSessionScope() {
    return catalogFacetSessionScope() + '|exact_colour=' +
      String(state.exactColourHex || '') + '|tolerance=' +
      String(state.exactColourTolerance || 12);
  }

  function prepareExactColourFacetState() {
    state.catalogFacetGeneration += 1;
    state.catalogFacetsDeferred = true;
    state.catalogFacetsLoading = false;
    state.catalogFacetsHydrated = false;
    state.catalogFacetsStale = true;
    state.catalogSecondaryFacetsLoading = false;
    state.catalogFacetRetryCount = 0;
    state.colourFacetsReady = false;
    if (state.catalogFacetRetryTimer) {
      window.clearTimeout(state.catalogFacetRetryTimer);
      state.catalogFacetRetryTimer = null;
    }
    if (state.colourFacetTimer) {
      window.clearTimeout(state.colourFacetTimer);
      state.colourFacetTimer = null;
    }

    // Keep the existing navigation usable while colour products load. Recent
    // exact counts for this shade can paint immediately; otherwise labels stay
    // count-free until the background facet response arrives.
    var cachedFilters = readCatalogFacetsFromSession(colourFacetSessionScope());
    if (cachedFilters.length) {
      paintColourNavigation(cachedFilters, true, true);
    } else {
      var instantFilters = state.catalogFilters.length
        ? state.catalogFilters
        : readColourNavigationFromStorage(catalogFacetSessionScope());
      if (!instantFilters.length) instantFilters = colourNavigationSeedFilters();
      paintColourNavigation(instantFilters, false, false);
    }
  }

  function refreshCatalogFacetsForCurrentScope() {
    state.catalogFacetGeneration += 1;
    state.catalogFacetsLoading = false;
    state.catalogSecondaryFacetsLoading = false;
    state.catalogFacetRetryCount = 0;
    state.quickFilterOrder = [];
    if (state.catalogFacetRetryTimer) {
      window.clearTimeout(state.catalogFacetRetryTimer);
      state.catalogFacetRetryTimer = null;
    }

    // Exact-colour cards and navigation are separate requests. Preserve the
    // existing controls now; exact values and counts refresh after the cards.
    if (state.exactColourHex) {
      state.catalogFacetsDeferred = true;
      state.catalogFacetsHydrated = false;
      state.catalogFacetsStale = true;
      state.colourFacetsReady = false;
      if (state.catalogFilters.length) {
        renderQuickFilters(state.catalogFilters);
        renderFilters(state.catalogFilters);
        renderActiveFilters();
      }
      return;
    }

    state.catalogFacetsDeferred = true;
    state.catalogFacetsHydrated = false;
    state.catalogFacetsStale = true;

    // Prefer an exact cached copy of this filtered scope. If there is none,
    // leave the existing controls visible until the parallel refresh arrives.
    var facetScope = catalogFacetSessionScope();
    var cachedFilters = readCatalogFacetsFromSession(facetScope);
    if (cachedFilters.length) {
      state.catalogFilters = cachedFilters;
      state.catalogFacetsHydrated = true;
    }
    if (state.catalogFilters.length) {
      renderQuickFilters(state.catalogFilters);
      renderFilters(state.catalogFilters);
      renderActiveFilters();
    }
  }

  function collectionWardrobeAudience() {
    var handle = String(state.collectionHandle || '').toLowerCase();
    if (handle.indexOf('womens-') === 0) return 'woman';
    if (handle.indexOf('mens-') === 0) return 'man';
    if (handle.indexOf('girls-') === 0) return 'girl';
    if (handle.indexOf('boys-') === 0) return 'boy';
    if (handle.indexOf('baby-') === 0) return 'baby';
    return '';
  }

  function guestWardrobeDepartmentLabel(audience) {
    return {
      man: "Men's",
      woman: "Women's",
      boy: 'Boys',
      girl: 'Girls',
      baby: 'Baby'
    }[audience] || 'My Wardrobe';
  }

  function wardrobeDisplayLabel(wardrobe, includeDefault) {
    if (!wardrobe) return 'My Wardrobe';
    if (!wardrobe.id) return guestWardrobeDepartmentLabel(wardrobe.audience);
    return String(wardrobe.name || 'My Wardrobe') +
      (includeDefault && wardrobe.isDefault ? ' — default' : '');
  }

  function wardrobeAccessibleName(wardrobe) {
    if (!wardrobe) return 'My Wardrobe';
    if (!wardrobe.id) return guestWardrobeDepartmentLabel(wardrobe.audience) + ' Wardrobe';
    return String(wardrobe.name || 'My Wardrobe');
  }

  function canAddCurrentCollectionToWardrobe() {
    var wardrobe = state.wardrobe;
    var context = collectionPreferenceContext();
    if (!state.wardrobeActive || !context ||
        !wardrobe || !Array.isArray(wardrobe.categories)) return false;
    var handle = String(context.categoryKey || '').toLowerCase();
    if (wardrobe.categories.indexOf(handle) !== -1) return false;
    var pageAudience = context.audience;
    return !pageAudience || pageAudience === wardrobe.audience;
  }

  function wardrobesForCurrentCollection() {
    var wardrobes = window.PrewornWardrobe && typeof window.PrewornWardrobe.getWardrobes === 'function'
      ? window.PrewornWardrobe.getWardrobes() : [];
    if (!hasExplicitWardrobeAudienceScope()) return wardrobes;
    var audiences = wardrobeAudiencesForCurrentScope();
    if (!audiences.length) return [];
    return wardrobes.filter(function (wardrobe) {
      return wardrobe && audiences.indexOf(wardrobe.audience) !== -1;
    });
  }

  function ensureApplicableCollectionWardrobe() {
    // An explicit Department owns the Wardrobe audience on search pages, just
    // as an audience-specific collection does on collection pages. Selecting a
    // matching Wardrobe emits one more change event; the selected-audience
    // guard below makes that event terminal and prevents the old recursion.
    if (!window.PrewornWardrobe || !hasExplicitWardrobeAudienceScope()) return false;
    var audiences = wardrobeAudiencesForCurrentScope();
    if (!audiences.length) return false;
    var applicable = wardrobesForCurrentCollection();
    if (!applicable.length) return false;
    var selected = typeof window.PrewornWardrobe.getWardrobe === 'function'
      ? window.PrewornWardrobe.getWardrobe() : state.wardrobe;
    if (selected && audiences.indexOf(selected.audience) !== -1) return false;
    var preferred = state.wardrobe && applicable.filter(function (wardrobe) {
      return Number(wardrobe.id || 0) === Number(state.wardrobe.id || 0);
    })[0] || applicable[0];
    var shouldActivate = state.wardrobeActive || hasLinkedWardrobe();
    if (preferred && preferred.id &&
        typeof window.PrewornWardrobe.selectWardrobe === 'function') {
      window.PrewornWardrobe.selectWardrobe(preferred.id, shouldActivate);
      return true;
    }
    if (preferred && preferred.audience &&
        typeof window.PrewornWardrobe.selectGuestWardrobe === 'function') {
      window.PrewornWardrobe.selectGuestWardrobe(preferred.audience, shouldActivate);
      return true;
    }
    return false;
  }

  function syncApplicableWardrobeForCurrentScope() {
    syncWardrobeState();
    state.wardrobeAudienceSyncing = true;
    try {
      ensureApplicableCollectionWardrobe();
    } finally {
      state.wardrobeAudienceSyncing = false;
    }
    syncWardrobeState();
    syncLinkedWardrobeParameters();
    renderWardrobeToggle();
  }

  function renderWardrobeToggle() {
    var toggle = document.getElementById('PrewornWardrobeSearchToggle');
    if (!toggle) return;
    var wardrobe = state.wardrobe;
    var ready = wardrobe && Array.isArray(wardrobe.categories) && wardrobe.categories.length;
    var isWardrobeOn = wardrobeScopeActive();
    toggle.classList.toggle('is-active', isWardrobeOn);
    toggle.classList.toggle('is-inactive', !!ready && !isWardrobeOn);
    toggle.classList.toggle('is-ready', !!ready);
    var title = toggle.querySelector('strong');
    var detail = toggle.querySelector('small');
    var button = toggle.querySelector('[data-pw-search-wardrobe]');
    var selector = toggle.querySelector('[data-pw-search-wardrobe-select]');
    var selectorShell = toggle.querySelector('[data-pw-search-wardrobe-select-shell]');
    var addButton = toggle.querySelector('[data-pw-add-collection]');
    var editButton = toggle.querySelector('[data-pw-edit-wardrobe]');
    var wardrobes = wardrobesForCurrentCollection();
    var canSwitch = ready && wardrobes.length > 1;
    var canAdd = ready && canAddCurrentCollectionToWardrobe();
    var preferenceContext = collectionPreferenceContext();
    if (title) {
      title.hidden = canSwitch;
      title.textContent = ready ? wardrobeDisplayLabel(wardrobe) : 'My Wardrobe';
    }
    if (selector) {
      selector.hidden = !canSwitch;
      selector.innerHTML = wardrobes.map(function (item) {
        var optionValue = item.id ? 'id:' + Number(item.id) : 'audience:' + item.audience;
        var optionSelected = wardrobe && (item.id
          ? Number(item.id) === Number(wardrobe.id)
          : item.audience === wardrobe.audience);
        return '<option value="' + escapeAttr(optionValue) + '"' + (optionSelected ? ' selected' : '') + '>' +
          escapeHtml(wardrobeDisplayLabel(item, true)) + '</option>';
      }).join('');
    }
    if (selectorShell) selectorShell.hidden = !canSwitch;
    if (detail) {
      var fallbackSizes = state.wardrobeSizeFallbackSizes.map(sizeDisplayLabel);
      var detailMessage = fallbackSizes.length
        ? 'No live pieces in ' + fallbackSizes.join(' or ') +
          ' here — showing every available size.'
        : (isWardrobeOn && wardrobeSearchUsesSoftScope())
        ? wardrobeSoftSearchMessage()
        : !ready
        ? 'Set sizes and categories for a more relevant edit'
        : (state.wardrobePageBrowse
          ? ''
          : (canAdd ? ((preferenceContext && preferenceContext.categoryLabel) ||
            state.collectionTitle || 'This collection') + ' isn’t in this wardrobe yet' : ''));
      detail.hidden = !detailMessage;
      detail.textContent = detailMessage;
    }
    if (button) {
      var stateLabel = button.querySelector('[data-pw-wardrobe-state]');
      if (stateLabel) {
        stateLabel.textContent = ready ? (isWardrobeOn ? 'On' : 'Off') : 'Set up';
      }
      button.setAttribute('aria-pressed', isWardrobeOn ? 'true' : 'false');
      button.setAttribute('aria-label', ready
        ? (isWardrobeOn ? 'Turn off ' : 'Turn on ') + wardrobeAccessibleName(wardrobe)
        : 'Set up My Wardrobe');
      button.hidden = state.wardrobePageBrowse && !!ready;
    }
    if (editButton) {
      editButton.hidden = !ready;
      editButton.setAttribute('aria-label', 'Edit ' + wardrobeAccessibleName(wardrobe));
    }
    if (addButton) {
      addButton.hidden = !canAdd;
      addButton.disabled = false;
      addButton.textContent = '+ Add to wardrobe';
      addButton.setAttribute('aria-label', 'Add ' + ((preferenceContext &&
        preferenceContext.categoryLabel) || state.collectionTitle || 'this collection') +
        ' to ' + wardrobeAccessibleName(wardrobe));
    }
    renderWardrobeSummary();
  }

  var SEARCH_QUERY = [
    'query PrewornSearch(',
    '  $query: String!,',
    '  $first: Int!,',
    '  $after: String,',
    '  $filters: [ProductFilter!],',
    '  $sortKey: SearchSortKeys,',
    '  $reverse: Boolean',
    ') {',
    '  search(',
    '    query: $query',
    '    first: $first',
    '    after: $after',
    '    types: [PRODUCT]',
    '    prefix: LAST',
    '    unavailableProducts: HIDE',
    '    productFilters: $filters',
    '    sortKey: $sortKey',
    '    reverse: $reverse',
    '  ) {',
    '    totalCount',
    '    pageInfo {',
    '      hasNextPage',
    '      hasPreviousPage',
    '      startCursor',
    '      endCursor',
    '    }',
    '    productFilters {',
    '      id',
    '      label',
    '      type',
    '      presentation',
    '      values {',
    '        id',
    '        label',
    '        count',
    '        input',
    '        swatch {',
    '          color',
    '          image {',
    '            image {',
    '              url',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '    nodes {',
    '      ... on Product {',
    '        id',
    '        handle',
    '        title',
    '        vendor',
    '        productType',
    '        createdAt',
    '        availableForSale',
    '        onlineStoreUrl',
    '        variants(first: 1) {',
    '          nodes {',
    '            sku',
    '          }',
    '        }',
    '        featuredImage {',
    '          url',
    '          altText',
    '          width',
    '          height',
    '        }',
    '        priceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '        compareAtPriceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var PRODUCTS_QUERY = [
    'query PrewornProducts(',
    '  $query: String,',
    '  $first: Int!,',
    '  $after: String,',
    '  $sortKey: ProductSortKeys,',
    '  $reverse: Boolean',
    ') {',
    '  products(',
    '    query: $query',
    '    first: $first',
    '    after: $after',
    '    sortKey: $sortKey',
    '    reverse: $reverse',
    '  ) {',
    '    pageInfo {',
    '      hasNextPage',
    '      hasPreviousPage',
    '      startCursor',
    '      endCursor',
    '    }',
    '    filters {',
    '      id',
    '      label',
    '      type',
    '      presentation',
    '      values {',
    '        id',
    '        label',
    '        count',
    '        input',
    '        swatch {',
    '          color',
    '          image {',
    '            image {',
    '              url',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '    nodes {',
    '      id',
    '      handle',
    '      title',
    '      vendor',
    '      productType',
    '      createdAt',
    '      availableForSale',
    '      onlineStoreUrl',
    '      variants(first: 1) {',
    '        nodes {',
    '          sku',
    '        }',
    '      }',
    '      featuredImage {',
    '        url',
    '        altText',
    '        width',
    '        height',
    '      }',
    '      priceRange {',
    '        minVariantPrice {',
    '          amount',
    '          currencyCode',
    '        }',
    '      }',
    '      compareAtPriceRange {',
    '        minVariantPrice {',
    '          amount',
    '          currencyCode',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var COLLECTION_QUERY = [
    'query PrewornCollection(',
    '  $handle: String!,',
    '  $first: Int!,',
    '  $after: String,',
    '  $filters: [ProductFilter!],',
    '  $sortKey: ProductCollectionSortKeys,',
    '  $reverse: Boolean',
    ') {',
    '  collection(handle: $handle) {',
    '    id',
    '    title',
    '    handle',
    '    products(',
    '      first: $first',
    '      after: $after',
    '      filters: $filters',
    '      sortKey: $sortKey',
    '      reverse: $reverse',
    '    ) {',
    '      pageInfo {',
    '        hasNextPage',
    '        hasPreviousPage',
    '        startCursor',
    '        endCursor',
    '      }',
    '      filters {',
    '        id',
    '        label',
    '        type',
    '        presentation',
    '        values {',
    '          id',
    '          label',
    '          count',
    '          input',
    '          swatch {',
    '            color',
    '            image {',
    '              image {',
    '                url',
    '              }',
    '            }',
    '          }',
    '        }',
    '      }',
    '      nodes {',
    '        id',
    '        handle',
    '        title',
    '        vendor',
    '        productType',
    '        createdAt',
    '        availableForSale',
    '        onlineStoreUrl',
    '        variants(first: 1) {',
    '          nodes {',
    '            sku',
    '          }',
    '        }',
    '        featuredImage {',
    '          url',
    '          altText',
    '          width',
    '          height',
    '        }',
    '        priceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '        compareAtPriceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function boot() {
    app = document.getElementById('PrewornWideSearch');

    if (!app) {
      return;
    }

    document.documentElement.classList.add('pw-catalogue-page');
    document.body.classList.add('pw-catalogue-page');

    var configuredCatalogApi = app.getAttribute('data-catalog-api') ||
      CONFIG.catalogApiUrl;

    if (!configuredCatalogApi && (!CONFIG.storefrontToken ||
        CONFIG.storefrontToken === 'PASTE_PUBLIC_STOREFRONT_TOKEN_HERE')) {
      renderFatal(
        'No catalogue data source has been configured.'
      );
      showNativeFallback();
      return;
    }

    state.mode = app.getAttribute('data-mode') || 'search';
    state.colourPage = app.getAttribute('data-colour-page') === 'true';
    state.wardrobePageBrowse = app.getAttribute('data-wardrobe-page-browse') === 'true';
    state.collectionHandle = app.getAttribute('data-collection-handle') || '';
    state.collectionTitle = app.getAttribute('data-collection-title') || '';
    state.collectionImage = app.getAttribute('data-collection-image') || '';
    state.collectionHero = app.getAttribute('data-collection-hero') === 'true';
    if (state.mode === 'wishlist') syncWishlistScope(readWishlist());

    var params = new URLSearchParams(window.location.search);
    var legacyExactColour = normaliseExactColour(params.get('colour'));
    var namedColourFilter = String(params.get('colour') || '').trim() &&
      !legacyExactColour;
    var configuredExactColour = normaliseExactColour(app.getAttribute('data-exact-colour'));
    state.exactColourHex = normaliseExactColour(
      params.get('colour_hex') || legacyExactColour ||
      (namedColourFilter ? '' : configuredExactColour)
    );
    state.exactColourTolerance = Math.max(3, Math.min(
      20,
      Number(params.get('colour_tolerance') || app.getAttribute('data-colour-tolerance') || 12) || 12
    ));
    state.exactColourName = exactColourName(state.exactColourHex);
    state.shadePickerHex = state.exactColourHex || configuredExactColour || '#5278A7';
    if (state.exactColourHex) {
      state.shadePickerOpen = true;
    }
    var requestedSort = (params.get('sort') ||
      app.getAttribute('data-default-sort') || '').toLowerCase();
    state.trending = requestedSort === 'trending';
    state.brandQuery = (params.get('brand') || '').trim();
    state.tagQueries = params.getAll('tags[]').map(function (value) {
      return String(value || '').trim();
    }).filter(function (value, index, values) {
      if (!value) return false;
      var key = value.toLowerCase();
      return values.findIndex(function (candidate) {
        return candidate.toLowerCase() === key;
      }) === index;
    });
    if (!state.tagQueries.length) {
      try {
        var configuredTags = JSON.parse(app.getAttribute('data-tag-queries') || '[]');
        if (Array.isArray(configuredTags)) {
          state.tagQueries = configuredTags.map(function (value) {
            return String(value || '').trim();
          }).filter(Boolean);
        }
      } catch (configuredTagError) {}
    }
    state.tagQuery = (params.get('tag') || app.getAttribute('data-tag-query') || '').trim();
    if (!state.tagQueries.length && state.tagQuery) state.tagQueries = [state.tagQuery];
    if (!state.tagQuery && state.tagQueries.length) state.tagQuery = state.tagQueries[0];
    state.tagLabel = (params.get('tag_label') || app.getAttribute('data-tag-label') || '').trim();
    if (!state.tagLabel && state.tagQuery) {
      state.tagLabel = state.tagQueries.map(function (tag) {
        return tag.replace(/^[^_]+_/, '').replace(/_/g, ' ').trim();
      }).join(' ');
    }
    function addInitialCatalogSelection(parameter, value) {
      value = String(value || '').trim();
      if (!value || isPlaceholderFacetValue(value)) return;
      state.selectedValues['catalog:' + parameter + ':' + value.toLowerCase()] = {
        input: {
          catalogParameter: parameter,
          catalogValue: value
        },
        label: value
      };
    }
    (params.get('type') || '').split(',').forEach(function (value) {
      addInitialCatalogSelection('type', value);
    });
    var initialDepartments = (params.get('department') || '').split(',').map(function (value) {
      return value.trim();
    }).filter(function (value) {
      return value !== '';
    });
    if (initialDepartments.length === 1 && /^kids?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Girls', 'Boys'];
    } else if (initialDepartments.length === 1 && /^womens?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Women', 'Womens'];
    } else if (initialDepartments.length === 1 && /^mens?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Men', 'Mens'];
    }
    var fixedTagAudiences = tagPageWardrobeAudiences();
    if (fixedTagAudiences.length === 1) {
      var fixedTagAudience = fixedTagAudiences[0];
      var departmentAudience = {
        women: 'woman', men: 'man', girls: 'girl', boys: 'boy', baby: 'baby'
      };
      initialDepartments = initialDepartments.filter(function (department) {
        return departmentAudience[departmentKey(department)] === fixedTagAudience;
      });
    }
    initialDepartments.forEach(function (department) {
      addInitialCatalogSelection('department', department);
    });
    [
      'category', 'style', 'vendor', 'size', 'colour',
      'condition', 'material', 'pattern'
    ].forEach(function (parameter) {
      (params.get(parameter) || '').split(',').forEach(function (value) {
        if (parameter === 'colour' && normaliseExactColour(value)) return;
        addInitialCatalogSelection(parameter, value);
      });
    });
    var initialNominalColour = selectedNominalColour();
    if (initialNominalColour) {
      state.shadePickerHex = nominalColourPickerHex(initialNominalColour) ||
        state.shadePickerHex;
      state.shadePickerOpen = true;
    }
    state.priceMin = (params.get('price_min') || '').trim();
    state.priceMax = (params.get('price_max') || '').trim();
    state.similarSku = (params.get('similar_sku') || '').trim();
    state.similarTitle = (params.get('similar_title') || '').trim();
    var initialSearchAnalyticsQuery = String(params.get('q') || '').trim();
    state.searchAnalyticsEligible = !!initialSearchAnalyticsQuery &&
      initialSearchAnalyticsQuery !== '*';
    state.query = normaliseBrowseQuery(
      initialSearchAnalyticsQuery ||
      app.getAttribute('data-query') ||
      state.brandQuery ||
      state.tagLabel ||
      ''
    );
    applyCanonicalDressSearchIntent(state.query);
    state.linkedWardrobeParameters = linkedWardrobeParameters(params);
    if (hasLinkedWardrobe() && window.PrewornWardrobe && !window.PrewornWardrobe.isActive()) {
      window.PrewornWardrobe.setActive(true);
    }
    syncWardrobeState();
    applySavedCollectionPreferenceFilter();

    var rawCollectionCount = app.getAttribute('data-collection-count');
    if (rawCollectionCount !== null && rawCollectionCount !== '') {
      state.collectionCount = Number(rawCollectionCount);
    }

    if (state.mode === 'collection') {
      state.sortKey = 'COLLECTION_DEFAULT';

      if (requestedSort === 'price_asc') {
        state.sortKey = 'PRICE';
        state.reverse = false;
      } else if (requestedSort === 'price_desc') {
        state.sortKey = 'PRICE';
        state.reverse = true;
      } else if (requestedSort === 'newest') {
        state.sortKey = 'CREATED';
        state.reverse = true;
      }

      if (!state.collectionHandle) {
        renderFatal('No collection handle supplied.');
        showNativeFallback();
        return;
      }
    } else if (state.mode === 'wishlist') {
      if (requestedSort === 'price_asc') {
        state.sortKey = 'PRICE';
        state.reverse = false;
      } else if (requestedSort === 'price_desc') {
        state.sortKey = 'PRICE';
        state.reverse = true;
      } else if (requestedSort === 'newest') {
        state.sortKey = 'CREATED';
        state.reverse = true;
      }
    } else {
      state.mode = 'search';

      if (state.trending) state.sortKey = 'TRENDING';
      else if (requestedSort === 'price_asc') {
        state.sortKey = 'PRICE';
        state.reverse = false;
      } else if (requestedSort === 'price_desc') {
        state.sortKey = 'PRICE';
        state.reverse = true;
      } else if (requestedSort === 'newest') {
        state.sortKey = 'CREATED';
        state.reverse = true;
      }

      syncEmptySearchLanding();
    }

    injectStyles();
    buildShell();
    if (state.mode === 'wishlist') renderWishlistPageState(readWishlist());
    bindStaticEvents();
    bindMobileCatalogueToolbarDock();
    bindToolbarActivityMetrics();
    requestCollectionStyleDirectory({ prepareOnly: true });
    bindPageCatalogueFilters();
    loadTagShadePalette();
    if (!state.emptySearchLanding) {
      if (state.exactColourHex) hydrateInstantColourFacets();
      else hydrateCatalogFacetsFromSession();
    }
    syncSortControl();
    // Start the small Size-only request immediately. Product cards and the
    // heavier full filter request must not determine whether this feature
    // appears on first navigation. Eligible tag pages resolve their real
    // Wardrobe collection asynchronously, then reuse this same panel.
    renderCollectionPreferencePrompt(
      state.catalogFilters,
      [],
      state.collectionCount
    );
    resolveTagWardrobePreferenceContext();
    bindBrowserHistory();
    window.addEventListener('preworn:wardrobe-change', function () {
      syncWardrobeState();
      if (state.wardrobeAudienceSyncing) {
        syncLinkedWardrobeParameters();
        renderWardrobeToggle();
        return;
      }
      // The contextual prompt has already changed its local size filter. Its
      // persistence notification must not trigger a duplicate catalogue load.
      if (state.preferencePromptSaving) {
        renderWardrobeToggle();
        return;
      }
      applySavedCollectionPreferenceFilter();
      if (activateLinkedWardrobeIfReady()) return;
      if (ensureWardrobePageActive()) return;
      syncLinkedWardrobeParameters();
      if (ensureApplicableCollectionWardrobe()) return;
      resetWardrobeSummary();
      resetWardrobeFacetState();
      renderWardrobeToggle();
      resetPagination();
      if (!wardrobePageCanBrowse()) {
        renderWardrobePageSetup();
        return;
      }
      navigateWithHistory(1, null);
    });
    if (state.mode === 'wishlist') {
      window.addEventListener('preworn:wishlist-change', refreshWishlistCatalogue);
      window.addEventListener('storage', function (event) {
        if (event.key === CONFIG.wishlistStorageKey) refreshWishlistCatalogue();
      });
    }
    var initialPage = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
    if (!ensureApplicableCollectionWardrobe() && !ensureWardrobePageActive()) {
      renderWardrobeToggle();
      if (!wardrobePageCanBrowse()) renderWardrobePageSetup();
      else if (!state.emptySearchLanding) loadPage(initialPage, null);
    }
  }

  function headingFilterGroups() {
    var definitions = {
      style: 'Style',
      type: 'Type',
      product_type: 'Collection type',
      category_scope: 'Category',
      pattern: 'Pattern',
      vendor: 'Brand',
      category: 'Category',
      material: 'Material',
      size: 'Size',
      condition: 'Condition',
      colour: 'Colour',
      department: 'Department'
    };
    var priority = [
      'style', 'type', 'product_type', 'category_scope', 'pattern', 'vendor', 'category',
      'material', 'size', 'condition', 'colour', 'department'
    ];
    var tagScope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var groups = {};
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = selected.input || {};
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (!definitions[parameter]) return;
      if (parameter === 'department' &&
          (!state.query || state.query === '*') && selectedDepartmentLabel()) return;
      var value = String(selected.label || input.catalogValue || '').trim();
      if (!value) return;

      var scopedValues = String(tagScope[parameter] || '').split(',').map(function (item) {
        return item.trim().toLowerCase();
      });
      if (scopedValues.indexOf(value.toLowerCase()) !== -1) return;
      if (parameter === 'vendor' && state.brandQuery &&
          value.toLowerCase() === state.brandQuery.toLowerCase()) return;

      if (parameter === 'department') {
        if (/^womens?$/i.test(value)) value = 'Women';
        else if (/^mens?$/i.test(value)) value = 'Men';
      }
      groups[parameter] = groups[parameter] || [];
      if (groups[parameter].map(function (item) {
        return item.toLowerCase();
      }).indexOf(value.toLowerCase()) === -1) {
        groups[parameter].push(value);
      }
    });
    return priority.filter(function (parameter) {
      return groups[parameter] && groups[parameter].length;
    }).map(function (parameter) {
      return {
        label: definitions[parameter],
        values: groups[parameter]
      };
    });
  }

  function headingFilterContextHtml() {
    var groups = headingFilterGroups();
    if (!groups.length) return '';
    return '<span class="pw-search__query-context">' + groups.map(function (group) {
      return '<span><b>' + escapeHtml(group.label) + ':</b> ' +
        escapeHtml(group.values.join(' / ')) + '</span>';
    }).join('<i aria-hidden="true">·</i>') + '</span>';
  }

  function heroResultContextGroups() {
    var groups = headingFilterGroups().map(function (group) {
      return {
        label: String(group.label || '').trim(),
        values: (Array.isArray(group.values) ? group.values : []).map(function (value) {
          return String(value || '').trim();
        }).filter(Boolean)
      };
    }).filter(function (group) {
      return group.label && group.values.length;
    });
    var productQuery = activeProductSearchQuery();
    if (productQuery) groups.unshift({ label: 'Search', values: [productQuery] });
    if (state.exactColourHex && !groups.some(function (group) {
      return group.label.toLowerCase() === 'colour';
    })) {
      groups.push({
        label: 'Exact colour',
        values: [state.exactColourName || exactColourName(state.exactColourHex)]
      });
    }
    if (state.priceMin || state.priceMax) {
      groups.push({
        label: 'Price',
        values: [state.priceMin && state.priceMax
          ? '£' + state.priceMin + '–£' + state.priceMax
          : state.priceMin ? 'Over £' + state.priceMin : 'Under £' + state.priceMax]
      });
    }
    return groups;
  }

  function publishImageHeroResult(totalCount) {
    if (!app || totalCount === null || typeof totalCount === 'undefined' ||
        totalCount === '' || !isFinite(Number(totalCount))) return;
    var numericTotal = Math.max(0, Number(totalCount));
    var contextGroups = heroResultContextGroups();
    app.setAttribute('data-pw-hero-result-total', String(numericTotal));
    app.setAttribute('data-pw-hero-result-context', JSON.stringify(contextGroups));
    app.dispatchEvent(new CustomEvent('preworn:hero-result-count', {
      bubbles: true,
      detail: {
        totalCount: numericTotal,
        contextGroups: contextGroups
      }
    }));
  }

  function searchHeadingHtml() {
    var heading;
    var selectedDepartment = selectedDepartmentLabel();
    var activeWardrobeDepartment = wardrobeScopeActive()
      ? wardrobeDepartmentLabel() : '';
    if (state.colourPage && state.exactColourHex) {
      heading = 'Matching results for <strong>' +
        escapeHtml(state.exactColourName || exactColourName(state.exactColourHex)) +
        '</strong>';
    } else if (state.mode === 'collection') {
      heading = escapeHtml(state.collectionTitle);
    } else if (state.trending) {
      heading = state.tagQuery
        ? 'Shop <strong>' + escapeHtml(state.tagLabel) + ' trending</strong>'
        : 'Shop <strong>what’s trending</strong>';
    } else if (state.tagQuery) {
      heading = 'Shop <strong>' + escapeHtml(state.tagLabel) + '</strong>';
    } else if (state.similarSku) {
      heading = '<span class="pw-search__similar-kicker">SIMILAR TO THE ONE JUST BOUGHT</span>' +
        '<strong>' + escapeHtml(state.similarTitle || state.query || 'One-off find') + '</strong>';
    } else if ((!state.query || state.query === '*') && selectedDepartment) {
      heading = 'Shop <strong>' + escapeHtml(selectedDepartment) + '</strong>';
    } else if ((!state.query || state.query === '*') && activeWardrobeDepartment) {
      heading = 'Shop <strong>' + escapeHtml(activeWardrobeDepartment) + '</strong>';
    } else if (!state.query) {
      heading = '';
    } else if (state.query === '*' && (state.wardrobeActive || hasLinkedWardrobe())) {
      heading = 'Shop <strong>your wardrobe</strong>';
    } else if (state.query === '*') {
      heading = 'Shop <strong>everything</strong>';
    } else {
      // Search pages use the editable in-page search field below rather than
      // repeating the submitted term as a static heading.
      heading = '';
    }
    var context = headingFilterContextHtml();
    return heading ? heading + context : context;
  }

  function renderSearchHeading() {
    var heading = document.querySelector('.pw-search__query');
    if (heading) heading.innerHTML = searchHeadingHtml();
  }

  function exactColourControlHtml() {
    var active = !!state.exactColourHex;
    var value = state.exactColourHex || tagShadeBaseHex() || '#5278A7';
    var name = active
      ? (state.exactColourName || exactColourName(value))
      : 'Choose exact colour';
    var detail = active ? value : 'Actual garment colour';
    return '<section class="pw-exact-colour' + (active ? ' is-active' : '') + '" data-pw-exact-colour>' +
      '<div class="pw-exact-colour__head"><strong>Exact colour</strong></div>' +
      '<label class="pw-exact-colour__picker">' +
        '<input type="color" value="' + escapeAttr(value) + '" data-pw-exact-colour-input aria-label="Choose an exact garment colour">' +
        '<span class="pw-exact-colour__swatch" data-pw-exact-colour-swatch style="--pw-exact:' + escapeAttr(value) + '"></span>' +
        '<span><b data-pw-exact-colour-name>' + escapeHtml(name) + '</b><small data-pw-exact-colour-hex>' + escapeHtml(detail) + '</small></span>' +
      '</label>' +
      '<label class="pw-exact-colour__tolerance"' + (active ? '' : ' hidden') + '>' +
        '<span><b>Match sensitivity</b><em data-pw-exact-tolerance-label>' + exactToleranceLabel(state.exactColourTolerance) + '</em></span>' +
        '<input type="range" min="3" max="20" value="' + Number(state.exactColourTolerance) + '" data-pw-exact-tolerance>' +
      '</label>' +
      '<button type="button" class="pw-exact-colour__clear" data-pw-clear-exact-colour' + (active ? '' : ' hidden') + '>Remove selected colour</button>' +
    '</section>';
  }

  function exactToleranceLabel(value) {
    value = Number(value || 12);
    if (value <= 3) return 'Exceptional';
    if (value <= 6) return 'Very close';
    if (value <= 12) return 'Close';
    return 'Broader';
  }

  function tagShadeLabel() {
    return String(app && app.getAttribute('data-shade-label') || state.tagLabel || '').trim();
  }

  function tagShadeBaseHex() {
    if (!app || app.getAttribute('data-shade-picker') !== 'true') return '';
    var configured = normaliseExactColour(app.getAttribute('data-shade-base'));
    if (configured) return configured;
    var names = {
      black: '#111111', white: '#F7F7F4', grey: '#888B8A', gray: '#888B8A',
      blue: '#2455D6', navy: '#182644', red: '#D92F2F', pink: '#EF9DB8',
      green: '#3B8B42', purple: '#7B4B9E', yellow: '#F2DF31', orange: '#E98B2A',
      brown: '#86563B', beige: '#DFCFAC', cream: '#F1EAD8', ivory: '#F4F0DF',
      gold: '#C7A441', silver: '#C8C8C8', khaki: '#8C8A59', burgundy: '#6D1F2B',
      maroon: '#6D1F2B', turquoise: '#39A7A7', teal: '#287D7A'
    };
    return names[tagShadeLabel().toLowerCase()] || '';
  }

  function usesFamilyColourPicker() {
    return !!app && app.getAttribute('data-family-colour-picker') === 'true';
  }

  function tagShadeRoot() {
    if (!app) return null;
    if (app.querySelector('[data-pw-tag-shade-list]')) return app;
    var mountedFamilyPicker = document.querySelector('[data-pw-family-shade-host]');
    if (mountedFamilyPicker && mountedFamilyPicker.querySelector('[data-pw-tag-shade-list]')) {
      return mountedFamilyPicker;
    }
    var finder = typeof app.closest === 'function'
      ? app.closest('[data-pw-colour-finder]') : null;
    return finder && finder.querySelector('[data-pw-tag-shade-list]') ? finder : app;
  }

  function tagShadePickerHtml() {
    var baseHex = tagShadeBaseHex();
    if (!baseHex || usesFamilyColourPicker()) return '';
    return '<section class="pw-tag-shades" data-pw-tag-shades aria-labelledby="PrewornTagShadesTitle">' +
      '<header class="pw-tag-shades__head">' +
        '<span><small>COLOUR MATCH</small><strong id="PrewornTagShadesTitle">Pick your shade</strong></span>' +
        '<p>Actual ' + escapeHtml(tagShadeLabel().toLowerCase()) + ' shades found in our catalogue</p>' +
      '</header>' +
      '<div class="pw-tag-shades__list" data-pw-tag-shade-list aria-live="polite" aria-busy="true">' +
        '<button type="button" class="pw-tag-shade is-active" data-pw-tag-shade-all aria-pressed="true">' +
          '<i style="--pw-tag-shade:' + escapeAttr(baseHex) + ';--preset-colour:' +
            escapeAttr(baseHex) + '" aria-hidden="true"></i>' +
        '<span>All ' + escapeHtml(tagShadeLabel()) + '</span>' +
        '</button>' +
        '<span class="pw-tag-shades__loading"><i aria-hidden="true"></i>Finding real shades…</span>' +
      '</div>' +
    '</section>';
  }

  function tagShadeRgb(hex) {
    hex = normaliseExactColour(hex);
    return hex ? [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16)
    ] : null;
  }

  function tagShadeRgbDistance(left, right) {
    left = tagShadeRgb(left);
    right = tagShadeRgb(right);
    if (!left || !right) return Infinity;
    return Math.sqrt(
      Math.pow(left[0] - right[0], 2) +
      Math.pow(left[1] - right[1], 2) +
      Math.pow(left[2] - right[2], 2)
    );
  }

  function tagShadeDisplayName(hex) {
    var rgb = tagShadeRgb(hex);
    var family = exactColourName(hex);
    if (!rgb) return family || 'Shade';
    var maximum = Math.max(rgb[0], rgb[1], rgb[2]) / 255;
    var minimum = Math.min(rgb[0], rgb[1], rgb[2]) / 255;
    var lightness = (maximum + minimum) / 2;
    if (family === 'Red' || family === 'Burgundy') {
      if (lightness < .2) return 'Oxblood';
      if (lightness < .29) return 'Wine';
      if (lightness < .38) return family === 'Burgundy' ? 'Burgundy' : 'Crimson';
      if (lightness > .67) return 'Soft coral';
      if (rgb[1] > rgb[2] * 1.35) return 'Vermilion';
      return 'Scarlet';
    }
    if (lightness < .24 && family !== 'Black') return 'Deep ' + family.toLowerCase();
    if (lightness > .76 && family !== 'White') return 'Soft ' + family.toLowerCase();
    return family || 'Shade';
  }

  function renderTagShadeSelection() {
    var shadeRoot = tagShadeRoot();
    if (!shadeRoot) return;
    var all = shadeRoot.querySelector('[data-pw-tag-shade-all]');
    if (all) {
      var allActive = !state.exactColourHex;
      all.classList.toggle('is-active', allActive);
      all.setAttribute('aria-pressed', allActive ? 'true' : 'false');
    }
    var buttons = shadeRoot.querySelectorAll('[data-pw-tag-shade]');
    for (var i = 0; i < buttons.length; i++) {
      var active = normaliseExactColour(buttons[i].getAttribute('data-pw-tag-shade')) ===
        state.exactColourHex;
      buttons[i].classList.toggle('is-active', active);
      buttons[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function selectDistinctTagShades(items, limit) {
    var candidates = [];
    var seen = {};
    (Array.isArray(items) ? items : []).forEach(function (item) {
      var match = item && item.visualColour ? item.visualColour : {};
      if (item && item.hex && !item.visualColour) match = item;
      var hex = normaliseExactColour(match.matchedHex || match.hex);
      if (!hex || seen[hex]) return;
      seen[hex] = true;
      candidates.push({
        hex: hex,
        percentage: Math.max(0, Math.min(100, Number(match.matchPercentage) || 0))
      });
    });
    var selected = [];
    candidates.forEach(function (candidate) {
      if (selected.length >= limit) return;
      var distinct = selected.every(function (existing) {
        return tagShadeRgbDistance(existing.hex, candidate.hex) >= 24;
      });
      if (distinct) selected.push(candidate);
    });
    if (selected.length < Math.min(6, limit)) {
      candidates.forEach(function (candidate) {
        if (selected.length >= limit || selected.some(function (existing) {
          return existing.hex === candidate.hex;
        })) return;
        var distinct = selected.every(function (existing) {
          return tagShadeRgbDistance(existing.hex, candidate.hex) >= 12;
        });
        if (distinct) selected.push(candidate);
      });
    }
    return selected;
  }

  function renderTagShadePalette(items) {
    var shadeRoot = tagShadeRoot();
    var list = shadeRoot && shadeRoot.querySelector('[data-pw-tag-shade-list]');
    if (!list) return;
    var shades = selectDistinctTagShades(items, 8);
    if (!shades.length) {
      var shell = shadeRoot.querySelector('[data-pw-tag-shades]');
      if (shell) shell.hidden = true;
      return;
    }
    var baseHex = tagShadeBaseHex();
    var html = '<button type="button" class="pw-tag-shade" data-pw-tag-shade-all aria-pressed="false">' +
      '<i style="--pw-tag-shade:' + escapeAttr(baseHex) + ';--preset-colour:' +
        escapeAttr(baseHex) + '" aria-hidden="true"></i>' +
      '<span>All ' + escapeHtml(tagShadeLabel()) + '</span></button>';
    shades.forEach(function (shade) {
      var name = tagShadeDisplayName(shade.hex);
      html += '<button type="button" class="pw-tag-shade" data-pw-tag-shade="' +
        escapeAttr(shade.hex) + '" data-pw-tag-shade-name="' + escapeAttr(name) +
        '" aria-pressed="false" aria-label="Shop ' + escapeAttr(name) + ' shade">' +
        '<i style="--pw-tag-shade:' + escapeAttr(shade.hex) + ';--preset-colour:' +
          escapeAttr(shade.hex) + '" aria-hidden="true"></i>' +
        '<span>' + escapeHtml(name) + '</span></button>';
    });
    list.innerHTML = html;
    list.setAttribute('aria-busy', 'false');
    var all = list.querySelector('[data-pw-tag-shade-all]');
    if (all) all.addEventListener('click', clearExactColour);
    var buttons = list.querySelectorAll('[data-pw-tag-shade]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        applyExactColour(
          this.getAttribute('data-pw-tag-shade'),
          8,
          this.getAttribute('data-pw-tag-shade-name')
        );
      });
    }
    renderTagShadeSelection();
  }

  async function loadTagShadePalette() {
    var baseHex = tagShadeBaseHex();
    var shadeRoot = tagShadeRoot();
    var list = shadeRoot && shadeRoot.querySelector('[data-pw-tag-shade-list]');
    if (!baseHex || !list) return;
    var endpoint = app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl;
    if (!endpoint) return;
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', 'shade_palette');
    url.searchParams.set('hex', baseHex);
    url.searchParams.set('tolerance', '18');
    url.searchParams.set('limit', '8');
    var structuredScope = structuredFacetScopeFromTags(state.tagQueries);
    if (structuredScope) {
      Object.keys(structuredScope).forEach(function (parameter) {
        url.searchParams.set(parameter, structuredScope[parameter]);
      });
    } else if (state.tagQueries.length === 1) {
      url.searchParams.set('tag', state.tagQueries[0]);
    } else {
      state.tagQueries.forEach(function (tag) { url.searchParams.append('tags[]', tag); });
    }
    var controller = typeof window.AbortController === 'function'
      ? new window.AbortController() : null;
    var timeout = controller ? window.setTimeout(function () { controller.abort(); }, 20000) : null;
    try {
      var response = await fetch(url.toString(), {
        method: 'GET', mode: 'cors', headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
      var payload = await response.json();
      if (!response.ok || !payload || payload.source !== 'preworn_visual_colour_palette') {
        throw new Error('Shade palette unavailable');
      }
      renderTagShadePalette(payload.shades);
    } catch (error) {
      var shell = shadeRoot && shadeRoot.querySelector('[data-pw-tag-shades]');
      if (shell) shell.hidden = true;
      console.warn('[Preworn Browse] Tag shade palette unavailable.', error);
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }
  }

  function colourMatchPercentage(match) {
    match = match || {};
    var suppliedPercentage = Number(match.matchPercentage);
    if (isFinite(suppliedPercentage)) {
      return Math.max(0, Math.min(100, Math.round(suppliedPercentage)));
    }
    var distance = Number(match.distance);
    if (!isFinite(distance)) return '';

    // ΔE is not itself a percentage. Use a perceptual fall-off and reduce the
    // shopper-facing score when the matching colour is only a small accent or
    // a secondary cluster in the garment image.
    var distanceScore = 100 / (1 + Math.pow(distance / 15, 2));
    var coverage = Number(match.coverage);
    var coverageFactor = isFinite(coverage)
      ? .72 + .28 * Math.min(1, Math.max(0, coverage) / .55)
      : 1;
    var rank = Math.max(1, Number(match.dominanceRank || 1));
    var rankFactor = rank === 1 ? 1 : (rank === 2 ? .96 : .92);
    return Math.max(0, Math.min(100, Math.round(
      distanceScore * coverageFactor * rankFactor
    )));
  }

  function colourMatchLabel(percentage) {
    percentage = Number(percentage);
    if (percentage >= 95) return 'Excellent match';
    if (percentage >= 85) return 'Very close';
    if (percentage >= 70) return 'Close match';
    if (percentage >= 55) return 'Similar shade';
    return 'Related shade';
  }

  function usesInlineShadePicker() {
    return !!app && !state.colourPage &&
      !app.closest('[data-pw-colour-finder]');
  }

  function inlineShadeCustomDialogHtml(initial) {
    initial = normaliseExactColour(initial) || '#5278A7';
    var hsv = exactColourHsv(initial);
    return '<div class="pwcolour__custom-dialog" data-pw-search-custom-dialog ' +
      'role="dialog" aria-modal="true" aria-labelledby="PrewornSearchCustomColourHeading" hidden>' +
        '<div class="pwcolour__custom-panel" data-pw-search-custom-panel>' +
          '<div class="pwcolour__custom-head">' +
            '<div><span>EXACT COLOUR</span>' +
              '<h2 id="PrewornSearchCustomColourHeading">Customise your shade</h2>' +
              '<p>The controls start from your currently selected colour.</p></div>' +
            '<button type="button" data-pw-search-custom-cancel ' +
              'aria-label="Close exact colour controls">×</button>' +
          '</div>' +
          '<div class="pwcolour__custom-current" aria-live="polite">' +
            '<i data-pw-search-custom-preview aria-hidden="true"></i>' +
            '<div><small>CHOSEN COLOUR</small>' +
              '<strong data-pw-search-custom-name>' + escapeHtml(exactColourName(initial)) + '</strong>' +
              '<code data-pw-search-custom-value>' + escapeHtml(initial) + '</code></div>' +
            '<label class="pwcolour__custom-hex"><span>HEX</span>' +
              '<input type="text" value="' + escapeAttr(initial) + '" maxlength="7" ' +
                'autocapitalize="characters" autocomplete="off" spellcheck="false" ' +
                'data-pw-search-custom-hex aria-label="Exact hexadecimal colour"></label>' +
          '</div>' +
          '<div class="pwcolour__custom-sliders">' +
            '<label><span>Hue <output data-pw-search-custom-hue-output>' +
              Math.round(hsv.h) + '°</output></span>' +
              '<input type="range" min="0" max="359" value="' + Math.round(hsv.h) + '" step="1" ' +
                'data-pw-search-custom-hue aria-label="Custom colour hue"></label>' +
            '<label><span>Saturation <output data-pw-search-custom-saturation-output>' +
              Math.round(hsv.s) + '%</output></span>' +
              '<input type="range" min="0" max="100" value="' + Math.round(hsv.s) + '" step="1" ' +
                'data-pw-search-custom-saturation aria-label="Custom colour saturation"></label>' +
            '<label><span>Brightness <output data-pw-search-custom-brightness-output>' +
              Math.round(hsv.v) + '%</output></span>' +
              '<input type="range" min="0" max="100" value="' + Math.round(hsv.v) + '" step="1" ' +
                'data-pw-search-custom-brightness aria-label="Custom colour brightness"></label>' +
          '</div>' +
          '<div class="pwcolour__custom-actions">' +
            '<button type="button" data-pw-search-custom-cancel>Cancel</button>' +
            '<button type="button" data-pw-search-custom-apply>Use this colour</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function inlineShadePickerHtml() {
    if (!usesInlineShadePicker()) return '';
    var initial = activeShadePickerHex();
    var hsv = exactColourHsv(initial);
    var presets = [
      ['#111314', 'Black'], ['#FBFAF5', 'White'], ['#8F9692', 'Grey'],
      ['#496FAC', 'Blue'], ['#447757', 'Green'], ['#B9353C', 'Red'],
      ['#D890AA', 'Pink'], ['#74513D', 'Brown'], ['#D9C3A5', 'Beige'],
      ['#71518B', 'Purple'], ['#E3BF31', 'Yellow'], ['#DF722D', 'Orange'],
      ['conic-gradient(#D64C56,#E4BE35,#4F8A5D,#4775B8,#8B58A1,#D64C56)',
        'Multicoloured']
    ];
    return '<section class="pwcolour__picker-wrap pwcolour__picker-wrap--full-width" ' +
      'data-pw-search-shade-picker aria-label="Refine the selected colour shade"' +
      (inlineShadePickerIsOpen() ? '' : ' hidden') + '>' +
        '<div class="pwcolour__picker" data-pw-search-shade-panel style="--pw-picker-hue:' +
          Math.round(hsv.h) + ';--pw-picker-colour:' + escapeAttr(initial) +
          ';--pw-picker-x:' + hsv.s.toFixed(2) + '%;--pw-picker-y:' +
          (100 - hsv.v).toFixed(2) + '%;">' +
          '<div class="pwcolour__picker-field" data-pw-search-shade-field role="slider" tabindex="0" ' +
            'aria-label="Pick colour saturation and brightness" aria-valuemin="0" aria-valuemax="100" ' +
            'aria-valuenow="' + Math.round(hsv.v) + '" aria-valuetext="' +
            escapeAttr(exactColourName(initial) + ', ' + initial) + '">' +
            '<span class="pwcolour__picker-marker" data-pw-search-shade-marker aria-hidden="true"></span>' +
            '<span class="pwcolour__picker-hint"><b>Pick Your Shade</b></span>' +
          '</div>' +
          '<div class="pwcolour__picker-actions">' +
            '<div class="pwcolour__picker-controls">' +
              '<button class="pwcolour__picker-tool" type="button" data-pw-search-shade-eyedropper ' +
                'aria-label="Pick a colour from your screen" title="Pick a colour from your screen">' +
                '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m19 3 2 2-5 5 1 1-3 3-2-2-6 6-3 1 1-3 6-6-2-2 3-3 1 1 5-5Z"/></svg>' +
              '</button>' +
              '<button class="pwcolour__picker-tool pwcolour__picker-photo" type="button" ' +
                'data-pw-search-shade-photo aria-label="Choose a colour from a photo" title="Choose a colour from a photo">' +
                '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h4l1.5-2h5L16 7h4v12H4V7Z"/><circle cx="12" cy="13" r="3.5"/></svg>' +
              '</button>' +
              '<span class="pwcolour__picker-swatch" data-pw-search-shade-swatch style="--shade:' +
                escapeAttr(initial) + '" aria-hidden="true"></span>' +
              '<button class="pwcolour__picker-exact" type="button" data-pw-search-shade-exact ' +
                'aria-label="Choose an exact hexadecimal colour" title="Choose an exact colour"><span>HEX</span></button>' +
            '</div>' +
            '<nav class="pwcolour__picker-presets" aria-label="Quick colour choices">' +
              presets.map(function (preset) {
                return '<button type="button" style="--preset-colour:' + preset[0] + '" data-pw-search-shade-preset="' +
                  preset[1] + '"><i></i><span>' + (preset[2] || preset[1]) + '</span></button>';
              }).join('') +
            '</nav>' +
          '</div>' +
        '</div>' +
      '</section>' +
      inlineShadeCustomDialogHtml(initial);
  }

  function inlineShadePickerRoot() {
    if (inlineShadePicker && inlineShadePicker.isConnected !== false) {
      return inlineShadePicker;
    }
    inlineShadePicker = app && app.querySelector
      ? app.querySelector('[data-pw-search-shade-picker]') : null;
    return inlineShadePicker;
  }

  function mountInlineShadePickerBelowSpectrum() {
    var shell = app && app.querySelector
      ? app.querySelector('[data-pw-search-shade-picker]') : null;
    var customDialog = app && app.querySelector
      ? app.querySelector('[data-pw-search-custom-dialog]') : null;
    inlineShadePicker = shell;
    if (!shell) return false;

    var spectrum = document.querySelector('[data-pw-colour-spectrum]');
    var headerSection = spectrum && spectrum.closest
      ? spectrum.closest('.shopify-section') : null;
    if (!headerSection || !headerSection.parentNode) return false;

    var previousHost = document.querySelector('[data-pw-inline-shade-host]');
    if (previousHost && previousHost.parentNode) {
      previousHost.parentNode.removeChild(previousHost);
    }
    var host = document.createElement('div');
    host.className = 'pwcolour pwcolour--inline-browse pwcolour-inline-shade-host';
    host.setAttribute('data-pw-inline-shade-host', '');
    host.setAttribute('aria-label', 'Colour shade controls');
    headerSection.parentNode.insertBefore(host, headerSection.nextSibling);
    host.appendChild(shell);
    if (customDialog) host.appendChild(customDialog);
    return true;
  }

  function mountFamilyShadePickerBelowSpectrum() {
    if (!usesFamilyColourPicker() || !app || typeof app.closest !== 'function') return false;
    var finder = app.closest('[data-pw-colour-finder]');
    var pickerWrap = finder && finder.querySelector('[data-pw-family-shade-picker]');
    if (!finder || !pickerWrap) return false;

    var spectrum = document.querySelector('[data-pw-colour-spectrum]');
    var headerSection = spectrum && spectrum.closest
      ? spectrum.closest('.shopify-section') : null;
    if (!headerSection || !headerSection.parentNode) return false;

    var brandPage = app.closest('[data-pw-brand-page]');
    var imageTagLanding = !!(brandPage &&
      brandPage.classList.contains('pwbrandpage--image-hero'));
    var existingHost = document.querySelector('[data-pw-family-shade-host]');
    if (existingHost && existingHost.contains(pickerWrap)) {
      bindMobileProgressiveFamilyShade(existingHost, brandPage, imageTagLanding);
      return true;
    }
    if (existingHost && existingHost.parentNode) {
      existingHost.parentNode.removeChild(existingHost);
    }
    var host = document.createElement('div');
    host.className = 'pwcolour pwcolour--tag pwcolour-family-shade-host';
    host.setAttribute('data-pw-family-shade-host', '');
    host.setAttribute('data-colour-family', finder.getAttribute('data-colour-family') || '');
    host.setAttribute('aria-label', 'Pick a ' +
      (finder.getAttribute('data-colour-family') || 'colour') + ' shade');
    if (imageTagLanding && window.matchMedia('(max-width: 760px)').matches) {
      host.hidden = true;
      host.setAttribute('aria-hidden', 'true');
    }
    pickerWrap.classList.remove('pwcolour__picker-wrap--embedded');
    headerSection.parentNode.insertBefore(host, headerSection.nextSibling);
    host.appendChild(pickerWrap);
    bindMobileProgressiveFamilyShade(host, brandPage, imageTagLanding);
    return true;
  }

  function bindMobileProgressiveFamilyShade(host, brandPage, enabled) {
    if (!host || !enabled || host._pwMobileProgressiveShadeReady) return;
    host._pwMobileProgressiveShadeReady = true;
    host.classList.add('pwcolour-family-shade-host--progressive');
    host.setAttribute('data-pw-mobile-progressive-shade', '');

    var mobileViewport = window.matchMedia('(max-width: 760px)');
    var mobileShadeOpen = false;

    function syncVisibility() {
      var hide = mobileViewport.matches && !mobileShadeOpen;
      host.hidden = hide;
      host.classList.toggle('is-mobile-shade-open', mobileViewport.matches && mobileShadeOpen);
      if (hide) host.setAttribute('aria-hidden', 'true');
      else host.removeAttribute('aria-hidden');
    }

    function revealFromHeaderSpectrum(event) {
      var detail = event && event.detail || {};
      if (!mobileViewport.matches || detail.source !== 'header-spectrum') return;
      mobileShadeOpen = true;
      syncVisibility();
      brandPage.dispatchEvent(new CustomEvent('preworn:tag-hero-collapse', {
        detail: { reason: 'colour-spectrum' }
      }));
    }

    function resetAfterHeroReturn(event) {
      var detail = event && event.detail || {};
      if (!mobileViewport.matches || detail.reason !== 'top') return;
      mobileShadeOpen = false;
      syncVisibility();
    }

    function handleViewportChange() {
      // The progressive disclosure is mobile-only. Desktop always retains the
      // complete shade field and preset row beneath the global spectrum.
      syncVisibility();
    }

    window.addEventListener('preworn:exact-colour-change', revealFromHeaderSpectrum);
    brandPage.addEventListener('preworn:tag-hero-will-expand', resetAfterHeroReturn);
    if (typeof mobileViewport.addEventListener === 'function') {
      mobileViewport.addEventListener('change', handleViewportChange);
    } else if (typeof mobileViewport.addListener === 'function') {
      mobileViewport.addListener(handleViewportChange);
    }
    syncVisibility();
  }

  function paintInlineShadePicker(hex) {
    var shell = inlineShadePickerRoot();
    if (!shell) return;
    var active = inlineShadePickerIsOpen();
    shell.hidden = !active;
    app.classList.toggle('has-exact-colour', active);
    if (app.parentElement) {
      app.parentElement.classList.toggle('pwcolour-inline-host', active);
    }
    var sectionHost = app.closest('.shopify-section');
    if (sectionHost) sectionHost.classList.toggle('pwcolour-inline-section-host', active);
    if (!active) return;
    hex = normaliseExactColour(hex) || activeShadePickerHex();
    if (!hex) return;
    state.shadePickerHex = hex;
    var hsv = exactColourHsv(hex);
    var panel = shell.querySelector('[data-pw-search-shade-panel]');
    var field = shell.querySelector('[data-pw-search-shade-field]');
    var swatch = shell.querySelector('[data-pw-search-shade-swatch]');
    if (panel) {
      panel.style.setProperty('--pw-picker-hue', String(Math.round(hsv.h)));
      panel.style.setProperty('--pw-picker-colour', hex);
      panel.style.setProperty('--pw-picker-x', hsv.s + '%');
      panel.style.setProperty('--pw-picker-y', (100 - hsv.v) + '%');
    }
    if (swatch) swatch.style.setProperty('--shade', hex);
    if (field) {
      field.setAttribute('aria-valuenow', String(Math.round(hsv.v)));
      field.setAttribute('aria-valuetext', exactColourName(hex) + ', ' + hex);
    }
    shell.querySelectorAll('[data-pw-search-shade-preset]').forEach(function (button) {
      button.classList.toggle(
        'is-active',
        String(button.getAttribute('data-pw-search-shade-preset') || '').toLowerCase() ===
          selectedNominalColour().toLowerCase()
      );
    });
  }

  function bindInlineShadePicker() {
    var shell = inlineShadePickerRoot();
    if (!shell) return;
    var field = shell.querySelector('[data-pw-search-shade-field]');
    var previewHex = activeShadePickerHex();
    var commitTimer = null;
    var dragging = false;
    var customDialog = document.querySelector('[data-pw-search-custom-dialog]');
    var customPanel = customDialog && customDialog.querySelector('[data-pw-search-custom-panel]');
    var customPreview = customDialog && customDialog.querySelector('[data-pw-search-custom-preview]');
    var customName = customDialog && customDialog.querySelector('[data-pw-search-custom-name]');
    var customValue = customDialog && customDialog.querySelector('[data-pw-search-custom-value]');
    var customHexInput = customDialog && customDialog.querySelector('[data-pw-search-custom-hex]');
    var customHue = customDialog && customDialog.querySelector('[data-pw-search-custom-hue]');
    var customSaturation = customDialog && customDialog.querySelector('[data-pw-search-custom-saturation]');
    var customBrightness = customDialog && customDialog.querySelector('[data-pw-search-custom-brightness]');
    var customHueOutput = customDialog && customDialog.querySelector('[data-pw-search-custom-hue-output]');
    var customSaturationOutput = customDialog &&
      customDialog.querySelector('[data-pw-search-custom-saturation-output]');
    var customBrightnessOutput = customDialog &&
      customDialog.querySelector('[data-pw-search-custom-brightness-output]');
    var customApply = customDialog && customDialog.querySelector('[data-pw-search-custom-apply]');
    var customCancelButtons = customDialog
      ? customDialog.querySelectorAll('[data-pw-search-custom-cancel]') : [];
    var customDraftHex = previewHex;
    var customLastFocus = null;

    function renderSearchCustomIdentity(hex) {
      customDraftHex = normaliseExactColour(hex) || customDraftHex;
      if (customPreview) customPreview.style.background = customDraftHex;
      if (customName) customName.textContent = exactColourName(customDraftHex);
      if (customValue) customValue.textContent = customDraftHex;
      if (customHexInput && document.activeElement !== customHexInput) {
        customHexInput.value = customDraftHex;
      }
      if (customPanel) customPanel.style.setProperty('--pw-custom-colour', customDraftHex);
    }

    function renderSearchCustomDraft(hue, saturation, brightness) {
      hue = clampColourNumber(Math.round(Number(hue) || 0), 0, 359);
      saturation = clampColourNumber(Math.round(Number(saturation) || 0), 0, 100);
      brightness = clampColourNumber(Math.round(Number(brightness) || 0), 0, 100);
      if (customHue) customHue.value = String(hue);
      if (customSaturation) customSaturation.value = String(saturation);
      if (customBrightness) customBrightness.value = String(brightness);
      if (customHueOutput) customHueOutput.textContent = hue + '°';
      if (customSaturationOutput) customSaturationOutput.textContent = saturation + '%';
      if (customBrightnessOutput) customBrightnessOutput.textContent = brightness + '%';
      renderSearchCustomIdentity(exactColourFromHsv(hue, saturation, brightness));
      if (customPanel) {
        customPanel.style.setProperty('--pw-custom-saturation-track',
          'linear-gradient(90deg, ' + exactColourFromHsv(hue, 0, brightness) + ', ' +
            exactColourFromHsv(hue, 100, brightness) + ')');
        customPanel.style.setProperty('--pw-custom-brightness-track',
          'linear-gradient(90deg, #000000, ' + exactColourFromHsv(hue, saturation, 100) + ')');
      }
    }

    function seedSearchCustomControls(hex) {
      hex = normaliseExactColour(hex);
      if (!hex) return;
      var hsv = exactColourHsv(hex);
      renderSearchCustomDraft(hsv.h, hsv.s, hsv.v);
      // Retain the exact source HEX until a slider is moved. The range inputs
      // use whole-number steps, so regenerating immediately could alter it.
      renderSearchCustomIdentity(hex);
    }

    function updateSearchCustomFromSliders() {
      if (!customHue || !customSaturation || !customBrightness) return;
      renderSearchCustomDraft(customHue.value, customSaturation.value, customBrightness.value);
    }

    function openSearchCustomDialog() {
      if (!customDialog) return;
      customLastFocus = document.activeElement;
      seedSearchCustomControls(
        state.exactColourHex || state.shadePickerHex || previewHex || '#5278A7'
      );
      customDialog.hidden = false;
      document.body.classList.add('pwcolour-custom-open');
      window.requestAnimationFrame(function () {
        if (!customHue) return;
        try { customHue.focus({ preventScroll: true }); }
        catch (error) { customHue.focus(); }
      });
    }

    function closeSearchCustomDialog(restoreFocus) {
      if (!customDialog) return;
      customDialog.hidden = true;
      document.body.classList.remove('pwcolour-custom-open');
      if (restoreFocus !== false && customLastFocus &&
          typeof customLastFocus.focus === 'function') {
        try { customLastFocus.focus({ preventScroll: true }); }
        catch (error) { customLastFocus.focus(); }
      }
    }

    function preview(hex, queueCommit) {
      previewHex = normaliseExactColour(hex) || previewHex;
      paintInlineShadePicker(previewHex);
      if (!queueCommit) return;
      window.clearTimeout(commitTimer);
      commitTimer = window.setTimeout(function () {
        applyExactColour(previewHex, state.exactColourTolerance, exactColourName(previewHex));
      }, 220);
    }

    function commit() {
      window.clearTimeout(commitTimer);
      applyExactColour(previewHex, state.exactColourTolerance, exactColourName(previewHex));
    }

    function previewFromPointer(event, queueCommit) {
      if (!field) return;
      var rectangle = field.getBoundingClientRect();
      if (!rectangle.width || !rectangle.height) return;
      var hsv = exactColourHsv(previewHex || state.exactColourHex);
      var saturation = clampColourNumber((event.clientX - rectangle.left) / rectangle.width * 100, 0, 100);
      var value = clampColourNumber(100 - ((event.clientY - rectangle.top) / rectangle.height * 100), 0, 100);
      preview(exactColourFromHsv(hsv.h, saturation, value), queueCommit);
    }

    if (field) {
      field.addEventListener('pointerdown', function (event) {
        dragging = true;
        // Named presets repaint the field without rebuilding this event
        // handler. Always begin from the shade currently on screen so a Pink
        // field cannot reuse the blue value captured when the page first
        // loaded.
        previewHex = state.exactColourHex || state.shadePickerHex || previewHex;
        if (field.setPointerCapture) {
          try { field.setPointerCapture(event.pointerId); } catch (error) {}
        }
        previewFromPointer(event, true);
      });
      field.addEventListener('pointermove', function (event) {
        if (dragging) previewFromPointer(event, true);
      });
      field.addEventListener('pointerup', function (event) {
        if (!dragging) return;
        previewFromPointer(event, false);
        dragging = false;
        commit();
      });
      field.addEventListener('pointercancel', function () {
        dragging = false;
        window.clearTimeout(commitTimer);
        paintInlineShadePicker(state.exactColourHex || state.shadePickerHex);
      });
      field.addEventListener('keydown', function (event) {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].indexOf(event.key) === -1) return;
        event.preventDefault();
        var hsv = exactColourHsv(state.exactColourHex || previewHex);
        if (event.key === 'ArrowLeft') hsv.s -= 2;
        else if (event.key === 'ArrowRight') hsv.s += 2;
        else if (event.key === 'ArrowUp') hsv.v += 2;
        else if (event.key === 'ArrowDown') hsv.v -= 2;
        else if (event.key === 'Home') hsv.s = 0;
        else hsv.s = 100;
        previewHex = exactColourFromHsv(hsv.h, hsv.s, hsv.v);
        commit();
      });
    }

    shell.querySelectorAll('[data-pw-search-shade-preset]').forEach(function (button) {
      button.addEventListener('click', function () {
        applyNominalColour(this.getAttribute('data-pw-search-shade-preset'));
      });
    });

    var exactButton = shell.querySelector('[data-pw-search-shade-exact]');
    if (exactButton) exactButton.addEventListener('click', function () {
      if (customDialog) {
        openSearchCustomDialog();
        return;
      }
      var input = app.querySelector('[data-pw-exact-colour-input]');
      if (input) input.click();
    });

    [customHue, customSaturation, customBrightness].forEach(function (input) {
      if (input) input.addEventListener('input', updateSearchCustomFromSliders);
    });
    if (customHexInput) {
      customHexInput.addEventListener('input', function () {
        var typed = String(customHexInput.value || '').trim();
        if (/^[0-9a-f]{6}$/i.test(typed)) typed = '#' + typed;
        customHexInput.classList.toggle('is-invalid', !normaliseExactColour(typed));
        if (normaliseExactColour(typed)) seedSearchCustomControls(typed);
      });
    }
    if (customApply) customApply.addEventListener('click', function () {
      previewHex = customDraftHex;
      closeSearchCustomDialog(true);
      applyExactColour(
        customDraftHex,
        state.exactColourTolerance,
        exactColourName(customDraftHex)
      );
    });
    customCancelButtons.forEach(function (button) {
      button.addEventListener('click', function () { closeSearchCustomDialog(true); });
    });
    if (customDialog) {
      customDialog.addEventListener('click', function (event) {
        if (event.target === customDialog) closeSearchCustomDialog(true);
      });
      customDialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeSearchCustomDialog(true);
          return;
        }
        if (event.key !== 'Tab' || !customPanel) return;
        var focusable = Array.prototype.slice.call(
          customPanel.querySelectorAll('button, input')
        ).filter(function (element) {
          return !element.disabled && element.offsetParent !== null;
        });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    var photoButton = shell.querySelector('[data-pw-search-shade-photo]');
    if (photoButton) photoButton.addEventListener('click', function () {
      var globalPhotoButton = document.querySelector('[data-pwcs-photo-open]');
      if (globalPhotoButton) globalPhotoButton.click();
    });

    var eyedropperButton = shell.querySelector('[data-pw-search-shade-eyedropper]');
    if (eyedropperButton) eyedropperButton.addEventListener('click', function () {
      if (typeof window.EyeDropper !== 'function') {
        if (customDialog) {
          openSearchCustomDialog();
          return;
        }
        var input = app.querySelector('[data-pw-exact-colour-input]');
        if (input) input.click();
        return;
      }
      new window.EyeDropper().open().then(function (result) {
        var selected = result && normaliseExactColour(result.sRGBHex);
        if (!selected) return;
        previewHex = selected;
        commit();
      }).catch(function () {});
    });
  }

  function colourPageProductSearchHtml() {
    if (!state.colourPage && state.mode !== 'search' && state.mode !== 'wishlist' &&
        state.mode !== 'collection') return '';
    var value = currentProductSearchText();
    var searchLabel = state.mode === 'wishlist'
      ? 'Search within your saved pieces'
      : (state.mode === 'collection'
        ? 'Search within ' + (state.collectionTitle || 'this collection')
        : (state.colourPage
        ? 'Search products within this colour'
        : 'Search brands, styles, sizes and colours'));
    return '<form class="pw-colour-product-search pw-main-catalogue-search" ' +
      'data-pw-colour-product-search data-pw-main-catalogue-search role="search">' +
      '<label class="pw-colour-product-search__field">' +
        '<span class="pw-visually-hidden">' + escapeHtml(searchLabel) + '</span>' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.8"></circle><path d="m16 16 5 5"></path></svg>' +
        '<input type="search" name="q" value="' + escapeAttr(value) + '" ' +
          'placeholder="' + escapeAttr(searchLabel) + '" autocomplete="off" ' +
          'data-pw-colour-product-search-input>' +
        '<button type="button" class="pw-colour-product-search__clear" ' +
          'data-pw-colour-product-search-clear aria-label="Clear product search"' +
          (value ? '' : ' hidden') + '><span aria-hidden="true">&times;</span></button>' +
      '</label>' +
      '<button type="submit" class="pw-colour-product-search__submit">Search</button>' +
    '</form>';
  }

  function bindColourPageProductSearch() {
    var form = app && app.querySelector('[data-pw-colour-product-search]');
    if (!form) return;
    var input = form.querySelector('[data-pw-colour-product-search-input]');
    var clear = form.querySelector('[data-pw-colour-product-search-clear]');
    if (!input) return;

    function updateClearButton() {
      if (clear) clear.hidden = !String(input.value || '').trim();
    }

    function navigateSearchImageHero(nextQuery) {
      var searchHero = app && app.closest && app.closest('[data-pw-search-image-hero]');
      if (state.mode !== 'search' || !searchHero) return false;
      var searchPath = app.getAttribute('data-search-url') || window.location.pathname;
      var target = new URL(searchPath, window.location.origin);
      if (nextQuery) target.searchParams.set('q', nextQuery);
      target.searchParams.set('options[prefix]', 'last');
      window.location.assign(target.pathname + target.search);
      return true;
    }

    function applyProductSearch() {
      var nextQuery = String(input.value || '').trim();
      if (!nextQuery && state.colourPage) nextQuery = '*';
      updateClearButton();
      if (nextQuery === currentProductSearchText()) return;
      // Search hero artwork is resolved by Liquid from the submitted term.
      // Reload when that term changes so a stale hero can never remain above a
      // different query and the next matching <query>.png can be discovered.
      if (navigateSearchImageHero(nextQuery)) return;
      clearInterpretedSearchSelections();
      state.query = nextQuery;
      state.searchAnalyticsEligible = !!nextQuery && nextQuery !== '*';
      state.searchAnalyticsPublished = false;
      state.searchAnalyticsSource = state.mode === 'collection'
        ? 'collection_search' : 'search_page';
      applyCanonicalDressSearchIntent(nextQuery);
      if (state.mode === 'collection') {
        var collectionHero = app.closest && app.closest('[data-pw-standalone-image-hero]');
        if (collectionHero) {
          collectionHero.dispatchEvent(new CustomEvent('preworn:tag-hero-collapse', {
            detail: { reason: 'search' }
          }));
        }
      }
      syncEmptySearchLanding();
      refreshCatalogFacetsForCurrentScope();
      resetPagination();
      navigateWithHistory(1, null);
    }

    input.addEventListener('input', updateClearButton);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      applyProductSearch();
    });
    if (clear) clear.addEventListener('click', function () {
      input.value = '';
      applyProductSearch();
      input.focus();
    });
  }

  function collectionStyleDirectoryHtml() {
    return '<section class="pw-collection-styles" data-pw-collection-style-directory hidden ' +
      'data-pw-directory-dimension="type" aria-label="Shop by type">' +
        '<div class="pw-collection-styles__shell">' +
          '<button class="pw-collection-styles__arrow pw-collection-styles__arrow--previous" ' +
            'type="button" data-pw-collection-style-scroll="-1" aria-label="Show previous types">←</button>' +
          '<div class="pw-collection-styles__track" data-pw-collection-style-track ' +
            'role="list" aria-label="Available types" aria-busy="true"></div>' +
          '<button class="pw-collection-styles__arrow pw-collection-styles__arrow--next" ' +
            'type="button" data-pw-collection-style-scroll="1" aria-label="Show more types">→</button>' +
        '</div>' +
      '</section>';
  }

  function collectionStyleDirectoryRoot() {
    if (!app) return null;
    return app.querySelector('[data-pw-collection-style-directory]') ||
      document.querySelector('[data-pw-search-directory-owner="preworn-wide-search"]');
  }

  function mountCollectionStyleDirectory() {
    var directory = app && app.querySelector('[data-pw-collection-style-directory]');
    var page = app && typeof app.closest === 'function'
      ? app.closest('[data-pw-brand-page]') : null;
    var legacy = page && page.querySelector('[data-pw-brand-style-directory-section]');
    if (!directory) return false;
    directory.setAttribute('data-pw-search-directory-owner', 'preworn-wide-search');
    if (!legacy || !legacy.parentNode) return true;
    // Keep the server-rendered rail in place until useful names and images are
    // ready. Hiding it here leaves an empty gap that makes the hero remeasure
    // and jump when the live directory later arrives.
    legacy.setAttribute('data-pw-directory-replaced', 'true');
    legacy.parentNode.insertBefore(directory, legacy);
    return true;
  }

  function revealCollectionStyleDirectory(directory) {
    if (!directory) return false;
    var page = app && typeof app.closest === 'function'
      ? app.closest('[data-pw-brand-page]') : null;
    var legacy = page && page.querySelector('[data-pw-brand-style-directory-section]');
    if (legacy && legacy !== directory) legacy.hidden = true;
    directory.hidden = false;
    return true;
  }

  function collectionStyleImage(value, sku) {
    value = String(value || '').trim();
    sku = String(sku || '').trim();
    if (!sku || !/^(?:https?:)?\/\//i.test(value)) return '';
    var path;
    try {
      path = new URL(value, window.location.href).pathname;
    } catch (error) {
      path = value.split(/[?#]/)[0];
    }
    var filename = path.slice(path.lastIndexOf('/') + 1);
    try { filename = decodeURIComponent(filename); }
    catch (error) { /* Compare the original basename when decoding fails. */ }
    return filename.toLowerCase() === (sku + '_1_new.jpg').toLowerCase()
      ? value : '';
  }

  var COLLECTION_DIRECTORY_VISUAL_CACHE_KEY = 'preworn:directory-visuals:v1';
  var COLLECTION_DIRECTORY_VISUAL_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;
  var COLLECTION_DIRECTORY_VISUAL_CACHE_LIMIT = 16;

  function readCollectionDirectoryVisuals(signature, dimension) {
    try {
      var raw = window.localStorage.getItem(COLLECTION_DIRECTORY_VISUAL_CACHE_KEY);
      var cache = raw ? JSON.parse(raw) : null;
      var entries = cache && Array.isArray(cache.entries) ? cache.entries : [];
      for (var index = 0; index < entries.length; index++) {
        var entry = entries[index] || {};
        if (entry.signature !== signature || entry.dimension !== dimension ||
            Date.now() - Number(entry.savedAt || 0) >
              COLLECTION_DIRECTORY_VISUAL_CACHE_TTL ||
            !Array.isArray(entry.items) || entry.items.length < 2) continue;
        return entry.items.map(function (item) {
          return {
            name: String(item && item.name || '').trim(),
            availableCount: null,
            imageSku: String(item && item.imageSku || '').trim(),
            imageUrl: collectionStyleImage(
              item && item.imageUrl || '',
              item && item.imageSku || ''
            ),
            colourMatchPercentage: item && item.colourMatchPercentage != null
              ? Number(item.colourMatchPercentage) : null,
            colourMatchScore: item && item.colourMatchScore != null
              ? Number(item.colourMatchScore) : null
          };
        }).filter(function (item) { return !!item.name; });
      }
    } catch (error) {
      // A blocked or full storage area must never delay live catalogue browsing.
    }
    return [];
  }

  function writeCollectionDirectoryVisuals(signature, dimension, items) {
    var visuals = (items || []).map(function (item) {
      return {
        name: String(item && item.name || '').trim(),
        imageSku: String(item && item.imageSku || '').trim(),
        imageUrl: collectionStyleImage(
          item && item.imageUrl || '',
          item && item.imageSku || ''
        ),
        colourMatchPercentage: item && item.colourMatchPercentage != null
          ? Number(item.colourMatchPercentage) : null,
        colourMatchScore: item && item.colourMatchScore != null
          ? Number(item.colourMatchScore) : null
      };
    }).filter(function (item) { return !!item.name; }).slice(0, 100);
    if (!signature || visuals.length < 2) return;
    try {
      var raw = window.localStorage.getItem(COLLECTION_DIRECTORY_VISUAL_CACHE_KEY);
      var cache = raw ? JSON.parse(raw) : null;
      var entries = cache && Array.isArray(cache.entries) ? cache.entries : [];
      entries = entries.filter(function (entry) {
        return entry && entry.signature !== signature &&
          Date.now() - Number(entry.savedAt || 0) <=
            COLLECTION_DIRECTORY_VISUAL_CACHE_TTL;
      });
      entries.unshift({
        signature: signature,
        dimension: dimension,
        savedAt: Date.now(),
        items: visuals
      });
      window.localStorage.setItem(COLLECTION_DIRECTORY_VISUAL_CACHE_KEY, JSON.stringify({
        entries: entries.slice(0, COLLECTION_DIRECTORY_VISUAL_CACHE_LIMIT)
      }));
    } catch (error) {
      // The live directory remains authoritative when browser storage is unavailable.
    }
  }

  function selectedCollectionDirectoryValue(dimension) {
    dimension = dimension === 'style' ? 'style' : 'type';
    var keys = Object.keys(state.selectedValues);
    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]] || {};
      var input = normalizeFilterInput(selected.input || {});
      if (String(input.catalogParameter || '').toLowerCase() === dimension) {
        return String(input.catalogValue || selected.label || '').trim();
      }
    }
    return '';
  }

  function requestedCollectionDirectoryDimension() {
    if (selectedCollectionDirectoryValue('type') ||
        selectedCollectionDirectoryValue('style')) return 'style';

    // Tag landing pages can fix Type outside selectedValues (for example
    // Type_Jumper in data-tag-queries). Starting those pages on Type means the
    // first colour-matched products can only yield one card, so the visual rail
    // waits for the slower aggregate request to fall back to Style. Recognise
    // the fixed tag scope up front and paint Style names/images immediately
    // from the first product response instead.
    var fixedTagScope = structuredFacetScopeFromTags(state.tagQueries) || {};
    if (fixedTagScope.type && !fixedTagScope.style) return 'style';
    return 'type';
  }

  function collectionDirectoryScopeTitle() {
    if (state.mode === 'collection') return String(state.collectionTitle || 'Collection').trim();
    return String(state.tagLabel || state.brandQuery || '').trim();
  }

  function updateCollectionDirectoryHeading(dimension) {
    var directory = collectionStyleDirectoryRoot();
    if (!directory) return;
    dimension = dimension === 'style' ? 'style' : 'type';
    state.collectionStyleDirectoryDimension = dimension;
    directory.setAttribute('data-pw-directory-dimension', dimension);
    var label = dimension === 'style' ? 'style' : 'type';
    var scope = collectionDirectoryScopeTitle();
    var track = directory.querySelector('[data-pw-collection-style-track]');
    var previous = directory.querySelector('[data-pw-collection-style-scroll="-1"]');
    var next = directory.querySelector('[data-pw-collection-style-scroll="1"]');
    directory.setAttribute('aria-label', 'Shop ' + (scope ? scope + ' ' : '') +
      'by ' + label);
    if (track) track.setAttribute('aria-label', 'Available ' +
      (dimension === 'style' ? 'styles' : 'types'));
    if (previous) previous.setAttribute('aria-label', 'Show previous ' +
      (dimension === 'style' ? 'styles' : 'types'));
    if (next) next.setAttribute('aria-label', 'Show more ' +
      (dimension === 'style' ? 'styles' : 'types'));
  }

  function syncCollectionStyleDirectorySelection() {
    var directory = collectionStyleDirectoryRoot();
    if (!directory) return;
    var dimension = directory.getAttribute('data-pw-directory-dimension') === 'style'
      ? 'style' : 'type';
    var selected = selectedCollectionDirectoryValue(dimension).toLowerCase();
    var cards = directory.querySelectorAll('[data-pw-collection-style-value]');
    for (var i = 0; i < cards.length; i++) {
      var value = String(cards[i].getAttribute('data-pw-collection-style-value') || '')
        .trim().toLowerCase();
      var active = value ? value === selected : !selected;
      cards[i].classList.toggle('is-active', active);
      if (cards[i].classList.contains('pw-collection-styles__card')) {
        cards[i].setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    }
    var track = directory.querySelector('[data-pw-collection-style-track]');
    if (!track) return;
    var preserveMobileOrder = Number(window.innerWidth || 0) > 0 &&
      Number(window.innerWidth || 0) <= 749;
    var styleCards = Array.prototype.slice.call(
      track.querySelectorAll('.pw-collection-styles__card:not(.is-back)')
    );
    styleCards.sort(function (left, right) {
      var leftValue = String(left.getAttribute('data-pw-collection-style-value') || '')
        .trim().toLowerCase();
      var rightValue = String(right.getAttribute('data-pw-collection-style-value') || '')
        .trim().toLowerCase();
      var leftPriority = !leftValue ? -2 : (!preserveMobileOrder && selected && leftValue === selected ? -1 :
        Number(left.getAttribute('data-pw-collection-style-rank') || 0));
      var rightPriority = !rightValue ? -2 : (!preserveMobileOrder && selected && rightValue === selected ? -1 :
        Number(right.getAttribute('data-pw-collection-style-rank') || 0));
      return leftPriority - rightPriority;
    });
    for (var cardIndex = 0; cardIndex < styleCards.length; cardIndex++) {
      track.appendChild(styleCards[cardIndex]);
    }
    if (selected && !preserveMobileOrder) track.scrollLeft = 0;
  }

  function collectionStyleImagesFromProducts(products, dimension) {
    dimension = dimension === 'style' ? 'style' : 'type';
    var images = {};
    (products || []).forEach(function (product) {
      var attributes = product && product.attributes || {};
      var name = String(attributes[dimension] || product && product[dimension] || '').trim();
      var sku = String(product && product.primarySku || '').trim();
      var image = product && product.featuredImage
        ? collectionStyleImage(product.featuredImage.url || '', sku) : '';
      if (name && image && !images[name.toLowerCase()]) {
        images[name.toLowerCase()] = { imageUrl: image, imageSku: sku };
      }
    });
    return images;
  }

  function collectionStyleDirectoryItemsFromProducts(products, dimension) {
    dimension = dimension === 'style' ? 'style' : 'type';
    var seen = {};
    var items = [];
    (products || []).forEach(function (product) {
      var attributes = product && product.attributes || {};
      var name = String(attributes[dimension] || product && product[dimension] || '').trim();
      var key = name.toLowerCase();
      if (!name || seen[key] || isPlaceholderFacetValue(name) ||
          /^(?:other|unknown|n\/a)$/i.test(name)) return;
      var sku = String(product && product.primarySku || '').trim();
      var image = product && product.featuredImage
        ? collectionStyleImage(product.featuredImage.url || '', sku) : '';
      if (!image) return;
      seen[key] = true;
      items.push({
        name: name,
        availableCount: null,
        imageSku: sku,
        imageUrl: image,
        colourMatchPercentage: product && product.visualColour &&
          product.visualColour.matchPercentage != null
          ? Number(product.visualColour.matchPercentage) : null,
        colourMatchScore: product && product.visualColour &&
          product.visualColour.score != null
          ? Number(product.visualColour.score) : null
      });
    });
    return items;
  }

  function hydrateCollectionStyleDirectoryImages(products) {
    if (!state.collectionStyleDirectoryItems.length || !app) return false;
    var images = collectionStyleImagesFromProducts(
      products,
      state.collectionStyleDirectoryDimension
    );
    var changed = false;
    state.collectionStyleDirectoryItems.forEach(function (style) {
      var image = images[style.name.toLowerCase()] || null;
      if (!style.imageUrl && image && image.imageUrl) {
        style.imageUrl = image.imageUrl;
        style.imageSku = image.imageSku;
        changed = true;
      }
    });
    if (!changed) return false;
    var directory = collectionStyleDirectoryRoot();
    var track = directory && directory.querySelector('[data-pw-collection-style-track]');
    if (!track) return false;
    state.collectionStyleDirectoryItems.forEach(function (style) {
      if (!style.imageUrl) return;
      var card = null;
      var candidates = track.querySelectorAll('[data-pw-collection-style-key]');
      for (var candidateIndex = 0; candidateIndex < candidates.length; candidateIndex++) {
        if (String(candidates[candidateIndex].getAttribute('data-pw-collection-style-key') || '') ===
            style.name.toLowerCase()) {
          card = candidates[candidateIndex];
          break;
        }
      }
      var media = card && card.querySelector('.pw-collection-styles__media');
      if (!media || media.querySelector('img')) return;
      media.innerHTML = '<img class="pw-collection-styles__image" src="' +
        escapeAttr(style.imageUrl) + '" alt="" loading="lazy" decoding="async">';
    });
    return true;
  }

  function collectionDirectorySearchTypeScore(name, dimension) {
    if (dimension !== 'type') return 0;
    var query = activeProductSearchQuery().toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
    var type = String(name || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!query || !type) return 0;
    var aliases = {
      't shirt': ['t shirt', 'tshirt', 'tee', 'tees'],
      'button up': ['button up', 'buttonup'],
      'jumper': ['jumper', 'jumpers', 'sweater', 'sweaters', 'pullover', 'pullovers'],
      'sweatshirt': ['sweatshirt', 'sweatshirts'],
      'hoodie': ['hoodie', 'hoodies'],
      'jacket': ['jacket', 'jackets'],
      'blouse': ['blouse', 'blouses'],
      'jeans': ['jeans', 'jean'],
      'trousers': ['trousers', 'trouser', 'pants', 'chinos'],
      'dress': ['dress', 'dresses'],
      'skirt': ['skirt', 'skirts'],
      'shorts': ['shorts'],
      'leggings': ['leggings'],
      'coat': ['coat', 'coats'],
      'cardigan': ['cardigan', 'cardigans'],
      'blazer': ['blazer', 'blazers'],
      'waistcoat': ['waistcoat', 'waistcoats'],
      'tank': ['tank', 'tanks', 'camisole', 'camisoles'],
      'polo': ['polo', 'polos']
    };
    var candidates = aliases[type] || [type];
    var paddedQuery = ' ' + query + ' ';
    for (var index = 0; index < candidates.length; index++) {
      var candidate = String(candidates[index] || '').trim();
      if (candidate && paddedQuery.indexOf(' ' + candidate + ' ') !== -1) {
        return candidate === type ? 2 : 1;
      }
    }
    return 0;
  }

  function renderCollectionStyleDirectory(styles, dimension, replaceScope, countsPending) {
    if (!app) return false;
    dimension = dimension === 'style' ? 'style' : 'type';
    var directory = collectionStyleDirectoryRoot();
    var track = directory && directory.querySelector('[data-pw-collection-style-track]');
    if (!directory || !track) return false;
    var dimensionChanged = state.collectionStyleDirectoryDimension !== dimension;
    var previousItems = state.collectionStyleDirectoryItems.slice();
    var seen = {};
    var items = (Array.isArray(styles) ? styles : []).map(function (style) {
      style = style || {};
      var rawMatchPercentage = style.colourMatchPercentage;
      var rawMatchScore = style.colourMatchScore;
      var matchPercentage = rawMatchPercentage === null ||
        typeof rawMatchPercentage === 'undefined' || rawMatchPercentage === ''
        ? null : Number(rawMatchPercentage);
      var matchScore = rawMatchScore === null ||
        typeof rawMatchScore === 'undefined' || rawMatchScore === ''
        ? null : Number(rawMatchScore);
      var rawAvailableCount = style.availableCount;
      if (rawAvailableCount === null || typeof rawAvailableCount === 'undefined' ||
          rawAvailableCount === '') rawAvailableCount = style.count;
      var countKnown = rawAvailableCount !== null &&
        typeof rawAvailableCount !== 'undefined' && rawAvailableCount !== '' &&
        isFinite(Number(rawAvailableCount));
      var name = String(style.name || style.value || style.label || '').trim();
      return {
        name: name,
        availableCount: countKnown ? Math.max(0, Number(rawAvailableCount) || 0) : null,
        countKnown: countKnown,
        imageSku: String(style.imageSku || style.primarySku || '').trim(),
        imageUrl: collectionStyleImage(
          style.imageUrl || '',
          style.imageSku || style.primarySku || ''
        ),
        colourMatchPercentage: matchPercentage !== null && isFinite(matchPercentage)
          ? Math.max(0, Math.min(100, Math.round(matchPercentage))) : null,
        colourMatchScore: matchScore !== null && isFinite(matchScore) ? matchScore : null,
        queryRelevance: collectionDirectorySearchTypeScore(name, dimension)
      };
    }).filter(function (style) {
      var key = style.name.toLowerCase();
      if (!key || (style.countKnown && style.availableCount < 1) || seen[key] ||
          isPlaceholderFacetValue(style.name) ||
          /^(?:other|unknown|n\/a)$/i.test(style.name)) return false;
      seen[key] = true;
      return true;
    }).sort(function (left, right) {
      if (left.queryRelevance !== right.queryRelevance) {
        return right.queryRelevance - left.queryRelevance;
      }
      var leftHasMatch = left.colourMatchPercentage !== null;
      var rightHasMatch = right.colourMatchPercentage !== null;
      if (leftHasMatch !== rightHasMatch) return leftHasMatch ? -1 : 1;
      if (leftHasMatch && left.colourMatchPercentage !== right.colourMatchPercentage) {
        return right.colourMatchPercentage - left.colourMatchPercentage;
      }
      if (leftHasMatch && left.colourMatchScore !== null &&
          right.colourMatchScore !== null &&
          Math.abs(left.colourMatchScore - right.colourMatchScore) > .0001) {
        return left.colourMatchScore - right.colourMatchScore;
      }
      return Number(right.availableCount || 0) - Number(left.availableCount || 0) ||
        left.name.localeCompare(right.name, 'en-GB', { sensitivity: 'base' });
    });
    var cachedByName = {};
    previousItems.forEach(function (style) {
      cachedByName[style.name.toLowerCase()] = style;
    });
    items.forEach(function (style) {
      var cached = cachedByName[style.name.toLowerCase()];
      if (!style.imageUrl && cached && cached.imageUrl) style.imageUrl = cached.imageUrl;
    });
    if (items.length < 2 && countsPending && !dimensionChanged && previousItems.length >= 2) {
      items = previousItems.slice();
    }
    // Selecting a Type normally drills into Style. Some Types have no useful
    // Style split, though. Keep the complete Type rail mounted while the
    // parent-Type fallback request runs instead of replacing it with an empty
    // strip. This also keeps the selected Type visibly highlighted.
    if (items.length < 2 && dimension === 'style' &&
        selectedCollectionDirectoryValue('type') &&
        state.collectionStyleDirectoryDimension === 'type' &&
        previousItems.length >= 2) {
      directory.hidden = false;
      directory.classList.remove('is-refreshing');
      track.setAttribute('aria-busy', 'false');
      state.collectionStyleDirectoryReady = true;
      syncCollectionStyleDirectorySelection();
      return false;
    }
    if (replaceScope || dimensionChanged) state.collectionStyleDirectoryItems = [];
    if (items.length < 2) {
      directory.hidden = true;
      directory.classList.remove('is-refreshing', 'is-counts-pending');
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
      state.collectionStyleDirectoryReady = false;
      return false;
    }
    state.collectionStyleDirectoryItems = items;
    updateCollectionDirectoryHeading(dimension);

    var allMedia = '<span class="pw-collection-styles__all-tab" aria-hidden="true">ALL</span>';
    var pluralLabel = dimension === 'style' ? 'Styles' : 'Types';
    var selectedType = selectedCollectionDirectoryValue('type');
    var html = dimension === 'style' && selectedType
      ? '<button type="button" class="pw-collection-styles__card is-back" role="listitem" ' +
          'data-pw-directory-back aria-label="Back to types">' +
          '<span class="pw-collection-styles__back-arrow" aria-hidden="true">←</span>' +
          '<strong>Back to types</strong><small>' + escapeHtml(selectedType) + '</small></button>'
      : '';
    // Names and images make the directory useful immediately. Counts are an
    // optional enhancement, so leave their reserved line empty rather than
    // showing a loading shimmer that makes the whole rail feel unfinished.
    var pendingCountAttributes = countsPending
      ? ' aria-hidden="true" data-pw-directory-count-pending' : '';
    var allCountMarkup = countsPending
      ? ''
      : items.length.toLocaleString('en-GB') + ' ' +
          (items.length === 1 ? dimension : pluralLabel.toLowerCase());
    html += '<button type="button" class="pw-collection-styles__card is-all" ' +
      'role="listitem" data-pw-collection-style-value="" ' +
      'data-pw-directory-value-dimension="' + dimension + '" ' +
      'data-pw-collection-style-rank="-1" aria-pressed="false">' +
        '<span class="pw-collection-styles__media">' + allMedia + '</span>' +
        '<strong>All ' + pluralLabel + '</strong><small data-pw-directory-count' +
          pendingCountAttributes + '>' +
          allCountMarkup + '</small></button>';

    html += items.map(function (style, styleIndex) {
      var imageLoading = styleIndex < 10 ? 'eager' : 'lazy';
      var media = style.imageUrl
        ? '<img class="pw-collection-styles__image" src="' + escapeAttr(style.imageUrl) +
            '" alt="" loading="' + imageLoading + '" decoding="async">'
        : '<span class="pw-collection-styles__placeholder" aria-hidden="true">' +
            escapeHtml(style.name.slice(0, 2).toUpperCase()) + '</span>';
      var countMarkup = style.countKnown
        ? style.availableCount.toLocaleString('en-GB') + ' available'
        : '';
      var countLabel = style.countKnown
        ? ', ' + style.availableCount.toLocaleString('en-GB') + ' available' : '';
      return '<button type="button" class="pw-collection-styles__card" role="listitem" ' +
        'data-pw-collection-style-value="' + escapeAttr(style.name) + '" ' +
        'data-pw-directory-value-dimension="' + dimension + '" ' +
        'data-pw-collection-style-key="' + escapeAttr(style.name.toLowerCase()) + '" ' +
        'data-pw-collection-style-rank="' + styleIndex + '" aria-pressed="false" ' +
        'aria-label="Shop ' + escapeAttr(style.name) + escapeAttr(countLabel) + '">' +
          '<span class="pw-collection-styles__media">' + media + '</span>' +
          '<strong>' + escapeHtml(style.name) + '</strong>' +
          '<small data-pw-directory-count' +
            (style.countKnown ? '' : pendingCountAttributes) + '>' +
            countMarkup + '</small></button>';
    }).join('');

    track.innerHTML = html;
    track.scrollLeft = 0;
    // The cards are fully usable while only their optional counts are pending.
    track.setAttribute('aria-busy', 'false');
    directory.classList.toggle('is-counts-pending', !!countsPending);
    directory.classList.remove('is-refreshing');
    revealCollectionStyleDirectory(directory);
    state.collectionStyleDirectoryReady = true;
    syncCollectionStyleDirectorySelection();
    return true;
  }

  function collectionStyleDirectoryFromFilters(filters, products) {
    if (!state.collectionStyleDirectoryCountsReady) {
      var dimension = requestedCollectionDirectoryDimension();
      var productItems = collectionStyleDirectoryItemsFromProducts(products, dimension);
      if (productItems.length >= 2) {
        var merged = productItems.slice();
        var seen = {};
        merged.forEach(function (item) { seen[item.name.toLowerCase()] = true; });
        if (state.collectionStyleDirectoryDimension === dimension) {
          state.collectionStyleDirectoryItems.forEach(function (item) {
            if (!seen[item.name.toLowerCase()]) merged.push(item);
          });
        }
        renderCollectionStyleDirectory(merged, dimension, true, true);
      }
    }
    if (!state.collectionStyleDirectoryReady) return false;
    hydrateCollectionStyleDirectoryImages(products);
    syncCollectionStyleDirectorySelection();
    return true;
  }

  function collectionTypeDirectoryFallbackUrl(sourceUrl, structuredTagScope) {
    var fallbackUrl = new URL(sourceUrl.toString());
    fallbackUrl.searchParams.set('directory_dimension', 'type');
    // Type and Style from selectedValues describe the current drill-down. The
    // fallback needs its parent scope, otherwise an active T-Shirt filter can
    // only aggregate one T-Shirt card. Fixed tag-page scopes are reapplied
    // below because they define the page itself rather than a shopper choice.
    fallbackUrl.searchParams.delete('type');
    fallbackUrl.searchParams.delete('style');
    if (structuredTagScope) {
      ['type', 'style'].forEach(function (parameter) {
        if (structuredTagScope[parameter]) {
          fallbackUrl.searchParams.set(parameter, structuredTagScope[parameter]);
        }
      });
    }
    return fallbackUrl;
  }

  function requestCollectionTypeDirectoryFallback(
    sourceUrl,
    structuredTagScope,
    signature,
    generation,
    controller
  ) {
    var fallbackUrl = collectionTypeDirectoryFallbackUrl(
      sourceUrl,
      structuredTagScope
    );
    return fetch(fallbackUrl.toString(), {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (!response.ok) throw new Error('Type directory fallback HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      if (generation !== state.collectionStyleDirectoryGeneration) return false;
      if (!payload || payload.source !== 'preworn_catalog_index' ||
          !Array.isArray(payload.styleDirectory)) {
        throw new Error('Invalid Type directory fallback response');
      }
      var responseDimension = payload.directoryDimension || 'type';
      if (responseDimension !== 'type') return false;
      var rendered = renderCollectionStyleDirectory(
        payload.styleDirectory,
        'type',
        true,
        false
      );
      if (rendered) {
        state.collectionStyleDirectoryCountsReady = true;
        writeCollectionDirectoryVisuals(
          signature,
          'type',
          state.collectionStyleDirectoryItems
        );
      }
      return rendered;
    });
  }

  function requestCollectionStyleDirectory(options) {
    options = options || {};
    if (!app) return null;
    var endpoint = app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl;
    if (!endpoint) return null;
    var url = new URL(endpoint, window.location.href);
    var useColourSearch = !!state.exactColourHex;
    var dimension = requestedCollectionDirectoryDimension();
    url.searchParams.set('action', useColourSearch ? 'colour_search' : 'search');
    if (state.mode === 'collection' && state.collectionHandle) {
      url.searchParams.set('collection', state.collectionHandle);
    }
    appendWishlistParameters(url.searchParams);
    url.searchParams.set('facets_only', '1');
    url.searchParams.set('browse_directory_only', '1');
    url.searchParams.set('browse_directory_version', '4');
    url.searchParams.set('directory_dimension', dimension);
    url.searchParams.set('limit', '12');
    var productQuery = activeProductSearchQuery();
    var structuredTagScope = structuredFacetScopeFromTags(state.tagQueries);
    if (state.tagQueries.length && !structuredTagScope) {
      if (state.tagQueries.length === 1) {
        url.searchParams.set('tag', state.tagQueries[0]);
      } else {
        state.tagQueries.forEach(function (tag) {
          url.searchParams.append('tags[]', tag);
        });
      }
      if (state.tagLabel) url.searchParams.set('tag_label', state.tagLabel);
    } else if (state.brandQuery) {
      url.searchParams.set('brand', state.brandQuery);
      url.searchParams.set('vendor', state.brandQuery);
    }
    if (productQuery) url.searchParams.set('q', productQuery);
    appendCatalogFilters(url.searchParams, dimension);
    appendWardrobeParameters(url.searchParams);
    applyStructuredFacetScope(url.searchParams, structuredTagScope);
    if (useColourSearch) {
      url.searchParams.set('hex', state.exactColourHex);
      url.searchParams.set('tolerance', String(state.exactColourTolerance));
      url.searchParams.set('fast_results', '0');
      url.searchParams.set('include_facets', '0');
    }
    var signature = url.searchParams.toString();
    var sameSignature = signature === state.collectionStyleDirectorySignature;
    if (!sameSignature) {
      prepareCollectionDirectoryTransition(dimension);
      state.collectionStyleDirectorySignature = signature;
      var cachedVisuals = readCollectionDirectoryVisuals(signature, dimension);
      if (cachedVisuals.length >= 2) {
        renderCollectionStyleDirectory(cachedVisuals, dimension, true, true);
      }
    }
    if (options.prepareOnly) return Promise.resolve(state.collectionStyleDirectoryReady);
    if (sameSignature) {
      if (state.collectionStyleDirectoryRequest) return state.collectionStyleDirectoryRequest;
      if (state.collectionStyleDirectoryCountsReady) return Promise.resolve(true);
    }
    state.collectionStyleDirectoryGeneration += 1;
    var generation = state.collectionStyleDirectoryGeneration;
    if (state.collectionStyleDirectoryController) {
      try { state.collectionStyleDirectoryController.abort(); } catch (abortError) {}
    }
    var controller = typeof window.AbortController === 'function'
      ? new window.AbortController() : null;
    state.collectionStyleDirectoryController = controller;
    var timeout = controller ? window.setTimeout(function () { controller.abort(); }, 20000) : null;
    var countsHydrated = false;
    var visualRequest = Promise.resolve(false);

    // Exact-colour count hydration scans the complete candidate population.
    // Start a much smaller colour-index preview in parallel so every Style can
    // receive its current-colour image without waiting for those aggregates.
    if (useColourSearch) {
      var visualUrl = new URL(url.toString());
      visualUrl.searchParams.set('directory_visuals_only', '1');
      visualUrl.searchParams.set('fast_results', '1');
      visualUrl.searchParams.set('finder_fast', '1');
      visualRequest = fetch(visualUrl.toString(), {
        method: 'GET',
        mode: 'cors',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        if (!response.ok) throw new Error('Style visual preview HTTP ' + response.status);
        return response.json();
      }).then(function (payload) {
        if (generation !== state.collectionStyleDirectoryGeneration || countsHydrated) {
          return false;
        }
        if (!payload || payload.source !== 'preworn_catalog_index' ||
            !Array.isArray(payload.styleDirectory)) {
          throw new Error('Invalid style visual preview response');
        }
        var rendered = renderCollectionStyleDirectory(
          payload.styleDirectory,
          payload.directoryDimension || dimension,
          true,
          true
        );
        if (rendered) {
          writeCollectionDirectoryVisuals(
            signature,
            payload.directoryDimension || dimension,
            state.collectionStyleDirectoryItems
          );
        }
        return rendered;
      }).catch(function (error) {
        if (generation === state.collectionStyleDirectoryGeneration &&
            String(error && error.name || '') !== 'AbortError') {
          console.warn('[Preworn Browse] Style visual preview unavailable.', error);
        }
        return false;
      });
    }

    var countRequest = fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (!response.ok) throw new Error('Style directory HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      if (generation !== state.collectionStyleDirectoryGeneration) return false;
      if (!payload || payload.source !== 'preworn_catalog_index' ||
          !Array.isArray(payload.styleDirectory)) {
        throw new Error('Invalid style directory response');
      }
      countsHydrated = true;
      var rendered = renderCollectionStyleDirectory(
        payload.styleDirectory,
        payload.directoryDimension || dimension,
        true,
        false
      );
      if (rendered) {
        state.collectionStyleDirectoryCountsReady = true;
        writeCollectionDirectoryVisuals(
          signature,
          payload.directoryDimension || dimension,
          state.collectionStyleDirectoryItems
        );
      } else if (dimension === 'style' && selectedCollectionDirectoryValue('type')) {
        // A Type with zero or one Style has no useful second level. Restore the
        // parent Type choices (without the shopper's active Type/Style drill-
        // down) so the directory never disappears. This second request is also
        // what makes a copied or refreshed ?type= URL behave like an in-page
        // selection where the original Type cards were already available.
        return requestCollectionTypeDirectoryFallback(
          url,
          structuredTagScope,
          signature,
          generation,
          controller
        );
      }
      return rendered;
    }).catch(function (error) {
      if (generation !== state.collectionStyleDirectoryGeneration ||
          String(error && error.name || '') === 'AbortError') return false;
      state.collectionStyleDirectorySignature = '';
      var failedDirectory = collectionStyleDirectoryRoot();
      var failedTrack = failedDirectory &&
        failedDirectory.querySelector('[data-pw-collection-style-track]');
      // If count hydration fails, keep the already-useful names and images
      // visible and leave their unavailable counts hidden.
      if (failedDirectory) failedDirectory.classList.remove('is-refreshing');
      if (failedTrack) failedTrack.setAttribute('aria-busy', 'false');
      console.warn('[Preworn Browse] Collection style directory temporarily unavailable.', error);
      return false;
    });

    state.collectionStyleDirectoryRequest = Promise.all([
      visualRequest,
      countRequest
    ]).then(function (results) {
      return !!(results[1] || results[0]);
    }).finally(function () {
      if (timeout) window.clearTimeout(timeout);
      if (generation === state.collectionStyleDirectoryGeneration) {
        state.collectionStyleDirectoryRequest = null;
        state.collectionStyleDirectoryController = null;
      }
    });
    return state.collectionStyleDirectoryRequest;
  }

  function scheduleCollectionStyleDirectoryRequest() {
    if (state.collectionStyleDirectorySchedule) {
      window.clearTimeout(state.collectionStyleDirectorySchedule);
    }
    state.collectionStyleDirectorySchedule = window.setTimeout(function () {
      state.collectionStyleDirectorySchedule = null;
      requestCollectionStyleDirectory();
    }, 0);
  }

  function clearCollectionDirectoryParameters(parameters) {
    parameters = parameters || [];
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (parameters.indexOf(parameter) !== -1) {
        removeInterpretedSearchTerm(selected);
        delete state.selectedValues[key];
      }
    });
  }

  function prepareCollectionDirectoryTransition(dimension) {
    dimension = dimension === 'style' ? 'style' : 'type';
    state.collectionStyleDirectoryCountsReady = false;
    state.collectionStyleDirectorySignature = '';
    var directory = collectionStyleDirectoryRoot();
    var track = directory && directory.querySelector('[data-pw-collection-style-track]');
    var keepVisuals = !!(track &&
      track.querySelector('.pw-collection-styles__card'));
    if (keepVisuals) {
      if (track) {
        track.setAttribute('aria-busy', 'false');
        var pendingCounts = track.querySelectorAll('[data-pw-directory-count]');
        for (var countIndex = 0; countIndex < pendingCounts.length; countIndex++) {
          pendingCounts[countIndex].textContent = '';
          pendingCounts[countIndex].setAttribute('aria-hidden', 'true');
          pendingCounts[countIndex].setAttribute('data-pw-directory-count-pending', '');
        }
        var pendingCards = track.querySelectorAll('[data-pw-collection-style-key]');
        for (var cardIndex = 0; cardIndex < pendingCards.length; cardIndex++) {
          var pendingName = String(
            pendingCards[cardIndex].getAttribute('data-pw-collection-style-value') || ''
          ).trim();
          if (pendingName) {
            pendingCards[cardIndex].setAttribute('aria-label', 'Shop ' + pendingName);
          }
        }
      }
      if (directory) {
        directory.hidden = false;
        directory.classList.add('is-counts-pending');
        directory.classList.remove('is-refreshing');
      }
      state.collectionStyleDirectoryReady = true;
      return;
    }
    state.collectionStyleDirectoryDimension = dimension;
    state.collectionStyleDirectoryReady = false;
    state.collectionStyleDirectoryItems = [];
    if (track) {
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
    }
    if (directory) directory.hidden = true;
  }

  function setCollectionDirectoryValue(dimension, value) {
    dimension = dimension === 'style' ? 'style' : 'type';
    value = String(value || '').trim();
    var current = selectedCollectionDirectoryValue(dimension);
    if (value && current.toLowerCase() === value.toLowerCase()) return;
    clearCollectionDirectoryParameters(dimension === 'type'
      ? ['type', 'style'] : ['style']);
    if (value) {
      state.selectedValues['catalog:' + dimension + ':' + value.toLowerCase()] = {
        input: { catalogParameter: dimension, catalogValue: value },
        label: value
      };
    }
    prepareCollectionDirectoryTransition(dimension === 'type' && value ? 'style' : dimension);
    renderActiveFilters();
    refreshCatalogFacetsForCurrentScope();
    resetPagination();
    navigateWithHistory(1, null);
  }

  function returnCollectionDirectoryToTypes() {
    clearCollectionDirectoryParameters(['type', 'style']);
    prepareCollectionDirectoryTransition('type');
    renderActiveFilters();
    refreshCatalogFacetsForCurrentScope();
    resetPagination();
    navigateWithHistory(1, null);
  }

  // Preserve native touch swiping and add grab-to-scroll for desktop mice.
  // A completed drag suppresses the synthetic click so releasing over a card
  // never applies a Type or Style accidentally.
  function bindCollectionDirectoryDrag(track) {
    if (!track || track._pwCollectionDragReady ||
        typeof track.addEventListener !== 'function') return;
    track._pwCollectionDragReady = true;
    var pressed = false;
    var dragged = false;
    var pointerId = null;
    var startX = 0;
    var startLeft = 0;

    track.addEventListener('pointerdown', function (event) {
      if ((event.pointerType && event.pointerType !== 'mouse') || event.button !== 0) return;
      pressed = true;
      dragged = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', function (event) {
      if (!pressed || event.pointerId !== pointerId) return;
      var distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) < 8) return;
      if (!dragged) {
        dragged = true;
        if (typeof track.setPointerCapture === 'function') {
          try { track.setPointerCapture(pointerId); } catch (error) {}
        }
      }
      track.classList.add('is-dragging');
      track.scrollLeft = startLeft - distance;
      event.preventDefault();
    });
    function release(event) {
      if (!pressed || (event.pointerId != null && event.pointerId !== pointerId)) return;
      if (dragged) track._pwCollectionSuppressClickUntil = Date.now() + 350;
      pressed = false;
      dragged = false;
      track.classList.remove('is-dragging');
      if (typeof track.releasePointerCapture === 'function' && pointerId != null) {
        try { track.releasePointerCapture(pointerId); } catch (error) {}
      }
      pointerId = null;
    }
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('dragstart', function (event) { event.preventDefault(); });
    track.addEventListener('click', function (event) {
      if (Date.now() >= (track._pwCollectionSuppressClickUntil || 0)) return;
      event.preventDefault();
      event.stopPropagation();
    });
  }

  function bindCollectionStyleDirectory() {
    var directory = collectionStyleDirectoryRoot();
    if (!directory) return;
    bindCollectionDirectoryDrag(
      directory.querySelector('[data-pw-collection-style-track]')
    );
    directory.addEventListener('click', function (event) {
      var back = event.target.closest('[data-pw-directory-back]');
      if (back && directory.contains(back)) {
        returnCollectionDirectoryToTypes();
        return;
      }
      var styleButton = event.target.closest('[data-pw-collection-style-value]');
      if (styleButton && directory.contains(styleButton)) {
        setCollectionDirectoryValue(
          styleButton.getAttribute('data-pw-directory-value-dimension'),
          styleButton.getAttribute('data-pw-collection-style-value')
        );
        return;
      }
      var scrollButton = event.target.closest('[data-pw-collection-style-scroll]');
      var track = directory.querySelector('[data-pw-collection-style-track]');
      if (!scrollButton || !track || !directory.contains(scrollButton)) return;
      var direction = Number(scrollButton.getAttribute('data-pw-collection-style-scroll')) < 0 ? -1 : 1;
      var distance = Math.max(260, Math.round(track.clientWidth * .84)) * direction;
      if (typeof track.scrollBy === 'function') {
        track.scrollBy({ left: distance, behavior: 'smooth' });
      } else {
        track.scrollLeft += distance;
      }
    });
  }

  function buildShell() {
    var heading = searchHeadingHtml();
    var introHtml =
      heading ? '<header class="pw-search__intro' +
        (state.colourPage ? ' pw-search__intro--colour-page' : '') + '">' +
          '<h1 class="pw-search__query">' + heading + '</h1>' +
        '</header>' : '';

    var defaultSortLabel = state.exactColourHex
      ? 'Closest colour first'
      : state.trending
      ? 'Trending now'
      : (state.similarSku
        ? 'Best match'
        : 'Most relevant · newest first');
    var newestSortOption = '<option value="DATE_DESC">Newest first</option>';

    var wardrobeToggleHtml =
      '<div class="pw-wardrobe-search-toggle" id="PrewornWardrobeSearchToggle">' +
        '<span class="pw-wardrobe-search-leading">' +
          '<span class="pw-wardrobe-search-emblem" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" focusable="false"><path d="M8.7 8.2a3.3 3.3 0 1 1 6.6 0c0 1.3-.7 2.1-1.8 2.8l5.6 3.2a1.7 1.7 0 0 1-.9 3.2H5.8a1.7 1.7 0 0 1-.9-3.2l7.1-4.1"/></svg>' +
          '</span>' +
          '<span class="pw-wardrobe-search-copy"><strong>My Wardrobe</strong><span class="pw-wardrobe-search-select-shell" data-pw-search-wardrobe-select-shell hidden><select class="pw-wardrobe-search-select" data-pw-search-wardrobe-select aria-label="Choose active wardrobe" hidden></select></span><small hidden></small></span>' +
        '</span>' +
        '<span class="pw-wardrobe-search-metrics" data-pw-wardrobe-metrics hidden aria-label="Live wardrobe activity">' +
          '<span><strong data-pw-wardrobe-available-count-value>—</strong><small>Available</small></span>' +
          '<span><strong data-pw-wardrobe-new-count-value>—</strong><small>New this week</small></span>' +
          '<span><strong data-pw-wardrobe-sold-count-value>—</strong><small>Sold · 30 days</small></span>' +
        '</span>' +
        '<span class="pw-wardrobe-search-actions">' +
          '<button type="button" class="pw-wardrobe-search-add" data-pw-add-collection hidden>+ Add to wardrobe</button>' +
          '<button type="button" class="pw-wardrobe-search-edit" data-pw-edit-wardrobe hidden>Edit wardrobe</button>' +
          '<button type="button" class="pw-wardrobe-search-power" data-pw-search-wardrobe><span class="pw-wardrobe-search-switch" aria-hidden="true"><i></i></span><span data-pw-wardrobe-state>Set up</span></button>' +
        '</span>' +
      '</div>';

    var unifiedBrowseLayout = usesInlineShadePicker();
    app.classList.toggle('is-empty-search', state.emptySearchLanding);
    app.classList.toggle('is-wardrobe-page-browse', state.wardrobePageBrowse);
    app.classList.toggle('pwcolour', unifiedBrowseLayout);
    app.classList.toggle('pwcolour--inline-browse', unifiedBrowseLayout);
    app.innerHTML =
      inlineShadePickerHtml() +
      collectionStyleDirectoryHtml() +
      '<div class="pw-search' + ((state.colourPage || unifiedBrowseLayout) ? ' pw-search--colour-page' : '') +
        ((state.mode === 'search' || state.mode === 'wishlist') ? ' pw-search--search-page' : '') + '">' +
        '<aside class="pw-search__filters" id="PrewornFilterRail" role="dialog" aria-modal="false" aria-labelledby="PrewornFilterDrawerTitle">' +
          '<div class="pw-search__filter-head">' +
            '<button type="button" class="pw-search__filter-close pw-search__filter-close--icon" data-pw-filter-close aria-label="Close filters">' +
              '<span aria-hidden="true">&times;</span>' +
            '</button>' +
            '<strong id="PrewornFilterDrawerTitle">Filter Products</strong>' +
            '<div class="pw-search__filter-actions">' +
              '<button type="button" class="pw-search__clear" data-pw-clear>Clear all</button>' +
              '<button type="button" class="pw-search__filter-close-text" data-pw-filter-close>Close</button>' +
            '</div>' +
          '</div>' +
          '<div class="pw-search__filter-scroll" data-pw-filter-scroll>' +
            exactColourControlHtml() +
            '<div id="PrewornSearchFiltersBody"></div>' +
          '</div>' +
          '<div class="pw-search__filter-footer">' +
            '<button type="button" class="pw-search__filter-view" data-pw-filter-view>' +
              '<span>View</span> <strong data-pw-filter-view-count>…</strong> <span data-pw-filter-view-label>products</span>' +
            '</button>' +
          '</div>' +
        '</aside>' +

        '<section class="pw-search__results">' +
          ((state.colourPage || state.mode === 'search' || state.mode === 'wishlist' ||
            state.collectionHero) ? '' : introHtml) +

          tagShadePickerHtml() +

          (state.mode === 'collection' || app.getAttribute('data-tag-page') === 'true'
            ? '<div class="pw-pref-prompt-host" id="PrewornCollectionPreferencePrompt" hidden></div>'
            : '') +

          colourPageProductSearchHtml() +

          wardrobeToggleHtml +

          '<div class="pw-quick-filters" id="PrewornQuickFilters" aria-label="Quick filters"></div>' +
          '<div class="pw-active-filters" id="PrewornActiveFilters" hidden></div>' +

          '<div class="pw-search__result-count"><strong id="PrewornToolbarCount"></strong></div>' +
          '<span class="pw-search__toolbar-sentinel" data-pw-mobile-toolbar-sentinel aria-hidden="true"></span>' +
          '<div class="pw-search__toolbar" data-pw-mobile-catalogue-tools>' +
            '<button type="button" class="pw-search__filter-toggle" data-pw-filter-toggle aria-expanded="false" aria-controls="PrewornFilterRail">' +
              '<svg class="pw-search__filter-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
                '<path d="M3 6h5m4 0h9M3 12h10m4 0h4M3 18h3m4 0h11"/>' +
                '<circle cx="10" cy="6" r="2"/><circle cx="15" cy="12" r="2"/><circle cx="8" cy="18" r="2"/>' +
              '</svg>' +
              '<span class="pw-search__filter-label">Filter</span>' +
              '<span class="pw-search__filter-count" id="PrewornActiveFilterCount" hidden>0</span>' +
            '</button>' +

            '<label class="pw-search__sort">' +
              '<span class="pw-search__sort-desktop">Sort:</span>' +
              '<span class="pw-search__sort-mobile" aria-hidden="true">' +
                '<svg class="pw-search__sort-icon" viewBox="0 0 24 24" focusable="false">' +
                  '<path d="M8 4v16M8 4 5 7M8 4l3 3M16 20V4m0 16-3-3m3 3 3-3"/>' +
                '</svg><strong>Sort</strong>' +
              '</span>' +
              '<select id="PrewornSearchSort" aria-label="Sort products">' +
                '<option value="DEFAULT">' + defaultSortLabel + '</option>' +
                newestSortOption +
                '<option value="PRICE_ASC">Price: low to high</option>' +
                '<option value="PRICE_DESC">Price: high to low</option>' +
              '</select>' +
            '</label>' +
          '</div>' +

          '<div class="pw-search__loading" id="PrewornSearchLoading" hidden>' +
            '<span class="pw-search__spinner"></span> Loading products…' +
          '</div>' +

          '<div class="pw-search__error" id="PrewornSearchError" hidden></div>' +

          '<ul class="pw-search__grid" id="PrewornSearchGrid"></ul>' +

          '<nav class="pw-search__pagination" id="PrewornSearchPagination" aria-label="Product pagination"></nav>' +
        '</section>' +
      '</div>';

    // The shade controls belong directly beneath the global Shop by Colour
    // spectrum. Moving the completed component (rather than duplicating its
    // markup) keeps one source of state and places it before page-specific
    // style rails, metrics, search controls and products.
    mountCollectionStyleDirectory();
    mountInlineShadePickerBelowSpectrum();
    mountFamilyShadePickerBelowSpectrum();
    paintInlineShadePicker(state.exactColourHex);

  }

  function renderWardrobePageSetup() {
    if (!state.wardrobePageBrowse || !app) return;
    app.classList.add('is-wardrobe-unconfigured');
    setLoading(false);
    clearError();
    var quickFilters = document.getElementById('PrewornQuickFilters');
    var activeFilters = document.getElementById('PrewornActiveFilters');
    var toolbarCount = document.getElementById('PrewornToolbarCount');
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    var pagination = document.getElementById('PrewornSearchPagination');
    var grid = document.getElementById('PrewornSearchGrid');
    if (quickFilters) quickFilters.innerHTML = '';
    if (activeFilters) {
      activeFilters.innerHTML = '';
      activeFilters.hidden = true;
    }
    if (toolbarCount) toolbarCount.textContent = '';
    if (filterBody) filterBody.innerHTML = '';
    if (pagination) pagination.innerHTML = '';
    if (grid) {
      grid.innerHTML = '<li class="pw-search__empty pw-search__empty--wardrobe">' +
        '<strong>Build your wardrobe to see every matching piece</strong>' +
        '<span>Choose your department, sizes and styles, then this page will stay updated with all available stock.</span>' +
        '<button type="button" data-pw-build-wardrobe>Build my wardrobe</button>' +
      '</li>';
      grid.setAttribute('aria-busy', 'false');
      var setupButton = grid.querySelector('[data-pw-build-wardrobe]');
      if (setupButton) setupButton.addEventListener('click', function () {
        if (window.PrewornWardrobe && typeof window.PrewornWardrobe.open === 'function') {
          window.PrewornWardrobe.open();
        }
      });
    }
    app.setAttribute('aria-busy', 'false');
  }

  function renderExactColourControl(preservePicker) {
    var shell = app && app.querySelector('[data-pw-exact-colour]');
    if (!shell) return;
    var active = !!state.exactColourHex;
    var value = state.exactColourHex || nominalColourPickerHex(selectedNominalColour()) || state.shadePickerHex ||
      tagShadeBaseHex() || '#5278A7';
    var input = shell.querySelector('[data-pw-exact-colour-input]');
    var swatch = shell.querySelector('[data-pw-exact-colour-swatch]');
    var name = shell.querySelector('[data-pw-exact-colour-name]');
    var hex = shell.querySelector('[data-pw-exact-colour-hex]');
    var tolerance = shell.querySelector('[data-pw-exact-tolerance]');
    var toleranceShell = shell.querySelector('.pw-exact-colour__tolerance');
    var toleranceLabel = shell.querySelector('[data-pw-exact-tolerance-label]');
    var clear = shell.querySelector('[data-pw-clear-exact-colour]');
    shell.classList.toggle('is-active', active);
    if (input && document.activeElement !== input) input.value = value;
    if (swatch) swatch.style.setProperty('--pw-exact', value);
    if (name) name.textContent = active
      ? (state.exactColourName || exactColourName(value))
      : 'Choose exact colour';
    if (hex) hex.textContent = active ? value : 'Actual garment colour';
    if (tolerance) tolerance.value = String(state.exactColourTolerance);
    if (toleranceShell) toleranceShell.hidden = !active;
    if (toleranceLabel) toleranceLabel.textContent = exactToleranceLabel(state.exactColourTolerance);
    if (clear) clear.hidden = !active;
    paintInlineShadePicker(state.exactColourHex || state.shadePickerHex || value);
    renderTagShadeSelection();
    if (app) {
      window.dispatchEvent(new CustomEvent('preworn:exact-colour-state', {
        detail: {
          hex: state.exactColourHex,
          tolerance: state.exactColourTolerance,
          name: state.exactColourName || (state.exactColourHex
            ? exactColourName(state.exactColourHex) : ''),
          preservePicker: !!preservePicker || !!selectedNominalColour(),
          pickerHex: state.shadePickerHex
        }
      }));
      window.dispatchEvent(new CustomEvent('preworn:nominal-colour-state', {
        detail: {
          colour: selectedNominalColour(),
          pickerHex: state.shadePickerHex
        }
      }));
    }
  }

  function applyExactColour(hex, tolerance, name) {
    hex = normaliseExactColour(hex);
    if (!hex) return;
    tolerance = Math.max(3, Math.min(20, Number(tolerance || state.exactColourTolerance || 12) || 12));
    var removedNominalColour = clearNominalColourSelections();
    var changed = state.exactColourHex !== hex ||
      Number(state.exactColourTolerance) !== tolerance || removedNominalColour;
    state.exactColourHex = hex;
    state.exactColourTolerance = tolerance;
    state.exactColourName = String(name || exactColourName(hex));
    state.shadePickerHex = hex;
    state.shadePickerOpen = true;
    renderExactColourControl();
    renderSearchHeading();
    syncSortControl();
    if (!changed && (state.loading || state.lastResult)) return;
    state.colourRequestGeneration += 1;
    abortActiveColourRequest();
    prepareExactColourFacetState();
    resetPagination();
    navigateWithHistory(1, null);
  }

  function applyNominalColour(colour) {
    colour = String(colour || '').trim();
    if (!colour) return;
    var key = 'catalog:colour:' + colour.toLowerCase();
    var alreadySelected = !!state.selectedValues[key];
    var hadExactColour = !!state.exactColourHex;

    state.shadePickerHex = state.exactColourHex || state.shadePickerHex || '#5278A7';
    state.shadePickerOpen = true;
    clearNominalColourSelections();
    if (!alreadySelected) {
      state.selectedValues[key] = {
        input: { catalogParameter: 'colour', catalogValue: colour },
        label: colour
      };
      state.shadePickerHex = nominalColourPickerHex(colour) || state.shadePickerHex;
    }

    if (hadExactColour) {
      state.colourRequestGeneration += 1;
      abortActiveColourRequest();
    }
    state.exactColourHex = '';
    state.exactColourName = '';
    renderExactColourControl(true);
    renderSearchHeading();
    syncSortControl();
    resetWardrobeFacetState();
    resetPagination();
    navigateWithHistory(1, null);
  }

  function clearExactColour() {
    if (!state.exactColourHex) return;
    state.colourRequestGeneration += 1;
    abortActiveColourRequest();
    state.exactColourHex = '';
    state.exactColourName = '';
    state.shadePickerOpen = false;
    renderExactColourControl();
    renderSearchHeading();
    syncSortControl();
    resetWardrobeFacetState();
    resetPagination();
    navigateWithHistory(1, null);
  }

  function mobileCatalogueViewportWidth() {
    var widths = [
      document.documentElement && document.documentElement.clientWidth,
      window.visualViewport && window.visualViewport.width,
      window.screen && window.screen.width,
      window.screen && window.screen.availWidth,
      window.innerWidth
    ].map(Number).filter(function (width) {
      return isFinite(width) && width > 0;
    });
    return widths.length ? Math.round(Math.min.apply(Math, widths)) : 1000;
  }

  function isMobileCatalogueViewport() {
    if (typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 749px)').matches) return true;

    var narrowestWidth = mobileCatalogueViewportWidth();
    var touchDevice = ('ontouchstart' in window) ||
      (window.navigator && Number(window.navigator.maxTouchPoints || 0) > 0);

    // Mobile browser chrome and an invalid/late viewport meta can temporarily
    // inflate innerWidth while the actual visual screen remains phone-sized.
    return narrowestWidth <= 749 || (touchDevice && narrowestWidth <= 900);
  }

  function bindStaticEvents() {
    bindInlineShadePicker();
    bindColourPageProductSearch();
    bindCollectionStyleDirectory();
    var exactInput = app.querySelector('[data-pw-exact-colour-input]');
    if (exactInput) {
      exactInput.addEventListener('click', function (event) {
        if (!usesInlineShadePicker()) return;
        var exactButton = inlineShadePickerRoot() &&
          inlineShadePickerRoot().querySelector('[data-pw-search-shade-exact]');
        if (!exactButton) return;
        event.preventDefault();
        exactButton.click();
      });
      exactInput.addEventListener('input', function () {
        var preview = normaliseExactColour(this.value) || '#5278A7';
        var swatch = app.querySelector('[data-pw-exact-colour-swatch]');
        var name = app.querySelector('[data-pw-exact-colour-name]');
        var hex = app.querySelector('[data-pw-exact-colour-hex]');
        if (swatch) swatch.style.setProperty('--pw-exact', preview);
        if (name) name.textContent = exactColourName(preview);
        if (hex) hex.textContent = preview;
      });
      exactInput.addEventListener('change', function () {
        applyExactColour(this.value, state.exactColourTolerance, exactColourName(this.value));
      });
    }
    var exactTolerance = app.querySelector('[data-pw-exact-tolerance]');
    if (exactTolerance) {
      exactTolerance.addEventListener('input', function () {
        var label = app.querySelector('[data-pw-exact-tolerance-label]');
        if (label) label.textContent = exactToleranceLabel(this.value);
      });
      exactTolerance.addEventListener('change', function () {
        if (!state.exactColourHex) return;
        applyExactColour(state.exactColourHex, this.value, state.exactColourName);
      });
    }
    var exactClear = app.querySelector('[data-pw-clear-exact-colour]');
    if (exactClear) exactClear.addEventListener('click', clearExactColour);
    window.addEventListener('preworn:exact-colour-change', function (event) {
      var detail = event && event.detail ? event.detail : {};
      applyExactColour(detail.hex, detail.tolerance, detail.name);
    });
    window.addEventListener('preworn:nominal-colour-change', function (event) {
      var detail = event && event.detail ? event.detail : {};
      applyNominalColour(detail.colour);
    });

    var editWardrobe = app.querySelector('[data-pw-edit-wardrobe]');
    if (editWardrobe) {
      editWardrobe.addEventListener('click', function () {
        if (window.PrewornWardrobe && typeof window.PrewornWardrobe.open === 'function') {
          window.PrewornWardrobe.open();
        }
      });
    }
    var wardrobeSelect = app.querySelector('[data-pw-search-wardrobe-select]');
    if (wardrobeSelect) {
      wardrobeSelect.addEventListener('change', function () {
        if (!window.PrewornWardrobe) return;
        var selectorValue = String(this.value || '');
        var guestAudience = selectorValue.indexOf('audience:') === 0
          ? selectorValue.slice('audience:'.length) : '';
        var wardrobeId = selectorValue.indexOf('id:') === 0
          ? Number(selectorValue.slice('id:'.length)) : 0;
        var nextWardrobe = typeof window.PrewornWardrobe.getWardrobes === 'function'
          ? window.PrewornWardrobe.getWardrobes().filter(function (item) {
            return guestAudience
              ? !item.id && item.audience === guestAudience
              : Number(item.id) === wardrobeId;
          })[0]
          : null;
        if (!nextWardrobe) return;
        state.linkedWardrobeParameters = {};
        if (guestAudience &&
            typeof window.PrewornWardrobe.selectGuestWardrobe === 'function') {
          window.PrewornWardrobe.selectGuestWardrobe(guestAudience, true);
          return;
        }
        if (wardrobeId && typeof window.PrewornWardrobe.selectWardrobe === 'function') {
          window.PrewornWardrobe.selectWardrobe(wardrobeId, true);
        }
      });
    }
    var addCollection = app.querySelector('[data-pw-add-collection]');
    if (addCollection) {
      addCollection.addEventListener('click', function () {
        if (!canAddCurrentCollectionToWardrobe()) return;
        if (!window.PrewornWardrobe || typeof window.PrewornWardrobe.addCategory !== 'function') {
          if (window.PrewornWardrobe) window.PrewornWardrobe.open();
          return;
        }
        var button = this;
        var preferenceContext = collectionPreferenceContext();
        if (!preferenceContext) return;
        button.disabled = true;
        button.textContent = 'Adding…';
        window.PrewornWardrobe.addCategory(preferenceContext.categoryKey).catch(function () {
          button.disabled = false;
          button.textContent = 'Try again';
        });
      });
    }
    var wardrobeToggle = app.querySelector('[data-pw-search-wardrobe]');
    if (wardrobeToggle) {
      wardrobeToggle.addEventListener('click', function () {
        syncWardrobeState();
        if (!state.wardrobe || !state.wardrobe.categories || !state.wardrobe.categories.length) {
          if (window.PrewornWardrobe) window.PrewornWardrobe.open();
          return;
        }
        if (state.wardrobePageBrowse) {
          if (!state.wardrobeActive) window.PrewornWardrobe.setActive(true);
          return;
        }
        state.linkedWardrobeParameters = {};
        window.PrewornWardrobe.setActive(!state.wardrobeActive);
      });
    }
    var clear = app.querySelector('[data-pw-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        state.selectedValues = {};
        state.interpretedSearchText = '';
        state.interpretedSearchIntent = null;
        state.priceMin = '';
        state.priceMax = '';
        state.exactColourHex = '';
        state.exactColourName = '';
        state.shadePickerOpen = false;
        clearCollectionQuery();
        clearSearchQuery(true);
        prepareCollectionDirectoryTransition('type');
        renderExactColourControl();
        syncSortControl();
        resetWardrobeFacetState();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var sort = document.getElementById('PrewornSearchSort');
    if (sort) {
      sort.addEventListener('change', function () {
        state.trending = false;
        if (this.value === 'PRICE_ASC') {
          state.sortKey = 'PRICE';
          state.reverse = false;
        } else if (this.value === 'PRICE_DESC') {
          state.sortKey = 'PRICE';
          state.reverse = true;
        } else if (this.value === 'DATE_DESC') {
          state.sortKey = 'CREATED';
          state.reverse = true;
        } else {
          state.sortKey = state.mode === 'collection'
            ? 'COLLECTION_DEFAULT'
            : 'RELEVANCE';
          state.reverse = false;
        }

        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var filterToggle = app.querySelector('[data-pw-filter-toggle]');
    var filterRail = document.getElementById('PrewornFilterRail');

    if (filterToggle && filterRail) {
      filterToggle.addEventListener('click', function () {
        setMobileFiltersOpen(!filterRail.classList.contains('is-mobile-open'));
      });
    }

    var filterCloseButtons = app.querySelectorAll('[data-pw-filter-close]');
    for (var fcb = 0; fcb < filterCloseButtons.length; fcb++) {
      filterCloseButtons[fcb].addEventListener('click', function () {
        setMobileFiltersOpen(false, true);
      });
    }

    var filterView = app.querySelector('[data-pw-filter-view]');
    if (filterView) {
      filterView.addEventListener('click', function () {
        setMobileFiltersOpen(false, true);
      });
    }

    app.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && filterRail &&
          filterRail.classList.contains('is-mobile-open')) {
        setMobileFiltersOpen(false, true);
      }
    });

    window.addEventListener('resize', function () {
      if (!filterRail) return;
      var keepOpen = isMobileCatalogueViewport() &&
        filterRail.classList.contains('is-mobile-open');
      setMobileFiltersOpen(keepOpen, false);
    });

    setMobileFiltersOpen(false, false);
  }

  function setMobileFiltersOpen(open, restoreFocus) {
    var filterRail = document.getElementById('PrewornFilterRail');
    var filterToggles = document.querySelectorAll('[data-pw-filter-toggle]');
    var filterToggle = document.querySelector('.pw-search__toolbar--dock.is-active [data-pw-filter-toggle]') ||
      document.querySelector('[data-pw-filter-toggle]');
    if (!filterRail) return;
    var wasOpen = filterRail.classList.contains('is-mobile-open');

    if (isMobileCatalogueViewport()) {
      var viewportWidth = mobileCatalogueViewportWidth();
      filterRail.style.setProperty('width', viewportWidth + 'px', 'important');
      filterRail.style.setProperty('max-width', viewportWidth + 'px', 'important');
      filterRail.style.setProperty('right', 'auto', 'important');
    } else {
      filterRail.style.removeProperty('width');
      filterRail.style.removeProperty('max-width');
      filterRail.style.removeProperty('right');
    }

    filterRail.classList.toggle('is-mobile-open', !!open);
    filterRail.setAttribute('aria-modal', open && isMobileCatalogueViewport() ? 'true' : 'false');
    if (!open && !isMobileCatalogueViewport()) {
      filterRail.removeAttribute('aria-hidden');
    } else {
      filterRail.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
    document.body.classList.toggle('pw-search-filter-open', !!open);

    for (var ft = 0; ft < filterToggles.length; ft++) {
      filterToggles[ft].setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (open && !wasOpen) {
      resetMobileFilterLayer();
      var closeButton = filterRail.querySelector('[data-pw-filter-close]');
      if (closeButton) {
        window.setTimeout(function () { closeButton.focus(); }, 0);
      }
    } else if (!open && restoreFocus && filterToggle) {
      filterToggle.focus();
    }
  }

  function resetMobileFilterLayer() {
    var filterRail = document.getElementById('PrewornFilterRail');
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    state.mobileActiveFilterGroup = '';
    if (filterRail) filterRail.classList.remove('is-mobile-filter-layered');
    if (!filterBody) return;
    filterBody.classList.remove('is-mobile-layered');
    var groups = filterBody.querySelectorAll('[data-pw-filter-group]');
    for (var i = 0; i < groups.length; i++) {
      groups[i].classList.remove('is-mobile-active');
    }
    var buttons = filterBody.querySelectorAll('[data-pw-filter-layer-button]');
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].classList.remove('is-active');
      buttons[b].setAttribute('aria-selected', 'false');
    }
  }

  function openMobileFilterLayer(group) {
    if (!group || !isMobileCatalogueViewport()) return false;
    var filterRail = document.getElementById('PrewornFilterRail');
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    if (!filterRail || !filterBody) return false;
    var key = group.getAttribute('data-pw-filter-group') || '';
    state.mobileActiveFilterGroup = key;
    filterRail.classList.add('is-mobile-filter-layered');
    filterBody.classList.add('is-mobile-layered');
    var filterScroll = filterRail.querySelector('[data-pw-filter-scroll]');
    if (filterScroll) filterScroll.scrollTop = 0;
    var groups = filterBody.querySelectorAll('[data-pw-filter-group]');
    for (var i = 0; i < groups.length; i++) {
      var active = groups[i] === group;
      groups[i].classList.toggle('is-mobile-active', active);
      groups[i].open = active;
    }
    var buttons = filterBody.querySelectorAll('[data-pw-filter-layer-button]');
    for (var b = 0; b < buttons.length; b++) {
      var selected = buttons[b].getAttribute('data-pw-filter-layer-button') === key;
      buttons[b].classList.toggle('is-active', selected);
      buttons[b].setAttribute('aria-selected', selected ? 'true' : 'false');
    }
    var pane = group.querySelector('.pw-filter__body');
    if (pane) pane.scrollTop = 0;
    return true;
  }

  function bindMobileFilterLayerEvents() {
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    if (!filterBody || filterBody._pwMobileLayerEventsReady) return;
    filterBody._pwMobileLayerEventsReady = true;

    // Facet responses rebuild every category button. Delegate from the stable
    // drawer body so a late or rapid refresh can never leave visible buttons
    // with stale or missing click listeners.
    filterBody.addEventListener('click', function (event) {
      var target = event.target;
      if (target && target.nodeType === 3) target = target.parentElement;
      var button = target && typeof target.closest === 'function'
        ? target.closest('[data-pw-filter-layer-button]')
        : null;
      if (!button || !filterBody.contains(button)) return;
      event.preventDefault();
      var key = button.getAttribute('data-pw-filter-layer-button') || '';
      var filterGroups = filterBody.querySelectorAll('[data-pw-filter-group]');
      for (var groupIndex = 0; groupIndex < filterGroups.length; groupIndex++) {
        if (filterGroups[groupIndex].getAttribute('data-pw-filter-group') !== key) continue;
        openMobileFilterLayer(filterGroups[groupIndex]);
        return;
      }
    });
  }

  function buildMobileFilterLayers() {
    var filterBody = document.getElementById('PrewornSearchFiltersBody');
    if (!filterBody || filterBody.querySelector('.pw-filter-layer__panes')) return;
    var groups = filterBody.querySelectorAll('[data-pw-filter-group]');
    if (!groups.length) return;
    var nav = document.createElement('div');
    var panes = document.createElement('div');
    nav.className = 'pw-filter-layer__nav';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Filter categories');
    panes.className = 'pw-filter-layer__panes';
    var groupList = [];
    for (var i = 0; i < groups.length; i++) groupList.push(groups[i]);
    for (var g = 0; g < groupList.length; g++) {
      var group = groupList[g];
      var key = group.getAttribute('data-pw-filter-group') || '';
      var summaryLabel = group.querySelector('summary span');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'pw-filter-layer__button';
      button.setAttribute('data-pw-filter-layer-button', key);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', 'false');
      var buttonLabel = document.createElement('span');
      buttonLabel.className = 'pw-filter-layer__label';
      buttonLabel.textContent = summaryLabel ? summaryLabel.textContent : 'Filter';
      button.appendChild(buttonLabel);
      // Bind the freshly-created category control directly as well as keeping
      // the delegated fallback below. Some mobile WebViews retarget a tap
      // during drawer/viewport animation and do not reliably bubble it from
      // the nested label span.
      (function (layerButton, layerGroup) {
        layerButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          openMobileFilterLayer(layerGroup);
        });
      })(button, group);
      var selectedCount = 0;
      var groupValues = group.querySelectorAll('[data-pw-filter-id]');
      for (var v = 0; v < groupValues.length; v++) {
        if (state.selectedValues[groupValues[v].getAttribute('data-pw-filter-id')]) {
          selectedCount += 1;
        }
      }
      var selection = document.createElement('span');
      selection.className = 'pw-filter-layer__selection';
      selection.hidden = selectedCount === 0;
      selection.textContent = String(selectedCount);
      button.appendChild(selection);
      if (selectedCount) {
        button.setAttribute('aria-label', buttonLabel.textContent + ', ' +
          selectedCount + ' selected');
      }
      var groupBody = group.querySelector('.pw-filter__body');
      if (groupBody && groupValues.length) {
        var hint = document.createElement('p');
        hint.className = 'pw-filter-layer__hint';
        hint.textContent = 'Choose one or more';
        groupBody.insertBefore(hint, groupBody.firstChild);
      }
      nav.appendChild(button);
      panes.appendChild(group);
    }
    filterBody.appendChild(nav);
    filterBody.appendChild(panes);
  }

  function bindMobileCatalogueToolbarDock() {
    if (!app) return;
    var source = app.querySelector('[data-pw-mobile-catalogue-tools]');
    var sentinel = app.querySelector('[data-pw-mobile-toolbar-sentinel]');
    var results = source && source.closest('.pw-search__results');
    if (!source || !sentinel || !results || source._pwMobileDockReady) return;
    source._pwMobileDockReady = true;

    var dock = null;
    var frame = 0;
    var intersectionObserver = null;
    var resizeObserver = null;
    var mutationObserver = null;

    function syncDock() {
      if (!dock) return;
      var sourceSelect = source.querySelector('select');
      var dockSelect = dock.querySelector('select');
      if (sourceSelect && dockSelect) {
        dockSelect.value = sourceSelect.value;
        dockSelect.disabled = sourceSelect.disabled;
        dockSelect.title = sourceSelect.title;
        var sourceSort = sourceSelect.closest('.pw-search__sort');
        var dockSort = dockSelect.closest('.pw-search__sort');
        if (sourceSort && dockSort) {
          dockSort.classList.toggle('is-disabled', sourceSort.classList.contains('is-disabled'));
        }
      }
      var sourceBadge = source.querySelector('.pw-search__filter-count');
      var dockBadge = dock.querySelector('.pw-search__filter-count');
      if (sourceBadge && dockBadge) {
        dockBadge.textContent = sourceBadge.textContent;
        dockBadge.hidden = sourceBadge.hidden;
      }
      var sourceToggle = source.querySelector('[data-pw-filter-toggle]');
      var dockToggle = dock.querySelector('[data-pw-filter-toggle]');
      if (sourceToggle && dockToggle) {
        dockToggle.setAttribute('aria-expanded', sourceToggle.getAttribute('aria-expanded') || 'false');
      }
    }

    function placeDock() {
      if (!dock) return;
      var rect = app.getBoundingClientRect();
      var viewportWidth = mobileCatalogueViewportWidth();
      var left = Math.max(0, Math.min(viewportWidth, Math.round(rect.left)));
      var rightEdge = Math.max(left, Math.min(viewportWidth, Math.round(rect.right)));
      var width = Math.max(0, rightEdge - left);
      dock.style.setProperty('left', left + 'px', 'important');
      dock.style.setProperty('right', 'auto', 'important');
      dock.style.setProperty('width', width + 'px', 'important');
      dock.style.setProperty('max-width', width + 'px', 'important');
    }

    function createDock() {
      if (dock) return;
      dock = source.cloneNode(true);
      dock.classList.remove('is-mobile-dock-source');
      dock.classList.add('pw-search__toolbar--dock');
      dock.removeAttribute('data-pw-mobile-catalogue-tools');
      dock.setAttribute('data-pw-mobile-catalogue-dock', '');
      var identified = dock.querySelectorAll('[id]');
      for (var idIndex = 0; idIndex < identified.length; idIndex++) {
        identified[idIndex].removeAttribute('id');
      }
      var dockToggle = dock.querySelector('[data-pw-filter-toggle]');
      if (dockToggle) {
        dockToggle.addEventListener('click', function () {
          setMobileFiltersOpen(true);
        });
      }
      var dockSelect = dock.querySelector('select');
      var sourceSelect = source.querySelector('select');
      if (dockSelect && sourceSelect) {
        dockSelect.addEventListener('change', function () {
          sourceSelect.value = dockSelect.value;
          sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
          syncDock();
        });
      }
      document.body.appendChild(dock);
      dock.setAttribute('aria-hidden', 'true');
      placeDock();
      syncDock();
    }

    function releaseDock() {
      source.classList.remove('is-mobile-dock-source');
      if (!dock) return;
      dock.classList.remove('is-active');
      dock.setAttribute('aria-hidden', 'true');
    }

    function activateDock() {
      createDock();
      if (!dock) return;
      source.classList.add('is-mobile-dock-source');
      dock.classList.add('is-active');
      dock.setAttribute('aria-hidden', 'false');
      placeDock();
      syncDock();
    }

    function updateDock() {
      frame = 0;
      var mobile = isMobileCatalogueViewport();
      var sourceDisplay = window.getComputedStyle(source).display;
      if (!mobile || sourceDisplay === 'none') {
        releaseDock();
        return;
      }
      var sentinelRect = sentinel.getBoundingClientRect();
      var resultsRect = results.getBoundingClientRect();
      // The theme can scroll a nested catalogue container while window.scrollY
      // remains zero (especially while Android browser chrome is collapsing).
      // The sentinel's viewport position is the canonical crossing signal.
      var shouldDock = sentinelRect.top <= 0 && resultsRect.bottom > 58;
      if (!shouldDock) {
        releaseDock();
        return;
      }
      activateDock();
    }

    function scheduleDock() {
      if (frame) return;
      frame = window.requestAnimationFrame(updateDock);
    }

    window.addEventListener('scroll', scheduleDock, { passive: true });
    document.addEventListener('scroll', scheduleDock, { passive: true, capture: true });
    document.addEventListener('touchmove', scheduleDock, { passive: true });
    document.addEventListener('touchend', scheduleDock, { passive: true });
    window.addEventListener('resize', scheduleDock);
    window.addEventListener('orientationchange', scheduleDock);
    if ('onscrollend' in window) window.addEventListener('scrollend', scheduleDock, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleDock);
      window.visualViewport.addEventListener('scroll', scheduleDock);
    }
    if (typeof window.IntersectionObserver === 'function') {
      intersectionObserver = new window.IntersectionObserver(scheduleDock, { threshold: [0, 1] });
      intersectionObserver.observe(sentinel);
    }
    if (typeof window.ResizeObserver === 'function') {
      resizeObserver = new window.ResizeObserver(scheduleDock);
      resizeObserver.observe(app);
      resizeObserver.observe(results);
      resizeObserver.observe(source);
    }
    if (typeof window.MutationObserver === 'function') {
      mutationObserver = new window.MutationObserver(function () {
        syncDock();
        scheduleDock();
      });
      mutationObserver.observe(source, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    createDock();
    scheduleDock();
    window.setInterval(scheduleDock, 100);
  }

  function resetPagination() {
    state.currentPage = 1;
    state.pageCursors = { 1: null };
    state.colourFirstPageSeed = [];
  }

  function syncSortControl() {
    var sort = document.getElementById('PrewornSearchSort');
    if (!sort) return;
    var defaultOption = sort.querySelector('option[value="DEFAULT"]');
    if (defaultOption) {
      defaultOption.textContent = state.exactColourHex
        ? 'Closest colour first'
        : state.trending
          ? 'Trending now'
          : (state.similarSku ? 'Best match' : 'Most relevant · newest first');
    }
    sort.disabled = !!state.exactColourHex;
    var sortShell = sort.closest ? sort.closest('.pw-search__sort') : null;
    if (sortShell) sortShell.classList.toggle('is-disabled', sort.disabled);
    sort.title = state.exactColourHex
      ? 'Exact-colour results are always sorted by closest colour.'
      : '';
    if (state.exactColourHex) {
      sort.value = 'DEFAULT';
      return;
    }
    if (state.sortKey === 'PRICE') {
      sort.value = state.reverse ? 'PRICE_DESC' : 'PRICE_ASC';
    } else if (state.sortKey === 'CREATED') {
      sort.value = 'DATE_DESC';
    } else {
      sort.value = 'DEFAULT';
    }
  }

  function historyUrl(pageNumber) {
    var url = new URL(window.location.href);
    [
      'department', 'category', 'type', 'style', 'vendor', 'size',
      'colour', 'condition', 'material', 'pattern', 'price_min',
      'price_max', 'colour_hex', 'colour_tolerance', 'page'
    ].forEach(function (parameter) {
      url.searchParams.delete(parameter);
    });
    WARDROBE_URL_PARAMETERS.forEach(function (parameter) {
      url.searchParams.delete(parameter);
    });
    url.searchParams.delete('wardrobe_mode');
    url.searchParams.delete('q');
    var productQuery = activeProductSearchQuery();
    if (productQuery) url.searchParams.set('q', productQuery);
    appendCatalogFilters(url.searchParams);
    appendWardrobeParameters(url.searchParams);
    if (state.exactColourHex) {
      url.searchParams.set('colour_hex', state.exactColourHex);
      url.searchParams.set('colour_tolerance', String(state.exactColourTolerance));
      url.searchParams.delete('sort');
    } else {
      url.searchParams.set('sort', catalogSortValue());
    }
    if (pageNumber > 1) url.searchParams.set('page', String(pageNumber));
    return url.pathname + url.search + url.hash;
  }

  function navigateWithHistory(pageNumber, afterCursor) {
    var target = historyUrl(pageNumber);
    var current = window.location.pathname + window.location.search + window.location.hash;
    if (target !== current) {
      window.history.pushState(
        Object.assign({}, window.history.state || {}, {
          prewornBrowse: true,
          page: pageNumber
        }),
        '',
        target
      );
    }
    if (typeof window.CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('preworn:catalogue-url-change', {
        detail: { url: target }
      }));
    }
    renderSearchHeading();
    requestCollectionStyleDirectory({ prepareOnly: true });
    if (state.emptySearchLanding) {
      showLoading(false);
      return;
    }
    loadPage(pageNumber, afterCursor);
  }

  function bindBrowserHistory() {
    if (state.browserHistoryBound) return;
    state.browserHistoryBound = true;
    window.addEventListener('popstate', function () {
      // Re-enter through the normal URL bootstrap so every filter, facet,
      // sort and page is restored without creating another history entry.
      window.location.reload();
    });
  }

  function selectedCatalogueFilters() {
    var selected = {};
    Object.keys(state.selectedValues).forEach(function (key) {
      var value = state.selectedValues[key] || {};
      var input = normalizeFilterInput(value.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      var catalogValue = String(input.catalogValue || '').trim();
      if (!parameter || !catalogValue || isPlaceholderFacetValue(value)) return;
      if (!selected[parameter]) selected[parameter] = [];
      selected[parameter].push(catalogValue);
    });
    return selected;
  }

  function announceCatalogueFilterState() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return;
    app.dispatchEvent(new CustomEvent('preworn:catalogue-filter-state', {
      detail: { selected: selectedCatalogueFilters() }
    }));
  }

  function pageCatalogueScrollTarget(preference) {
    if (!app || preference !== 'directory') return app;
    var page = typeof app.closest === 'function'
      ? app.closest('[data-pw-brand-page]') : null;
    if (!page) return app;
    var anchor = page.querySelector('[data-pw-style-directory-anchor]');
    if (anchor) return anchor;
    var live = page.querySelector('[data-pw-collection-style-directory]');
    if (live && !live.hidden && live.getBoundingClientRect().height > 0) return live;
    var legacy = page.querySelector('[data-pw-brand-style-directory-section]');
    if (legacy && !legacy.hidden && legacy.getBoundingClientRect().height > 0) return legacy;
    return app;
  }

  function scrollToPageCatalogue(preference) {
    if (!app) return;
    var target = pageCatalogueScrollTarget(preference);
    var offset = target === app ? 110 : 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function bindPageCatalogueFilters() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return;
    app.addEventListener('preworn:catalogue-filter', function (event) {
      var detail = event.detail || {};
      var parameter = String(detail.parameter || '').toLowerCase();
      var allowed = [
        'department', 'type', 'style', 'vendor', 'size', 'material', 'pattern', 'colour', 'category'
      ];
      if (allowed.indexOf(parameter) === -1) return;
      var value = String(detail.value || '').trim();
      if (value && isPlaceholderFacetValue(value)) return;

      if (parameter === 'department') {
        clearCollectionDirectoryParameters(['type', 'style']);
      } else if (parameter === 'type') {
        clearCollectionDirectoryParameters(['style']);
      }

      Object.keys(state.selectedValues).forEach(function (key) {
        var selected = state.selectedValues[key] || {};
        var input = normalizeFilterInput(selected.input || {});
        if (String(input.catalogParameter || '').toLowerCase() === parameter) {
          delete state.selectedValues[key];
        }
      });
      if (value) {
        state.selectedValues['catalog:' + parameter + ':' + value.toLowerCase()] = {
          input: { catalogParameter: parameter, catalogValue: value },
          label: String(detail.label || value)
        };
      }

      if (parameter === 'department') syncApplicableWardrobeForCurrentScope();

      renderActiveFilters();
      refreshCatalogFacetsForCurrentScope();
      resetPagination();
      navigateWithHistory(1, null);
      if (detail.scroll) {
        var scrollPreference = String(detail.scrollTarget || 'catalogue');
        var scrollDelay = Math.max(0, Number(detail.scrollDelay) || 0);
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          scrollDelay = 0;
        }
        var alignPageCatalogue = function () {
          window.requestAnimationFrame(function () {
            scrollToPageCatalogue(scrollPreference);
          });
        };
        if (scrollDelay) window.setTimeout(alignPageCatalogue, scrollDelay);
        else alignPageCatalogue();
      }
    });
  }

  function clearCollectionQuery() {
    if (state.mode !== 'collection' || !state.query) return;

    state.query = '';
    state.collectionQueryMatched = false;
    state.collectionQueryFilterId = '';
    state.collectionKeywordSearch = false;
    state.collectionSearchQuery = '';
    state.collectionScopeFilters = [];

  }

  function hasRemovableSearchQuery() {
    return state.mode === 'search' && !!activeProductSearchQuery() &&
      !state.trending && !state.similarSku;
  }

  function clearSearchQuery(force) {
    if (!force && !hasRemovableSearchQuery()) return;
    var previousQuery = currentProductSearchText().toLowerCase();
    clearInterpretedSearchSelections();
    state.query = state.colourPage ? '*' : '';
    state.searchAnalyticsEligible = false;
    state.searchAnalyticsPublished = false;
    state.searchAnalyticsSource = '';
    syncEmptySearchLanding();

    // Keep the desktop and mobile header search fields in step with the
    // removable filter pill without disturbing a newly typed replacement.
    var searchInputs = document.querySelectorAll('input[name="q"]');
    for (var index = 0; index < searchInputs.length; index++) {
      if (String(searchInputs[index].value || '').trim().toLowerCase() === previousQuery) {
        searchInputs[index].value = '';
      }
    }
    var colourSearchClear = app && app.querySelector('[data-pw-colour-product-search-clear]');
    if (colourSearchClear) colourSearchClear.hidden = true;
  }

  function buildProductFilters() {
    var filters = [];
    var keys = Object.keys(state.selectedValues);

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      if (selected && selected.input) {
        filters.push(selected.input);
      }
    }

    var min = parseFloat(state.priceMin);
    var max = parseFloat(state.priceMax);

    if (!isNaN(min) || !isNaN(max)) {
      var range = {};

      if (!isNaN(min)) range.min = min;
      if (!isNaN(max)) range.max = max;

      filters.push({ price: range });
    }

    return filters;
  }

  function normalizeCollectionQuery(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function collectionQueryMatchScore(label, query) {
    var cleanLabel = normalizeCollectionQuery(label);
    var cleanQuery = normalizeCollectionQuery(query);

    if (!cleanLabel || !cleanQuery) return -1;
    if (cleanLabel === cleanQuery) return 100;

    var compactLabel = cleanLabel.replace(/\s/g, '');
    var compactQuery = cleanQuery.replace(/\s/g, '');

    if (compactLabel === compactQuery) return 95;

    return -1;
  }

  function findCollectionQueryFilter(filters, query) {
    var rankedFilters = (filters || []).slice().sort(function (a, b) {
      return quickFilterPriority(a) - quickFilterPriority(b);
    });
    var best = null;

    for (var f = 0; f < rankedFilters.length; f++) {
      var filter = rankedFilters[f];
      if (filter.type !== 'LIST' && filter.type !== 'BOOLEAN') continue;

      var values = filter.values || [];
      var priorityPenalty = quickFilterPriority(filter);

      for (var v = 0; v < values.length; v++) {
        var value = values[v];
        if (!value.id || Number(value.count || 0) <= 0) continue;

        var score = collectionQueryMatchScore(value.label, query);
        if (score < 0) continue;

        score -= Math.min(priorityPenalty, 10);

        if (!best || score > best.score) {
          best = {
            value: value,
            score: score
          };
        }
      }
    }

    return best ? best.value : null;
  }

  function collectionDepartment() {
    var handle = String(state.collectionHandle || '').toLowerCase();

    if (handle.indexOf('womens-') === 0) return 'Women';
    if (handle.indexOf('mens-') === 0) return 'Men';
    if (handle.indexOf('girls-') === 0) return 'Girls';
    if (handle.indexOf('boys-') === 0) return 'Boys';
    if (handle.indexOf('baby-') === 0) return 'Baby';
    return '';
  }

  function collectionPreferenceContext() {
    if (state.mode !== 'collection' || !state.collectionHandle) {
      return state.tagWardrobePreferenceContext;
    }
    var handle = String(state.collectionHandle || '').toLowerCase();
    var audience = collectionWardrobeAudience();
    if (!audience) return null;
    if (/(?:^|-)(?:shoe|shoes|trainer|trainers|footwear|sandal|sandals|heel|heels|boot|boots)(?:-|$)/.test(handle)) {
      return null;
    }
    if ((audience === 'man' || audience === 'boy') &&
        /(?:^|-)(?:dress|dresses|jumpsuit|jumpsuits|playsuit|playsuits)(?:-|$)/.test(handle)) {
      return null;
    }

    var sizeType = /(?:dress|jumpsuit|playsuit)/.test(handle)
      ? 'dress'
      : (/(?:jean|trouser|short|skirt|skort|legging|jegging|bottom)/.test(handle)
        ? 'bottom'
        : (/(?:ties|accessor|suits-tailoring|suits-suit-separates|all-clothing)/.test(handle)
          ? 'none' : 'top'));
    if (sizeType === 'none') return null;

    var label = String(state.collectionTitle || handle.replace(/-/g, ' '))
      .replace(/\b(?:women(?:['’]s|s)?|men(?:['’]s|s)?|girls?['’]?|boys?['’]?|bab(?:y|ies))\b[\s\-:]*/ig, '')
      .replace(/\s+/g, ' ').trim();
    if (!label) label = 'this collection';

    return {
      context: 'collection',
      categoryKey: handle,
      categoryLabel: label,
      audience: audience,
      sizeType: sizeType
    };
  }

  function normaliseWardrobeCategoryMatch(value) {
    return String(value || '').toLowerCase()
      .replace(/[’']/g, '')
      .replace(/\b(?:women(?:s)?|men(?:s)?|girls?|boys?|bab(?:y|ies))\b/g, ' ')
      .replace(/\b(?:and)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(dresses|jeans|trousers|shorts|skirts|coats|jackets|shirts|tops|hoodies|sweatshirts|jumpers|cardigans)\b/g,
        function (word) {
          return {
            dresses: 'dress', jeans: 'jean', trousers: 'trouser', shorts: 'short',
            skirts: 'skirt', coats: 'coat', jackets: 'jacket', shirts: 'shirt',
            tops: 'top', hoodies: 'hoodie', sweatshirts: 'sweatshirt',
            jumpers: 'jumper', cardigans: 'cardigan'
          }[word] || word;
        })
      .replace(/\s+/g, ' ').trim();
  }

  function wardrobeCategorySlug(value) {
    return String(value || '').toLowerCase()
      .replace(/[’']/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  function tagWardrobePreferenceCandidate() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return null;
    var audiences = tagPageWardrobeAudiences();
    if (audiences.length !== 1) return null;
    var scope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var source = '';
    var value = '';
    ['product_type', 'type', 'category_scope', 'category'].some(function (parameter) {
      var values = String(scope[parameter] || '').split(',').map(function (item) {
        return item.replace(/\s+/g, ' ').trim();
      }).filter(Boolean);
      if (values.length !== 1) return false;
      source = parameter;
      value = values[0];
      return true;
    });
    if (!source || !value) return null;

    var audience = audiences[0];
    var prefix = {
      woman: 'womens-', man: 'mens-', girl: 'girls-', boy: 'boys-', baby: 'baby-'
    }[audience] || '';
    var rawSlug = wardrobeCategorySlug(value);
    var strippedSlug = wardrobeCategorySlug(String(value).replace(
      /\b(?:women(?:['’]s|s)?|men(?:['’]s|s)?|girls?['’]?|boys?['’]?|bab(?:y|ies))\b[\s\-:]*/ig,
      ''
    ));
    var pageHandle = wardrobeCategorySlug(app.getAttribute('data-page-handle') || '');
    var keys = [];
    [pageHandle.indexOf(prefix) === 0 ? pageHandle : '', rawSlug, prefix + strippedSlug]
      .forEach(function (key) {
        if (key && keys.indexOf(key) === -1) keys.push(key);
      });
    var labels = [
      value,
      app.getAttribute('data-page-title') || '',
      state.tagLabel || ''
    ].map(normaliseWardrobeCategoryMatch).filter(function (label, index, values) {
      return label && values.indexOf(label) === index;
    });
    return {
      audience: audience,
      keys: keys,
      labels: labels,
      source: source
    };
  }

  function resolveTagWardrobePreferenceContext() {
    var candidate = tagWardrobePreferenceCandidate();
    if (!candidate || state.tagWardrobePreferenceLoading ||
        state.tagWardrobePreferenceResolved || !window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.getCategoryOptions !== 'function') {
      state.tagWardrobePreferenceResolved = state.tagWardrobePreferenceResolved || !candidate;
      return;
    }
    state.tagWardrobePreferenceLoading = true;
    window.PrewornWardrobe.getCategoryOptions(candidate.audience).then(function (options) {
      options = Array.isArray(options) ? options : [];
      var match = null;
      options.some(function (option) {
        var key = String(option && option.key || '').toLowerCase();
        if (!key || candidate.keys.indexOf(key) === -1) return false;
        match = option;
        return true;
      });
      if (!match) {
        options.some(function (option) {
          var label = normaliseWardrobeCategoryMatch(option && option.label);
          if (!label || candidate.labels.indexOf(label) === -1) return false;
          match = option;
          return true;
        });
      }
      if (!match) return;
      var sizeType = String(match.sizeType || '').toLowerCase();
      if (['top', 'bottom', 'dress', 'shoe'].indexOf(sizeType) === -1 ||
          sizeType === 'shoe') return;
      state.tagWardrobePreferenceContext = {
        context: 'tag_page',
        categoryKey: String(match.key || '').toLowerCase(),
        categoryLabel: String(match.label || state.tagLabel || 'this category'),
        audience: candidate.audience,
        sizeType: sizeType
      };
    }).catch(function () {
      state.tagWardrobePreferenceContext = null;
    }).then(function () {
      state.tagWardrobePreferenceLoading = false;
      state.tagWardrobePreferenceResolved = true;
      var filtersChanged = applySavedCollectionPreferenceFilter();
      renderWardrobeToggle();
      renderCollectionPreferencePrompt(
        state.catalogFilters,
        state.lastResult && state.lastResult.nodes || [],
        state.lastResult && state.lastResult.totalCount
      );
      if (filtersChanged) {
        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      }
    });
  }

  function wardrobeHasAnyPreference(wardrobe) {
    if (!wardrobe) return false;
    if (Array.isArray(wardrobe.categories) && wardrobe.categories.length) return true;
    var sizes = wardrobe.sizes || {};
    if (['top', 'bottom', 'dress', 'shoe'].some(function (type) {
      return Array.isArray(sizes[type]) && sizes[type].length;
    })) return true;
    return !!(wardrobe.categoryPreferences &&
      Object.keys(wardrobe.categoryPreferences).length);
  }

  function savedCollectionPreferenceSizes(context) {
    context = context || collectionPreferenceContext();
    var wardrobe = state.wardrobe;
    if (!context || !wardrobe) return [];
    if (wardrobeHasAnyPreference(wardrobe) && wardrobe.audience !== context.audience) return [];
    var preference = wardrobe.categoryPreferences &&
      wardrobe.categoryPreferences[context.categoryKey];
    if (preference && preference.sizeMode === 'all') return [];
    var values = preference && preference.sizeMode === 'custom' &&
      Array.isArray(preference.sizes) && preference.sizes.length
      ? preference.sizes
      : (wardrobe.sizes && Array.isArray(wardrobe.sizes[context.sizeType])
        ? wardrobe.sizes[context.sizeType] : []);
    return values.map(function (value) {
      return String(value || '').trim();
    }).filter(function (value) {
      return value && sizeFacetValueIsVisible({
        label: value,
        input: { catalogParameter: 'size', catalogValue: value }
      }, context.audience);
    });
  }

  function savedCollectionPreferenceColours(context) {
    context = context || collectionPreferenceContext();
    var wardrobe = state.wardrobe;
    if (!context || !wardrobe || wardrobe.audience !== context.audience) return [];
    var values = wardrobe.colours && wardrobe.colours[context.categoryKey];
    return Array.isArray(values) ? values.slice() : [];
  }

  function savedCollectionPreferencePatterns(context) {
    context = context || collectionPreferenceContext();
    var wardrobe = state.wardrobe;
    if (!context || !wardrobe || wardrobe.audience !== context.audience) return [];
    var preference = wardrobe.categoryPreferences &&
      wardrobe.categoryPreferences[context.categoryKey];
    return preference && Array.isArray(preference.patterns)
      ? preference.patterns.slice() : [];
  }

  function canonicalCollectionPreferenceColour(value) {
    var normalised = String(value || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
    var aliases = {
      black: 'Black', white: 'White', blue: 'Blue', red: 'Red', green: 'Green',
      grey: 'Grey', gray: 'Grey', pink: 'Pink', brown: 'Brown', beige: 'Beige',
      purple: 'Purple', yellow: 'Yellow', orange: 'Orange', gold: 'Gold',
      silver: 'Silver', multicoloured: 'Multicoloured', multicolor: 'Multicoloured',
      'multi coloured': 'Multicoloured', 'multi color': 'Multicoloured'
    };
    return aliases[normalised] || '';
  }

  function collectionPreferenceFacet(filters, parameterNames, labelName) {
    var matched = null;
    (filters || []).some(function (filter) {
      var values = filter && Array.isArray(filter.values) ? filter.values : [];
      var input = values.length ? normalizeFilterInput(values[0].input || {}) : {};
      var parameter = String(input.catalogParameter || '').toLowerCase();
      var label = String(filter && filter.label || '').toLowerCase();
      if (parameterNames.indexOf(parameter) !== -1 || label.indexOf(labelName) !== -1) {
        matched = filter;
        return true;
      }
      return false;
    });
    return matched && Array.isArray(matched.values) ? matched.values : [];
  }

  function collectionPreferenceColourChoices(filters, selected) {
    selected = Array.isArray(selected) ? selected : [];
    var seen = {};
    var choices = collectionPreferenceFacet(filters, ['colour', 'color'], 'colour')
      .map(function (item) {
        var input = normalizeFilterInput(item.input || {});
        var value = canonicalCollectionPreferenceColour(
          input.catalogValue || item.label
        );
        return {
          value: value,
          label: value,
          count: Math.max(0, Number(item.count || 0)),
          swatch: getSwatch(item)
        };
      }).filter(function (choice) {
        var key = choice.value.toLowerCase();
        if (!choice.value || seen[key]) return false;
        seen[key] = true;
        return choice.count > 0 || selected.indexOf(choice.value) !== -1;
      });
    selected.forEach(function (value) {
      value = canonicalCollectionPreferenceColour(value);
      var key = value.toLowerCase();
      if (!value || seen[key]) return;
      seen[key] = true;
      choices.push({ value: value, label: value, count: 0,
        swatch: 'background:' + fallbackColour(value) + ';' });
    });
    choices.sort(function (a, b) {
      var aSelected = selected.indexOf(a.value) !== -1 ? 1 : 0;
      var bSelected = selected.indexOf(b.value) !== -1 ? 1 : 0;
      return bSelected - aSelected || b.count - a.count || a.label.localeCompare(b.label);
    });
    return choices.slice(0, 12);
  }

  function collectionPreferencePatternChoices(filters, selected) {
    selected = Array.isArray(selected) ? selected : [];
    var seen = {};
    var choices = collectionPreferenceFacet(
      filters,
      ['pattern', 'pattern_name'],
      'pattern'
    ).map(function (item) {
      var input = normalizeFilterInput(item.input || {});
      var value = String(input.catalogValue || item.label || '').replace(/\s+/g, ' ').trim();
      return {
        value: value,
        label: String(item.label || value).replace(/\s+/g, ' ').trim(),
        count: Math.max(0, Number(item.count || 0))
      };
    }).filter(function (choice) {
      var key = choice.value.toLowerCase();
      if (!choice.value || seen[key]) return false;
      seen[key] = true;
      return choice.count > 0 || selected.indexOf(choice.value) !== -1;
    });
    selected.forEach(function (value) {
      value = String(value || '').replace(/\s+/g, ' ').trim();
      var key = value.toLowerCase();
      if (!value || seen[key]) return;
      seen[key] = true;
      choices.push({ value: value, label: value, count: 0 });
    });
    choices.sort(function (a, b) {
      var aSelected = selected.indexOf(a.value) !== -1 ? 1 : 0;
      var bSelected = selected.indexOf(b.value) !== -1 ? 1 : 0;
      return bSelected - aSelected || b.count - a.count || a.label.localeCompare(b.label);
    });
    return choices.slice(0, 10);
  }

  function selectedSizeFilters() {
    var selected = [];
    Object.keys(state.selectedValues).forEach(function (key) {
      var value = state.selectedValues[key] || {};
      var input = normalizeFilterInput(value.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (parameter !== 'size') return;
      selected.push({ key: key, value: value, input: input });
    });
    return selected;
  }

  function applySavedCollectionPreferenceFilter() {
    var context = collectionPreferenceContext();
    var savedSizes = savedCollectionPreferenceSizes(context);
    if (!context) return false;
    var selected = selectedSizeFilters();
    var contextual = selected.filter(function (entry) {
      return !!entry.value.preferencePrompt;
    });
    if (!state.wardrobeActive) {
      if (!contextual.length && savedSizes.length && selected.length) {
        var inactiveSaved = savedSizes.map(function (size) {
          return String(size || '').trim().toLowerCase();
        });
        var inactiveSelected = selected.map(function (entry) {
          return String(entry.input.catalogValue || '').trim().toLowerCase();
        });
        var inactiveMatchesSaved = inactiveSelected.length === inactiveSaved.length &&
          inactiveSelected.every(function (size) {
            return inactiveSaved.indexOf(size) !== -1;
          });
        if (inactiveMatchesSaved) contextual = selected.slice();
      }
      if (!contextual.length) return false;
      contextual.forEach(function (entry) { delete state.selectedValues[entry.key]; });
      state.preferencePromptAppliedSizes = [];
      return true;
    }
    if (!savedSizes.length) return false;
    if (selected.length && !contextual.length) {
      // A page refresh reconstructs URL filters without the in-memory marker
      // that identifies a contextual Wardrobe size. If the complete URL size
      // selection exactly matches the saved category answer, reclaim it as the
      // prompt's selection. A genuinely different manual size still wins and
      // continues to hide the contextual prompt.
      var normalisedSaved = savedSizes.map(function (size) {
        return String(size || '').trim().toLowerCase();
      });
      var normalisedSelected = selected.map(function (entry) {
        return String(entry.input.catalogValue || '').trim().toLowerCase();
      });
      var matchesSavedAnswer = normalisedSelected.length === normalisedSaved.length &&
        normalisedSelected.every(function (size) {
          return normalisedSaved.indexOf(size) !== -1;
        });
      if (!matchesSavedAnswer) return false;
      selected.forEach(function (entry) {
        entry.value.preferencePrompt = true;
      });
      contextual = selected.slice();
      state.preferencePromptAppliedSizes = savedSizes.slice();
    }
    var contextualSizes = contextual.map(function (entry) {
      return String(entry.input.catalogValue || '');
    });
    var sameSizes = contextualSizes.length === savedSizes.length &&
      savedSizes.every(function (size) { return contextualSizes.indexOf(size) !== -1; });
    if (sameSizes) {
      state.preferencePromptAppliedSizes = savedSizes.slice();
      return false;
    }
    contextual.forEach(function (entry) { delete state.selectedValues[entry.key]; });
    savedSizes.forEach(function (size) {
      state.selectedValues['catalog:size:' + size.toLowerCase()] = {
        input: { catalogParameter: 'size', catalogValue: size },
        label: sizeDisplayLabel(size),
        preferencePrompt: true
      };
    });
    state.preferencePromptAppliedSizes = savedSizes.slice();
    return true;
  }

  function collectionPreferenceSizeValues(filters) {
    var sizeFilter = null;
    (filters || []).some(function (filter) {
      var label = String(filter && filter.label || '').toLowerCase();
      var values = filter && Array.isArray(filter.values) ? filter.values : [];
      var firstInput = values.length ? normalizeFilterInput(values[0].input || {}) : {};
      if (label === 'size' || label.indexOf('size') !== -1 ||
          String(firstInput.catalogParameter || '').toLowerCase() === 'size') {
        sizeFilter = filter;
        return true;
      }
      return false;
    });
    return sizeFilter && Array.isArray(sizeFilter.values) ? sizeFilter.values : [];
  }

  function collectionPreferenceChoices(filters, savedSizes, context) {
    savedSizes = Array.isArray(savedSizes) ? savedSizes : (savedSizes ? [savedSizes] : []);
    var seen = {};
    var choices = collectionPreferenceSizeValues(filters).map(function (value) {
      var input = normalizeFilterInput(value.input || {});
      var raw = String(input.catalogValue || value.label || '').trim();
      var label = sizeDisplayLabel(value.label || raw);
      return {
        value: raw,
        label: label,
        count: Math.max(0, Number(value.count || 0)),
        visible: sizeFacetValueIsVisible(value, context && context.audience)
      };
    }).filter(function (choice) {
      var key = choice.value.toLowerCase();
      if (!choice.value || !choice.visible || seen[key]) return false;
      seen[key] = true;
      return choice.count > 0 || savedSizes.indexOf(choice.value) !== -1;
    });
    choices.sort(compareSizeValues);
    return choices;
  }

  function loadCollectionPreferenceChoices(context, savedSizes) {
    if (!context) return false;
    if (state.preferencePromptChoicesLoading) return true;
    var endpoint = app && (app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl);
    if (!endpoint) return false;
    var url = new URL(endpoint, window.location.href);
    var colourSearch = !!state.exactColourHex;
    url.searchParams.set('action', colourSearch ? 'colour_search' : 'search');
    url.searchParams.set('page', '1');
    url.searchParams.set('limit', '1');
    url.searchParams.set('include_facets', '1');
    url.searchParams.set('facets_only', '1');
    url.searchParams.set('facet_fields', 'size_value');
    if (context.context === 'collection' && state.collectionHandle) {
      url.searchParams.set('collection', state.collectionHandle);
    }
    if (activeProductSearchQuery()) {
      url.searchParams.set('q', activeProductSearchQuery());
    }
    if (colourSearch) {
      url.searchParams.set('hex', state.exactColourHex);
      url.searchParams.set('tolerance', String(state.exactColourTolerance));
      url.searchParams.set('fast_results', '0');
    } else {
      url.searchParams.set('sort', catalogSortValue());
    }
    appendCatalogFilters(url.searchParams, 'size');
    appendWardrobeParameters(url.searchParams);
    if (context.context === 'tag_page') {
      applyStructuredFacetScope(
        url.searchParams,
        structuredFacetScopeFromTags(state.tagQueries)
      );
    }

    // An active Wardrobe may contain this same saved size. `@all` keeps every
    // other wardrobe preference while asking the API for all editable sizes
    // in just this collection.
    var categorySizes = String(url.searchParams.get('wardrobe_category_sizes') || '')
      .split(';').filter(function (part) {
        return part && part.split(':')[0] !== context.categoryKey;
      });
    if (url.searchParams.get('wardrobe_audience')) {
      categorySizes.push(context.categoryKey + ':@all');
      url.searchParams.set('wardrobe_category_sizes', categorySizes.join(';'));
    }

    var scope = url.toString();
    if (state.preferencePromptChoicesScope === scope) {
      return state.preferencePromptChoicesLoading;
    }
    state.preferencePromptChoicesScope = scope;
    state.preferencePromptChoicesLoading = true;
    fetch(scope, { method: 'GET', mode: 'cors', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).then(function (payload) {
        // Facet-only catalogue responses expose their filters as raw `facets`.
        // Convert those through the same adapter as normal search responses so
        // this prompt can show every available size, not just the active size
        // carried over from the already-filtered result set.
        var hydratedFilters = Array.isArray(payload.facets)
          ? adaptCatalogFacets(payload.facets)
          : (payload.productFilters || payload.filters || []);
        var choices = collectionPreferenceChoices(
          hydratedFilters,
          savedSizes,
          context
        );
        state.preferencePromptChoicesKey = context.categoryKey;
        if (choices.length < 2) {
          state.preferencePromptChoices = [];
          if (preferencePromptController) {
            preferencePromptController.update({ choices: [], loading: false });
          }
          var unavailableRoot = document.getElementById('PrewornCollectionPreferencePrompt');
          if (unavailableRoot) unavailableRoot.hidden = true;
          syncCollectionPreferencePromptSlot(unavailableRoot);
          return;
        }
        state.preferencePromptChoices = choices;
        renderCollectionPreferencePrompt(
          hydratedFilters,
          state.lastResult && state.lastResult.nodes || [],
          state.lastResult && state.lastResult.totalCount
        );
      }).catch(function () {
        // The saved summary remains usable if optional choice hydration fails.
        state.preferencePromptChoicesScope = '';
        if (preferencePromptController) {
          preferencePromptController.update({ choices: [], loading: false });
        }
        var failedRoot = document.getElementById('PrewornCollectionPreferencePrompt');
        if (failedRoot) failedRoot.hidden = true;
        syncCollectionPreferencePromptSlot(failedRoot);
      }).then(function () {
        state.preferencePromptChoicesLoading = false;
      });
    return true;
  }

  function collectionPreferenceImage(products) {
    for (var index = 0; index < (products || []).length; index++) {
      var product = products[index];
      if (productUsesPrimarySkuImage(product) && product.featuredImage &&
          product.featuredImage.url) return product.featuredImage.url;
    }
    return '';
  }

  function emitPreferenceEvent(name, properties) {
    var detail = properties || {};
    window.dispatchEvent(new CustomEvent('preworn:' + name, { detail: detail }));
    window.dispatchEvent(new CustomEvent('preworn:analytics', {
      detail: { event: name, properties: detail }
    }));
  }

  function selectCollectionPreferenceSizes(context, sizeValues) {
    if (!window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.setCategorySizes !== 'function') {
      return Promise.reject(new Error('My Wardrobe is temporarily unavailable.'));
    }
    sizeValues = (Array.isArray(sizeValues) ? sizeValues : [sizeValues])
      .map(function (value) { return String(value || '').trim(); })
      .filter(Boolean);
    if (!sizeValues.length) return Promise.reject(new Error('Choose at least one size.'));
    state.wardrobeSizeFallbackKey = '';
    state.wardrobeSizeFallbackSizes = [];
    var previous = selectedSizeFilters();
    previous.forEach(function (entry) { delete state.selectedValues[entry.key]; });
    sizeValues.forEach(function (sizeValue) {
      state.selectedValues['catalog:size:' + sizeValue.toLowerCase()] = {
        input: { catalogParameter: 'size', catalogValue: sizeValue },
        label: sizeDisplayLabel(sizeValue),
        preferencePrompt: true
      };
    });
    state.preferencePromptAppliedSizes = sizeValues.slice();
    state.preferencePromptSaving = true;
    emitPreferenceEvent('for_you_enabled', {
      context: context.context,
      category: context.categoryKey,
      preference: 'size',
      sizeType: context.sizeType,
      selectedCount: sizeValues.length
    });

    // A saved collection answer is also the point at which Wardrobe becomes
    // visibly active. Wardrobe stores this as the applicable garment-family
    // default, while the current collection remains the page's browse scope.
    var saveRequest = window.PrewornWardrobe.setCategorySizes(
      context.categoryKey,
      sizeValues,
      context.sizeType,
      context.audience,
      { activate: true, source: 'collection_prompt' }
    );

    refreshCatalogFacetsForCurrentScope();
    resetPagination();
    navigateWithHistory(1, null);

    return saveRequest.then(function (wardrobe) {
      state.preferencePromptSaving = false;
      state.wardrobe = wardrobe;
      return wardrobe;
    }).catch(function (error) {
      state.preferencePromptSaving = false;
      selectedSizeFilters().forEach(function (entry) {
        if (entry.value.preferencePrompt) delete state.selectedValues[entry.key];
      });
      previous.forEach(function (entry) {
        state.selectedValues[entry.key] = entry.value;
      });
      state.preferencePromptAppliedSizes = previous.map(function (entry) {
        return String(entry.input.catalogValue || '');
      }).filter(Boolean);
      refreshCatalogFacetsForCurrentScope();
      resetPagination();
      navigateWithHistory(1, null);
      throw error;
    });
  }

  function selectCollectionPreferenceColours(context, colourValues) {
    if (!window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.setCategoryColours !== 'function') {
      return Promise.reject(new Error('Colour preferences are temporarily unavailable.'));
    }
    var previousColours = state.preferencePromptSelectedColours.slice();
    state.preferencePromptTastesKey = context.categoryKey;
    state.preferencePromptSelectedColours = colourValues.slice();
    state.preferencePromptSaving = true;
    var saveRequest = window.PrewornWardrobe.setCategoryColours(
      context.categoryKey,
      colourValues,
      context.audience,
      { activate: false, source: 'collection_prompt' }
    );
    refreshCatalogFacetsForCurrentScope();
    resetPagination();
    navigateWithHistory(1, null);
    return saveRequest.then(function (wardrobe) {
      state.preferencePromptSaving = false;
      state.wardrobe = wardrobe;
      emitPreferenceEvent('style_profile_enriched', {
        context: context.context,
        category: context.categoryKey,
        preference: 'colour',
        selectedCount: colourValues.length
      });
      return wardrobe;
    }).catch(function (error) {
      state.preferencePromptSaving = false;
      state.preferencePromptSelectedColours = previousColours;
      refreshCatalogFacetsForCurrentScope();
      resetPagination();
      navigateWithHistory(1, null);
      throw error;
    });
  }

  function selectCollectionPreferencePatterns(context, patternValues) {
    if (!window.PrewornWardrobe ||
        typeof window.PrewornWardrobe.setCategoryPatterns !== 'function') {
      return Promise.reject(new Error('Pattern preferences are temporarily unavailable.'));
    }
    var previousPatterns = state.preferencePromptSelectedPatterns.slice();
    state.preferencePromptTastesKey = context.categoryKey;
    state.preferencePromptSelectedPatterns = patternValues.slice();
    state.preferencePromptSaving = true;
    var saveRequest = window.PrewornWardrobe.setCategoryPatterns(
      context.categoryKey,
      patternValues,
      context.audience,
      { activate: false, source: 'collection_prompt' }
    );
    refreshCatalogFacetsForCurrentScope();
    resetPagination();
    navigateWithHistory(1, null);
    return saveRequest.then(function (wardrobe) {
      state.preferencePromptSaving = false;
      state.wardrobe = wardrobe;
      emitPreferenceEvent('style_profile_enriched', {
        context: context.context,
        category: context.categoryKey,
        preference: 'pattern',
        selectedCount: patternValues.length
      });
      return wardrobe;
    }).catch(function (error) {
      state.preferencePromptSaving = false;
      state.preferencePromptSelectedPatterns = previousPatterns;
      refreshCatalogFacetsForCurrentScope();
      resetPagination();
      navigateWithHistory(1, null);
      throw error;
    });
  }

  function syncCollectionPreferencePromptSlot(root) {
    var wardrobeBar = document.getElementById('PrewornWardrobeSearchToggle');
    if (wardrobeBar) wardrobeBar.hidden = !!(root && !root.hidden);
  }

  function renderCollectionPreferencePrompt(filters, products, totalCount) {
    var root = document.getElementById('PrewornCollectionPreferencePrompt');
    var context = collectionPreferenceContext();
    if (!root || !context || !window.PrewornPreferencePrompt) {
      if (root) root.hidden = true;
      syncCollectionPreferencePromptSlot(root);
      return;
    }
    var sizeSelections = selectedSizeFilters();
    var manualSize = sizeSelections.some(function (entry) {
      return !entry.value.preferencePrompt;
    });
    if (manualSize) {
      root.hidden = true;
      syncCollectionPreferencePromptSlot(root);
      return;
    }

    var savedSizes = savedCollectionPreferenceSizes(context);
    var selectedSizes = sizeSelections.length
      ? sizeSelections.map(function (entry) {
        return String(entry.input.catalogValue || '');
      }).filter(Boolean)
      : savedSizes;
    var choiceSelections = savedSizes.concat(selectedSizes).filter(function (value, index, values) {
      return value && values.indexOf(value) === index;
    });
    var choices = collectionPreferenceChoices(filters, choiceSelections, context);
    if (choices.length >= 2) {
      state.preferencePromptChoices = choices;
      state.preferencePromptChoicesKey = context.categoryKey;
    } else if (state.preferencePromptChoicesKey === context.categoryKey &&
        state.preferencePromptChoices.length) {
      choices = state.preferencePromptChoices;
    }
    var choicesLoading = false;
    if (choices.length < 2) {
      choicesLoading = loadCollectionPreferenceChoices(context, choiceSelections);
    }
    if (choices.length < 2 && !selectedSizes.length) {
      if (!choicesLoading) {
        root.hidden = true;
        syncCollectionPreferencePromptSlot(root);
        return;
      }
    }

    var selectedColours = savedCollectionPreferenceColours(context);
    var selectedPatterns = savedCollectionPreferencePatterns(context);
    if (state.preferencePromptTastesKey !== context.categoryKey ||
        !state.preferencePromptSaving) {
      state.preferencePromptTastesKey = context.categoryKey;
      state.preferencePromptSelectedColours = selectedColours.slice();
      state.preferencePromptSelectedPatterns = selectedPatterns.slice();
    } else {
      selectedColours = state.preferencePromptSelectedColours.slice();
      selectedPatterns = state.preferencePromptSelectedPatterns.slice();
    }
    var colourChoices = collectionPreferenceColourChoices(filters, selectedColours);
    var patternChoices = collectionPreferencePatternChoices(filters, selectedPatterns);

    var config = {
      context: context.context,
      categoryKey: context.categoryKey,
      categoryLabel: context.categoryLabel,
      sizeType: context.sizeType,
      choices: choices,
      loading: choicesLoading && choices.length < 2 && !selectedSizes.length,
      selected: selectedSizes,
      colourChoices: colourChoices,
      selectedColours: selectedColours,
      patternChoices: patternChoices,
      selectedPatterns: selectedPatterns,
      matchingCount: selectedSizes.length ? totalCount : null,
      imageUrl: collectionPreferenceImage(products),
      storageLabel: window.PREWORN_WARDROBE_CONFIG &&
        window.PREWORN_WARDROBE_CONFIG.loggedIn
          ? 'Saved to My Wardrobe' : 'Remember this on this device',
      wardrobeActive: state.wardrobeActive,
      onSelect: function (values) {
        return selectCollectionPreferenceSizes(context, values);
      },
      onColoursChange: function (values) {
        return selectCollectionPreferenceColours(context, values);
      },
      onPatternsChange: function (values) {
        return selectCollectionPreferencePatterns(context, values);
      },
      onWardrobeToggle: function (active) {
        if (!window.PrewornWardrobe) {
          return Promise.reject(new Error('My Wardrobe is temporarily unavailable.'));
        }
        state.linkedWardrobeParameters = {};
        if (!active) {
          window.PrewornWardrobe.setActive(false);
          return Promise.resolve(false);
        }
        var selectedWardrobe = state.wardrobe;
        if (selectedWardrobe && selectedWardrobe.id &&
            typeof window.PrewornWardrobe.selectWardrobe === 'function') {
          window.PrewornWardrobe.selectWardrobe(selectedWardrobe.id, true);
          return Promise.resolve(true);
        }
        if (context.audience &&
            typeof window.PrewornWardrobe.selectGuestWardrobe === 'function' &&
            window.PrewornWardrobe.selectGuestWardrobe(context.audience, true)) {
          return Promise.resolve(true);
        }
        window.PrewornWardrobe.setActive(true);
        return Promise.resolve(window.PrewornWardrobe.isActive());
      },
      onBuild: function () {
        if (window.PrewornWardrobe && typeof window.PrewornWardrobe.open === 'function') {
          window.PrewornWardrobe.open();
        }
      },
      onDismiss: function () {
        syncCollectionPreferencePromptSlot(root);
      }
    };

    if (!preferencePromptController) {
      preferencePromptController = window.PrewornPreferencePrompt.mount(root, config);
    } else {
      preferencePromptController.update(config);
    }
    syncCollectionPreferencePromptSlot(root);
  }

  function escapeSearchQueryValue(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function collectionProductTypeQuery(products) {
    var seen = {};
    var types = [];

    for (var p = 0; p < (products || []).length; p++) {
      var type = String(products[p].productType || '').trim();
      var key = type.toLowerCase();

      if (!type || seen[key]) continue;
      seen[key] = true;
      types.push(type);
    }

    if (!types.length) return '';

    var clauses = types.map(function (type) {
      return 'product_type:"' + escapeSearchQueryValue(type) + '"';
    });

    return clauses.length === 1
      ? clauses[0]
      : '(' + clauses.join(' OR ') + ')';
  }

  function buildCollectionSearchScope(filters, products) {
    var scope = [];
    var department = collectionDepartment();
    var departmentMatched = false;

    for (var f = 0; f < (filters || []).length; f++) {
      var filter = filters[f];
      var filterLabel = normalizeCollectionQuery(filter.label);
      var values = filter.values || [];

      if (filterLabel === 'type' || filterLabel === 'product type') {
        for (var t = 0; t < values.length; t++) {
          if (Number(values[t].count || 0) > 0) {
            scope.push(normalizeFilterInput(values[t].input));
          }
        }
      }

      if (filterLabel === 'department' && department) {
        for (var d = 0; d < values.length; d++) {
          if (
            normalizeCollectionQuery(values[d].label) ===
            normalizeCollectionQuery(department)
          ) {
            scope.push(normalizeFilterInput(values[d].input));
            departmentMatched = true;
            break;
          }
        }
      }
    }

    var productTypeQuery = collectionProductTypeQuery(products);
    state.collectionSearchQuery = state.query;

    if (productTypeQuery) {
      state.collectionSearchQuery += ' ' + productTypeQuery;
    }

    if (department && !departmentMatched && !productTypeQuery) {
      state.collectionSearchQuery += ' ' + department;
    }

    return scope.filter(function (input) {
      return input && Object.keys(input).length > 0;
    });
  }

  function applyCollectionQueryFilter(filters, products) {
    if (
      state.mode !== 'collection' ||
      !state.query ||
      state.collectionQueryAttempted
    ) {
      return false;
    }

    state.collectionQueryAttempted = true;
    filters = visibleCatalogFilters(filters);

    var match = findCollectionQueryFilter(filters, state.query);
    if (!match) {
      state.collectionKeywordSearch = true;
      state.collectionScopeFilters = buildCollectionSearchScope(filters, products);
      resetPagination();

      console.info(
        '[Preworn Browse] Using keyword search scoped by collection taxonomy:',
        state.query
      );
      return true;
    }

    state.selectedValues[match.id] = {
      input: normalizeFilterInput(match.input),
      label: match.label
    };
    state.collectionQueryMatched = true;
    state.collectionQueryFilterId = match.id;
    resetPagination();
    return true;
  }

  function catalogFilterParameter(facetId) {
    var map = {
      department: 'department',
      navigation_category: 'category',
      item_type: 'type',
      style: 'style',
      vendor: 'vendor',
      size_value: 'size',
      colour: 'colour',
      condition_name: 'condition',
      material: 'material',
      pattern_name: 'pattern'
    };

    return map[facetId] || '';
  }

  function catalogSortValue() {
    if (state.sortKey === 'TRENDING') return 'trending';
    if (state.sortKey === 'PRICE') {
      return state.reverse ? 'price_desc' : 'price_asc';
    }
    if (state.sortKey === 'CREATED') return 'newest';
    return 'featured';
  }

  function appendCatalogFilters(parameters, excludedParameter) {
    var grouped = {};
    var keys = Object.keys(state.selectedValues);
    excludedParameter = String(excludedParameter || '').trim().toLowerCase();

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      var input = normalizeFilterInput(selected ? selected.input : null);
      var parameter = input.catalogParameter || '';
      var value = input.catalogValue;

      // This also lets the DB endpoint recover if a Storefront fallback was
      // used before the catalogue API became available again.
      if (!parameter && input.productVendor) {
        parameter = 'vendor';
        value = input.productVendor;
      } else if (!parameter && input.productType) {
        parameter = 'type';
        value = input.productType;
      } else if (!parameter && input.variantOption &&
          String(input.variantOption.name || '').toLowerCase() === 'size') {
        parameter = 'size';
        value = input.variantOption.value;
      }

      if (!parameter || String(parameter).toLowerCase() === excludedParameter ||
          value === null || typeof value === 'undefined' ||
          isPlaceholderFacetValue(selected)) continue;
      if (!grouped[parameter]) grouped[parameter] = [];
      grouped[parameter].push(String(value));
    }

    Object.keys(grouped).forEach(function (parameter) {
      parameters.set(parameter, grouped[parameter].join(','));
    });

    if (state.priceMin !== '') parameters.set('price_min', state.priceMin);
    if (state.priceMax !== '') parameters.set('price_max', state.priceMax);

  }

  function appendWishlistParameters(parameters) {
    if (state.mode !== 'wishlist') return;
    // The explicit scope flag makes an empty or malformed browser wishlist
    // fail closed in the catalogue API instead of becoming an all-products
    // request. Product IDs are preferred because they keep the URL compact;
    // handles cover older saved-item records that pre-date GraphQL IDs.
    parameters.set('wishlist', '1');
    if (state.wishlistProductIds.length) {
      parameters.set('wishlist_ids', state.wishlistProductIds.join(','));
    }
    if (state.wishlistHandles.length) {
      parameters.set('wishlist_handles', state.wishlistHandles.join(','));
    }
  }

  function appendWardrobeParameters(parameters) {
    // The selected Department is authoritative. A linked or active Wardrobe
    // from another audience must never leak into this request.
    var wardrobeParameters = wardrobeParametersForCurrentScope();
    Object.keys(wardrobeParameters).forEach(function (parameter) {
      parameters.set(parameter, wardrobeParameters[parameter]);
    });
    if (wardrobeSearchUsesSoftScope() && parameters.get('wardrobe_audience')) {
      parameters.set('wardrobe_mode', 'soft');
    }
    appendCollectionPreferenceRankingParameters(parameters);
  }

  function inheritedWardrobeSizeContext() {
    if (!wardrobeScopeActive()) return null;
    var context = collectionPreferenceContext();
    var wardrobe = state.wardrobe;
    if (!context || !wardrobe || wardrobe.audience !== context.audience) return null;
    var preference = wardrobe.categoryPreferences &&
      wardrobe.categoryPreferences[context.categoryKey];
    if (preference && (preference.sizeMode === 'custom' ||
        preference.sizeMode === 'all')) return null;
    var sizes = wardrobe.sizes && Array.isArray(wardrobe.sizes[context.sizeType])
      ? wardrobe.sizes[context.sizeType].map(function (value) {
          return String(value || '').trim();
        }).filter(Boolean)
      : [];
    if (!sizes.length) return null;
    var selected = selectedSizeFilters();
    if (selected.length) {
      // Contextual Wardrobe sizes are represented as normal URL/facet sizes so
      // the active filters stay honest. They are still safe to relax after a
      // zero-result response. A size the shopper chose manually is absolute
      // and must never be silently removed.
      if (selected.some(function (entry) {
        return !entry.value || entry.value.preferencePrompt !== true;
      })) return null;
      var inherited = sizes.map(function (value) {
        return String(value || '').trim().toLowerCase();
      });
      var contextual = selected.map(function (entry) {
        return String(entry.input.catalogValue || '').trim().toLowerCase();
      });
      if (contextual.length !== inherited.length ||
          !contextual.every(function (value) {
            return inherited.indexOf(value) !== -1;
          })) return null;
    }
    return {
      categoryKey: context.categoryKey,
      sizeType: context.sizeType,
      sizes: sizes,
      key: [wardrobe.audience, context.categoryKey, context.sizeType, sizes.join('|')].join(':')
    };
  }

  function relaxInheritedWardrobeSize(parameters, context) {
    if (!parameters || !context || !parameters.get('wardrobe_audience')) return;
    // `applySavedCollectionPreferenceFilter()` exposes an inherited family
    // size as a regular active size filter. Remove that transparent client
    // representation as well as relaxing the server-side Wardrobe branch.
    parameters.delete('size');
    var categorySizes = String(parameters.get('wardrobe_category_sizes') || '')
      .split(';').filter(function (part) {
        return part && part.split(':')[0] !== context.categoryKey;
      });
    categorySizes.push(context.categoryKey + ':@all');
    parameters.set('wardrobe_category_sizes', categorySizes.join(';'));
  }

  function appendCollectionPreferenceRankingParameters(parameters) {
    // These choices are a ranking signal even when the broader Wardrobe is
    // active.  The Wardrobe parameters may narrow the catalogue, but must not
    // suppress the user's current category-specific colour/pattern ordering.
    if (!state.wardrobeActive) return;
    var context = collectionPreferenceContext();
    if (!context) return;
    var optimistic = state.preferencePromptSaving &&
      state.preferencePromptTastesKey === context.categoryKey;
    var colours = optimistic
      ? state.preferencePromptSelectedColours.slice()
      : savedCollectionPreferenceColours(context);
    var patterns = optimistic
      ? state.preferencePromptSelectedPatterns.slice()
      : savedCollectionPreferencePatterns(context);
    // Keep the prompt state and the request state sourced from the same saved
    // category profile. This also covers the first request after a refresh,
    // before the prompt has had an opportunity to render.
    if (!optimistic) {
      state.preferencePromptTastesKey = context.categoryKey;
      state.preferencePromptSelectedColours = colours.slice();
      state.preferencePromptSelectedPatterns = patterns.slice();
    }
    if (colours.length) parameters.set('preference_colours', colours.join('|'));
    if (patterns.length) parameters.set('preference_patterns', patterns.join('|'));
  }

  function applyStructuredFacetScope(parameters, scope) {
    if (!parameters || !scope) return;
    Object.keys(scope).forEach(function (parameter) {
      var fixedValues = String(scope[parameter] || '').split(',').map(function (value) {
        return value.trim();
      }).filter(Boolean);
      var selectedValues = String(parameters.get(parameter) || '').split(',').map(function (value) {
        return value.trim();
      }).filter(Boolean);
      if (!selectedValues.length) {
        parameters.set(parameter, fixedValues.join(','));
        return;
      }

      // A page may intentionally contain several values in one namespace,
      // such as Department_Boys + Department_Girls. A shopper's selection is
      // a drill-down inside that fixed universe, so preserve the intersection
      // instead of overwriting it with the broad page scope again.
      var narrowedValues = selectedValues.filter(function (selected) {
        return fixedValues.some(function (fixed) {
          return fixed.toLowerCase() === selected.toLowerCase();
        });
      });
      parameters.set(parameter, (narrowedValues.length ? narrowedValues : fixedValues).join(','));
    });
  }

  function structuredFacetScopeFromTags(tags) {
    var namespaces = {
      department: 'department',
      category: 'category_scope',
      'navigation category': 'category',
      type: 'type',
      'item type': 'type',
      'collection type': 'product_type',
      'product type': 'product_type',
      'shopify type': 'product_type',
      style: 'style',
      brand: 'vendor',
      vendor: 'vendor',
      colour: 'colour',
      color: 'colour',
      material: 'material',
      pattern: 'pattern',
      size: 'size',
      condition: 'condition'
    };
    var grouped = {};
    var list = Array.isArray(tags) ? tags : [];
    for (var i = 0; i < list.length; i++) {
      var tag = String(list[i] || '').trim();
      var separator = tag.indexOf('_');
      if (separator < 1 || separator >= tag.length - 1) return null;
      var namespace = tag.slice(0, separator).replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ').trim().toLowerCase();
      var parameter = namespaces[namespace];
      var value = tag.slice(separator + 1).replace(/_/g, ' ').trim();
      if (!parameter || !value) return null;
      var tagValues = [value];
      if (parameter === 'size') {
        var numericUkSize = value.match(/^\d+(?:\.\d+)?$/);
        var prefixedUkSize = value.match(/^UK\s*(\d+(?:\.\d+)?)$/i);
        if (numericUkSize) tagValues.push('UK ' + numericUkSize[0]);
        else if (prefixedUkSize) tagValues.push(prefixedUkSize[1]);
      }
      tagValues.forEach(function (value) {
        grouped[parameter] = grouped[parameter] || [];
        if (!grouped[parameter].some(function (existing) {
          return existing.toLowerCase() === value.toLowerCase();
        })) {
          grouped[parameter].push(value);
        }
      });
    }
    if (!list.length) return null;
    var scope = {};
    Object.keys(grouped).forEach(function (parameter) {
      // The catalogue API treats comma-separated values inside one parameter
      // as OR, while separate parameters remain AND. This lets a page combine
      // several Collection Type_ values (Jeans, Leggings, Shorts) without
      // weakening a Department_, Style_ or other independent constraint.
      scope[parameter] = grouped[parameter].join(',');
    });
    return scope;
  }

  function adaptCatalogFacets(facets) {
    var filters = [];

    for (var i = 0; i < (facets || []).length; i++) {
      var facet = facets[i] || {};
      var parameter = catalogFilterParameter(facet.id);
      if (!parameter) continue;

      var values = [];
      for (var v = 0; v < (facet.values || []).length; v++) {
        var sourceValue = facet.values[v] || {};
        var value = String(sourceValue.value || sourceValue.label || '').trim();
        if (!value || isPlaceholderFacetValue(value)) continue;

        values.push({
          id: 'catalog:' + parameter + ':' + value.toLowerCase(),
          label: String(sourceValue.label || value),
          count: Math.max(0, Number(sourceValue.count || 0)),
          input: {
            catalogParameter: parameter,
            catalogValue: value
          }
        });
      }

      if (!values.length) continue;

      filters.push({
        id: 'catalog:' + parameter,
        label: String(facet.label || facet.id || 'Filter'),
        type: 'LIST',
        presentation: 'TEXT',
        values: values
      });
    }

    filters.push({
      id: 'catalog:price',
      label: 'Price',
      type: 'PRICE_RANGE',
      presentation: 'TEXT',
      values: []
    });

    return filters;
  }

  function mergeCatalogFacetFilters(existing, incoming) {
    var merged = {};
    (existing || []).concat(incoming || []).forEach(function (filter) {
      if (filter && filter.id) merged[filter.id] = filter;
    });
    return visibleCatalogFilters(Object.keys(merged).map(function (id) {
      return merged[id];
    }).sort(function (a, b) {
      return quickFilterPriority(a) - quickFilterPriority(b);
    }));
  }

  function catalogFilterParameterFromFilter(filter) {
    var values = filter && Array.isArray(filter.values) ? filter.values : [];
    var firstInput = values.length
      ? normalizeFilterInput(values[0].input || {}) : {};
    var parameter = String(firstInput.catalogParameter || '').toLowerCase();
    if (parameter) return parameter;
    var idMatch = String(filter && filter.id || '').match(/^catalog:([^:]+)$/i);
    return idMatch ? idMatch[1].toLowerCase() : '';
  }

  function preserveMultiSelectFacetOptions(existing, incoming) {
    var active = selectedCatalogueFilters();
    var activeParameters = Object.keys(active).filter(function (parameter) {
      return Array.isArray(active[parameter]) && active[parameter].length > 0;
    });
    if (!activeParameters.length) return incoming;

    var existingByParameter = {};
    (existing || []).forEach(function (filter) {
      var parameter = catalogFilterParameterFromFilter(filter);
      if (parameter) existingByParameter[parameter] = filter;
    });

    var seen = {};
    var preserved = (incoming || []).map(function (filter) {
      var parameter = catalogFilterParameterFromFilter(filter);
      if (!parameter || activeParameters.indexOf(parameter) === -1 ||
          !existingByParameter[parameter]) return filter;
      seen[parameter] = true;
      var currentValues = Array.isArray(filter.values) ? filter.values : [];
      var currentById = {};
      currentValues.forEach(function (value) {
        currentById[String(value.id || '').toLowerCase()] = value;
      });
      var values = [];
      var included = {};
      (existingByParameter[parameter].values || []).forEach(function (value) {
        var id = String(value.id || '').toLowerCase();
        values.push(currentById[id] || value);
        included[id] = true;
      });
      currentValues.forEach(function (value) {
        var id = String(value.id || '').toLowerCase();
        if (!included[id]) values.push(value);
      });
      var copy = {};
      Object.keys(filter || {}).forEach(function (key) { copy[key] = filter[key]; });
      copy.values = values;
      return copy;
    });

    activeParameters.forEach(function (parameter) {
      if (!seen[parameter] && existingByParameter[parameter]) {
        preserved.push(existingByParameter[parameter]);
      }
    });
    return visibleCatalogFilters(preserved);
  }

  var COLOUR_RESULT_SESSION_TTL = 60 * 1000;

  function colourResultSessionKey(scope) {
    return catalogFacetSessionKey(scope).replace(
      'preworn:catalog-facets:v1:',
      'preworn:colour-results:v2:'
    );
  }

  function productUsesPrimarySkuImage(product) {
    var variantNodes = product && product.variants && product.variants.nodes;
    var sku = String(
      product && product.primarySku ||
      variantNodes && variantNodes[0] && variantNodes[0].sku ||
      ''
    ).trim();
    var imageUrl = String(product && product.featuredImage &&
      product.featuredImage.url || '').trim();
    if (!sku || !imageUrl) return false;

    var path;
    try {
      path = new URL(imageUrl, window.location.href).pathname;
    } catch (error) {
      path = imageUrl.split(/[?#]/)[0];
    }
    var filename = path.slice(path.lastIndexOf('/') + 1);
    try { filename = decodeURIComponent(filename); }
    catch (error) { /* Compare the original basename when decoding fails. */ }
    return filename.toLowerCase() === (sku + '_1_new.jpg').toLowerCase();
  }

  function readColourResultFromSession(scope) {
    try {
      var raw = window.sessionStorage.getItem(colourResultSessionKey(scope));
      var cached = raw ? JSON.parse(raw) : null;
      if (!cached || cached.scope !== scope || !cached.result ||
          Date.now() - Number(cached.savedAt || 0) > COLOUR_RESULT_SESSION_TTL) {
        return null;
      }
      return cached.result;
    } catch (error) {
      return null;
    }
  }

  function writeColourResultToSession(scope, result) {
    try {
      window.sessionStorage.setItem(colourResultSessionKey(scope), JSON.stringify({
        scope: scope,
        savedAt: Date.now(),
        result: result
      }));
    } catch (error) {
      // Storage pressure must not affect live colour search.
    }
  }

  async function loadCatalogResult(pageNumber, options) {
    options = options || {};

    var endpoint = app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl;
    if (!endpoint) throw new Error('The catalogue API URL is not configured.');

    var useColourSearch = !!state.exactColourHex &&
      (!options.facetsOnly || options.colourFacetsOnly);
    var includeFacets = typeof options.includeFacets === 'boolean'
      ? options.includeFacets
      : pageNumber === 1 && state.catalogFilters.length === 0;
    var requestTimeout = Number(options.timeout || (useColourSearch
      ? CONFIG.colourRequestTimeout : CONFIG.catalogRequestTimeout));

    var url = new URL(endpoint, window.location.href);
    // Cloudways/Varnish previously retained one originless response without
    // CORS headers. Version the public cache key while that object expires.
    url.searchParams.set('api_v', '20261027-catalogue-freshness-v2');
    url.searchParams.set('action', useColourSearch ? 'colour_search' : 'search');
    url.searchParams.set('page', String(pageNumber));
    url.searchParams.set('limit', String(CONFIG.pageSize));
    if (useColourSearch) {
      url.searchParams.set('hex', state.exactColourHex);
      url.searchParams.set('tolerance', String(state.exactColourTolerance));
      url.searchParams.set('include_facets', includeFacets ? '1' : '0');
      if (state.colourPage && !activeProductSearchQuery() &&
          !Object.keys(state.selectedValues).length && !wardrobeScopeActive()) {
        url.searchParams.set('finder_fast', '1');
      }
      if (options.colourFacetsOnly) {
        url.searchParams.set('facets_only', '1');
        url.searchParams.set('fast_results', '0');
      } else {
        // Only the first 50 cards use the small indexed LAB shortlist. Every
        // later page is ranked lazily from the wider population.
        url.searchParams.set('fast_results', pageNumber === 1 ? '1' : '0');
        if (pageNumber > 1 && state.colourFirstPageSeed.length) {
          url.searchParams.set('seed_ids', state.colourFirstPageSeed.join(','));
        }
      }
      if (Array.isArray(options.facetFields) && options.facetFields.length) {
        url.searchParams.set('facet_fields', options.facetFields.join(','));
      }
    } else {
      url.searchParams.set('sort', catalogSortValue());
      url.searchParams.set('include_facets', includeFacets ? '1' : '0');
      if (options.facetsOnly) url.searchParams.set('facets_only', '1');
      if (options.countMode) url.searchParams.set('count_mode', options.countMode);
      if (Array.isArray(options.facetFields) && options.facetFields.length) {
        url.searchParams.set('facet_fields', options.facetFields.join(','));
      }
    }

    // Recognised namespace tags and these catalogue columns share the same
    // metafield source. Use the indexed columns for both products and facets;
    // tag-only/custom namespaces continue through exact membership below.
    var structuredTagScope = structuredFacetScopeFromTags(state.tagQueries);
    var productQuery = activeProductSearchQuery();

    if (!useColourSearch && !options.facetsOnly && options.routeLookup) {
      var routeQuery = currentProductSearchText();
      if (routeQuery && routeQuery !== '*') {
        url.searchParams.set('route_lookup', '1');
        url.searchParams.set('route_q', routeQuery);
      }
    }

    if (state.similarSku) {
      url.searchParams.set('similar_sku', state.similarSku);
      url.searchParams.set('q', state.similarTitle || state.query || 'Preloved fashion');
    } else if (state.tagQueries.length) {
      // Keep the human-readable label out of full-text search: membership is
      // always the complete exact-tag scope supplied by `tag` or `tags[]`.
      if (!structuredTagScope) {
        if (state.tagQueries.length === 1) {
          url.searchParams.set('tag', state.tagQueries[0]);
        } else {
          state.tagQueries.forEach(function (tag) {
            url.searchParams.append('tags[]', tag);
          });
        }
      }
      if (state.tagLabel) url.searchParams.set('tag_label', state.tagLabel);
      if (productQuery) url.searchParams.set('q', productQuery);
    } else if (state.brandQuery) {
      // `brand` supplies the brand-scoped demand signal used by the trending
      // sorter; `vendor` remains the catalogue filter that constrains results.
      url.searchParams.set('brand', state.brandQuery);
      url.searchParams.set('vendor', state.brandQuery);
      if (productQuery) url.searchParams.set('q', productQuery);
    } else if (productQuery && !state.trending) {
      url.searchParams.set('q', productQuery);
    }
    if (state.mode === 'collection' && state.collectionHandle) {
      url.searchParams.set('collection', state.collectionHandle);
    }
    appendWishlistParameters(url.searchParams);
    appendCatalogFilters(url.searchParams);
    appendWardrobeParameters(url.searchParams);
    var inheritedSizeContext = inheritedWardrobeSizeContext();
    var relaxInheritedSize = !!(inheritedSizeContext &&
      (options.relaxInheritedWardrobeSize === true ||
        state.wardrobeSizeFallbackKey === inheritedSizeContext.key));
    if (relaxInheritedSize) {
      relaxInheritedWardrobeSize(url.searchParams, inheritedSizeContext);
    }
    applyStructuredFacetScope(url.searchParams, structuredTagScope);

    var primaryColourRequest = useColourSearch && !options.colourFacetsOnly;
    var colourResultScope = primaryColourRequest ? url.toString() : '';
    if (colourResultScope) {
      var cachedColourResult = readColourResultFromSession(colourResultScope);
      if (cachedColourResult) {
        cachedColourResult.productFilters = state.catalogFilters;
        cachedColourResult.fromColourCache = true;
        return cachedColourResult;
      }
    }

    var controller = typeof window.AbortController === 'function'
      ? new window.AbortController() : null;
    if (primaryColourRequest && controller) {
      state.activeColourController = controller;
    }
    var timeout = controller ? window.setTimeout(function () {
      controller.abort();
    }, requestTimeout) : null;
    var response;

    try {
      response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
    } catch (requestError) {
      if (primaryColourRequest && controller &&
          state.activeColourController !== controller) {
        throw requestError;
      }
      var networkRetries = Number(options.colourNetworkRetries || 0);
      if (useColourSearch && networkRetries < CONFIG.colourNetworkRetries) {
        await new Promise(function (resolve) {
          window.setTimeout(resolve, 750 * (networkRetries + 1));
        });
        var networkRetryOptions = {};
        Object.keys(options).forEach(function (key) {
          networkRetryOptions[key] = options[key];
        });
        networkRetryOptions.colourNetworkRetries = networkRetries + 1;
        return loadCatalogResult(pageNumber, networkRetryOptions);
      }
      throw requestError;
    } finally {
      if (timeout) window.clearTimeout(timeout);
      if (primaryColourRequest && state.activeColourController === controller) {
        state.activeColourController = null;
      }
    }

    var payload;
    try {
      payload = await response.json();
    } catch (parseError) {
      var parseRetries = Number(options.colourParseRetries || 0);
      if (useColourSearch && parseRetries < 1) {
        await new Promise(function (resolve) {
          window.setTimeout(resolve, 500);
        });
        var parseRetryOptions = {};
        Object.keys(options).forEach(function (key) {
          parseRetryOptions[key] = options[key];
        });
        parseRetryOptions.colourParseRetries = parseRetries + 1;
        return loadCatalogResult(pageNumber, parseRetryOptions);
      }
      throw parseError;
    }
    if (!response.ok) {
      if (
        response.status === 503 &&
        payload && payload.error === 'catalogue_busy' &&
        Number(options.busyRetryCount || 0) <
          (useColourSearch ? CONFIG.colourBusyRetries : 1)
      ) {
        var retrySeconds = Math.max(1, Math.min(5, Number(payload.retryAfter) || 1));
        await new Promise(function (resolve) {
          window.setTimeout(resolve, retrySeconds * 1000);
        });
        var retryOptions = {};
        Object.keys(options).forEach(function (key) {
          retryOptions[key] = options[key];
        });
        retryOptions.busyRetryCount = Number(options.busyRetryCount || 0) + 1;
        return loadCatalogResult(pageNumber, retryOptions);
      }
      var serverRetries = Number(options.colourServerRetries || 0);
      if (useColourSearch && response.status >= 500 && response.status !== 503 &&
          serverRetries < CONFIG.colourServerRetries) {
        await new Promise(function (resolve) {
          window.setTimeout(resolve, 1000);
        });
        var serverRetryOptions = {};
        Object.keys(options).forEach(function (key) {
          serverRetryOptions[key] = options[key];
        });
        serverRetryOptions.colourServerRetries = serverRetries + 1;
        return loadCatalogResult(pageNumber, serverRetryOptions);
      }
      throw new Error(
        'Catalogue API HTTP ' + response.status + ': ' +
        String(payload.error || 'request_failed')
      );
    }

    var validSource = payload && (
      payload.source === 'preworn_catalog_index' ||
      (useColourSearch && payload.source === 'preworn_visual_colour_index')
    );
    if (!validSource ||
        !Array.isArray(payload.items)) {
      throw new Error('The catalogue API returned an invalid response.');
    }

    var responseFilters = state.catalogFilters;
    if (Array.isArray(payload.facets) &&
        (payload.facets.length || payload.facetsIncluded)) {
      responseFilters = adaptCatalogFacets(payload.facets);
      if (options.mergeFacets) {
        responseFilters = mergeCatalogFacetFilters(
          state.catalogFilters,
          responseFilters
        );
      }
      responseFilters = preserveMultiSelectFacetOptions(
        state.catalogFilters,
        responseFilters
      );
      var activeFacetScope = options.colourFacetsOnly
        ? colourFacetSessionScope() : catalogFacetSessionScope();
      var currentFacetRequest = options.facetGeneration === state.catalogFacetGeneration &&
        (!options.facetScope || options.facetScope === activeFacetScope);
      if (!options.facetsOnly || currentFacetRequest) {
        state.catalogFilters = responseFilters;
        if (payload.facetsIncluded) {
          state.catalogFacetsDeferred = false;
          if (!options.facetsOnly) state.catalogFacetsStale = false;
        }
      }
    }

    var totalCountKnown = payload.totalCount !== null &&
      typeof payload.totalCount !== 'undefined' && payload.totalCount !== '' &&
      isFinite(Number(payload.totalCount));
    var totalPagesKnown = payload.totalPages !== null &&
      typeof payload.totalPages !== 'undefined' && payload.totalPages !== '' &&
      isFinite(Number(payload.totalPages));

    // The API applies the same condition before totals and pagination are
    // calculated. Retain this guard so a stale API deployment can never put a
    // non-canonical product image into a customer-facing grid.
    var responseItems = payload.items.filter(productUsesPrimarySkuImage);
    var catalogResult = {
      catalogSource: true,
      colourSource: useColourSearch,
      countIsEstimate: !!payload.countIsEstimate,
      totalDeferred: payload.totalDeferred === true,
      facetTotalIncluded: payload.facetTotalIncluded === true,
      colourBands: payload.bands || null,
      interpretedQuery: payload.interpretedQuery || null,
      searchRoute: payload.searchRoute || null,
      colourSeedProductIds: Array.isArray(payload.seedProductIds)
        ? payload.seedProductIds.map(String) : [],
      totalCount: totalCountKnown
        ? Math.max(0, Number(payload.totalCount)) : null,
      totalPages: totalPagesKnown
        ? Math.max(0, Number(payload.totalPages)) : null,
      pageInfo: {
        hasNextPage: !!payload.hasNextPage,
        hasPreviousPage: !!payload.hasPreviousPage,
        startCursor: null,
        endCursor: null
      },
      productFilters: responseFilters,
      nodes: responseItems,
      wardrobeSizeRelaxed: relaxInheritedSize,
      wardrobeSizeContext: relaxInheritedSize ? inheritedSizeContext : null
    };
    if (colourResultScope) {
      writeColourResultToSession(colourResultScope, catalogResult);
    }
    return catalogResult;
  }

  var CATALOG_FACET_SESSION_TTL = 10 * 60 * 1000;
  var COLOUR_NAVIGATION_STORAGE_TTL = 7 * 24 * 60 * 60 * 1000;
  var CATALOG_PRIMARY_FACET_FIELDS = [
    'department', 'item_type', 'style', 'vendor', 'size_value'
  ];
  var CATALOG_SECONDARY_FACET_FIELDS = [
    'navigation_category', 'colour', 'pattern_name',
    'condition_name', 'material'
  ];

  function colourNavigationSeedFilters() {
    function list(parameter, label, values) {
      return {
        id: 'catalog:' + parameter,
        label: label,
        type: 'LIST',
        presentation: 'TEXT',
        values: values.map(function (value, index) {
          return {
            id: 'catalog:' + parameter + ':' + value.toLowerCase(),
            label: value,
            // A positive placeholder keeps each option usable. Counts remain
            // hidden while colourFacetsReady is false.
            count: Math.max(1, values.length - index),
            input: {
              catalogParameter: parameter,
              catalogValue: value
            }
          };
        })
      };
    }

    return [
      list('department', 'Department', ['Men', 'Women', 'Boys', 'Girls', 'Baby']),
      list('type', 'Type', [
        'T-Shirt', 'Jumper', 'Blouse', 'Tank', 'Sweatshirt', 'Hoodie',
        'Jacket', 'Polo', 'Button-Up', 'Skirt', 'Dress', 'Jeans',
        'Trousers', 'Shorts', 'Coat', 'Cardigan'
      ]),
      {
        id: 'catalog:price',
        label: 'Price',
        type: 'PRICE_RANGE',
        presentation: 'TEXT',
        values: []
      }
    ];
  }

  function catalogFacetSessionScope() {
    var selected = Object.keys(state.selectedValues).map(function (key) {
      var value = state.selectedValues[key] || {};
      var input = normalizeFilterInput(value.input || {});
      return [
        String(input.catalogParameter || ''),
        String(input.catalogValue || '')
      ].join('=');
    }).sort();
    var wardrobe = wardrobeParametersForCurrentScope();
    var wardrobeScope = {};
    Object.keys(wardrobe || {}).sort().forEach(function (key) {
      wardrobeScope[key] = String(wardrobe[key] || '');
    });

    return JSON.stringify({
      mode: state.mode,
      query: activeProductSearchQuery(),
      trending: !!state.trending,
      brand: state.brandQuery,
      tags: state.tagQueries.slice().sort(),
      similarSku: state.similarSku,
      collection: state.collectionHandle,
      wishlist: state.wishlistSignature,
      collectionKeywordSearch: !!state.collectionKeywordSearch,
      collectionSearchQuery: state.collectionSearchQuery,
      selected: selected,
      priceMin: state.priceMin,
      priceMax: state.priceMax,
      wardrobe: wardrobeScope
    });
  }

  function catalogFacetSessionKey(scope) {
    // Two small independent hashes keep the sessionStorage key compact while
    // the stored scope below still protects against a theoretical collision.
    var first = 2166136261;
    var second = 5381;
    for (var index = 0; index < scope.length; index++) {
      first ^= scope.charCodeAt(index);
      first = Math.imul(first, 16777619);
      second = ((second << 5) + second) ^ scope.charCodeAt(index);
    }
    return 'preworn:catalog-facets:v1:' +
      (first >>> 0).toString(36) + ':' + (second >>> 0).toString(36);
  }

  function colourNavigationStorageKey(scope) {
    return catalogFacetSessionKey(scope).replace(
      'preworn:catalog-facets:v1:',
      'preworn:colour-navigation:v1:'
    );
  }

  function readColourNavigationFromStorage(scope) {
    try {
      var raw = window.localStorage.getItem(colourNavigationStorageKey(scope));
      var cached = raw ? JSON.parse(raw) : null;
      if (!cached || cached.scope !== scope || !Array.isArray(cached.filters) ||
          !cached.filters.length ||
          Date.now() - Number(cached.savedAt || 0) > COLOUR_NAVIGATION_STORAGE_TTL) {
        return [];
      }
      return visibleCatalogFilters(cached.filters);
    } catch (error) {
      return [];
    }
  }

  function writeColourNavigationToStorage(scope, filters) {
    filters = visibleCatalogFilters(filters);
    if (!Array.isArray(filters) || !filters.length) return;
    try {
      window.localStorage.setItem(colourNavigationStorageKey(scope), JSON.stringify({
        scope: scope,
        savedAt: Date.now(),
        filters: filters
      }));
    } catch (error) {
      // The built-in navigation remains available when persistent storage is
      // disabled or full.
    }
  }

  function readCatalogFacetsFromSession(scope) {
    try {
      var raw = window.sessionStorage.getItem(catalogFacetSessionKey(scope));
      var cached = raw ? JSON.parse(raw) : null;
      if (!cached || cached.scope !== scope || !Array.isArray(cached.filters) ||
          Date.now() - Number(cached.savedAt || 0) > CATALOG_FACET_SESSION_TTL) {
        return [];
      }
      return visibleCatalogFilters(cached.filters);
    } catch (error) {
      return [];
    }
  }

  function writeCatalogFacetsToSession(scope, filters) {
    filters = visibleCatalogFilters(filters);
    if (!Array.isArray(filters) || !filters.length) return;
    try {
      window.sessionStorage.setItem(catalogFacetSessionKey(scope), JSON.stringify({
        scope: scope,
        savedAt: Date.now(),
        filters: filters
      }));
    } catch (error) {
      // Private browsing/storage limits must never prevent filters rendering.
    }
  }

  function paintColourNavigation(filters, countsReady, hydrated) {
    filters = visibleCatalogFilters(filters);
    if (!Array.isArray(filters) || !filters.length) return false;
    state.catalogFilters = filters;
    state.catalogFacetsDeferred = true;
    state.catalogFacetsHydrated = !!hydrated;
    state.catalogFacetsStale = !countsReady;
    state.colourFacetsReady = !!countsReady;
    renderQuickFilters(filters);
    renderFilters(filters);
    renderActiveFilters();
    return true;
  }

  function hydrateInstantColourFacets() {
    if (!state.exactColourHex) return false;
    var exactFilters = readCatalogFacetsFromSession(colourFacetSessionScope());
    if (paintColourNavigation(exactFilters, true, true)) return true;

    // An arbitrary HEX rarely has its own cache on first visit. Reuse the
    // latest complete rail for the same filters/wardrobe without showing its
    // stale counts, then replace it as soon as the live colour facets arrive.
    var baseScope = catalogFacetSessionScope();
    var recentFilters = readColourNavigationFromStorage(baseScope);
    if (paintColourNavigation(recentFilters, false, false)) return true;

    return paintColourNavigation(colourNavigationSeedFilters(), false, false);
  }

  function hydrateCatalogFacetsFromSession() {
    if (state.exactColourHex || state.catalogFilters.length) return false;
    var filters = readCatalogFacetsFromSession(catalogFacetSessionScope());
    if (!filters.length) return false;
    state.catalogFilters = filters;
    state.catalogFacetsHydrated = true;
    state.catalogFacetsDeferred = true;
    renderQuickFilters(filters);
    renderFilters(filters);
    renderActiveFilters();
    return true;
  }

  function applyDeferredCatalogTotal(result, facetGeneration, facetScope) {
    if (!result || !result.facetTotalIncluded || result.totalCount === null ||
        facetGeneration !== state.catalogFacetGeneration ||
        facetScope !== catalogFacetSessionScope() ||
        !state.lastResult || !state.lastResult.catalogSource ||
        state.lastResult.colourSource) return;

    state.lastResult.totalCount = Math.max(0, Number(result.totalCount));
    state.lastResult.totalPages = Math.max(
      0,
      Number(result.totalPages || 0) ||
        Math.ceil(state.lastResult.totalCount / CONFIG.pageSize)
    );
    renderCount(
      state.lastResult.totalCount,
      state.lastResult.productFilters || state.catalogFilters || [],
      state.lastResult.nodes || [],
      state.lastResult.pageInfo || {},
      state.lastResult
    );
    renderPagination(state.lastResult);
    if (window.PrewornSearchDebug) {
      window.PrewornSearchDebug.totalCount = state.lastResult.totalCount;
      window.PrewornSearchDebug.totalPages = state.lastResult.totalPages;
    }
  }

  function loadSecondaryCatalogFacets(facetGeneration, facetScope) {
    if (state.catalogSecondaryFacetsLoading || state.exactColourHex ||
        facetGeneration !== state.catalogFacetGeneration ||
        facetScope !== catalogFacetSessionScope()) return;

    state.catalogSecondaryFacetsLoading = true;
    loadCatalogResult(1, {
      includeFacets: true,
      facetsOnly: true,
      facetFields: CATALOG_SECONDARY_FACET_FIELDS,
      mergeFacets: true,
      facetGeneration: facetGeneration,
      facetScope: facetScope,
      timeout: 30000
    }).then(function (result) {
      if (facetGeneration !== state.catalogFacetGeneration ||
          facetScope !== catalogFacetSessionScope()) return;
      var filters = result.productFilters || [];
      writeCatalogFacetsToSession(facetScope, filters);
      renderQuickFilters(filters);
      renderFilters(filters);
      renderActiveFilters();
      renderCollectionPreferencePrompt(
        filters,
        state.lastResult && state.lastResult.nodes || [],
        state.lastResult && state.lastResult.totalCount
      );
    }).catch(function (error) {
      // The primary rail is already usable. Keep it visible when an optional
      // lower-priority group cannot refresh, and try again on the next visit.
      console.warn(
        '[Preworn Browse] Additional catalogue filters are temporarily unavailable.',
        error
      );
    }).finally(function () {
      if (facetGeneration === state.catalogFacetGeneration &&
          facetScope === catalogFacetSessionScope()) {
        state.catalogSecondaryFacetsLoading = false;
      }
    });
  }

  function loadDeferredColourFacets() {
    if (!state.exactColourHex || state.catalogFacetsLoading) return;

    state.catalogFacetsLoading = true;
    var facetGeneration = state.catalogFacetGeneration;
    var facetScope = colourFacetSessionScope();
    loadCatalogResult(1, {
      includeFacets: true,
      facetsOnly: true,
      colourFacetsOnly: true,
      facetGeneration: facetGeneration,
      facetScope: facetScope,
      timeout: 45000
    }).then(function (result) {
      if (!state.exactColourHex ||
          facetGeneration !== state.catalogFacetGeneration ||
          facetScope !== colourFacetSessionScope()) return;
      state.catalogFacetsDeferred = false;
      state.catalogFacetsHydrated = false;
      state.catalogFacetsStale = false;
      state.colourFacetsReady = true;
      var filters = result.productFilters || [];
      state.catalogFilters = filters;
      writeCatalogFacetsToSession(facetScope, filters);
      writeColourNavigationToStorage(catalogFacetSessionScope(), filters);
      renderQuickFilters(filters);
      renderFilters(filters);
      renderActiveFilters();
      if (state.lastResult && state.lastResult.colourSource &&
          result.totalCount !== null) {
        state.lastResult.totalCount = Math.max(0, Number(result.totalCount));
        state.lastResult.totalPages = Math.max(
          0,
          Number(result.totalPages || 0) ||
            Math.ceil(state.lastResult.totalCount / CONFIG.pageSize)
        );
        state.lastResult.countIsEstimate = !!result.countIsEstimate;
        state.lastResult.pageInfo.hasNextPage =
          state.lastResult.totalCount > CONFIG.pageSize;
        renderCount(
          state.lastResult.totalCount,
          filters,
          state.lastResult.nodes || [],
          state.lastResult.pageInfo || {},
          state.lastResult
        );
        renderPagination(state.lastResult);
      }
    }).catch(function (error) {
      if (facetGeneration !== state.catalogFacetGeneration) return;
      state.catalogFacetsDeferred = false;
      console.warn(
        '[Preworn Browse] Colour filter counts are temporarily unavailable.',
        error
      );
      // Product cards are already usable. Keep the previous navigation rather
      // than replacing it with an error or retry loop.
    }).finally(function () {
      if (facetGeneration === state.catalogFacetGeneration) {
        state.catalogFacetsLoading = false;
      }
    });
  }

  function scheduleDeferredColourFacets() {
    if (!state.exactColourHex) return;
    if (state.colourFacetTimer) window.clearTimeout(state.colourFacetTimer);
    var facetGeneration = state.catalogFacetGeneration;
    var facetScope = colourFacetSessionScope();
    state.colourFacetTimer = window.setTimeout(function () {
      state.colourFacetTimer = null;
      if (facetGeneration !== state.catalogFacetGeneration ||
          facetScope !== colourFacetSessionScope()) return;
      loadDeferredColourFacets();
    }, state.colourPage ? 240 : 450);
  }

  function loadDeferredCatalogFacets() {
    if (state.exactColourHex) return;
    if (state.catalogFacetsLoading ||
        (state.catalogFilters.length && !state.catalogFacetsHydrated &&
          !state.catalogFacetsStale)) return;

    if (Date.now() < state.catalogUnavailableUntil) {
      var retryDelay = Math.max(
        250,
        state.catalogUnavailableUntil - Date.now() + 50
      );
      var waitingBody = document.getElementById('PrewornSearchFiltersBody');
      state.catalogFacetsDeferred = true;
      if (waitingBody) {
        waitingBody.innerHTML =
          '<p class="pw-search__muted">Loading filters…</p>';
      }
      if (state.catalogFacetRetryTimer) {
        window.clearTimeout(state.catalogFacetRetryTimer);
      }
      state.catalogFacetRetryTimer = window.setTimeout(function () {
        state.catalogFacetRetryTimer = null;
        loadDeferredCatalogFacets();
      }, retryDelay);
      return;
    }

    state.catalogFacetsLoading = true;
    var facetGeneration = state.catalogFacetGeneration;
    var facetScope = catalogFacetSessionScope();
    var mergeHydratedFacets = state.catalogFacetsHydrated;
    loadCatalogResult(1, {
      includeFacets: true,
      facetsOnly: true,
      facetFields: CATALOG_PRIMARY_FACET_FIELDS,
      mergeFacets: mergeHydratedFacets,
      facetGeneration: facetGeneration,
      facetScope: facetScope,
      // The unfiltered All catalogue aggregates ten facets across more than
      // 500k products and can be slower immediately after a database restart.
      timeout: 30000
    }).then(function (result) {
      if (facetGeneration !== state.catalogFacetGeneration ||
          facetScope !== catalogFacetSessionScope()) return;
      state.catalogFacetsDeferred = false;
      state.catalogFacetsHydrated = false;
      state.catalogFacetsStale = false;
      state.catalogFacetRetryCount = 0;
      var filters = result.productFilters || [];
      applyDeferredCatalogTotal(result, facetGeneration, facetScope);
      writeCatalogFacetsToSession(facetScope, filters);
      collectionStyleDirectoryFromFilters(
        filters,
        state.lastResult && state.lastResult.nodes || []
      );
      renderQuickFilters(filters);
      renderFilters(filters);
      renderActiveFilters();
      renderCollectionPreferencePrompt(
        filters,
        state.lastResult && state.lastResult.nodes || [],
        state.lastResult && state.lastResult.totalCount
      );
      window.setTimeout(function () {
        loadSecondaryCatalogFacets(facetGeneration, facetScope);
      }, 100);
    }).catch(function (error) {
      if (facetGeneration !== state.catalogFacetGeneration) return;
      console.warn(
        '[Preworn Browse] Catalogue filters are temporarily unavailable.',
        error
      );
      var body = document.getElementById('PrewornSearchFiltersBody');

      // A heavy facet request must never retry automatically and multiply the
      // load while MariaDB is recovering. Product results remain usable and
      // the customer can explicitly retry the optional filters.
      state.catalogFacetRetryCount++;
      state.catalogFacetsDeferred = false;
      if (body && !state.catalogFilters.length) {
        body.innerHTML =
          '<p class="pw-search__muted">Filters could not load. ' +
          '<button type="button" class="link underlined-link" ' +
          'data-pw-retry-facets>Try again</button></p>';
        var retryButton = body.querySelector('[data-pw-retry-facets]');
        if (retryButton) {
          retryButton.addEventListener('click', function () {
            state.catalogFacetRetryCount = 0;
            state.catalogFacetsDeferred = true;
            body.innerHTML =
              '<p class="pw-search__muted">Loading filters…</p>';
            loadDeferredCatalogFacets();
          });
        }
      }
    }).finally(function () {
      if (facetGeneration === state.catalogFacetGeneration) {
        state.catalogFacetsLoading = false;
      }
    });
  }

  function buildStorefrontProductFilters() {
    return buildProductFilters().map(function (input) {
      if (!input || !input.catalogParameter) return input;
      if (input.catalogParameter === 'vendor') {
        return { productVendor: input.catalogValue };
      }
      if (input.catalogParameter === 'type') {
        return {
          productType: input.catalogValue === 'Dress'
            ? 'Womens Dresses'
            : input.catalogValue
        };
      }
      return null;
    }).filter(function (input) {
      return !!input;
    });
  }

  async function loadStorefrontResult(afterCursor) {
    if (!CONFIG.storefrontToken ||
        CONFIG.storefrontToken === 'PASTE_PUBLIC_STOREFRONT_TOKEN_HERE') {
      throw new Error('The Shopify fallback is not configured.');
    }

    var graphqlQuery;
    var variables;
    var storefrontFilters = buildStorefrontProductFilters();

    var useProductsQuery = state.mode === 'search' &&
      !state.collectionKeywordSearch &&
      !state.query &&
      storefrontFilters.length === 0 &&
      (state.sortKey === 'CREATED' || state.sortKey === 'TRENDING');

    if (useProductsQuery) {
      graphqlQuery = PRODUCTS_QUERY;
      variables = {
        query: null,
        first: CONFIG.pageSize,
        after: afterCursor || null,
        sortKey: 'CREATED_AT',
        reverse: true
      };
    } else if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      graphqlQuery = COLLECTION_QUERY;
      variables = {
        handle: state.collectionHandle,
        first: CONFIG.pageSize,
        after: afterCursor || null,
        filters: storefrontFilters,
        sortKey: state.sortKey === 'PRICE' || state.sortKey === 'CREATED'
          ? state.sortKey
          : 'CREATED',
        reverse: state.sortKey === 'COLLECTION_DEFAULT' ? true : state.reverse
      };
    } else {
      graphqlQuery = SEARCH_QUERY;
      variables = {
        query: state.collectionKeywordSearch
          ? state.collectionSearchQuery
          : (state.query || '*'),
        first: CONFIG.pageSize,
        after: afterCursor || null,
        filters: state.collectionKeywordSearch
          ? state.collectionScopeFilters.concat(storefrontFilters)
          : storefrontFilters,
        sortKey: state.collectionKeywordSearch &&
          state.sortKey === 'COLLECTION_DEFAULT'
            ? 'RELEVANCE'
            : (state.sortKey === 'PRICE' ? 'PRICE' : 'RELEVANCE'),
        reverse: state.reverse
      };
    }

    var response = await fetch(
      'https://' + CONFIG.shopDomain + '/api/' + CONFIG.apiVersion + '/graphql.json',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
        },
        body: JSON.stringify({ query: graphqlQuery, variables: variables })
      }
    );
    var payload = await response.json();

    if (!response.ok) {
      throw new Error('HTTP ' + response.status + ': ' + JSON.stringify(payload));
    }
    if (payload.errors && payload.errors.length) {
      throw new Error(payload.errors.map(function (error) {
        return error.message;
      }).join(' | '));
    }
    if (!payload.data) throw new Error('Shopify returned no browse data.');

    if (graphqlQuery === PRODUCTS_QUERY) {
      if (!payload.data.products) throw new Error('Shopify returned no product data.');
      return payload.data.products;
    }
    if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      if (!payload.data.collection) throw new Error('Collection not found.');
      return payload.data.collection.products;
    }
    if (!payload.data.search) throw new Error('Shopify returned no search data.');
    return payload.data.search;
  }

  function isUnfilteredCollectionCatalogMiss(result, pageNumber) {
    if (
      state.mode !== 'collection' ||
      state.exactColourHex ||
      state.collectionKeywordSearch ||
      Number(pageNumber) !== 1 ||
      !result ||
      !result.catalogSource ||
      Number(result.totalCount || 0) !== 0
    ) {
      return false;
    }

    return !state.query && !wardrobeScopeActive() &&
      Object.keys(state.selectedValues).length === 0 &&
      !state.priceMin &&
      !state.priceMax;
  }

  async function loadPage(pageNumber, afterCursor) {
    if (state.loading) {
      // Keep the latest multi-select state. Once the in-flight request ends,
      // run one fresh request containing every option selected in the meantime.
      state.pendingReload = true;
      return;
    }

    var reloadWithCollectionQuery = false;
    var runPendingReload = false;
    var colourRequestGeneration = state.colourRequestGeneration;
    var wishlistRequestGeneration = state.wishlistGeneration;
    state.pendingReload = false;
    state.loading = true;
    if (app) app.classList.remove('is-wardrobe-unconfigured');
    setLoading(true);
    clearError();

    try {
      var result;
      var source = 'Shopify Storefront API';
      var hadCatalogResult = state.usingCatalog ||
        !!(state.lastResult && state.lastResult.catalogSource);

      if (
        !CONFIG.catalogApiEnabled ||
        (state.mode === 'collection' && state.collectionCatalogStale)
      ) {
        if (state.mode === 'wishlist' || wardrobeScopeActive() || state.exactColourHex) {
          throw new Error(state.mode === 'wishlist'
            ? 'Your saved pieces need the PREWORN catalogue service. Please try again shortly.'
            : (state.exactColourHex
            ? 'Exact colour search needs the PREWORN catalogue service. Please try again shortly.'
            : 'My Wardrobe needs the PREWORN catalogue service. Please try again shortly.'));
        }
        // Avoid touching a known-unhealthy cross-origin endpoint. An aborted
        // fetch is correctly shown in red by DevTools even when fallback works.
        state.usingCatalog = false;
        state.catalogFacetsDeferred = false;
        if (state.collectionCatalogStale) {
          source = 'Shopify Storefront API collection fallback';
        }
        result = await loadStorefrontResult(afterCursor);
      } else {
        try {
          if (!state.exactColourHex && Date.now() < state.catalogUnavailableUntil) {
            throw new Error('Catalogue API is in temporary failover mode.');
          }

          // Products and standard facets are independent reads. Normal browse
          // pages can start both together. A Wardrobe facet build is much more
          // expensive, so let the 50 product cards win the database slot first;
          // the rail and its exact total follow without blocking the grid.
          if (
            pageNumber === 1 &&
            !state.exactColourHex &&
            !wardrobeScopeActive() &&
            (!state.catalogFilters.length || state.catalogFacetsHydrated ||
              state.catalogFacetsStale)
          ) {
            state.catalogFacetsDeferred = true;
            loadDeferredCatalogFacets();
          }

          var primaryCatalogOptions = {
            // Exact-colour navigation is deliberately a second request. The
            // customer should never wait for it before seeing product cards.
            includeFacets: false,
            countMode: wardrobeScopeActive() && !state.exactColourHex
              ? 'none' : 'cached',
            routeLookup: pageNumber === 1 && state.searchAnalyticsEligible &&
              !state.searchAnalyticsPublished && !state.searchRouteNavigating,
            timeout: state.exactColourHex
              ? CONFIG.colourRequestTimeout : CONFIG.catalogRequestTimeout
          };
          result = await loadCatalogResult(pageNumber, primaryCatalogOptions);
          var inheritedSizeContext = inheritedWardrobeSizeContext();
          if (Number(pageNumber) === 1 && inheritedSizeContext &&
              !result.wardrobeSizeRelaxed &&
              (!result.nodes || result.nodes.length === 0) &&
              (result.totalCount === null || Number(result.totalCount || 0) === 0)) {
            // A family size should help discovery, never turn a valid
            // collection into a dead end. Retry once with that inherited size
            // relaxed; the page explains that every available size is shown.
            var relaxedCatalogOptions = Object.assign({}, primaryCatalogOptions, {
              relaxInheritedWardrobeSize: true
            });
            result = await loadCatalogResult(pageNumber, relaxedCatalogOptions);
            result.wardrobeSizeRelaxed = true;
            result.wardrobeSizeContext = inheritedSizeContext;
          }
          if (colourRequestGeneration !== state.colourRequestGeneration) {
            var staleColourError = new Error('A newer colour was selected.');
            staleColourError.staleColourRequest = true;
            throw staleColourError;
          }
          if (wishlistRequestGeneration !== state.wishlistGeneration) {
            var staleWishlistError = new Error('The saved-piece scope changed.');
            staleWishlistError.staleWishlistRequest = true;
            throw staleWishlistError;
          }
          if (isUnfilteredCollectionCatalogMiss(result, pageNumber)) {
            // Shopify evaluates newly created automated collections
            // immediately, while the separate MySQL membership projection is
            // reconciled by the next bulk catalogue sync. Never let a valid
            // but stale zero replace Shopify's authoritative collection.
            state.collectionCatalogStale = true;
            result = null;
            throw new Error(
              'Catalogue collection membership is stale; verifying with Shopify.'
            );
          }
          state.catalogUnavailableUntil = 0;
          source = 'PREWORN catalogue database';
          if (pageNumber === 1 && state.catalogFilters.length === 0) {
            state.catalogFacetsDeferred = true;
          }
        } catch (catalogError) {
          if (catalogError.staleColourRequest ||
              colourRequestGeneration !== state.colourRequestGeneration) {
            throw catalogError;
          }
          if (!state.exactColourHex) {
            state.catalogUnavailableUntil = Date.now() + CONFIG.catalogFailureCooldown;
          }

          if (state.mode === 'wishlist' || wardrobeScopeActive() || state.exactColourHex) {
            throw catalogError;
          }

          if (!result) {
            if (hadCatalogResult) throw catalogError;

            state.usingCatalog = false;
            console.warn(
              '[Preworn Browse] Catalogue API unavailable; using Shopify fallback.',
              catalogError
            );
            source = 'Shopify Storefront API fallback';
            result = await loadStorefrontResult(afterCursor);
          }
        }
      }

      if (result.catalogSource) {
        state.usingCatalog = true;
        state.collectionKeywordSearch = false;
        state.collectionSearchQuery = '';
        state.collectionScopeFilters = [];
      }

      if (result.wardrobeSizeRelaxed && result.wardrobeSizeContext) {
        state.wardrobeSizeFallbackKey = result.wardrobeSizeContext.key;
        state.wardrobeSizeFallbackSizes = result.wardrobeSizeContext.sizes.slice();
      } else {
        state.wardrobeSizeFallbackKey = '';
        state.wardrobeSizeFallbackSizes = [];
      }

      // Shopify is only an emergency fallback, so its total can be broader
      // than the displayed page. Request the first variant SKU above and
      // enforce the same invariant before any fallback card is rendered.
      result.nodes = (result.nodes || []).filter(productUsesPrimarySkuImage);

      var availableFilters = result.productFilters || result.filters || [];
      if (result.catalogSource && state.catalogFilters.length) {
        availableFilters = state.catalogFilters;
        result.productFilters = state.catalogFilters;
      }
      if (
        !result.catalogSource &&
        pageNumber === 1 &&
        applyCollectionQueryFilter(availableFilters, result.nodes || [])
      ) {
        reloadWithCollectionQuery = true;
      }

      window.PrewornSearchDebug = {
        source: source,
        mode: state.mode,
        page: pageNumber,
        query: state.query,
        interpretedSearchText: state.interpretedSearchText,
        interpretedQuery: result.interpretedQuery || state.interpretedSearchIntent,
        exactColour: state.exactColourHex,
        exactColourTolerance: state.exactColourTolerance,
        wardrobeScoped: state.wardrobeActive || hasLinkedWardrobe(),
        wardrobeSizeRelaxed: !!state.wardrobeSizeFallbackKey,
        wardrobeParameters: Object.assign({}, state.linkedWardrobeParameters),
        collectionHandle: state.collectionHandle,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        productsLoaded: result.nodes ? result.nodes.length : 0,
        filterCount: availableFilters.length,
        hasNextPage: result.pageInfo ? result.pageInfo.hasNextPage : false,
        startCursor: result.pageInfo ? result.pageInfo.startCursor : null,
        endCursor: result.pageInfo ? result.pageInfo.endCursor : null
      };

      console.log('[PREWORN API]', window.PrewornSearchDebug);
      if (!reloadWithCollectionQuery) {
        state.currentPage = pageNumber;
        state.lastResult = result;
        if (state.exactColourHex && pageNumber === 1 &&
            result.colourSeedProductIds && result.colourSeedProductIds.length) {
          state.colourFirstPageSeed = result.colourSeedProductIds.slice(0, 60);
        }

        if (result.pageInfo && result.pageInfo.hasNextPage &&
            result.pageInfo.endCursor) {
          state.pageCursors[pageNumber + 1] = result.pageInfo.endCursor;
        }

        renderResult(result);
        hideNativeBrowse();
        scrollResultsIntoView(pageNumber);

        if (
          result.catalogSource &&
          pageNumber === 1 &&
          state.catalogFacetsDeferred
        ) {
          if (state.exactColourHex) scheduleDeferredColourFacets();
          else loadDeferredCatalogFacets();
        }
      }
    } catch (error) {
      var staleColourRequest = !!error.staleColourRequest ||
        colourRequestGeneration !== state.colourRequestGeneration;
      var staleWishlistRequest = !!error.staleWishlistRequest ||
        wishlistRequestGeneration !== state.wishlistGeneration;
      if (staleColourRequest || staleWishlistRequest) {
        state.pendingReload = state.mode !== 'wishlist' || state.wishlistItemCount > 0;
      }
      else console.error('[Preworn Browse]', error);

      if (staleColourRequest || staleWishlistRequest) {
        // The newest selection is queued below. Do not flash an error for the
        // deliberately superseded colour or saved-piece request.
      } else if (state.mode === 'wishlist') {
        showError('Your saved pieces could not refresh. Unfiltered products have not been shown; please try again.');
        hideNativeBrowse();
      } else if (state.exactColourHex) {
        hideStaleExactColourResults();
        showError(
          'Exact colour search is taking longer than expected. Your filters are still selected.',
          true
        );
        hideNativeBrowse();
      } else if (wardrobeScopeActive()) {
        showError('My Wardrobe could not refresh. It remains on, and unfiltered fallback results have not been shown. Please try again.');
        hideNativeBrowse();
      } else if (hadCatalogResult) {
        showError(
          'The catalogue request took too long. Your previous database results have been kept; please try the filter again.'
        );
        hideNativeBrowse();
      } else {
        showError(
          'The enhanced product browser could not load, so the normal Shopify results are available below. ' +
          error.message
        );
        showNativeFallback();
      }
    } finally {
      state.loading = false;
      setLoading(false);
      runPendingReload = state.pendingReload;
      state.pendingReload = false;
    }

    if (reloadWithCollectionQuery || runPendingReload) loadPage(1, null);
  }

  function deferExactColourResultControls(filters) {
    var generation = state.colourRequestGeneration;
    var selectedHex = state.exactColourHex;
    function renderControls() {
      if (generation !== state.colourRequestGeneration ||
          selectedHex !== state.exactColourHex) return;
      renderQuickFilters(filters);
      renderFilters(filters);
      renderActiveFilters();
    }
    function afterFirstPaint() {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(renderControls, { timeout: 700 });
      } else {
        window.setTimeout(renderControls, 0);
      }
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(afterFirstPaint);
    } else {
      window.setTimeout(afterFirstPaint, 0);
    }
  }

  function renderResult(result) {
    var availableFilters = result.productFilters || result.filters || [];
    if (result.catalogSource && state.catalogFilters.length) {
      availableFilters = state.catalogFilters;
    }
    var visibleFilters = availableFilters;

    if (state.collectionKeywordSearch) {
      visibleFilters = availableFilters.filter(function (filter) {
        var label = normalizeCollectionQuery(filter.label);
        return label !== 'type' &&
          label !== 'product type' &&
          label !== 'department';
      });
    }

    // Paint the useful visual directory before totals and the product grid.
    // Its numerical counts are hydrated independently in the next task.
    collectionStyleDirectoryFromFilters(availableFilters, result.nodes || []);
    renderCount(
      result.totalCount,
      availableFilters,
      result.nodes || [],
      result.pageInfo || {},
      result
    );
    renderCollectionPreferencePrompt(
      availableFilters,
      result.nodes || [],
      result.totalCount
    );
    renderProducts(result.nodes || []);
    scheduleCollectionStyleDirectoryRequest();
    renderExactColourControl();
    syncSortControl();
    renderPagination(result);
    if (state.exactColourHex) {
      // Cards are the primary experience. Give the browser a paint before
      // rebuilding potentially large quick-filter and rail DOM trees.
      deferExactColourResultControls(visibleFilters);
    } else {
      renderQuickFilters(visibleFilters);
      renderFilters(visibleFilters);
      renderActiveFilters();
    }
    scheduleWardrobeSummary();
  }

  function brandActivityMetric(selector) {
    var metric = document.querySelector(selector);
    var value = metric ? String(metric.textContent || '').trim() : '';
    return value && value !== '—' && value !== '-' ? value : '';
  }

  function renderToolbarPrimary(toolbarCount, label) {
    if (!toolbarCount) return;
    toolbarCount.textContent = '';
    label = String(label || '').replace(/\bitems?\b/gi, function (itemLabel) {
      return itemLabel.toUpperCase();
    });
    if (!label) return;
    var primary = document.createElement('span');
    primary.className = 'pw-search__toolbar-primary';
    primary.textContent = label;
    toolbarCount.appendChild(primary);
  }

  function appendToolbarActivityMetric(toolbarCount, value, label, mobileLabel, modifier) {
    if (!toolbarCount || !label || (modifier !== 'pw-search__toolbar-availability' && !value)) return;
    var metric = document.createElement('span');
    metric.className = 'pw-search__toolbar-stat ' + modifier;
    if (value) {
      var number = document.createElement('b');
      number.textContent = value;
      metric.appendChild(number);
    }
    var desktopCopy = document.createElement('span');
    desktopCopy.className = 'pw-search__toolbar-stat-label pw-search__toolbar-stat-label--desktop';
    desktopCopy.textContent = label;
    metric.appendChild(desktopCopy);
    var mobileCopy = document.createElement('span');
    mobileCopy.className = 'pw-search__toolbar-stat-label pw-search__toolbar-stat-label--mobile';
    mobileCopy.textContent = mobileLabel || label;
    metric.appendChild(mobileCopy);
    toolbarCount.appendChild(metric);
  }

  function syncToolbarActivityMetrics() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return;
    var toolbarCount = document.getElementById('PrewornToolbarCount');
    if (!toolbarCount || !String(toolbarCount.textContent || '').trim()) return;
    var existing = toolbarCount.querySelectorAll('.pw-search__toolbar-stat');
    for (var i = 0; i < existing.length; i++) existing[i].remove();
    appendToolbarActivityMetric(
      toolbarCount,
      '',
      'Available now',
      'Available',
      'pw-search__toolbar-availability'
    );
    appendToolbarActivityMetric(
      toolbarCount,
      brandActivityMetric('[data-pw-brand-new]'),
      'New this week',
      'New',
      'pw-search__toolbar-new'
    );
    appendToolbarActivityMetric(
      toolbarCount,
      brandActivityMetric('[data-pw-brand-sold]'),
      'Sold · 30 days',
      'Sold',
      'pw-search__toolbar-sold'
    );
  }

  function bindToolbarActivityMetrics() {
    if (!app || app.getAttribute('data-tag-page') !== 'true') return;
    var metrics = document.querySelector('.pwbrandpage__catalogue-metrics');
    if (!metrics) return;
    syncToolbarActivityMetrics();
    if (typeof MutationObserver !== 'function') return;
    if (state.toolbarActivityObserver) state.toolbarActivityObserver.disconnect();
    state.toolbarActivityObserver = new MutationObserver(syncToolbarActivityMetrics);
    state.toolbarActivityObserver.observe(metrics, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function renderCount(totalCount, filters, products, pageInfo, result) {
    var count = document.getElementById('PrewornSearchCount');
    var toolbarCount = document.getElementById('PrewornToolbarCount');
    var drawerCount = app && app.querySelector('[data-pw-filter-view-count]');
    var drawerLabel = app && app.querySelector('[data-pw-filter-view-label]');
    var numericTotal = totalCount !== null && typeof totalCount !== 'undefined' &&
      isFinite(Number(totalCount)) ? Math.max(0, Number(totalCount)) : null;
    publishImageHeroResult(numericTotal);
    publishSearchAnalytics(numericTotal, result);
    if (app && app.getAttribute('data-tag-page') === 'true' &&
        numericTotal !== null && !hasActiveFilters() && !state.query &&
        !state.exactColourHex && !wardrobeScopeActive()) {
      // The first unfiltered catalogue response is the authoritative count
      // for this exact tag-page scope. Publish it to the enclosing hero so a
      // stale or slow metrics cache can never disagree with the grid below.
      app.setAttribute('data-pw-tag-scope-count', String(numericTotal));
      app.dispatchEvent(new CustomEvent('preworn:tag-scope-count', {
        bubbles: true,
        detail: { totalCount: numericTotal }
      }));
    }
    if (drawerCount && numericTotal !== null) {
      drawerCount.textContent = numericTotal.toLocaleString('en-GB');
      if (drawerLabel) drawerLabel.textContent = numericTotal === 1 ? 'product' : 'products';
    }
    if (!count && !toolbarCount) return;

    var description;
    var toolbarLabel;

    if (result && result.catalogSource) {
      var isMatching = hasActiveFilters() ||
        (state.mode === 'collection' && !!state.query);
      var catalogTotalKnown = totalCount !== null &&
        typeof totalCount !== 'undefined' && isFinite(Number(totalCount));

      if (catalogTotalKnown) {
        var exactTotal = Math.max(0, Number(totalCount));
        var exactDisplay = exactTotal.toLocaleString('en-GB');
        description = (result.colourSource && result.countIsEstimate ? 'More than ' : '') +
          exactDisplay + (isMatching
          ? ' matching preloved '
          : ' unique preloved ') + (exactTotal === 1 ? 'piece' : 'pieces');
        if (result.colourSource) description += ' · closest colours first';
        toolbarLabel = exactDisplay + ' ' +
          (exactTotal === 1 ? 'item' : 'items');
      } else {
        var loadedThrough = Math.max(
          0,
          ((state.currentPage - 1) * CONFIG.pageSize) + products.length
        );
        var hasMore = !!pageInfo.hasNextPage;
        var provisionalDisplay = loadedThrough.toLocaleString('en-GB');
        if (result.colourSource && result.totalDeferred) {
          description = '';
          toolbarLabel = '';
        } else {
          description = (hasMore ? 'More than ' : '') + provisionalDisplay +
            (isMatching ? ' matching preloved ' : ' unique preloved ') +
            (loadedThrough === 1 ? 'piece' : 'pieces');
          toolbarLabel = (hasMore ? provisionalDisplay + '+' : provisionalDisplay) +
            ' ' + (loadedThrough === 1 ? 'item' : 'items');
        }
      }
    } else if (state.mode === 'collection' && state.collectionKeywordSearch) {
      var keywordTotal = Math.min(
        Number(totalCount || 0),
        CONFIG.shopifyFallbackMaxResults
      );
      var keywordDisplay = Number(totalCount || 0) > CONFIG.shopifyFallbackMaxResults
        ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
        : keywordTotal.toLocaleString('en-GB');

      description = keywordDisplay + ' matching preloved ' +
        (keywordTotal === 1 ? 'piece' : 'pieces');
      toolbarLabel = keywordDisplay + ' ' +
        (keywordTotal === 1 ? 'item' : 'items');
    } else if (state.mode === 'collection') {
      if (hasActiveFilters()) {
        var filteredCount = getFilteredCollectionCount(filters, products, pageInfo);

        if (filteredCount !== null) {
          var filteredDisplay = filteredCount.toLocaleString('en-GB');
          description = filteredDisplay + ' matching preloved ' +
            (filteredCount === 1 ? 'piece' : 'pieces');
          toolbarLabel = filteredDisplay + ' ' +
            (filteredCount === 1 ? 'item' : 'items');
        } else {
          description = 'A filtered selection of unique preloved pieces';
          toolbarLabel = 'Filtered items';
        }
      } else if (
        typeof state.collectionCount === 'number' &&
        isFinite(state.collectionCount)
      ) {
        var collectionDisplay = state.collectionCount > CONFIG.shopifyFallbackMaxResults
          ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
          : state.collectionCount.toLocaleString('en-GB');

        description = collectionDisplay + ' unique preloved pieces';
        toolbarLabel = collectionDisplay + ' items';
      } else {
        description = 'Unique preloved pieces';
        toolbarLabel = 'Items';
      }
    } else {
      var shown = totalCount > CONFIG.shopifyFallbackMaxResults
        ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
        : Number(totalCount || 0).toLocaleString('en-GB');

      description = shown + ' matching preloved pieces';
      toolbarLabel = shown + ' items';
    }

    if (count) count.textContent = description;
    if (toolbarCount) {
      renderToolbarPrimary(toolbarCount, toolbarLabel);
      if (toolbarLabel) syncToolbarActivityMetrics();
    }
  }

  function publishSearchAnalytics(totalCount, result) {
    if (state.searchAnalyticsPublished || !state.searchAnalyticsEligible ||
        state.currentPage !== 1 || totalCount === null ||
        typeof window.CustomEvent !== 'function') return;
    // Canonical searches such as "red dress" are translated into structured
    // catalogue filters, so state.query can be empty even though the shopper
    // submitted a real q value. Preserve the customer's original search text.
    var query = currentProductSearchText();
    if (!query || query === '*') return;

    state.searchAnalyticsPublished = true;
    var route = result && result.searchRoute ? result.searchRoute : null;
    var routeDestination = safeSearchRouteDestination(route);
    // Product merchandising routes must keep respecting an active Wardrobe.
    // The exact allowlisted returns portal is navigation rather than product
    // merchandising, so it remains available while Wardrobe filters are on.
    if (routeDestination && wardrobeScopeActive() &&
        routeDestination !== RETURNS_PORTAL_SEARCH_ROUTE) {
      routeDestination = '';
    }
    var source = state.searchAnalyticsSource;
    if (!source) {
      if (state.similarSku) source = 'similar_item';
      else if (state.mode === 'collection') source = 'collection_search';
      else source = 'search_page';
    }
    app.dispatchEvent(new CustomEvent('preworn:search-results', {
      bubbles: true,
      detail: {
        query: query,
        contextType: state.mode === 'collection' ? 'collection' : 'search',
        contextHandle: state.mode === 'collection' ? state.collectionHandle : '',
        contextTitle: state.mode === 'collection' ? state.collectionTitle : 'Search',
        path: window.location.pathname,
        source: source,
        resultCount: totalCount,
        resultCountIsEstimate: !!(result && result.countIsEstimate),
        catalogSource: result && result.catalogSource ? 'preworn_catalog' : 'shopify',
        wardrobeActive: wardrobeScopeActive(),
        routeId: routeDestination ? Number(route.routeId) : 0,
        routeDestination: routeDestination
      }
    }));
    if (routeDestination) followSearchRoute(route);
  }

  function getFilteredCollectionCount(filters, products, pageInfo) {
    var singleValueGroups = ['type', 'product type', 'brand', 'vendor'];

    for (var g = 0; g < singleValueGroups.length; g++) {
      for (var f = 0; f < filters.length; f++) {
        var filter = filters[f];
        var label = String(filter.label || '').toLowerCase().trim();

        if (label !== singleValueGroups[g]) continue;

        var values = filter.values || [];
        var containsSelectedValue = values.some(function (value) {
          return !!state.selectedValues[value.id];
        });

        if (!values.length || containsSelectedValue) continue;

        var total = values.reduce(function (sum, value) {
          return sum + Math.max(0, Number(value.count || 0));
        }, 0);

        if (total > 0) return total;
      }
    }

    if (!pageInfo.hasNextPage && state.currentPage === 1) {
      return products.length;
    }

    return null;
  }

  function renderProducts(products) {
    var grid = document.getElementById('PrewornSearchGrid');
    if (!grid) return;
    grid.hidden = false;

    if (!products.length) {
      grid.innerHTML =
        '<li class="pw-search__empty">No products match these filters.</li>';
      return;
    }

    var html = '';
    var wishlistIndex = getWishlistIndex();

    for (var i = 0; i < products.length; i++) {
      html += productCard(products[i], wishlistIndex, i);
    }

    grid.innerHTML = html;
    for (var c = 0; c < products.length; c++) {
      var colourMatch = products[c] && products[c].visualColour;
      var card = grid.children[c];
      var media = card && (card.querySelector('.pw-card__media') || card);
      if (!colourMatch || !media) continue;
      var chip = document.createElement('span');
      var matchPercentage = colourMatchPercentage(colourMatch);
      chip.className = 'pw-colour-match-chip';
      chip.style.setProperty('--pw-match-colour', colourMatch.matchedHex || state.exactColourHex);
      chip.innerHTML = '<i aria-hidden="true"></i>' +
        escapeHtml(colourMatchLabel(matchPercentage)) + ' · ' +
        escapeHtml(matchPercentage) + '% match';
      media.appendChild(chip);
    }
    bindWishlistButtons(grid);
  }

  function readWishlist() {
    try {
      var stored = window.localStorage.getItem(CONFIG.wishlistStorageKey);
      var parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be read.', error);
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(CONFIG.wishlistStorageKey, JSON.stringify(items));
      return true;
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be updated.', error);
      return false;
    }
  }

  function wishlistProductId(item) {
    var raw = String(item && (
      item.legacyResourceId || item.productId || item.id
    ) || '').trim();
    var gid = raw.match(/^gid:\/\/shopify\/Product\/(\d+)$/i);
    if (gid) return gid[1];
    return /^\d+$/.test(raw) ? raw : '';
  }

  function wishlistProductHandle(item) {
    var handle = String(item && item.handle || '').trim();
    if (!handle && item && item.url) {
      try {
        var path = new URL(String(item.url), window.location.href).pathname;
        var match = path.match(/\/products\/([^/?#]+)/i);
        if (match) handle = decodeURIComponent(match[1]);
      } catch (error) {}
    }
    return /^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle) ? handle : '';
  }

  function syncWishlistScope(items) {
    items = Array.isArray(items) ? items : [];
    var previousSignature = state.wishlistSignature;
    var productIds = [];
    var handles = [];
    var seenIds = {};
    var seenHandles = {};
    var identityCount = 0;
    var limit = 300;

    for (var i = 0; i < items.length; i++) {
      var productId = wishlistProductId(items[i]);
      var handle = productId ? '' : wishlistProductHandle(items[i]);
      if ((!productId || seenIds[productId]) && (!handle || seenHandles[handle])) {
        continue;
      }
      if (identityCount >= limit) break;
      if (productId) {
        seenIds[productId] = true;
        productIds.push(productId);
      } else if (handle) {
        seenHandles[handle] = true;
        handles.push(handle);
      }
      identityCount += 1;
    }

    productIds.sort();
    handles.sort();
    state.wishlistProductIds = productIds;
    state.wishlistHandles = handles;
    state.wishlistItemCount = items.length;
    state.wishlistScopeLimitReached = identityCount >= limit && items.length > identityCount;
    state.wishlistSignature = productIds.join(',') + '|' + handles.join(',');
    if (state.wishlistSignature !== previousSignature) state.wishlistGeneration += 1;
    return state.wishlistSignature !== previousSignature;
  }

  function renderWishlistPageState(items) {
    if (!app || state.mode !== 'wishlist') return;
    items = Array.isArray(items) ? items : [];
    var page = app.closest && app.closest('[data-pw-wishlist-page]');
    if (!page) page = document;
    var hasItems = items.length > 0;
    var catalogue = page.querySelector('[data-pw-wishlist-catalog]');
    var empty = page.querySelector('[data-pw-wishlist-empty]');
    var emptyShell = page.querySelector('[data-pw-wishlist-empty-shell]');
    var note = page.querySelector('[data-pw-wishlist-limit-note]');
    var countText = String(items.length);
    var heroText = items.length < 10 ? '0' + countText : countText;
    var heroCounts = page.querySelectorAll('[data-pw-wishlist-hero-count]');
    var inlineCounts = page.querySelectorAll('[data-pw-wishlist-inline-count]');

    for (var h = 0; h < heroCounts.length; h++) heroCounts[h].textContent = heroText;
    for (var c = 0; c < inlineCounts.length; c++) {
      inlineCounts[c].textContent = '(' + countText + ')';
    }
    if (catalogue) catalogue.hidden = !hasItems;
    if (empty) empty.hidden = hasItems;
    if (emptyShell) emptyShell.hidden = hasItems;
    if (note) note.hidden = !hasItems || !state.wishlistScopeLimitReached;
    if (page.classList) page.classList.toggle('is-empty', !hasItems);
  }

  function refreshWishlistCatalogue() {
    if (!app || state.mode !== 'wishlist') return;
    var items = readWishlist();
    var scopeChanged = syncWishlistScope(items);
    renderWishlistPageState(items);
    if (!scopeChanged) return;

    resetPagination();
    refreshCatalogFacetsForCurrentScope();
    requestCollectionStyleDirectory({ prepareOnly: true });
    if (!items.length) {
      state.lastResult = null;
      state.pendingReload = false;
      setLoading(false);
      var grid = document.getElementById('PrewornSearchGrid');
      var pagination = document.getElementById('PrewornSearchPagination');
      var directory = collectionStyleDirectoryRoot();
      if (grid) grid.innerHTML = '';
      if (pagination) pagination.innerHTML = '';
      if (directory) directory.hidden = true;
      return;
    }
    navigateWithHistory(1, null);
  }

  function getWishlistIndex() {
    var items = readWishlist();
    var index = {};

    for (var i = 0; i < items.length; i++) {
      if (!items[i]) continue;
      var productId = wishlistProductId(items[i]);
      var handle = wishlistProductHandle(items[i]);
      if (items[i].id) index[String(items[i].id)] = true;
      if (productId) index['product:' + productId] = true;
      if (handle) index['handle:' + handle.toLowerCase()] = true;
    }

    return index;
  }

  function wishlistItemsMatch(left, right) {
    var leftProductId = wishlistProductId(left);
    var rightProductId = wishlistProductId(right);
    if (leftProductId && rightProductId && leftProductId === rightProductId) return true;
    var leftHandle = wishlistProductHandle(left).toLowerCase();
    var rightHandle = wishlistProductHandle(right).toLowerCase();
    if (leftHandle && rightHandle && leftHandle === rightHandle) return true;
    return !!(left && right && left.id && right.id && String(left.id) === String(right.id));
  }

  /* Product suppliers do not crop their catalogue photography consistently.
     Some otherwise transparent-looking files contain a large white canvas,
     which makes the garment look much smaller than its neighbours when the
     whole bitmap is fitted into the card. Detect that canvas once per image
     URL and zoom/recentre only the affected images. */
  var productImageAnalysisCache = {};
  var productImageObserver = null;
  var productImageResizeBound = false;
  var productImageResizeTimer = null;

  function productImageForegroundBounds(pixels, width, height) {
    if (!pixels || !width || !height) return null;

    function isForeground(x, y) {
      var offset = (y * width + x) * 4;
      var alpha = pixels[offset + 3];
      if (alpha < 24) return false;

      var red = pixels[offset];
      var green = pixels[offset + 1];
      var blue = pixels[offset + 2];
      var darkest = Math.min(red, green, blue);
      var lightest = Math.max(red, green, blue);

      return !(darkest >= 238 && lightest - darkest <= 24);
    }

    /* Do not crop lifestyle photography or coloured backdrops. This feature
       is deliberately limited to product cut-outs on white/transparent
       canvases. */
    var cornerSize = Math.max(3, Math.round(Math.min(width, height) * 0.04));
    var cornerPixels = 0;
    var foregroundCorners = 0;
    var cornerOrigins = [
      [0, 0],
      [width - cornerSize, 0],
      [0, height - cornerSize],
      [width - cornerSize, height - cornerSize]
    ];

    for (var c = 0; c < cornerOrigins.length; c++) {
      for (var cy = cornerOrigins[c][1]; cy < cornerOrigins[c][1] + cornerSize; cy++) {
        for (var cx = cornerOrigins[c][0]; cx < cornerOrigins[c][0] + cornerSize; cx++) {
          cornerPixels++;
          if (isForeground(cx, cy)) foregroundCorners++;
        }
      }
    }

    if (foregroundCorners / cornerPixels > 0.18) return null;

    var columnCounts = new Array(width);
    var rowCounts = new Array(height);
    for (var x = 0; x < width; x++) columnCounts[x] = 0;
    for (var y = 0; y < height; y++) rowCounts[y] = 0;

    for (var scanY = 0; scanY < height; scanY++) {
      for (var scanX = 0; scanX < width; scanX++) {
        if (!isForeground(scanX, scanY)) continue;
        columnCounts[scanX]++;
        rowCounts[scanY]++;
      }
    }

    /* Ignore isolated JPEG speckles and faint compression marks at the canvas
       edge while retaining thin sleeves, straps and trouser legs. */
    var minimumColumnPixels = Math.max(2, Math.round(height * 0.016));
    var minimumRowPixels = Math.max(2, Math.round(width * 0.016));
    var left = 0;
    var right = width - 1;
    var top = 0;
    var bottom = height - 1;

    while (left < width && columnCounts[left] < minimumColumnPixels) left++;
    while (right >= 0 && columnCounts[right] < minimumColumnPixels) right--;
    while (top < height && rowCounts[top] < minimumRowPixels) top++;
    while (bottom >= 0 && rowCounts[bottom] < minimumRowPixels) bottom--;

    if (left >= right || top >= bottom) return null;

    left = Math.max(0, left - 1);
    right = Math.min(width - 1, right + 1);
    top = Math.max(0, top - 1);
    bottom = Math.min(height - 1, bottom + 1);

    return {
      left: left / width,
      right: (right + 1) / width,
      top: top / height,
      bottom: (bottom + 1) / height
    };
  }

  function applyProductImageBounds(image, bounds) {
    if (!image || !bounds || !image.isConnected) return false;
    image._pwProductImageBounds = bounds;
    var mediaSelector = image.getAttribute('data-pw-normalise-media') || '.pw-card__media';
    var media = image.closest ? image.closest(mediaSelector) : image.parentNode;
    if (!media) return false;

    /* Measure the untransformed image every time. getBoundingClientRect()
       includes an existing transform, which previously made a resize or
       repeated bind partially undo the first normalisation pass. */
    image.style.removeProperty('--pw-product-image-scale');
    image.style.removeProperty('--pw-product-image-hover-scale');
    image.style.removeProperty('--pw-product-image-x');
    image.style.removeProperty('--pw-product-image-y');
    var imageRect = image.getBoundingClientRect();
    var mediaRect = media.getBoundingClientRect();
    if (!imageRect.width || !imageRect.height || !mediaRect.width || !mediaRect.height) return false;

    /* The first Nike examples are 1008x672 landscape JPEGs inside a tall CSS
       image box. Their actual object-fit area is therefore much shorter than
       getBoundingClientRect().height. Measure that contained image rectangle,
       not the surrounding element, or landscape canvases look falsely full. */
    var computed = typeof window.getComputedStyle === 'function'
      ? window.getComputedStyle(image) : null;
    var paddingLeft = computed ? parseFloat(computed.paddingLeft) || 0 : 0;
    var paddingRight = computed ? parseFloat(computed.paddingRight) || 0 : 0;
    var paddingTop = computed ? parseFloat(computed.paddingTop) || 0 : 0;
    var paddingBottom = computed ? parseFloat(computed.paddingBottom) || 0 : 0;
    var contentWidth = Math.max(1, imageRect.width - paddingLeft - paddingRight);
    var contentHeight = Math.max(1, imageRect.height - paddingTop - paddingBottom);
    var intrinsicWidth = Number(image.naturalWidth || bounds.intrinsicWidth || contentWidth);
    var intrinsicHeight = Number(image.naturalHeight || bounds.intrinsicHeight || contentHeight);
    var intrinsicRatio = intrinsicWidth > 0 && intrinsicHeight > 0
      ? intrinsicWidth / intrinsicHeight : contentWidth / contentHeight;
    var objectWidth = contentWidth;
    var objectHeight = objectWidth / intrinsicRatio;
    var objectFit = computed ? String(computed.objectFit || '') : '';
    if (objectFit === 'cover') {
      if (objectHeight < contentHeight) {
        objectHeight = contentHeight;
        objectWidth = objectHeight * intrinsicRatio;
      }
    } else if (objectHeight > contentHeight) {
        objectHeight = contentHeight;
        objectWidth = objectHeight * intrinsicRatio;
    }
    var objectLeft = paddingLeft + (contentWidth - objectWidth) / 2;
    var objectTop = paddingTop + (contentHeight - objectHeight) / 2;
    var foregroundWidth = objectWidth * Math.max(0.01, bounds.right - bounds.left);
    var foregroundHeight = objectHeight * Math.max(0.01, bounds.bottom - bounds.top);
    var heroMode = image.getAttribute('data-pw-normalise-fill') === 'hero';
    var widthScale = mediaRect.width * (heroMode ? 0.90 : 0.90) / foregroundWidth;
    var heightScale = mediaRect.height * (heroMode ? 0.90 : 0.76) / foregroundHeight;
    var scale = Math.min(widthScale, heightScale, heroMode ? 2.15 : 2.70);

    /* Leave already well-cropped files completely untouched. */
    if (!isFinite(scale) || scale < 1.02) scale = 1;

    var centreX = (bounds.left + bounds.right) / 2;
    var centreY = (bounds.top + bounds.bottom) / 2;
    var foregroundCentreX = objectLeft + centreX * objectWidth;
    var foregroundCentreY = objectTop + centreY * objectHeight;
    var imageCentreX = imageRect.width / 2;
    var imageCentreY = imageRect.height / 2;
    var translateX = -(foregroundCentreX - imageCentreX) * scale;
    var translateY = -(foregroundCentreY - imageCentreY) * scale;

    /* A centred crop can still clip an asymmetric garment when the source
       canvas contains uneven whitespace. Keep the complete detected product
       inside a small safe area rather than sacrificing the neckline or hem. */
    if (heroMode) {
      var safeX = imageRect.width * 0.035;
      var safeY = imageRect.height * 0.035;
      var foregroundLeft = objectLeft + bounds.left * objectWidth;
      var foregroundRight = objectLeft + bounds.right * objectWidth;
      var foregroundTop = objectTop + bounds.top * objectHeight;
      var foregroundBottom = objectTop + bounds.bottom * objectHeight;
      var transformedLeft = imageCentreX + (foregroundLeft - imageCentreX) * scale + translateX;
      var transformedRight = imageCentreX + (foregroundRight - imageCentreX) * scale + translateX;
      var transformedTop = imageCentreY + (foregroundTop - imageCentreY) * scale + translateY;
      var transformedBottom = imageCentreY + (foregroundBottom - imageCentreY) * scale + translateY;
      if (transformedLeft < safeX) translateX += safeX - transformedLeft;
      if (transformedRight > imageRect.width - safeX) translateX -= transformedRight - (imageRect.width - safeX);
      if (transformedTop < safeY) translateY += safeY - transformedTop;
      if (transformedBottom > imageRect.height - safeY) translateY -= transformedBottom - (imageRect.height - safeY);
    }

    image.style.setProperty('--pw-product-image-scale', scale.toFixed(4));
    image.style.setProperty('--pw-product-image-hover-scale', (scale * 1.025).toFixed(4));
    image.style.setProperty('--pw-product-image-x', translateX.toFixed(2) + 'px');
    image.style.setProperty('--pw-product-image-y', translateY.toFixed(2) + 'px');
    image.setAttribute('data-pw-image-normalised', scale > 1 ? 'true' : 'false');
    return true;
  }

  function finishProductImageAnalysis(image, status, bounds) {
    if (!image || !image.isConnected) return;
    if (status === 'ready' && bounds && !applyProductImageBounds(image, bounds)) {
      // A lazy card can be analysed before the browser has given its media box
      // measurable dimensions. Keep it in the hidden pending state and retry
      // after layout instead of revealing the unscaled supplier canvas.
      var attempts = Number(image._pwProductImageLayoutAttempts || 0);
      if (attempts < 8) {
        image._pwProductImageLayoutAttempts = attempts + 1;
        window.setTimeout(function () {
          finishProductImageAnalysis(image, status, bounds);
        }, Math.min(400, 40 * Math.pow(2, attempts)));
        return;
      }
      status = 'unavailable';
    }
    image._pwProductImageLayoutAttempts = 0;
    image.setAttribute('data-pw-image-analysis', status === 'ready' ? 'complete' : 'unavailable');
  }

  function analyseProductImage(image) {
    if (!image || image._pwProductImageAnalysisStarted) return;
    image._pwProductImageAnalysisStarted = true;
    var source = image.currentSrc || image.getAttribute('src') || '';
    if (!source) {
      image.setAttribute('data-pw-image-analysis', 'unavailable');
      return;
    }

    var cached = productImageAnalysisCache[source];
    if (cached && cached.status === 'ready') {
      finishProductImageAnalysis(image, cached.status, cached.bounds);
      return;
    }
    if (cached && cached.status === 'pending') {
      cached.images.push(image);
      image.setAttribute('data-pw-image-analysis', 'pending');
      return;
    }
    if (cached && cached.status === 'unavailable') {
      image.setAttribute('data-pw-image-analysis', 'unavailable');
      return;
    }

    cached = { status: 'pending', images: [image], bounds: null };
    productImageAnalysisCache[source] = cached;
    image.setAttribute('data-pw-image-analysis', 'pending');

    var analyser = new Image();
    analyser.crossOrigin = 'anonymous';
    analyser.decoding = 'async';

    analyser.onload = function () {
      try {
        var sampleWidth = 128;
        var sampleHeight = 128;
        var canvas = document.createElement('canvas');
        canvas.width = sampleWidth;
        canvas.height = sampleHeight;
        var context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Canvas context unavailable');
        context.clearRect(0, 0, sampleWidth, sampleHeight);
        context.drawImage(analyser, 0, 0, sampleWidth, sampleHeight);
        var pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
        cached.bounds = productImageForegroundBounds(pixels, sampleWidth, sampleHeight);
        if (cached.bounds) {
          cached.bounds.intrinsicWidth = analyser.naturalWidth || sampleWidth;
          cached.bounds.intrinsicHeight = analyser.naturalHeight || sampleHeight;
        }
        cached.status = 'ready';
      } catch (error) {
        cached.status = 'unavailable';
      }

      for (var i = 0; i < cached.images.length; i++) {
        var target = cached.images[i];
        if (!target || !target.isConnected) continue;
        finishProductImageAnalysis(target, cached.status, cached.bounds);
      }
      cached.images = [];
    };

    analyser.onerror = function () {
      cached.status = 'unavailable';
      for (var i = 0; i < cached.images.length; i++) {
        if (cached.images[i]) cached.images[i].setAttribute('data-pw-image-analysis', 'unavailable');
      }
      cached.images = [];
    };

    analyser.src = source;
  }

  function bindProductImageNormalisation(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var images = scope.querySelectorAll('.pw-card .pw-card__product-image');

    if (!productImageObserver && typeof window.IntersectionObserver === 'function') {
      productImageObserver = new window.IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (!entries[e].isIntersecting) continue;
          productImageObserver.unobserve(entries[e].target);
          analyseProductImage(entries[e].target);
        }
      }, { rootMargin: '1200px 0px' });
    }

    for (var i = 0; i < images.length; i++) {
      (function (image) {
        if (productImageObserver) {
          productImageObserver.observe(image);
          return;
        }
        var schedule = window.requestIdleCallback || function (callback) {
          return window.setTimeout(callback, 1);
        };
        schedule(function () { analyseProductImage(image); }, { timeout: 800 });
      })(images[i]);
    }

    if (!productImageResizeBound) {
      productImageResizeBound = true;
      window.addEventListener('resize', function () {
        window.clearTimeout(productImageResizeTimer);
        productImageResizeTimer = window.setTimeout(function () {
          var currentImages = document.querySelectorAll('.pw-card .pw-card__product-image');
          for (var r = 0; r < currentImages.length; r++) {
            if (currentImages[r]._pwProductImageBounds) {
              applyProductImageBounds(currentImages[r], currentImages[r]._pwProductImageBounds);
            }
          }
        }, 120);
      }, { passive: true });
    }
  }

  function bindWishlistButtons(scope) {
    bindProductImageNormalisation(scope);
    var buttons = scope.querySelectorAll('[data-pw-wishlist]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        var rawItem = this.getAttribute('data-pw-wishlist-item');
        var item;

        try {
          item = JSON.parse(rawItem);
        } catch (error) {
          console.warn('[Preworn Wishlist] Invalid product data.', error);
          return;
        }

        var items = readWishlist();
        var itemId = String(item.id);
        var existingIndex = -1;

        for (var w = 0; w < items.length; w++) {
          if (wishlistItemsMatch(items[w], item)) {
            existingIndex = w;
            break;
          }
        }

        var isSaved = existingIndex === -1;
        if (isSaved) {
          items.push(item);
        } else {
          items.splice(existingIndex, 1);
        }

        if (!writeWishlist(items)) return;

        syncWishlistButtons(itemId, isSaved);

        if (typeof window.CustomEvent === 'function') {
          window.dispatchEvent(new CustomEvent('preworn:wishlist-change', {
            detail: { item: item, saved: isSaved, count: items.length }
          }));
        }
      });
    }

    bindProductCardOutfitButtons(scope);
  }

  function productCardOutfitHref(product) {
    var handle = String(product && product.handle || '').trim();
    return OUTFIT_URL + (handle ? '?start_product=' + encodeURIComponent(handle) : '') + '#BuildAnOutfit';
  }

  function startProductCardOutfit(button) {
    var product;
    try {
      product = JSON.parse(button.getAttribute('data-pw-outfit-item') || 'null');
    } catch (error) {
      product = null;
    }
    if (!product || !String(product.handle || '').trim()) return;

    try {
      window.localStorage.setItem(OUTFIT_START_KEY, JSON.stringify({
        version: 1,
        savedAt: Date.now(),
        pending: true,
        product: product
      }));
    } catch (error) {
      button.classList.add('is-error');
      button.setAttribute('aria-label', 'Build outfit unavailable. Try again.');
      return;
    }

    window.location.assign(productCardOutfitHref(product));
  }

  function bindProductCardOutfitButtons(scope) {
    var buttons = scope.querySelectorAll('[data-pw-card-outfit]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-pw-card-outfit-bound') === 'true') continue;
      buttons[i].setAttribute('data-pw-card-outfit-bound', 'true');
      buttons[i].addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        startProductCardOutfit(this);
      });
    }
  }

  function syncWishlistButtons(itemId, isSaved) {
    var buttons = document.querySelectorAll('[data-pw-wishlist-id="' + cssSelectorValue(itemId) + '"]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('is-saved', isSaved);
      buttons[i].setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      buttons[i].setAttribute(
        'aria-label',
        isSaved ? 'Remove from wishlist' : 'Add to wishlist'
      );
      buttons[i].setAttribute(
        'title',
        isSaved ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }
  }

  function cssSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/(["\\])/g, '\\$1');
  }
function getTieredProductPrice(rawPrice) {
  var originalPrice = parseFloat(rawPrice);

  if (!isFinite(originalPrice)) {
    return null;
  }

  var discountPercent = 55;
  var multiplier = 0.45;

  if (originalPrice <= 5) {
    discountPercent = 10;
    multiplier = 0.90;
  }
  else if (originalPrice <= 10) {
    discountPercent = 20;
    multiplier = 0.80;
  }
  else if (originalPrice <= 15) {
    discountPercent = 30;
    multiplier = 0.70;
  }
  else if (originalPrice <= 20) {
    discountPercent = 40;
    multiplier = 0.60;
  }
  else if (originalPrice < 30) {
    discountPercent = 45;
    multiplier = 0.55;
  }

  var discountedPrice =
    Math.round((originalPrice * multiplier) * 100) / 100;

  return {
    original: originalPrice,
    discounted: discountedPrice,
    discountPercent: discountPercent
  };
}

function productDepartmentPresentation(product, attributes) {
  product = product || {};
  attributes = attributes || {};
  var sources = [
    attributes.department,
    product.department,
    product.departmentName,
    product.audience
  ];
  var key = 'other';

  for (var i = 0; i < sources.length; i++) {
    key = departmentKey(sources[i]);
    if (key !== 'other') break;
  }

  if (key === 'other') {
    var searchable = [
      product.title,
      product.productType,
      Array.isArray(product.tags) ? product.tags.join(' ') : product.tags
    ].join(' ').toLowerCase();

    if (/\b(?:bab(?:y|ies)|infants?)\b/.test(searchable)) key = 'baby';
    else if (/\bgirls?\b/.test(searchable)) key = 'girls';
    else if (/\bboys?\b/.test(searchable)) key = 'boys';
    else if (/\b(?:women|woman|womens|female)(?:['’]s)?\b/.test(searchable)) key = 'women';
    else if (/\b(?:men|man|mens|male)(?:['’]s)?\b/.test(searchable)) key = 'men';
  }

  var labels = {
    women: "Women's",
    men: "Men's",
    girls: 'Girls',
    boys: 'Boys',
    baby: 'Baby'
  };

  return labels[key] ? { key: key, label: labels[key] } : null;
}

function productPresentation(product) {
  var vendor = String(product.vendor || '').trim();
  var title = String(product.title || '').trim();
  var attributes = product.attributes || {};
  var exactType = String(attributes.type || '').trim();
  var exactStyle = String(attributes.style || '').trim();
  var effectiveType = String(exactType || product.productType || '').trim();
  var working = title;
  var size = String(attributes.size || '').trim();
  var colour = String(attributes.colour || '').trim();
  var department = productDepartmentPresentation(product, attributes);

  if (vendor) {
    working = working.replace(
      new RegExp('^' + escapeRegExp(vendor) + '\\s*[-–—:]?\\s*', 'i'),
      ''
    );
  }

  working = working
    .replace(/\b(?:women|men|girl|boy)(?:['’]s|s)?\b/gi, ' ')
    .replace(/\bunisex\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  var explicitSize = size ? null : working.match(/\bsize\s+([a-z0-9./+-]+)/i);
  var looseSize = size ? null : working.match(/\b(6XL|5XL|4XL|3XL|2XL|1XL|XXXL|XXL|XL|L|M|S|XS|XXS|small|medium|large)\b/i);
  var sizeMatch = explicitSize || looseSize;

  if (sizeMatch) {
    size = sizeMatch[1];
    working = working.replace(sizeMatch[0], ' ');
  } else if (size) {
    working = working
      .replace(new RegExp('\\bsize\\s+' + escapeRegExp(size) + '\\b', 'i'), ' ')
      .replace(new RegExp('\\b' + escapeRegExp(size) + '\\b', 'i'), ' ');
  }

  var colours = [
    'Black', 'White', 'Grey', 'Gray', 'Navy', 'Blue', 'Green', 'Red',
    'Pink', 'Purple', 'Orange', 'Yellow', 'Brown', 'Beige', 'Cream',
    'Burgundy', 'Khaki', 'Teal', 'Multi', 'Silver', 'Gold'
  ];

  if (colour) {
    working = working.replace(
      new RegExp('\\b' + escapeRegExp(colour) + '\\b', 'i'),
      ' '
    );
  } else {
    for (var c = 0; c < colours.length; c++) {
      var colourPattern = new RegExp('\\b' + colours[c] + '\\b', 'i');
      if (colourPattern.test(working)) {
        colour = colours[c] === 'Gray' ? 'Grey' : colours[c];
        working = working.replace(colourPattern, ' ');
        break;
      }
    }
  }

  working = working
    .replace(/\s*[-–—|:]\s*/g, ' ')
    .replace(/[‘’']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // A large number of older products have a useful item type but no stored
  // Style metafield. Do not let the generic type (for example "Dress") hide
  // a clear style in the product title. The same shared presentation is used
  // by search, collections, homepage rows and Shop similar.
  var retailTitle = exactStyle
    ? styleAndTypeTitle(exactStyle, exactType)
    : conciseRetailTitle(working, effectiveType, '');
  if (!retailTitle) retailTitle = exactType || effectiveType || 'Preloved piece';

  var meta = [];
  var sizeText = '';
  if (size) {
    var displaySize = String(size).toUpperCase();
    if (displaySize === 'SMALL') displaySize = 'S';
    if (displaySize === 'MEDIUM') displaySize = 'M';
    if (displaySize === 'LARGE') displaySize = 'L';
    sizeText = 'Size ' + displaySize;
    meta.push(sizeText);
  }
  if (department) meta.push(department.label);
  if (colour) meta.push(colour);

  return {
    title: retailTitle,
    meta: meta.join(' · '),
    sizeText: sizeText,
    colour: colour,
    department: department
  };
}

function productMetaHtml(presentation) {
  presentation = presentation || {};
  var department = presentation.department;
  var html = '';

  if (department && department.label) {
    html += '<span class="pw-card__department pw-card__department--' +
      escapeAttr(department.key) + '">' + escapeHtml(department.label) + '</span>';
  }
  if (presentation.sizeText) {
    html += '<span class="pw-card__size">' + escapeHtml(presentation.sizeText) + '</span>';
  }
  if (presentation.colour) {
    if (html) html += '<span class="pw-card__meta-divider" aria-hidden="true">·</span>';
    html += '<span class="pw-card__meta-colour">' + escapeHtml(presentation.colour) + '</span>';
  }

  return html ? '<div class="pw-card__meta">' + html + '</div>' : '';
}

function styleAndTypeTitle(style, type) {
  var styleValue = String(style || '').replace(/\s+/g, ' ').trim();
  var typeValue = String(type || '').replace(/\s+/g, ' ').trim();

  if (!styleValue) return typeValue || 'Preloved piece';
  if (!typeValue) return styleValue;

  var styleKey = styleValue.toLowerCase().replace(/[^a-z0-9]+/g, '');
  var typeKey = typeValue.toLowerCase().replace(/[^a-z0-9]+/g, '');

  if (styleKey === typeKey || styleKey.indexOf(typeKey) !== -1) {
    return styleValue;
  }
  if (typeKey.indexOf(styleKey) !== -1) {
    return typeValue;
  }

  return styleValue + ' ' + typeValue;
}

function conciseRetailTitle(title, productType, productStyle) {
  var source = String(title || '').trim();
  var titleSearchable = source.toLowerCase();
  var productTypeSearchable = String(productType || '').toLowerCase();
  var searchable = (
    source + ' ' + String(productType || '') + ' ' + String(productStyle || '')
  ).toLowerCase();
  var typeDefinitions = [
    ['Hoodie', /\bhoodies?\b/],
    ['Sweatshirt', /\bsweatshirts?\b/],
    ['Cardigan', /\bcardigans?\b/],
    ['Jumper', /\bjumpers?\b/],
    ['Sweater', /\bsweaters?\b/],
    ['Fleece', /\bfleeces?\b/],
    ['Polo Shirt', /\bpolo(?:\s+shirts?)?\b/],
    ['T-Shirt', /\b(?:t[ -]?shirts?|tees?)\b/],
    ['Shirt', /\bshirts?\b/],
    ['Blouse', /\bblouses?\b/],
    ['Gilet', /\bgilets?\b/],
    ['Jacket', /\bjackets?\b/],
    ['Coat', /\bcoats?\b/],
    ['Blazer', /\bblazers?\b/],
    ['Dress', /\bdress(?:es)?\b/],
    ['Jeans', /\bjeans?\b/],
    ['Trousers', /\btrousers?\b/],
    ['Shorts', /\bshorts?\b/],
    ['Skirt', /\bskirts?\b/],
    ['Leggings', /\bleggings?\b/],
    ['Jumpsuit', /\bjumpsuits?\b/],
    ['Top', /\btops?\b/],
    ['Trainers', /\btrainers?\b/],
    ['Boots', /\bboots?\b/],
    ['Shoes', /\bshoes?\b/],
    ['Bag', /\bbags?\b/]
  ];
  var type = firstMatchedLabel(productTypeSearchable, typeDefinitions) ||
    firstMatchedLabel(titleSearchable, typeDefinitions);

  var primaryStyle = firstMatchedLabel(searchable, [
    ['Fit & Flare', /\bfit\s*(?:&|and)\s*flare\b/],
    ['Straight & Pencil', /\b(?:straight\s*(?:&|and)\s*pencil|pencil)\b/],
    ['A-Line', /\ba[ -]?line\b/],
    ['Puffer', /\bpuffer\b/],
    ['Full Zip', /\b(?:full[ -]?zip|zip[ -]?through)\b/],
    ['Quarter Zip', /\b(?:quarter[ -]?zip|1\s*\/\s*4[ -]?zip)\b/],
    ['Half Zip', /\b(?:half[ -]?zip|1\s*\/\s*2[ -]?zip)\b/],
    ['Zip Up', /\bzip[ -]?up\b/],
    ['Crew Neck', /\bcrew[ -]?neck\b/],
    ['V-Neck', /\bv[ -]?neck\b/],
    ['Roll Neck', /\broll[ -]?neck\b/],
    ['Funnel Neck', /\bfunnel[ -]?neck\b/],
    ['High Neck', /\bhigh[ -]?neck\b/],
    ['Off Shoulder', /\b(?:off[ -]?shoulder|bardot)\b/],
    ['One Shoulder', /\bone[ -]?shoulder\b/],
    ['Halter Neck', /\bhalter(?:[ -]?neck)?\b/],
    ['Wrap', /\bwrap(?:around)?\b/],
    ['Bodycon', /\bbody[ -]?con\b/],
    ['Shirt', /\bshirt[ -]?dress\b/],
    ['Shift', /\bshift\b/],
    ['Smock', /\bsmock\b/],
    ['Skater', /\bskater\b/],
    ['Swing', /\bswing\b/],
    ['Slip', /\bslip\b/],
    ['Tunic', /\btunic\b/],
    ['Tiered', /\btiered\b/],
    ['Pleated', /\bpleated?\b/],
    ['Maxi', /\bmaxi\b/],
    ['Midi', /\bmidi\b/],
    ['Mini', /\bmini\b/],
    ['Pullover', /\bpullovers?\b/],
    ['Cropped', /\bcropped\b/],
    ['Oversized', /\boversi[sz]ed\b/]
  ]);

  if (type === 'Hoodie' && primaryStyle === 'Pullover') {
    primaryStyle = '';
  }

  var detail = firstMatchedLabel(searchable, [
    ['High Neck', /\bhigh[ -]?neck\b/],
    ['Graphic Print', /\b(?:graphic(?:\s+print)?|printed)\b/],
    ['Colour Block', /\bcolou?r[ -]?block\b/],
    ['Embroidered', /\bembroider(?:ed|y)\b/],
    ['Striped', /\bstriped?\b/],
    ['Fleece', /\bfleece\b/],
    ['Logo', /\blogo\b/],
    ['Pocket', /\bpockets?\b/]
  ]);

  if (detail === type || detail === primaryStyle) detail = '';
  if (primaryStyle === 'High Neck') detail = '';

  var parts = [];
  if (primaryStyle) parts.push(primaryStyle);
  if (detail) parts.push(detail);
  if (type) parts.push(type);

  if (parts.length) return parts.join(' ');

  var fallback = String(productType || source || 'Preloved piece')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .slice(0, 5)
    .join(' ');

  return fallback || 'Preloved piece';
}

function firstMatchedLabel(source, definitions) {
  for (var i = 0; i < definitions.length; i++) {
    if (definitions[i][1].test(source)) return definitions[i][0];
  }
  return '';
}

function brandLogoHash(value) {
  var source = String(value || '').trim();
  if (typeof source.normalize === 'function') source = source.normalize('NFC');
  source = source.toLowerCase();
  var hash = 2166136261;
  for (var index = 0; index < source.length; index++) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return ('00000000' + (hash >>> 0).toString(16)).slice(-8);
}

/* Shopify vendor values are not fully canonical. Map reviewed spelling,
   punctuation and sub-brand variants to the verified logo owner so every
   shared card surface requests the same uploaded PNG. */
var BRAND_LOGO_VENDOR_ALIASES = {
  'm and s': 'Marks & Spencer',
  'marks and spencer': 'Marks & Spencer',
  'm and s collection': 'Marks & Spencer',
  'marks and spencer collection': 'Marks & Spencer',
  'marks and spencer x ghost': 'Marks & Spencer',
  'dororthy perkins': 'Dorothy Perkins',
  'h and m': 'H&M',
  'h and m divided': 'H&M',
  'divided by h and m': 'H&M',
  'divided h and m': 'H&M',
  'h and m essentials': 'H&M',
  'h and m logg': 'H&M',
  'h and m mama': 'H&M',
  'h and m sport': 'H&M',
  'new look': 'New Look',
  'new look 915': 'New Look',
  'new look curves': 'New Look',
  'primark cares': 'Primark',
  'rebel by primark': 'Primark',
  'zara basic': 'Zara',
  'zara trafaluc': 'Zara',
  'zara woman': 'Zara',
  'zara man': 'Zara',
  'zara kids': 'Zara',
  'zara knit': 'Zara',
  'topshop moto': 'Topshop',
  'adidas neo': 'Adidas',
  'adidas originals': 'Adidas',
  'river island': 'River Island',
  'the collection debenhams': 'Debenhams',
  'collection debenhams': 'Debenhams',
  'debenhams petite collection': 'Debenhams',
  'disney baby': 'Disney',
  'disney store': 'Disney',
  'disney stores': 'Disney',
  'disney world': 'Disney',
  'mango basics': 'Mango',
  'mango suit': 'Mango',
  'mango jeans': 'Mango',
  'levis': "Levi's",
  'levis premium': "Levi's",
  'denizen for levis': "Levi's",
  'burton menswear': 'Burton',
  'burton menswear london': 'Burton',
  'burton london': 'Burton',
  'montague burton': 'Burton',
  'pull and bear': 'Pull&Bear',
  'jack and jones': 'Jack & Jones',
  'premium by jack and jones': 'Jack & Jones',
  'jack and jones core': 'Jack & Jones',
  'jack and jones vintage': 'Jack & Jones',
  'by very': 'Very',
  'v by very': 'Very',
  'john lewis and partners': 'John Lewis',
  'john lewis women': 'John Lewis',
  'kin by john lewis': 'John Lewis',
  'calvin klein jeans': 'Calvin Klein',
  'mini boden': 'Boden',
  'boss hugo boss': 'Hugo Boss',
  'boss': 'Hugo Boss',
  'hugo': 'Hugo Boss',
  'victorias secret': "Victoria's Secret",
  'pink victorias secret': "Victoria's Secret",
  'victorias secret pink': "Victoria's Secret",
  'lyle and scott': 'Lyle & Scott',
  'dunnes': 'Dunnes Stores',
  'dunnes stored': 'Dunnes Stores',
  'helen steele x dunnes stores': 'Dunnes Stores',
  'st bernard for dunnes stores': 'Dunnes Stores',
  'polo ralph lauren': 'Ralph Lauren',
  'lauren ralph lauren': 'Ralph Lauren',
  'chaps ralph lauren': 'Ralph Lauren',
  'polo golf ralph lauren': 'Ralph Lauren',
  'polo jeans ralph lauren': 'Ralph Lauren',
  'ralph lauren sport': 'Ralph Lauren',
  'bench': 'Bench',
  'american eagle outfitters': 'American Eagle',
  'all saints': 'AllSaints',
  'allsaints spitalfields': 'AllSaints',
  'berghaus fashion': 'Berghaus',
  'coca cola': 'Coca-Cola',
  'barbour international': 'Barbour',
  'north face': 'The North Face',
  'fat face': 'FatFace',
  'sheinside': 'SheIn',
  'morrisons': "Morrison's",
  'netflix stranger things': 'Netflix',
  'asos design': 'ASOS',
  'asos edition': 'ASOS',
  'boohoo man': 'Boohoo',
  'boohooman': 'Boohoo',
  'denim co': 'Denim & Co.',
  'denim and co': 'Denim & Co.',
  'deniem and co': 'Denim & Co.',
  'denimco': 'Denim & Co.',
  'f and f': 'F&F',
  'f and f active': 'F&F',
  'f and f kids': 'F&F',
  'f and f school': 'F&F',
  'baby gap': 'Gap',
  'babygap': 'Gap',
  'gap factory store': 'Gap',
  'gap kids': 'Gap',
  'asda george': 'George',
  'm and co': 'M&Co',
  'spirit at m and co': 'M&Co',
  'miss selfirdge': 'Miss Selfridge',
  'monsoon fusion': 'Monsoon',
  'next tailoring': 'NEXT',
  'next woman': 'NEXT',
  'pretty little thing': 'PRETTYLITTLETHING',
  'prettylitlething': 'PRETTYLITTLETHING',
  'regatta professional': 'Regatta',
  'regatta great outdoors': 'Regatta',
  'tu school': 'Tu',
  'spotlight by warehouse': 'Warehouse'
};

function brandLogoAliasKey(value) {
  var source = String(value || '').trim().toLowerCase();
  if (typeof source.normalize === 'function') {
    source = source.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return source
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\s+/g, ' ');
}

function canonicalBrandLogoVendor(vendor) {
  var source = String(vendor || '').trim();
  return BRAND_LOGO_VENDOR_ALIASES[brandLogoAliasKey(source)] || source;
}

function appendBrandLogoAssetNames(names, vendor, includeLegacy) {
  var source = String(vendor || '').trim().toLowerCase();
  if (!source) return;
  var ascii = source;
  if (typeof ascii.normalize === 'function') {
    ascii = ascii.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  var slug = ascii
    .replace(/['’]/g, '')
    .replace(/&/g, '-and-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72)
    .replace(/-+$/g, '') || 'brand';

  names.push('pw-brand-' + slug + '-' + brandLogoHash(vendor));
  if (!includeLegacy) return names;
  var legacyNames = [
    source
      .replace(/ /g, '-')
      .replace(/&/g, '-')
      .replace(/é/g, 'e')
      .replace(/\./g, ''),
    ascii
      .replace(/['’]/g, '')
      .replace(/&/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  ];
  var unsafeLegacyAssets = {
    'planet': true,
    'baby': true,
    '11°': true
  };
  if (!unsafeLegacyAssets[source]) names = names.concat(legacyNames);
  return names;
}

function brandLogoAssetNames(vendor) {
  var requestedVendor = String(vendor || '').trim();
  if (!requestedVendor) return [];
  var canonicalVendor = canonicalBrandLogoVendor(requestedVendor);
  var names = [];
  names = appendBrandLogoAssetNames(names, canonicalVendor, true) || names;
  if (canonicalVendor.toLowerCase() !== requestedVendor.toLowerCase()) {
    names = appendBrandLogoAssetNames(names, requestedVendor, true) || names;
  }
  var unique = [];
  for (var i = 0; i < names.length; i++) {
    var filename = names[i] ? names[i] + '.png' : '';
    if (filename && unique.indexOf(filename) === -1) unique.push(filename);
  }
  return unique;
}

function brandLogoHtml(vendor) {
  var brand = String(vendor || '').trim();
  if (!brand) return '';

  var assetSource = app && app.getAttribute('data-brand-logo-base')
    ? app
    : document.querySelector('[data-brand-logo-base]');
  if (!assetSource) return '';

  var assetBase = String(assetSource.getAttribute('data-brand-logo-base') || '');
  if (!assetBase) return '';
  if (assetBase.charAt(assetBase.length - 1) !== '/') assetBase += '/';

  /* Prefer the standardized authentic PNG. Keep verified legacy artwork as a
     compatibility fallback while the canonical library is being deployed. */
  var assetNames = brandLogoAssetNames(brand);
  if (!assetNames.length) return '';
  var urls = [];
  for (var i = 0; i < assetNames.length; i++) {
    urls.push(assetBase + encodeURIComponent(assetNames[i]));
  }
  var fallback = String(assetSource.getAttribute('data-brand-logo-fallback') || '');
  var remaining = urls.slice(1);
  if (fallback && remaining.indexOf(fallback) === -1) remaining.push(fallback);

  return '<img class="pw-card__brand-logo" src="' +
    escapeAttr(urls[0]) +
    '" alt="" aria-hidden="true" loading="lazy"' +
    (fallback ? ' data-fallback="' + escapeAttr(fallback) + '"' : '') +
    ' data-logo-next="' + escapeAttr(remaining.join('|')) + '"' +
    ' onerror="var queue=(this.getAttribute(\'data-logo-next\')||\'\').split(\'|\');' +
      'var next=queue.shift();this.setAttribute(\'data-logo-next\',queue.join(\'|\'));' +
      'if(next){this.setAttribute(\'src\',next);}else{this.hidden=true;}">';
}

  function productIsNew(product) {
  var value = String(product && (
    product.createdAt || product.shopifyCreatedAt || product.publishedAt
  ) || '').trim();
  if (!value) return false;

  // MySQL catalogue timestamps are UTC but arrive without a timezone suffix.
  // Shopify fallback timestamps are already ISO-8601. Normalise both before
  // applying the same rolling window in search and collection results.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
    value = value.replace(' ', 'T') + 'Z';
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)) {
    value += 'Z';
  }

  var timestamp = Date.parse(value);
  if (!isFinite(timestamp)) return false;
  var day = 24 * 60 * 60 * 1000;
  var age = Date.now() - timestamp;
  return age >= -day && age <= CONFIG.newProductWindowDays * day;
}

  function productCard(product, wishlistIndex, cardIndex) {
  var image = product.featuredImage;
  var presentation = productPresentation(product);
  var soldActivity = product.soldActivity || null;

  var price = product.priceRange &&
              product.priceRange.minVariantPrice
    ? product.priceRange.minVariantPrice
    : null;

  var href =
    product.onlineStoreUrl ||
    ('/products/' + product.handle);

  /* Keep Shopify's collection context when a product is opened from a
     collection. This makes the product page's `collection` object available
     for breadcrumbs and attribute-search links. */
  if (state.mode === 'collection' && state.collectionHandle && product.handle) {
    href = '/collections/' + encodeURIComponent(state.collectionHandle) +
      '/products/' + encodeURIComponent(product.handle);
  }

  var tierPrice = price
    ? getTieredProductPrice(price.amount)
    : null;

  var originalPrice = '';
  var discountedPrice = '';
  var discountPercent = 0;
  var saving = '';

  if (tierPrice) {
    originalPrice = money(
      tierPrice.original,
      price.currencyCode
    );

    discountedPrice = money(
      tierPrice.discounted,
      price.currencyCode
    );

    discountPercent = tierPrice.discountPercent;
    saving = money(
      tierPrice.original - tierPrice.discounted,
      price.currencyCode
    );
  }

  var prioritiseImage = Number(cardIndex) >= 0 && Number(cardIndex) < 5;
  var imageHtml = image
    ? '<img class="pw-card__product-image" data-pw-image-analysis="pending" loading="' +
        (prioritiseImage ? 'eager' : 'lazy') + '" decoding="async" fetchpriority="' +
        (prioritiseImage ? 'high' : 'low') + '" src="' +
        escapeAttr(image.url) +
        '" alt="' +
        escapeAttr(image.altText || product.title) +
        '">'
    : '<div class="pw-card__no-image">No image</div>';

  var discountBadge = soldActivity
    ? '<div class="pw-card__discount pw-card__sold-badge" aria-label="Sold"><span>SOLD</span></div>'
    : '';

  var newBadge = !soldActivity && productIsNew(product)
    ? '<span class="pw-card__new-badge" aria-label="New in the last 30 days">NEW</span>'
    : '';

  if (!soldActivity && discountPercent > 0) {
    discountBadge =
      '<div class="pw-card__discount pw-discount--' + discountPercent + '" aria-label="' +
        discountPercent +
        '% off">' +

        '<span>' +
          discountPercent +
          '%</span>' +

        '<small>OFF</small>' +

      '</div>';
  }

  var wishlistId = String(product.id || product.handle || href);
  var wishlistProductIdValue = wishlistProductId(product);
  var isWishlisted = !!(wishlistIndex && (
    wishlistIndex[wishlistId] ||
    (wishlistProductIdValue && wishlistIndex['product:' + wishlistProductIdValue]) ||
    (product.handle && wishlistIndex['handle:' + String(product.handle).toLowerCase()])
  ));
  var wishlistItem = {
    id: wishlistId,
    handle: product.handle || '',
    url: href,
    vendor: product.vendor || '',
    title: presentation.title,
    meta: presentation.meta,
    image: image ? image.url : '',
    price: discountedPrice,
    originalPrice: originalPrice,
    saving: saving,
    discountPercent: discountPercent,
    category: String(
      (product.attributes && (product.attributes.type || product.attributes.category)) ||
      product.productType ||
      presentation.title ||
      ''
    ),
    collectionHandle: state.mode === 'collection' ? state.collectionHandle : '',
    collectionTitle: state.mode === 'collection' ? state.collectionTitle : ''
  };
  var outfitAttributes = product.attributes && typeof product.attributes === 'object'
    ? product.attributes : {};
  var outfitCollections = Array.isArray(product.collectionHandles)
    ? product.collectionHandles.map(String) : [];
  if (state.mode === 'collection' && state.collectionHandle &&
      outfitCollections.indexOf(state.collectionHandle) === -1) {
    outfitCollections.unshift(state.collectionHandle);
  }
  var outfitItem = {
    id: product.id || wishlistId,
    handle: product.handle || '',
    onlineStoreUrl: href,
    vendor: product.vendor || '',
    title: product.title || presentation.title,
    availableForSale: product.availableForSale !== false,
    category: String(outfitAttributes.type || outfitAttributes.category || product.productType || state.collectionHandle || presentation.title || ''),
    department: String(outfitAttributes.department || product.department || ''),
    collectionHandles: outfitCollections,
    featuredImage: image ? { url: image.url, altText: image.altText || product.title || presentation.title } : null,
    priceRange: price ? { minVariantPrice: { amount: String(price.amount || 0), currencyCode: String(price.currencyCode || 'GBP') } } : null,
    attributes: outfitAttributes
  };

  var actionsHtml = '';
  if (!soldActivity) {
    actionsHtml =
      '<div class="pw-card__actions">' +
        '<button type="button" class="pw-card__cart" data-pw-add-cart' +
          ' data-product-handle="' + escapeAttr(product.handle || '') + '"' +
          ' data-product-url="' + escapeAttr(href) + '"' +
          (product.availableForSale === false ? ' disabled' : '') +
          ' aria-label="' + (product.availableForSale === false ? 'Sold out' : 'Add to basket') + '"' +
          ' title="' + (product.availableForSale === false ? 'Sold out' : 'Add to basket') + '">' +
          '<svg class="pw-card__cart-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">' +
            '<path d="M5.5 8.5h13l1 12h-15l1-12Z"></path><path d="M9 9V6.5a3 3 0 0 1 6 0V9"></path><path d="M12 12v5M9.5 14.5h5"></path>' +
          '</svg>' +
          '<svg class="pw-card__cart-check" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m6.5 12.5 3.5 3.5 7.5-8"></path></svg>' +
          '<span class="pw-card__cart-label">Add to basket</span>' +
        '</button>' +
        '<button type="button" class="pw-card__outfit-start" data-pw-card-outfit' +
          ' data-pw-outfit-item="' + escapeAttr(JSON.stringify(outfitItem)) + '"' +
          (product.availableForSale === false ? ' disabled' : '') +
          ' aria-label="Build an outfit around ' + escapeAttr(presentation.title) + '"' +
          ' title="Build outfit">' +
          '<span class="pw-card__outfit-start-label">Build outfit</span>' +
        '</button>' +
        '<button type="button" class="pw-card__wishlist' +
          (isWishlisted ? ' is-saved' : '') + '"' +
          ' data-pw-wishlist' +
          ' data-pw-wishlist-id="' + escapeAttr(wishlistId) + '"' +
          ' data-pw-wishlist-item="' + escapeAttr(JSON.stringify(wishlistItem)) + '"' +
          ' aria-pressed="' + (isWishlisted ? 'true' : 'false') + '"' +
          ' aria-label="' + (isWishlisted ? 'Remove from wishlist' : 'Add to wishlist') + '"' +
          ' title="' + (isWishlisted ? 'Remove from wishlist' : 'Add to wishlist') + '">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path>' +
          '</svg>' +
        '</button>' +
      '</div>';
  }

  return (
    '<li class="pw-card' + (soldActivity ? ' pw-card--sold' : '') + '">' +

      '<div class="pw-card__media">' +

        '<a class="pw-card__image-link" href="' + escapeAttr(href) + '"' +
          ' aria-label="' + (soldActivity ? 'Find similar to ' : 'View ') +
          escapeAttr(product.title || presentation.title) + '">' +
          newBadge +
          brandLogoHtml(product.vendor) +
          imageHtml +
          discountBadge +
        '</a>' +

        actionsHtml +

      '</div>' +

      '<a class="pw-card__link" href="' + escapeAttr(href) + '">' +

        '<div class="pw-card__body">' +

          '<div class="pw-card__vendor">' +
            escapeHtml(product.vendor || '') +
          '</div>' +

          '<div class="pw-card__title">' +
            escapeHtml(presentation.title) +
          '</div>' +

          productMetaHtml(presentation) +

          (product.handle
            ? '<div class="pwdemand-slot pwdemand-slot--card" data-pw-demand data-product-handle="' + escapeAttr(product.handle) + '" data-pw-demand-context="card" hidden></div>'
            : '') +

          '<div class="pw-card__price">' +

            '<span class="pw-card__current">' +
              escapeHtml(discountedPrice) +
            '</span>' +

            '<span class="pw-card__original">' +
              escapeHtml(originalPrice) +
            '</span>' +

            (saving
              ? '<span class="pw-card__saving">Save ' + escapeHtml(saving) + '</span>'
              : '') +

          '</div>' +

          (soldActivity && soldActivity.age
            ? '<div class="pw-card__sold-context">JUST BOUGHT · ' + escapeHtml(soldActivity.age) + '</div>'
            : '') +

        '</div>' +

      '</a>' +

    '</li>'
  );
}

  function quickDepartmentLabel(key) {
    return {
      women: 'Women',
      men: 'Men',
      girls: 'Girls',
      boys: 'Boys',
      baby: 'Baby'
    }[key] || '';
  }

  function quickDepartmentValues(filters) {
    var available = {};
    for (var f = 0; f < (filters || []).length; f++) {
      var filter = filters[f] || {};
      var label = String(filter.label || filter.id || '').toLowerCase();
      if (label.indexOf('department') === -1) continue;
      for (var v = 0; v < (filter.values || []).length; v++) {
        var value = filter.values[v] || {};
        var key = departmentKey(value.label);
        if (key !== 'other' && !available[key]) available[key] = value;
      }
    }

    return ['women', 'men', 'girls', 'boys'].map(function (key) {
      if (available[key]) {
        return Object.assign({}, available[key], { label: quickDepartmentLabel(key) });
      }
      var label = quickDepartmentLabel(key);
      return {
        id: 'catalog:department:' + key,
        label: label,
        count: 1,
        input: { catalogParameter: 'department', catalogValue: label }
      };
    });
  }

  function quickDepartmentContext() {
    var fixedTagAudiences = tagPageWardrobeAudiences();
    if (fixedTagAudiences.length === 1) {
      return {
        key: {
          woman: 'women', man: 'men', girl: 'girls', boy: 'boys', baby: 'baby'
        }[fixedTagAudiences[0]] || '',
        explicitValue: null
      };
    }
    var keys = Object.keys(state.selectedValues);
    for (var index = 0; index < keys.length; index++) {
      var selected = state.selectedValues[keys[index]] || {};
      var input = normalizeFilterInput(selected.input || {});
      if (String(input.catalogParameter || '').toLowerCase() !== 'department') continue;
      var explicitKey = departmentKey(input.catalogValue || selected.label);
      if (explicitKey === 'other') continue;
      return {
        key: explicitKey,
        explicitValue: {
          id: keys[index],
          label: quickDepartmentLabel(explicitKey),
          count: 1,
          input: input
        }
      };
    }

    var tagScope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var contextual = String(tagScope.department || '').split(',')[0] || collectionDepartment() ||
      (wardrobeScopeActive() ? wardrobeDepartmentLabel() : '');
    var contextualKey = departmentKey(contextual);
    return contextualKey === 'other'
      ? { key: '', explicitValue: null }
      : { key: contextualKey, explicitValue: null };
  }

  function renderQuickFilters(filters) {
    var target = document.getElementById('PrewornQuickFilters');
    if (!target) return;
    filters = visibleCatalogFilters(filters);

    var departmentValues = quickDepartmentValues(filters);
    var departmentContext = quickDepartmentContext();
    var quickValues = [];
    var used = {};
    var usedLabels = {};
    var valueLimit = Math.max(4, CONFIG.maxQuickFilters);

    // The first decision is always audience. Do not mix garment and brand
    // shortcuts into an unnarrowed catalogue: their old fixed ranking is what
    // made Hoodies, Nike and Adidas appear on every page.
    if (!departmentContext.key) {
      departmentValues.forEach(function (value) {
        addQuickFilterValue(
          quickValues,
          value,
          value.label,
          used,
          usedLabels,
          valueLimit
        );
      });
    } else {
      var activeDepartment = departmentContext.explicitValue;
      if (!activeDepartment) {
        for (var departmentIndex = 0; departmentIndex < departmentValues.length; departmentIndex++) {
          if (departmentKey(departmentValues[departmentIndex].label) === departmentContext.key) {
            activeDepartment = departmentValues[departmentIndex];
            break;
          }
        }
      }
      if (!activeDepartment) {
        var contextualLabel = quickDepartmentLabel(departmentContext.key);
        activeDepartment = {
          id: 'catalog:department:' + departmentContext.key,
          label: contextualLabel,
          count: 1,
          input: { catalogParameter: 'department', catalogValue: contextualLabel }
        };
      }
      addQuickFilterValue(
        quickValues,
        activeDepartment,
        activeDepartment.label,
        used,
        usedLabels,
        valueLimit
      );

      // While the narrowed facet request is in flight, retain only the chosen
      // department. This avoids briefly showing shortcuts from the previous
      // audience. The live response then supplies the strongest types, styles,
      // brands and materials for the new result set.
      var facetNavigationReady = !state.catalogFacetsStale ||
        state.catalogFacetsHydrated || state.colourFacetsReady;
      if (facetNavigationReady) {
        var primaryFilters = filters.slice().sort(function (a, b) {
          return quickFilterPriority(a) - quickFilterPriority(b);
        }).filter(function (filter) {
          return quickFilterPriority(filter) < 10;
        });

        for (var f = 0; f < primaryFilters.length && quickValues.length < valueLimit; f++) {
          var filter = primaryFilters[f];
          var priority = quickFilterPriority(filter);
          var groupLimit = priority <= 1 ? 4 : priority === 2 ? 3 : 2;
          var groupAdded = 0;
          var values = (filter.values || []).slice().sort(function (a, b) {
            var difference = Number(b.count || 0) - Number(a.count || 0);
            return difference || String(a.label || '').localeCompare(String(b.label || ''));
          });

          for (var v = 0; v < values.length && quickValues.length < valueLimit; v++) {
            if (groupAdded >= groupLimit) break;
            if (Number(values[v].count || 0) <= 0 && !state.selectedValues[values[v].id]) continue;
            if (!isUsefulQuickLabel(values[v].label)) continue;
            if (addQuickFilterValue(
              quickValues,
              values[v],
              values[v].label,
              used,
              usedLabels,
              valueLimit
            )) groupAdded++;
          }
        }
      }
    }

    quickValues = preserveQuickFilterOrder(quickValues);

    target.innerHTML = quickValues.map(function (quick) {
      return '<button type="button" class="pw-quick-chip' +
        (state.selectedValues[quick.value.id] ? ' is-active' : '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(quick.value.id) + '"' +
        ' data-pw-filter-input="' +
          escapeAttr(JSON.stringify(normalizeFilterInput(quick.value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(quick.label) + '">' +
        escapeHtml(quick.label) +
      '</button>';
    }).join('');
  }

  function preserveQuickFilterOrder(values) {
    if (!state.quickFilterOrder.length) {
      state.quickFilterOrder = values.map(function (item) {
        return item.value.id;
      });
      return values;
    }

    var positions = {};
    for (var i = 0; i < state.quickFilterOrder.length; i++) {
      positions[state.quickFilterOrder[i]] = i;
    }

    values = values.map(function (item, index) {
      return { item: item, generatedIndex: index };
    }).sort(function (a, b) {
      var aKnown = Object.prototype.hasOwnProperty.call(positions, a.item.value.id);
      var bKnown = Object.prototype.hasOwnProperty.call(positions, b.item.value.id);

      if (aKnown && bKnown) return positions[a.item.value.id] - positions[b.item.value.id];
      if (aKnown) return -1;
      if (bKnown) return 1;
      return a.generatedIndex - b.generatedIndex;
    }).map(function (entry) {
      return entry.item;
    });

    for (var v = 0; v < values.length; v++) {
      if (!Object.prototype.hasOwnProperty.call(positions, values[v].value.id)) {
        state.quickFilterOrder.push(values[v].value.id);
      }
    }

    return values;
  }

  function addQuickFilterValue(target, value, label, used, usedLabels, limit) {
    if (!value) return false;

    var cleanLabel = String(label || value.label || '').trim();
    var labelKey = cleanLabel.toLowerCase();

    if (!value || !value.id || target.length >= limit || used[value.id] || usedLabels[labelKey]) {
      return false;
    }

    used[value.id] = true;
    usedLabels[labelKey] = true;
    target.push({ value: value, label: cleanLabel });
    return true;
  }

  function quickFilterPriority(filter) {
    var label = String(filter.label || '').toLowerCase();

    if (label.indexOf('type') !== -1 || label.indexOf('category') !== -1) return 0;
    if (label.indexOf('style') !== -1 || label.indexOf('feature') !== -1) return 1;
    if (label.indexOf('brand') !== -1 || label.indexOf('vendor') !== -1) return 2;
    if (label.indexOf('material') !== -1 || label.indexOf('fabric') !== -1) return 3;
    if (label.indexOf('vintage') !== -1 || label.indexOf('condition') !== -1) return 4;
    return 10;
  }

  function isUsefulQuickLabel(label) {
    var clean = String(label || '').trim();
    return clean.length > 1 && clean.length <= 24 && !/^all\b/i.test(clean) &&
      !isPlaceholderFacetValue(clean);
  }

  function renderActiveFilters() {
    var target = document.getElementById('PrewornActiveFilters');
    var count = document.getElementById('PrewornActiveFilterCount');
    if (!target) return;
    renderSearchHeading();

    var keys = Object.keys(state.selectedValues);
    var hasSearchQuery = hasRemovableSearchQuery();
    var hasCollectionQuery = state.mode === 'collection' && state.query &&
      (state.usingCatalog || state.collectionKeywordSearch);
    var activeCount = keys.length + ((state.priceMin || state.priceMax) ? 1 : 0) +
      (hasSearchQuery ? 1 : 0) + (hasCollectionQuery ? 1 : 0) +
      (state.exactColourHex ? 1 : 0);
    var html = '';

    if (hasSearchQuery) {
      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-search-query' +
          ' aria-label="Remove search ' + escapeAttr(state.query) + '">' +
          escapeHtml(state.query) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    if (state.exactColourHex) {
      html +=
        '<button type="button" class="pw-active-pill pw-active-pill--colour" data-pw-remove-exact-colour>' +
          '<i class="pw-active-pill__swatch" style="--pw-pill-colour:' +
            escapeAttr(state.exactColourHex) + '" aria-hidden="true"></i>' +
          escapeHtml(state.exactColourName || exactColourName(state.exactColourHex)) +
          ' ' + escapeHtml(state.exactColourHex) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    if (hasCollectionQuery) {
      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-collection-query>' +
          escapeHtml(state.query) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-filter="' +
          escapeAttr(keys[i]) + '">' +
          escapeHtml(selected.label || 'Filter') + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    if (state.priceMin || state.priceMax) {
      var priceLabel = state.priceMin && state.priceMax
        ? '£' + state.priceMin + '–£' + state.priceMax
        : state.priceMin
          ? 'Over £' + state.priceMin
          : 'Under £' + state.priceMax;

      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-price>' +
          escapeHtml(priceLabel) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    target.innerHTML = html;
    target.hidden = activeCount === 0;
    if (count) {
      count.textContent = String(activeCount);
      count.hidden = activeCount === 0;
    }

    var removeButtons = target.querySelectorAll('[data-pw-remove-filter]');
    for (var r = 0; r < removeButtons.length; r++) {
      removeButtons[r].addEventListener('click', function () {
        var filterId = this.getAttribute('data-pw-remove-filter');
        removeSelectedFilter(filterId);

        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var removeCollectionQuery = target.querySelector('[data-pw-remove-collection-query]');
    if (removeCollectionQuery) {
      removeCollectionQuery.addEventListener('click', function () {
        clearCollectionQuery();
        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var removeSearchQuery = target.querySelector('[data-pw-remove-search-query]');
    if (removeSearchQuery) {
      removeSearchQuery.addEventListener('click', function () {
        clearSearchQuery();
        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var removePrice = target.querySelector('[data-pw-remove-price]');
    if (removePrice) {
      removePrice.addEventListener('click', function () {
        state.priceMin = '';
        state.priceMax = '';
        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }
    var removeExactColour = target.querySelector('[data-pw-remove-exact-colour]');
    if (removeExactColour) removeExactColour.addEventListener('click', clearExactColour);
    syncCollectionStyleDirectorySelection();
    announceCatalogueFilterState();
  }

  function removeSelectedFilter(filterId) {
    var selected = state.selectedValues[filterId] || {};
    var input = normalizeFilterInput(selected.input || {});
    var parameter = String(input.catalogParameter || '').toLowerCase();
    removeInterpretedSearchTerm(selected);
    delete state.selectedValues[filterId];
    if (parameter === 'type' && !selectedCollectionDirectoryValue('type')) {
      state.collectionStyleDirectoryDimension = 'type';
    }
    if (filterId === state.collectionQueryFilterId) clearCollectionQuery();
  }

  function renderFilters(filters) {
    var body = document.getElementById('PrewornSearchFiltersBody');
    if (!body) return;
    filters = visibleCatalogFilters(filters);
    body.setAttribute(
      'aria-busy',
      state.exactColourHex && !state.colourFacetsReady ? 'true' : 'false'
    );

    if (!filters.length) {
      body.innerHTML = state.catalogFacetsDeferred
        ? '<p class="pw-search__muted">Loading filters…</p>'
        : '<p class="pw-search__muted">No filters available.</p>';
      bindFilterEvents();
      return;
    }

    filters = filters.slice().sort(function (a, b) {
      return filterPriority(a) - filterPriority(b);
    });

    var html = '';

    for (var i = 0; i < filters.length; i++) {
      var filter = filters[i];

      if (filter.type === 'PRICE_RANGE') {
        html += renderPriceFilter(filter);
        continue;
      }

      if (filter.type !== 'LIST' && filter.type !== 'BOOLEAN') {
        continue;
      }

      html += renderListFilter(filter);
    }

    body.innerHTML = html;
    bindFilterEvents();
  }

  function filterGroupKey(filter) {
    return String(filter.id || filter.label || 'filter')
      .toLowerCase()
      .replace(/[^a-z0-9:_-]+/g, '-');
  }

  function filterGroupIsOpen(filter, defaultOpen) {
    var key = filterGroupKey(filter);
    if (Object.prototype.hasOwnProperty.call(state.openFilterGroups, key)) {
      return state.openFilterGroups[key];
    }

    var values = filter.values || [];
    for (var i = 0; i < values.length; i++) {
      if (state.selectedValues[values[i].id]) return true;
    }

    return !!defaultOpen;
  }

  function filterPriority(filter) {
    var label = String(filter.label || '').toLowerCase();

    if (label.indexOf('department') !== -1) return 0;
    if (label === 'type' || label.indexOf('product type') !== -1) return 1;
    if (label.indexOf('style') !== -1) return 2;
    if (label.indexOf('size') !== -1) return 3;
    if (label.indexOf('brand') !== -1 || label.indexOf('vendor') !== -1) return 4;
    if (label.indexOf('colour') !== -1 || label.indexOf('color') !== -1) return 5;
    if (label.indexOf('category') !== -1) return 6;
    if (label.indexOf('material') !== -1) return 7;
    if (label.indexOf('pattern') !== -1) return 8;
    if (label.indexOf('condition') !== -1) return 98;
    if (filter.type === 'PRICE_RANGE' || label.indexOf('price') !== -1) return 99;
    return 50;
  }

  function renderListFilter(filter) {
    var labelLower = String(filter.label || '').toLowerCase();
    var isDepartment = labelLower.indexOf('department') !== -1;
    var isSize = labelLower === 'size' || labelLower.indexOf('size') !== -1;
    var isProminent = filterPriority(filter) <= 4;
    var isColour =
      labelLower === 'colour' ||
      labelLower === 'color' ||
      labelLower.indexOf('colour') !== -1 ||
      labelLower.indexOf('color') !== -1;

    var values = (filter.values || []).slice();
    var groupKey = filterGroupKey(filter);

    // Both the catalogue API and Shopify fallback can supply these values.
    // Enforce popularity order in the browser so fallback responses cannot
    // silently change Brand, Style and other lists back to alphabetical.
    if (!isDepartment && !isSize) {
      values.sort(compareFacetValueCounts);
    }

    if (isDepartment) {
      var departmentOrder = {
        men: 0,
        women: 1,
        boys: 2,
        girls: 3,
        baby: 4
      };

      values = values.map(function (value) {
        return Object.assign({}, value, {
          label: departmentDisplayLabel(value.label)
        });
      }).sort(function (a, b) {
        var aKey = departmentKey(a.label);
        var bKey = departmentKey(b.label);
        var aOrder = Object.prototype.hasOwnProperty.call(departmentOrder, aKey)
          ? departmentOrder[aKey] : 99;
        var bOrder = Object.prototype.hasOwnProperty.call(departmentOrder, bKey)
          ? departmentOrder[bKey] : 99;
        return aOrder - bOrder;
      });
    }

    if (isSize) {
      values = values.map(function (value) {
        return Object.assign({}, value, {
          label: sizeDisplayLabel(value.label)
        });
      }).sort(compareSizeValues);
    }

    var html =
      '<details class="pw-filter" data-pw-filter-group="' +
        escapeAttr(groupKey) + '" ' +
        (filterGroupIsOpen(filter, isProminent) ? 'open' : '') + '>' +
        '<summary>' +
          '<span>' + escapeHtml(filter.label) + '</span>' +
          '<span class="pw-filter__chevron">⌄</span>' +
        '</summary>' +
        '<div class="pw-filter__body">';

    if (isSize) {
      html += '<div class="pw-filter__sizes">';

      for (var i = 0; i < values.length; i++) {
        html += renderSizeValue(values[i]);
      }

      html += '</div>';

    } else if (isColour) {
      html += '<div class="pw-filter__colours">';

      for (var c = 0; c < values.length; c++) {
        html += renderColourValue(values[c]);
      }

      html += '</div>';

    } else {
      html += '<div class="pw-filter__list">';

      var visibleLimit = state.expandedFilterGroups[groupKey]
        ? values.length
        : 20;

      for (var j = 0; j < values.length; j++) {
        var hidden = j >= visibleLimit ? ' pw-filter-value--extra' : '';
        html += renderGenericValue(values[j], hidden);
      }

      if (values.length > visibleLimit) {
        html +=
          '<button type="button" class="pw-filter__more" data-pw-show-more data-pw-filter-group-key="' +
            escapeAttr(groupKey) + '">' +
            'Show more (' + (values.length - visibleLimit) + ')' +
          '</button>';
      }

      html += '</div>';
    }

    html += '</div></details>';

    return html;
  }

  function compareFacetValueCounts(a, b) {
    var countDifference = Number(b.count || 0) - Number(a.count || 0);
    if (countDifference !== 0) return countDifference;

    return String(a.label || '').localeCompare(
      String(b.label || ''),
      'en-GB',
      { sensitivity: 'base' }
    );
  }

  function departmentKey(label) {
    var clean = String(label || '')
      .toLowerCase()
      .replace(/^department[\s:_-]*/i, '')
      .replace(/[^a-z]/g, '');

    if (clean === 'men' || clean === 'mens' || clean === 'male') return 'men';
    if (clean === 'women' || clean === 'womens' || clean === 'female') return 'women';
    if (clean === 'boys' || clean === 'boy') return 'boys';
    if (clean === 'girls' || clean === 'girl') return 'girls';
    if (clean.indexOf('baby') === 0 || clean === 'infant') return 'baby';
    return 'other';
  }

  function departmentDisplayLabel(label) {
    var key = departmentKey(label);
    var displayLabels = {
      men: 'Men',
      women: 'Women',
      boys: 'Boys',
      girls: 'Girls',
      baby: 'Baby'
    };
    return displayLabels[key] || String(label || '')
      .replace(/^department[\s:_-]*/i, '')
      .trim();
  }

  function sizeDisplayLabel(label) {
    var clean = String(label || '')
      .replace(/^(?:uk\s+shoe\s+size|shoe\s+size|clothing\s+size|garment\s+size|size)[\s:_-]*/i, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var compact = clean.toLowerCase().replace(/[^a-z0-9]+/g, '');
    var named = {
      xxsmall: 'XXS',
      extrasmall: 'XS',
      xsmall: 'XS',
      small: 'S',
      medium: 'M',
      large: 'L',
      xlarge: 'XL',
      extralarge: 'XL',
      xxlarge: '2XL',
      xxxlarge: '3XL',
      onesize: 'One Size'
    };

    if (named[compact]) return named[compact];
    if (/^(xxs|xs|s|m|l|xl|xxl|xxxl|[1-9]xl)$/i.test(clean)) {
      var upper = clean.toUpperCase();
      if (upper === 'XXL') return '2XL';
      if (upper === 'XXXL') return '3XL';
      return upper;
    }

    var inches = clean.match(/^([0-9]+(?:\.[0-9]+)?)\s*(?:in|inch|inches|")$/i);
    if (inches) return inches[1] + ' in';
    var uk = clean.match(/^uk\s*([0-9]+(?:\.[05])?)$/i);
    if (uk) return 'UK ' + uk[1];
    return clean;
  }

  function currentSizeContextIsFootwear() {
    var terms = [
      state.collectionHandle,
      state.collectionTitle,
      state.tagLabel,
      state.tagQuery,
      state.query
    ];
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = normalizeFilterInput(selected.input || {});
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (parameter === 'type' || parameter === 'category' || parameter === 'style') {
        terms.push(input.catalogValue || selected.label);
      }
    });
    return terms.some(function (term) {
      return /(?:^|[\s_-])(?:shoe|shoes|trainer|trainers|footwear|sandal|sandals|heel|heels|boot|boots)(?:$|[\s_-])/i
        .test(String(term || ''));
    });
  }

  function mappedMensClothingSize(label) {
    var raw = String(label || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    var display = sizeDisplayLabel(raw);
    if (/^uk/i.test(display) || /\byears?\b/i.test(raw)) return false;
    if (/^(?:xxs|xs|s|m|l|xl|[2-9]xl|one size)$/i.test(display)) return true;
    if (/\d/.test(display) && !/inch/i.test(raw)) return false;
    return true;
  }

  function mappedWomensClothingSize(label) {
    var raw = String(label || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    var display = sizeDisplayLabel(raw);
    if (/\byears?\b/i.test(raw)) return false;
    if (/^(?:xxs|xs|s|m|l|xl|[2-9]xl|one size)$/i.test(display)) return true;
    if (/\d/.test(display)) return /^uk\s*[0-9]/i.test(display);
    return true;
  }

  function sizeFacetValueIsVisible(value, audience) {
    var contextAudience = String(audience || '').toLowerCase();
    if (!contextAudience) {
      contextAudience = {
        men: 'man', women: 'woman', boys: 'boy', girls: 'girl'
      }[quickDepartmentContext().key] || '';
    }
    var input = normalizeFilterInput(value && value.input || {});
    var label = input.catalogValue || (value && value.label);
    if (contextAudience === 'woman') return mappedWomensClothingSize(label);
    if (contextAudience === 'man' && !currentSizeContextIsFootwear()) {
      return mappedMensClothingSize(label);
    }
    return true;
  }

  function compareSizeValues(a, b) {
    var aKey = sizeSortKey(a.label);
    var bKey = sizeSortKey(b.label);
    return aKey < bKey ? -1 : (aKey > bKey ? 1 : 0);
  }

  function sizeSortKey(label) {
    var value = sizeDisplayLabel(label);
    var upper = value.toUpperCase();
    var standard = {
      XXS: 0,
      XS: 1,
      S: 2,
      M: 3,
      L: 4,
      XL: 5,
      '2XL': 6,
      '3XL': 7,
      '4XL': 8,
      '5XL': 9,
      '6XL': 10,
      'ONE SIZE': 20
    };

    if (Object.prototype.hasOwnProperty.call(standard, upper)) {
      return '0:' + String(standard[upper]).padStart(3, '0');
    }

    var plainNumber = value.match(/^([0-9]+(?:\.[0-9]+)?)(?:\s*\/\s*([0-9]+))?$/);
    if (plainNumber) {
      return '1:' + String(parseFloat(plainNumber[1]) * 10).padStart(5, '0') + ':' + value;
    }

    var uk = value.match(/^UK\s*([0-9]+(?:\.[05])?)$/i);
    if (uk) {
      return '2:' + String(parseFloat(uk[1]) * 10).padStart(5, '0');
    }

    var inches = value.match(/^([0-9]+(?:\.[0-9]+)?)\s*in$/i);
    if (inches) {
      return '3:' + String(parseFloat(inches[1]) * 10).padStart(5, '0');
    }

    return '9:' + upper;
  }

  function renderSizeValue(value) {
    var active = !!state.selectedValues[value.id];
    var colourCountsPending = state.exactColourHex && !state.colourFacetsReady;
    var title = active
      ? 'Remove ' + value.label + ' filter'
      : (colourCountsPending ? value.label : value.label + ' (' + value.count + ')');

    return (
      '<button type="button"' +
        ' class="pw-size' + (active ? ' is-active' : '') +
          (value.count === 0 && !active ? ' is-disabled' : '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        ' aria-pressed="' + (active ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeAttr((active ? 'Remove ' : 'Add ') + value.label + ' filter') + '"' +
        (value.count === 0 && !active ? ' disabled' : '') +
        ' title="' + escapeAttr(title) + '">' +
        '<span class="pw-size__label">' + escapeHtml(value.label) + '</span>' +
        (active ? '<span class="pw-size__remove" aria-hidden="true">×</span>' : '') +
      '</button>'
    );
  }

  function renderColourValue(value) {
    var active = !!state.selectedValues[value.id];
    var swatch = getSwatch(value);
    var colourCountsPending = state.exactColourHex && !state.colourFacetsReady;
    var title = active
      ? 'Remove ' + value.label + ' filter'
      : (colourCountsPending ? value.label : value.label + ' (' + value.count + ')');

    return (
      '<button type="button"' +
        ' class="pw-colour' + (active ? ' is-active' : '') +
          (value.count === 0 && !active ? ' is-disabled' : '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        ' aria-pressed="' + (active ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeAttr((active ? 'Remove ' : 'Add ') + value.label + ' filter') + '"' +
        (value.count === 0 && !active ? ' disabled' : '') +
        ' title="' + escapeAttr(title) + '">' +
        '<span class="pw-colour__dot" style="' + escapeAttr(swatch) + '"></span>' +
        (active ? '<span class="pw-colour__remove" aria-hidden="true">×</span>' : '') +
        '<span class="pw-visually-hidden">' + escapeHtml(value.label) + '</span>' +
      '</button>'
    );
  }

  function renderGenericValue(value, extraClass) {
    var active = !!state.selectedValues[value.id];
    var disabled = value.count === 0 && !active;
    var colourCountsPending = state.exactColourHex && !state.colourFacetsReady;

    return (
      '<button type="button"' +
        ' class="pw-filter-value' + (active ? ' is-active' : '') +
          (disabled ? ' is-disabled' : '') + (extraClass || '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        ' aria-pressed="' + (active ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeAttr((active ? 'Remove ' : 'Add ') + value.label + ' filter') + '"' +
        (disabled ? ' disabled' : '') + '>' +
        '<span class="pw-filter-value__box"></span>' +
        '<span class="pw-filter-value__label">' + escapeHtml(value.label) + '</span>' +
        (active
          ? '<span class="pw-filter-value__remove">Remove <i aria-hidden="true">×</i></span>'
          : (colourCountsPending ? '' : '<span class="pw-filter-value__count">' +
            Number(value.count || 0).toLocaleString('en-GB') + '</span>')) +
      '</button>'
    );
  }

  function renderPriceFilter(filter) {
    var groupKey = filterGroupKey(filter);
    return (
      '<details class="pw-filter" data-pw-filter-group="' +
        escapeAttr(groupKey) + '" ' +
        (filterGroupIsOpen(filter, false) ? 'open' : '') + '>' +
        '<summary>' +
          '<span>' + escapeHtml(filter.label || 'Price') + '</span>' +
          '<span class="pw-filter__chevron">⌄</span>' +
        '</summary>' +
        '<div class="pw-filter__body">' +
          '<div class="pw-price">' +
            '<label>From £<input id="PrewornPriceMin" type="number" min="0" step="0.01" value="' +
              escapeAttr(state.priceMin) + '"></label>' +
            '<label>To £<input id="PrewornPriceMax" type="number" min="0" step="0.01" value="' +
              escapeAttr(state.priceMax) + '"></label>' +
            '<button type="button" class="pw-price__apply" data-pw-price-apply>Apply</button>' +
          '</div>' +
        '</div>' +
      '</details>'
    );
  }

  function bindFilterEvents() {
    bindMobileFilterLayerEvents();
    buildMobileFilterLayers();
    var filterGroups = app.querySelectorAll('[data-pw-filter-group]');
    for (var g = 0; g < filterGroups.length; g++) {
      filterGroups[g].addEventListener('toggle', function () {
        var key = this.getAttribute('data-pw-filter-group');
        if (key) state.openFilterGroups[key] = this.open;
      });
    }

    if (state.mobileActiveFilterGroup && isMobileCatalogueViewport()) {
      for (var ag = 0; ag < filterGroups.length; ag++) {
        if (filterGroups[ag].getAttribute('data-pw-filter-group') === state.mobileActiveFilterGroup) {
          openMobileFilterLayer(filterGroups[ag]);
          break;
        }
      }
    }

    var values = app.querySelectorAll('[data-pw-filter-id]');

    for (var i = 0; i < values.length; i++) {
      values[i].addEventListener('click', function () {
        var id = this.getAttribute('data-pw-filter-id');
        var raw = this.getAttribute('data-pw-filter-input');
        var label = this.getAttribute('data-pw-filter-label');
        var parsedInput;

        try {
          parsedInput = JSON.parse(raw);
        } catch (e) {
          console.error('[Preworn Browse] Invalid filter input', e, raw);
          return;
        }

        if (isPlaceholderFacetValue({ label: label, input: parsedInput })) return;

        var isNominalColour = String(parsedInput.catalogParameter || '').toLowerCase() === 'colour';
        if (isNominalColour) {
          var removedExactColour = !!state.exactColourHex;
          state.shadePickerHex = state.exactColourHex || state.shadePickerHex || '#5278A7';
          state.shadePickerOpen = true;
          state.exactColourHex = '';
          state.exactColourName = '';
        }

        if (state.selectedValues[id]) {
          // Use the same complete removal path as the active pill. Collection
          // pages can store their inferred Type/Style as both a selected value
          // and a query constraint; clearing only the value makes it reappear.
          removeSelectedFilter(id);
        } else {
          state.selectedValues[id] = {
            input: parsedInput,
            label: label
          };
        }

        if (isNominalColour) {
          if (state.selectedValues[id]) {
            state.shadePickerHex = nominalColourPickerHex(label) || state.shadePickerHex;
          }
          renderExactColourControl(true);
          syncSortControl();
        }

        if (String(parsedInput.catalogParameter || '').toLowerCase() === 'department') {
          syncApplicableWardrobeForCurrentScope();
        }

        if (removedExactColour) resetWardrobeFacetState();
        else refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var priceButton = app.querySelector('[data-pw-price-apply]');
    if (priceButton) {
      priceButton.addEventListener('click', function () {
        var min = document.getElementById('PrewornPriceMin');
        var max = document.getElementById('PrewornPriceMax');

        state.priceMin = min ? min.value.trim() : '';
        state.priceMax = max ? max.value.trim() : '';

        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var quickPrices = app.querySelectorAll('[data-pw-price-max]');
    for (var q = 0; q < quickPrices.length; q++) {
      quickPrices[q].addEventListener('click', function () {
        var maximum = this.getAttribute('data-pw-price-max') || '';
        state.priceMin = '';
        state.priceMax = state.priceMax === maximum ? '' : maximum;
        refreshCatalogFacetsForCurrentScope();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var moreButtons = app.querySelectorAll('[data-pw-show-more]');

    for (var m = 0; m < moreButtons.length; m++) {
      moreButtons[m].addEventListener('click', function () {
        var groupKey = this.getAttribute('data-pw-filter-group-key');
        if (groupKey) state.expandedFilterGroups[groupKey] = true;
        var parent = this.parentNode;
        var extras = parent.querySelectorAll('.pw-filter-value--extra');

        for (var x = 0; x < extras.length; x++) {
          extras[x].classList.remove('pw-filter-value--extra');
        }

        this.remove();
      });
    }
  }

  function paginationSequence(currentPage, totalPages) {
    var pages = [];
    var start;
    var end;
    var page;

    if (totalPages <= 7) {
      for (page = 1; page <= totalPages; page++) pages.push(page);
      return pages;
    }

    start = Math.max(2, currentPage - 2);
    end = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 4) {
      start = 2;
      end = 5;
    } else if (currentPage >= totalPages - 3) {
      start = totalPages - 4;
      end = totalPages - 1;
    }

    pages.push(1);
    if (start > 2) pages.push('ellipsis-before');
    for (page = start; page <= end; page++) pages.push(page);
    if (end < totalPages - 1) pages.push('ellipsis-after');
    pages.push(totalPages);

    return pages;
  }

  function renderPagination(result) {
    var nav = document.getElementById('PrewornSearchPagination');
    if (!nav) return;
    nav.hidden = false;

    var hasPrevious = state.currentPage > 1;
    var hasNext = !!(result.pageInfo && result.pageInfo.hasNextPage);
    var hasKnownTotal = true;
    var totalPages;
    var status;

    if (result.catalogSource) {
      var catalogTotalKnown = result.totalCount !== null &&
        typeof result.totalCount !== 'undefined' &&
        isFinite(Number(result.totalCount));
      if (catalogTotalKnown) {
        totalPages = Math.max(
          1,
          Number(result.totalPages || 0) ||
            Math.ceil(Number(result.totalCount) / CONFIG.pageSize)
        );

        hasNext = hasNext && state.currentPage < totalPages;
        status = 'Page <strong>' + state.currentPage +
          '</strong> of <strong>' + totalPages.toLocaleString('en-GB') +
          '</strong>';
      } else {
        hasKnownTotal = false;
        totalPages = state.currentPage + (hasNext ? 1 : 0);
        status = 'Page <strong>' + state.currentPage + '</strong>';
      }
    } else if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      hasKnownTotal = false;
      totalPages = state.currentPage + (hasNext ? 1 : 0);
      status = 'Page <strong>' + state.currentPage + '</strong>';
    } else {
      var total = Math.min(
        Number(result.totalCount || 0),
        CONFIG.shopifyFallbackMaxResults
      );
      totalPages = Math.max(1, Math.ceil(total / CONFIG.pageSize));

      hasNext = hasNext && state.currentPage < totalPages;
      status = 'Page <strong>' + state.currentPage +
        '</strong> of <strong>' + totalPages + '</strong>';
    }

    var numbers = hasKnownTotal
      ? paginationSequence(state.currentPage, totalPages)
      : [state.currentPage];
    var numberMarkup = '';

    for (var i = 0; i < numbers.length; i++) {
      var pageNumber = numbers[i];

      if (typeof pageNumber !== 'number') {
        numberMarkup +=
          '<span class="pw-page-ellipsis" aria-hidden="true">•••</span>';
        continue;
      }

      var isCurrent = pageNumber === state.currentPage;
      var isNear = Math.abs(pageNumber - state.currentPage) <= 1;
      var canNavigate = result.catalogSource || pageNumber === 1 ||
        Object.prototype.hasOwnProperty.call(state.pageCursors, pageNumber);
      var pageClass = 'pw-page-number' +
        (isCurrent ? ' is-current' : '') +
        (isNear ? ' pw-page-number--near' : '');

      if (isCurrent) {
        numberMarkup += '<span class="' + pageClass +
          '" aria-current="page"><span class="pw-visually-hidden">Page </span>' +
          pageNumber + '</span>';
      } else if (canNavigate) {
        numberMarkup += '<button type="button" class="' + pageClass +
          '" data-pw-page="' + pageNumber + '" aria-label="Go to page ' +
          pageNumber + '">' + pageNumber + '</button>';
      } else {
        numberMarkup += '<span class="' + pageClass +
          ' is-disabled" aria-disabled="true">' + pageNumber + '</span>';
      }
    }

    nav.innerHTML =
      '<div class="pw-page-shell">' +
        '<span class="pw-page-kicker">Browse results</span>' +
        '<div class="pw-page-controls">' +
          '<button type="button" class="pw-page-button pw-page-button--previous" data-pw-prev ' +
            (hasPrevious ? '' : 'disabled') +
            '><span aria-hidden="true">←</span><span class="pw-page-button__label">Previous</span></button>' +
          '<div class="pw-page-numbers" aria-label="Choose a results page">' +
            numberMarkup +
          '</div>' +
          '<button type="button" class="pw-page-button pw-page-button--next" data-pw-next ' +
            (hasNext ? '' : 'disabled') +
            '><span class="pw-page-button__label">Next</span><span aria-hidden="true">→</span></button>' +
        '</div>' +
        '<span class="pw-page-status" aria-live="polite">' + status + '</span>' +
      '</div>';

    var prev = nav.querySelector('[data-pw-prev]');
    var next = nav.querySelector('[data-pw-next]');

    if (prev && hasPrevious) {
      prev.addEventListener('click', function () {
        var targetPage = state.currentPage - 1;
        var cursor = state.pageCursors[targetPage] || null;
        navigateWithHistory(targetPage, cursor);
      });
    }

    if (next && hasNext) {
      next.addEventListener('click', function () {
        var targetPage = state.currentPage + 1;
        var cursor = state.pageCursors[targetPage];

        if (!cursor && result.pageInfo) {
          cursor = result.pageInfo.endCursor;
          state.pageCursors[targetPage] = cursor;
        }

        navigateWithHistory(targetPage, cursor);
      });
    }

    var pageButtons = nav.querySelectorAll('[data-pw-page]');
    for (var p = 0; p < pageButtons.length; p++) {
      pageButtons[p].addEventListener('click', function () {
        var targetPage = Number(this.getAttribute('data-pw-page'));
        if (!targetPage || targetPage === state.currentPage) return;
        navigateWithHistory(targetPage, state.pageCursors[targetPage] || null);
      });
    }
  }

  function normalizeFilterInput(input) {
    if (input === null || typeof input === 'undefined') {
      return {};
    }

    if (typeof input === 'string') {
      try {
        return JSON.parse(input);
      } catch (e) {
        return {};
      }
    }

    return input;
  }

  function isPlaceholderFacetValue(value) {
    var objectValue = value && typeof value === 'object' ? value : {};
    var input = normalizeFilterInput(objectValue.input || {});
    var candidates = [
      typeof value === 'string' ? value : '',
      objectValue.label,
      objectValue.value,
      input.catalogValue,
      input.productType
    ];

    for (var i = 0; i < candidates.length; i++) {
      var key = String(candidates[i] || '').trim().toLowerCase()
        .replace(/[\s_-]+/g, ' ');
      if (key === 'uncategorized' || key === 'uncategorised') return true;
    }
    return false;
  }

  function visibleCatalogFilters(filters) {
    if (!Array.isArray(filters)) return [];

    return filters.map(function (filter) {
      var copy = {};
      Object.keys(filter || {}).forEach(function (key) {
        copy[key] = filter[key];
      });
      var label = String(filter && (filter.label || filter.id) || '').toLowerCase();
      var sourceValues = filter && Array.isArray(filter.values) ? filter.values : [];
      var firstInput = sourceValues.length
        ? normalizeFilterInput(sourceValues[0].input || {}) : {};
      var isSize = label === 'size' || label.indexOf('size') !== -1 ||
        String(firstInput.catalogParameter || '').toLowerCase() === 'size';
      copy.values = (filter && Array.isArray(filter.values) ? filter.values : [])
        .filter(function (value) {
          return !isPlaceholderFacetValue(value) &&
            (!isSize || sizeFacetValueIsVisible(value));
        });
      return copy;
    }).filter(function (filter) {
      return filter.type === 'PRICE_RANGE' || filter.values.length > 0;
    });
  }

  function getSwatch(value) {
    if (value.swatch) {
      if (value.swatch.color) {
        return 'background:' + value.swatch.color + ';';
      }

      if (value.swatch.image &&
          value.swatch.image.image &&
          value.swatch.image.image.url) {
        return 'background-image:url("' +
          cssUrl(value.swatch.image.image.url) +
          '");background-size:cover;background-position:center;';
      }
    }

    return 'background:' + fallbackColour(value.label) + ';';
  }

  function fallbackColour(label) {
    var name = String(label || '').toLowerCase().trim();

    var map = {
      black: '#111111',
      white: '#ffffff',
      grey: '#999999',
      gray: '#999999',
      blue: '#2455d6',
      navy: '#182644',
      red: '#d92f2f',
      pink: '#ef9db8',
      green: '#3b8b42',
      purple: '#7b4b9e',
      yellow: '#f2df31',
      orange: '#e98b2a',
      brown: '#86563b',
      beige: '#dfcfac',
      cream: '#f1ead8',
      gold: '#c7a441',
      silver: '#c8c8c8',
      khaki: '#8c8a59',
      burgundy: '#6d1f2b',
      maroon: '#6d1f2b',
      turquoise: '#39a7a7',
      teal: '#287d7a'
    };

    if (map[name]) return map[name];

    if (
      name.indexOf('multi') !== -1 ||
      name.indexOf('mixed') !== -1
    ) {
      return 'conic-gradient(#e53935,#fb8c00,#fdd835,#43a047,#1e88e5,#8e24aa,#e53935)';
    }

    return '#d9d9d9';
  }

  function hasActiveFilters() {
    return !!state.exactColourHex ||
      Object.keys(state.selectedValues).length > 0 ||
      state.priceMin !== '' ||
      state.priceMax !== '' ||
      wardrobeScopeActive();
  }

  function hideNativeBrowse() {
    var nativeGrid = document.getElementById('ProductGridContainer');

    if (nativeGrid && nativeGrid.parentElement) {
      nativeGrid.parentElement.style.display = 'none';
    }

    var nativeSort = document.querySelector('.facets-vertical-sort');
    if (nativeSort) nativeSort.style.display = 'none';

    var nativeSearchFilters = document.getElementById('main-search-filters');
    if (nativeSearchFilters) nativeSearchFilters.style.display = 'none';

    var nativeCollectionFilters = document.getElementById('main-collection-filters');
    if (nativeCollectionFilters) nativeCollectionFilters.style.display = 'none';

    app.style.display = 'block';
  }

  function showNativeFallback() {
    var bootStyles = document.getElementById('PrewornWideSearchBootStyles');
    if (bootStyles) bootStyles.remove();

    var nativeGrid = document.getElementById('ProductGridContainer');
    if (nativeGrid) nativeGrid.style.display = '';

    if (nativeGrid && nativeGrid.parentElement) {
      nativeGrid.parentElement.style.display = '';
    }

    var nativeSort = document.querySelector('.facets-vertical-sort');
    if (nativeSort) nativeSort.style.display = '';

    var nativeSearchFilters = document.getElementById('main-search-filters');
    if (nativeSearchFilters) nativeSearchFilters.style.display = '';

    var nativeCollectionFilters = document.getElementById('main-collection-filters');
    if (nativeCollectionFilters) nativeCollectionFilters.style.display = '';
  }

  function setLoading(show) {
    var loading = document.getElementById('PrewornSearchLoading');

    if (loading) {
      loading.hidden = !show;
    }

    if (app) {
      app.classList.toggle('is-loading', !!show);
      app.setAttribute('aria-busy', show ? 'true' : 'false');
    }
  }

  function showError(message, allowRetry) {
    var error = document.getElementById('PrewornSearchError');
    if (!error) return;

    error.textContent = '';
    var copy = document.createElement('span');
    copy.textContent = message;
    error.appendChild(copy);
    if (allowRetry) {
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'pw-search__retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', function () {
        clearError();
        loadPage(state.currentPage || 1, null);
      });
      error.appendChild(retry);
    }
    error.hidden = false;
  }

  function hideStaleExactColourResults() {
    var grid = document.getElementById('PrewornSearchGrid');
    var pagination = document.getElementById('PrewornSearchPagination');
    var count = document.getElementById('PrewornSearchCount');
    var toolbarCount = document.getElementById('PrewornToolbarCount');
    if (grid) grid.hidden = true;
    if (pagination) {
      pagination.innerHTML = '';
      pagination.hidden = true;
    }
    if (count) count.textContent = 'Colour matches temporarily unavailable';
    if (toolbarCount) renderToolbarPrimary(toolbarCount, 'Colour matches');
  }

  function clearError() {
    var error = document.getElementById('PrewornSearchError');
    if (!error) return;

    error.textContent = '';
    error.hidden = true;
  }

  function renderFatal(message) {
    app.innerHTML =
      '<div style="padding:16px;border:1px solid #c00;color:#900;background:#fff;">' +
        escapeHtml(message) +
      '</div>';
  }

  function scrollResultsIntoView(pageNumber) {
    if (pageNumber <= 1) return;

    var rect = app.getBoundingClientRect();
    var target = window.pageYOffset + rect.top - 20;

    window.scrollTo({
      top: Math.max(0, target),
      behavior: 'smooth'
    });
  }

  function money(amount, currency) {
    var value = Number(amount);

    if (isNaN(value)) return '';

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(value);
    } catch (e) {
      return '£' + value.toFixed(2);
    }
  }

  function escapeHtml(value) {
    return String(value === null || typeof value === 'undefined' ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function cssUrl(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function injectStyles() {
    if (document.getElementById('PrewornSearchStyles')) return;

    var style = document.createElement('style');
    style.id = 'PrewornSearchStyles';

style.textContent = [
'#PrewornWideSearch{display:block;width:100%;max-width:none!important;min-height:100vh;background:#fff;}',

  '.pw-collection-styles[hidden]{display:none!important;}',

  '.pw-collection-styles{width:100%;padding:clamp(10px,.9vw,14px) clamp(14px,2.8vw,46px) clamp(11px,1vw,15px);color:#101f1a;background:#fbfbfa;border-top:1px solid rgba(10,31,24,.1);border-bottom:1px solid rgba(10,31,24,.1);box-sizing:border-box;}',

  '.pw-collection-styles__shell{position:relative;}',

  '.pw-collection-styles__track{display:grid;grid-auto-flow:column;grid-auto-columns:clamp(140px,9.6vw,174px);gap:10px;overflow-x:auto;padding:0 1px 3px;scroll-behavior:smooth;scroll-snap-type:x proximity;scrollbar-width:none;cursor:grab;}',

  '.pw-collection-styles__track.is-dragging{cursor:grabbing;scroll-behavior:auto;scroll-snap-type:none;user-select:none;}',

  '.pw-collection-styles__track::-webkit-scrollbar{display:none;}',

  '.pw-collection-styles__card{appearance:none;position:relative;display:flex;min-width:0;min-height:0;padding:0 0 12px;overflow:hidden;align-items:center;flex-direction:column;scroll-snap-align:start;border:1px solid #e0e3df;border-radius:7px;background:#fff;color:#11231d;cursor:pointer;font:inherit;text-align:center;box-sizing:border-box;transition:border-color .16s ease,background-color .16s ease,box-shadow .16s ease,transform .16s ease;}',

  '.pw-collection-styles__card:hover,.pw-collection-styles__card:focus-visible{border-color:#98c330;box-shadow:0 7px 17px rgba(10,31,24,.1);outline:0;transform:translateY(-2px);}',

  '.pw-collection-styles__card.is-active{border-color:#8fbd24;box-shadow:inset 0 0 0 1px #baff2a,0 7px 18px rgba(32,73,44,.13);}',

  '.pw-collection-styles__card.is-active:not(.is-all){border-width:2px;background:linear-gradient(145deg,#fff 42%,#f3ffd9 100%);}',

  '.pw-collection-styles__card.is-active:not(.is-all)::after{content:"✓";position:absolute;top:6px;right:6px;z-index:2;display:grid;width:25px;height:25px;padding:0;place-items:center;border:1px solid rgba(10,31,24,.1);border-radius:50%;background:#baff2a;color:#10251e;box-shadow:0 2px 7px rgba(10,31,24,.16);font-size:14px;font-weight:950;line-height:1;}',

  '.pw-collection-styles__card.is-active:not(.is-all) .pw-collection-styles__media{background:transparent;}',

  '.pw-collection-styles__media{position:relative;display:flex;width:100%;height:clamp(110px,7.5vw,136px);margin-bottom:9px;overflow:hidden;align-items:center;justify-content:center;background:#fff;}',

  '.pw-collection-styles__image{display:block;width:auto;height:calc(100% - 8px);max-width:92%;object-fit:contain;object-position:center;transition:transform .2s ease;}',

  '.pw-collection-styles__all-tab{display:flex;width:100%;height:100%;align-items:center;justify-content:center;flex-direction:column;gap:11px;color:#101716;background:#fff;font-size:clamp(18px,1.3vw,24px);font-weight:950;letter-spacing:-.045em;}',

  '.pw-collection-styles__all-tab::before{content:"";display:block;width:10px;height:10px;margin:0 13px 13px 0;border-radius:2px;background:#c4c9c6;box-shadow:13px 0 #c4c9c6,0 13px #c4c9c6,13px 13px #c4c9c6;}',

  '.pw-collection-styles__card.is-back{align-self:stretch;justify-content:center;padding:18px 14px;background:#173f34;color:#fff;border-color:#173f34;}',

  '.pw-collection-styles__card.is-back:hover,.pw-collection-styles__card.is-back:focus-visible{background:#214f42;border-color:#baff2a;}',

  '.pw-collection-styles__card.is-back strong{width:auto;white-space:normal;text-transform:uppercase;line-height:1.15;}',

  '.pw-collection-styles__card.is-back small{max-width:100%;overflow:hidden;color:#d8e6df;text-overflow:ellipsis;white-space:nowrap;}',

  '.pw-collection-styles__back-arrow{display:grid;width:42px;height:42px;margin-bottom:13px;place-items:center;border:1px solid rgba(255,255,255,.68);border-radius:50%;font-size:22px;line-height:1;}',

  '.pw-collection-styles__card:hover .pw-collection-styles__image{transform:scale(1.035);}',

  '.pw-collection-styles__placeholder{display:flex;width:100%;height:100%;align-items:center;justify-content:center;color:#557067;background:linear-gradient(145deg,#edf2ee,#dfe7e1);font-size:25px;font-weight:950;letter-spacing:-.05em;}',

  '.pw-collection-styles__card strong{display:block;width:calc(100% - 14px);overflow:hidden;font-size:clamp(12px,.82vw,14px);font-weight:900;line-height:1.08;text-overflow:ellipsis;white-space:nowrap;}',

  '.pw-collection-styles__card small{display:block;min-height:1.1em;margin-top:5px;color:#68716d;font-size:clamp(9px,.58vw,10px);font-weight:650;line-height:1.1;}',

  '.pw-collection-styles.is-counts-pending [data-pw-directory-count]{visibility:hidden;}',

  '.pw-collection-styles__card small{transition:color .18s ease,opacity .18s ease;}',

  '.pw-collection-styles__arrow{appearance:none;position:absolute;top:50%;z-index:3;display:flex;width:36px;height:36px;align-items:center;justify-content:center;padding:0;transform:translateY(-50%);border:1px solid #d8ddd9;border-radius:50%;background:rgba(255,255,255,.97);color:#19342b;box-shadow:0 4px 14px rgba(10,31,24,.12);cursor:pointer;font:inherit;font-size:15px;}',

  '.pw-collection-styles__arrow--previous{left:-22px;}',

  '.pw-collection-styles__arrow--next{right:-22px;}',

  '.pw-search{display:grid;grid-template-columns:270px minmax(0,1fr);gap:26px;width:100%;padding:0 28px 36px;box-sizing:border-box;background:#fff;}',

  '.pw-search__filters{position:sticky;top:130px;align-self:start;max-height:calc(100vh - 150px);overflow:auto;scrollbar-width:thin;border-right:1px solid #e5e5e5;padding-right:24px;min-width:0;}',

  '.pw-search__results{min-width:0;padding-top:30px;}',

  '.pw-search__filter-head{display:flex;align-items:center;justify-content:space-between;padding:6px 0 14px;border-bottom:1px solid #e5e5e5;}',

  '.pw-search__filter-head strong{letter-spacing:.01em;font-size:15px;color:#14231e;}',

  '.pw-search__filter-actions{display:flex;align-items:center;gap:12px;}',

  '.pw-search__clear{appearance:none;border:0;background:none;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;padding:0;}',

  '.pw-search__filter-close{display:none;appearance:none;width:38px;height:38px;align-items:center;justify-content:center;padding:0;border:1px solid #cfd4d0;border-radius:50%;background:#fff;color:#14231e;cursor:pointer;font:inherit;font-size:27px;line-height:1;}',

  '.pw-search__filter-close-text,.pw-search__filter-footer{display:none;}',

  '.pw-filter-layer__nav{display:none;}',

  '.pw-filter-layer__panes{display:contents;}',

  '.pw-filter-layer__hint{display:none;}',

  '.pw-exact-colour{margin:14px 0 4px;padding:13px;border:1px solid #dce3df;border-radius:12px;background:linear-gradient(145deg,#f7faf8,#fff);}',

  '.pw-exact-colour.is-active{border-color:#aebcb5;box-shadow:0 5px 18px rgba(11,46,35,.07);}',

  '.pw-exact-colour__head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:11px;color:#14231e;font-size:12px;}',

  '.pw-exact-colour__head strong{font-weight:850;letter-spacing:.04em;text-transform:uppercase;}',

  '.pw-exact-colour__picker{position:relative;display:grid;grid-template-columns:42px minmax(0,1fr);align-items:center;gap:10px;min-height:48px;cursor:pointer;}',

  '.pw-exact-colour__picker input[type=color]{position:absolute;left:0;top:3px;width:42px;height:42px;opacity:0;cursor:pointer;}',

  '.pw-exact-colour__swatch{display:block;width:42px;height:42px;border:3px solid #fff;border-radius:50%;background:var(--pw-exact);box-shadow:0 0 0 1px #c8cfcb,0 3px 9px rgba(0,0,0,.12);box-sizing:border-box;}',

  '.pw-exact-colour__picker:focus-within .pw-exact-colour__swatch{outline:3px solid rgba(49,93,59,.28);outline-offset:3px;}',

  '.pw-exact-colour__picker b,.pw-exact-colour__picker small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',

  '.pw-exact-colour__picker b{color:#17251f;font-size:13px;}',

  '.pw-exact-colour__picker small{margin-top:2px;color:#6c7772;font-size:10px;letter-spacing:.03em;}',

  '.pw-exact-colour__tolerance{display:block;margin-top:13px;}',

  '.pw-exact-colour__tolerance[hidden],.pw-exact-colour__clear[hidden]{display:none!important;}',

  '.pw-exact-colour__tolerance>span{display:flex;justify-content:space-between;gap:10px;color:#33413b;font-size:10px;}',

  '.pw-exact-colour__tolerance em{color:#64716b;font-style:normal;}',

  '.pw-exact-colour__tolerance input{width:100%;height:18px;margin:5px 0 0;accent-color:#173f34;cursor:pointer;}',

  '.pw-exact-colour__clear{appearance:none;width:100%;margin-top:8px;padding:7px 4px;border:0;background:transparent;color:#315d3b;cursor:pointer;font:inherit;font-size:11px;font-weight:750;text-decoration:underline;}',

  '.pw-tag-shades{margin:0 0 20px;padding:16px 18px 17px;border:1px solid #dce3df;border-radius:14px;background:linear-gradient(118deg,#f4f7f2 0%,#fff 58%,#f7f3f1 100%);}',

  '.pw-tag-shades[hidden]{display:none!important;}',

  '.pw-tag-shades__head{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;margin-bottom:13px;}',

  '.pw-tag-shades__head span,.pw-tag-shades__head small,.pw-tag-shades__head strong{display:block;}',

  '.pw-tag-shades__head small{margin-bottom:4px;color:#315d3b;font-size:8px;font-weight:850;letter-spacing:.15em;}',

  '.pw-tag-shades__head strong{color:#14231e;font-size:17px;font-weight:850;letter-spacing:-.02em;text-transform:uppercase;}',

  '.pw-tag-shades__head p{margin:0;color:#65716b;font-size:10px;line-height:1.35;text-align:right;}',

  '.pw-tag-shades__list{display:flex;align-items:center;gap:7px;min-width:0;overflow-x:auto;padding:2px 2px 6px;scrollbar-width:thin;scrollbar-color:#bdc8c1 transparent;}',

  '.pw-tag-shade{appearance:none;display:inline-flex;min-width:max-content;min-height:29px;align-items:center;gap:6px;padding:0 8px 0 4px;border:1px solid #d8dfda;border-radius:999px;background:#fff;color:#526159;cursor:pointer;font:inherit;font-size:8px;font-weight:800;text-align:left;transition:border-color .16s ease,box-shadow .16s ease,transform .16s ease;}',

  '.pw-tag-shade:hover{border-color:#315d3b;transform:translateY(-1px);}',

  '.pw-tag-shade:focus-visible{outline:3px solid rgba(200,255,61,.7);outline-offset:2px;}',

  '.pw-tag-shade.is-active{border-color:#174f42;box-shadow:inset 0 0 0 1px #174f42,0 4px 12px rgba(10,48,37,.1);}',

  '.pw-tag-shade>i{display:block;width:20px;height:20px;flex:0 0 20px;border:1px solid rgba(10,31,24,.18);border-radius:50%;background:var(--pw-tag-shade);box-sizing:border-box;}',

  '.pw-tag-shade span{display:block;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',

  '.pw-tag-shades__loading{display:inline-flex;align-items:center;gap:7px;min-width:150px;padding:0 7px;color:#6b7771;font-size:10px;}',

  '.pw-tag-shades__loading>i{width:13px;height:13px;border:2px solid #d7ded9;border-top-color:#174f42;border-radius:50%;animation:pwSpin .7s linear infinite;}',

  '.pw-search__intro{margin:0 0 16px;padding:0 0 17px;border-bottom:1px solid #e7e7e3;}',

  '.pw-search__query{margin:0 0 7px;font-size:clamp(24px,2vw,28px);font-weight:700;line-height:1.08;letter-spacing:-.025em;color:#14231e;}',

  '.pw-search__query-context{display:inline-flex;flex-wrap:wrap;align-items:center;gap:5px 8px;margin-left:10px;color:#56645e;font-size:clamp(11px,.82vw,13px);font-weight:500;letter-spacing:.01em;line-height:1.35;vertical-align:.16em;}',

  '.pw-search__query-context::before{content:"—";color:#8a938f;font-weight:500;}',

  '.pw-search__query-context>span{white-space:nowrap;}',

  '.pw-search__query-context b{color:#315d3b;font-size:.82em;font-weight:850;letter-spacing:.08em;text-transform:uppercase;}',

  '.pw-search__query-context>i{color:#a4aca8;font-style:normal;}',

  '.pw-search__similar-kicker{display:block;margin-bottom:8px;color:#4e5d56;font-size:10px;font-weight:850;letter-spacing:.14em;line-height:1;text-transform:uppercase;}',

  '.pw-search__query .pw-search__similar-kicker + strong{display:block;font-size:clamp(25px,2.4vw,36px);}',

  '.pw-search__count{margin:0;font-size:14px;color:#616862;}',

  '.pw-colour-product-search{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:9px;margin:0 0 12px;padding:10px;border:1px solid #dbe3de;border-radius:13px;background:linear-gradient(135deg,#f5f8f6,#fff);box-shadow:0 8px 24px rgba(20,35,30,.045);}',

  '.pw-colour-product-search__field{position:relative;display:flex;align-items:center;min-width:0;min-height:44px;border:1px solid #cdd7d1;border-radius:9px;background:#fff;transition:border-color .18s ease,box-shadow .18s ease;}',

  '.pw-colour-product-search__field:focus-within{border-color:#315d3b;box-shadow:0 0 0 3px rgba(49,93,59,.12);}',

  '.pw-colour-product-search__field>svg{flex:0 0 auto;width:19px;height:19px;margin-left:14px;fill:none;stroke:#315d3b;stroke-linecap:round;stroke-width:1.8;}',

  '.pw-colour-product-search__field input{appearance:none;width:100%;min-width:0;height:42px;padding:0 42px 0 11px;border:0;outline:0;background:transparent;color:#14231e;font:inherit;font-size:14px;}',

  '.pw-colour-product-search__field input::-webkit-search-cancel-button{display:none;}',

  '.pw-colour-product-search__clear{position:absolute;right:7px;top:50%;display:flex;width:29px;height:29px;align-items:center;justify-content:center;padding:0;transform:translateY(-50%);border:0;border-radius:50%;background:#edf2ef;color:#315d3b;cursor:pointer;font:inherit;font-size:21px;line-height:1;}',

  '.pw-colour-product-search__clear[hidden]{display:none;}',

  '.pw-colour-product-search__submit{min-width:104px;padding:0 19px;border:1px solid #173f34;border-radius:9px;background:#173f34;color:#fff;cursor:pointer;font:inherit;font-size:13px;font-weight:800;}',

  '.pw-colour-product-search__submit:hover{background:#245849;}',

  '#PrewornWideSearch.is-empty-search .pw-search{display:block;max-width:980px;min-height:58vh;margin:0 auto;padding:clamp(44px,8vh,100px) 28px 50px;}',

  '#PrewornWideSearch.is-empty-search .pw-search__results{padding:0;}',

  '#PrewornWideSearch.is-empty-search .pw-search__filters,#PrewornWideSearch.is-empty-search .pw-wardrobe-search-toggle,#PrewornWideSearch.is-empty-search .pw-quick-filters,#PrewornWideSearch.is-empty-search .pw-active-filters,#PrewornWideSearch.is-empty-search .pw-search__toolbar,#PrewornWideSearch.is-empty-search .pw-search__loading,#PrewornWideSearch.is-empty-search .pw-search__error,#PrewornWideSearch.is-empty-search .pw-search__grid,#PrewornWideSearch.is-empty-search .pw-search__pagination{display:none!important;}',

  '#PrewornWideSearch.is-empty-search .pw-colour-product-search{margin:0;box-shadow:0 14px 38px rgba(20,35,30,.08);}',

  '.pw-search--colour-page .pw-search__results{padding-top:18px;}',

  '.pw-search--search-page .pw-search__results{padding-top:10px;}',

  '.pw-search--search-page .pw-colour-product-search{margin-bottom:8px;padding:6px;}',

  '.pw-search--colour-page .pw-wardrobe-search-toggle{min-height:0;margin-bottom:12px;padding:9px 11px 9px 13px;border-radius:11px;}',

  '.pw-search--colour-page .pw-wardrobe-search-toggle::after{display:none;}',

  '.pw-search--colour-page .pw-wardrobe-search-leading{gap:9px;}',

  '.pw-search--colour-page .pw-wardrobe-search-emblem{flex-basis:36px;width:36px;height:36px;border-radius:10px;}',

  '.pw-search--colour-page .pw-wardrobe-search-emblem svg{width:19px;height:19px;}',

  '.pw-search--colour-page .pw-wardrobe-search-kicker{display:none;}',

  '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-power{min-width:92px;height:34px;padding:0 12px;}',

  '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-edit,.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-add{height:34px;}',

  '.pw-quick-filters{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 16px;}',

  '.pw-quick-chip{appearance:none;height:36px;padding:0 15px;border:1px solid #cfd4d0;border-radius:999px;background:#fff;color:#26332d;cursor:pointer;font:inherit;font-size:13px;white-space:nowrap;transition:border-color .18s ease,background .18s ease,color .18s ease;}',

  '.pw-quick-chip:hover{border-color:#315d3b;color:#1a401a;}',

  '.pw-quick-chip.is-active{border-color:#315d3b;background:#315d3b;color:#fff;}',

  '.pw-active-filters{display:flex;flex-wrap:wrap;gap:7px;margin:0 0 14px;-webkit-text-size-adjust:none;text-size-adjust:none;}',

  '.pw-active-filters[hidden]{display:none!important;}',

  '.pw-active-pill{appearance:none;display:inline-flex;align-items:center;gap:6px;min-height:30px;padding:5px 10px;border:0;border-radius:999px;background:#edf3ee;color:#1f4a2a;cursor:pointer;font-family:inherit!important;font-size:11px!important;line-height:1.1!important;-webkit-text-size-adjust:none;text-size-adjust:none;}',

  '.pw-active-pill span{font-size:11px!important;line-height:1!important;}',

  '.pw-active-pill__swatch{display:inline-block;width:14px;height:14px;border:2px solid #fff;border-radius:50%;background:var(--pw-pill-colour);box-shadow:0 0 0 1px rgba(20,35,30,.25);}',

  '.pw-search__toolbar{display:flex;justify-content:space-between;align-items:center;gap:20px;margin-bottom:18px;padding:11px 0;border-top:1px solid #e7e7e3;border-bottom:1px solid #e7e7e3;}',
  '.pw-search__result-count{margin:0 0 10px;color:#2f3733;font-size:13px;line-height:1.2;}',

  '.pw-search__toolbar-summary{display:flex;align-items:center;gap:12px;font-size:13px;color:#2f3733;}',

  '#PrewornToolbarCount{display:inline-flex;flex-wrap:wrap;align-items:baseline;gap:3px 8px;font-size:13px;line-height:1.2;}',

  '.pw-search__toolbar-primary,.pw-search__toolbar-stat{display:inline-flex;align-items:baseline;margin:0;font-size:13px;font-weight:850;letter-spacing:.04em;text-transform:uppercase;vertical-align:middle;white-space:nowrap;}',

  '.pw-search__toolbar-primary{color:#14231e;}',

  '.pw-search__toolbar-stat{gap:4px;color:#68736d;}',

  '.pw-search__toolbar-stat-label--mobile{display:none;}',

  '.pw-search__toolbar-stat::before{content:"·";margin:0;color:#9aa39e;font-size:12px;}',

  '.pw-search__toolbar-availability::before{display:none;}',

  '.pw-search__toolbar-stat b{margin:0;color:#14231e;font-size:13px;font-weight:900;letter-spacing:-.02em;}',

  '.pw-search__filter-toggle{display:none;appearance:none;height:36px;padding:0 13px;border:1px solid #cfd4d0;border-radius:4px;background:#fff;color:#14231e;font:inherit;font-size:13px;cursor:pointer;}',

  '.pw-search__filter-icon{display:block;flex:0 0 17px;width:17px;height:17px;fill:none;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;stroke-width:1.7;}',

  '.pw-search__filter-count{display:inline-grid;min-width:20px;height:20px;place-items:center;padding:0 5px;border-radius:999px;background:#baff2a;color:#10251e;box-sizing:border-box;font-size:10px;font-weight:900;line-height:1;}',

  '.pw-search__filter-count[hidden]{display:none!important;}',

  '.pw-search__sort{display:flex;align-items:center;gap:9px;font-size:13px;white-space:nowrap;}',

  '.pw-search__sort-mobile{display:none;}',

  '.pw-search__sort-icon{display:block;width:24px;height:24px;fill:none;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;stroke-width:1.7;}',

  '.pw-search__sort select{min-width:175px;height:38px;border:1px solid #cfd4d0;border-radius:3px;background:#fff;color:#202824;padding:0 32px 0 10px;}',

  '.pw-search__sort select:disabled{color:#45524c;background:#f1f4f2;cursor:not-allowed;opacity:1;}',


  /* PRODUCT GRID */

  '.pw-search__grid{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:18px 16px;align-items:stretch;}',

  '.pw-search__grid[hidden]{display:none!important;}',

  '.pw-card{min-width:0;align-self:stretch;background:#f7f9f7;border:1px solid #edf0ed;border-radius:18px;overflow:hidden;display:flex;flex-direction:column;height:100%;box-sizing:border-box;box-shadow:0 1px 2px rgba(8,39,31,.025);transition:border-color .2s ease,box-shadow .2s ease,transform .2s ease;}',

  '.pw-card:hover{border-color:#dce4df;box-shadow:0 12px 28px rgba(8,39,31,.08);transform:translateY(-2px);}',

  '.pw-card__link{display:flex;flex:1 1 auto;flex-direction:column;width:100%;height:auto;color:inherit;text-decoration:none;background:#f7f9f7;}',

  '.pw-card__image-link{display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:inherit;text-decoration:none;}',

  /* DISCOUNT DISPLAY */
'.pw-card__discount{position:absolute;right:12px;top:61px;width:44px;height:44px;border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#315d3b;color:#fff;text-align:center;z-index:3;line-height:1;box-shadow:0 3px 10px rgba(8,39,31,.13);transition:transform .18s ease;}',
'.pw-card:hover .pw-card__discount{transform:rotate(7deg) scale(1.06);}',
'.pw-card__discount span{display:block;font-size:13px;font-weight:700;line-height:14px;color:#fff;}',

'.pw-card__discount small{display:block;font-size:7px;font-weight:700;letter-spacing:.05em;margin-top:1px;color:#fff;}',

  '.pw-discount--10,.pw-discount--20,.pw-discount--30,.pw-discount--40,.pw-discount--45,.pw-discount--55{background:#315d3b;color:#fff;}',

  '.pw-card__sold-badge{background:#c8ff3d;color:#08271f;}',

  '.pw-card__sold-badge span{font-size:11px;letter-spacing:.08em;}',

  '.pw-card__new-badge{position:absolute;top:12px;left:12px;z-index:5;display:inline-flex;min-height:23px;align-items:center;justify-content:center;padding:0 11px;color:#08271f;background:#c8ff3d;border-radius:999px;font-size:8px;font-weight:850;letter-spacing:.06em;line-height:1;box-shadow:0 2px 7px rgba(10,31,24,.1);}',

  '.pw-card__new-badge + .pw-card__brand-logo{top:41px;}',

  '.pw-colour-match-chip{position:absolute;left:9px;bottom:9px;z-index:5;display:inline-flex;align-items:center;gap:5px;max-width:calc(100% - 112px);min-height:25px;padding:3px 8px;border:1px solid rgba(20,35,30,.12);border-radius:999px;background:rgba(255,255,255,.94);color:#24312c;box-shadow:0 2px 8px rgba(0,0,0,.08);font-size:9px;font-weight:750;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',

  '.pw-colour-match-chip i{flex:0 0 auto;width:9px;height:9px;border:1px solid rgba(0,0,0,.18);border-radius:50%;background:var(--pw-match-colour);}',

  '.pw-card__actions{position:absolute;inset:0;z-index:4;pointer-events:none;}',

  '.pw-card__wishlist,.pw-card__cart,.pw-card__outfit-start{appearance:none;position:absolute;right:12px;display:flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;color:#26332d;background:rgba(255,255,255,.96);border:1px solid #d8ded9;border-radius:50%;box-shadow:0 2px 8px rgba(8,39,31,.08);font:inherit;cursor:pointer;pointer-events:auto;transition:transform .18s ease,border-color .18s ease,color .18s ease,background .18s ease;}',

  '.pw-card__wishlist{top:12px;}',

  '.pw-card__cart{bottom:12px;overflow:hidden;white-space:nowrap;}',

  '.pw-card__outfit-start{bottom:12px;gap:5px;width:calc(42% - 16px);border-color:#c8ff3d;border-radius:999px;background:#c8ff3d;color:#102f27;opacity:0;visibility:hidden;pointer-events:none;white-space:nowrap;}',

  '.pw-card__cart{transition:none;}',

  '.pw-card__wishlist:hover,.pw-card__cart:hover{color:#174f42;border-color:#174f42;transform:scale(1.08);}',

  '.pw-card__outfit-start:hover{border-color:#102f27;background:#fff;color:#102f27;transform:none;}',

  '.pw-card__wishlist:focus-visible,.pw-card__cart:focus-visible,.pw-card__outfit-start:focus-visible{outline:2px solid #174f42;outline-offset:3px;}',

  '.pw-card__wishlist svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',

  '.pw-card__wishlist.is-saved{color:#071f18;background:#c8ff3d;border-color:#c8ff3d;box-shadow:0 3px 10px rgba(7,31,24,.14);}',

  '.pw-card__wishlist.is-saved svg{fill:currentColor;stroke:currentColor;}',

  '.pw-card__cart svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',

  '.pw-card__cart-label{display:none;margin-left:9px;font-size:12px;font-weight:750;letter-spacing:.01em;}',

  '.pw-card__outfit-start-label{display:block;font-size:10px;font-weight:850;letter-spacing:.015em;}',

  '.pw-card__cart-check{display:none;}',

  '.pw-card__cart.is-added{color:#fff;background:#174f42;border-color:#174f42;}',

  '.pw-card__cart.is-added .pw-card__cart-icon{display:none;}',

  '.pw-card__cart.is-added .pw-card__cart-check{display:block;}',

  '.pw-card__cart.is-loading{color:transparent;pointer-events:none;}',

  '.pw-card__cart.is-loading:after{content:"";position:absolute;width:16px;height:16px;border:2px solid #c8d0cc;border-top-color:#174f42;border-radius:50%;animation:pwSpin .7s linear infinite;}',

  '.pw-card__cart:disabled{opacity:.45;cursor:not-allowed;transform:none;}',

  '.pw-card__outfit-start:disabled{cursor:not-allowed;opacity:0;}',

  '@media(min-width:750px){' +
    '.pw-card:hover .pw-card__cart:not(.is-added):not(.is-loading):not(:disabled),.pw-card:focus-within .pw-card__cart:not(.is-added):not(.is-loading):not(:disabled){right:auto;left:12px;width:calc(58% - 16px);color:#fff;background:#0c4a3c;border-color:#0c4a3c;border-radius:999px;transform:none;transition:none;}' +
    '.pw-card:hover .pw-card__cart:not(.is-added):not(.is-loading):not(:disabled) .pw-card__cart-label,.pw-card:focus-within .pw-card__cart:not(.is-added):not(.is-loading):not(:disabled) .pw-card__cart-label{display:inline;}' +
    '.pw-card:hover .pw-card__cart.is-added,.pw-card:hover .pw-card__cart.is-loading,.pw-card:focus-within .pw-card__cart.is-added,.pw-card:focus-within .pw-card__cart.is-loading{right:auto;left:12px;}' +
    '.pw-card:hover .pw-card__outfit-start:not(:disabled),.pw-card:focus-within .pw-card__outfit-start:not(:disabled){opacity:1;visibility:visible;pointer-events:auto;}' +
  '}',

  '@media(hover:none),(pointer:coarse){.pw-card__cart:not(.is-added):not(.is-loading):not(:disabled){right:auto;left:7px;width:calc(58% - 10px);color:#fff;background:#0c4a3c;border-color:#0c4a3c;border-radius:999px;}.pw-card__cart:not(.is-added):not(.is-loading):not(:disabled) .pw-card__cart-label{display:inline;margin-left:5px;font-size:8px;}.pw-card__outfit-start:not(:disabled){right:7px;width:calc(42% - 10px);opacity:1;visibility:visible;pointer-events:auto;}.pw-card__outfit-start-label{font-size:8px;}}',

  '.pw-card-cart-toast{position:fixed;right:24px;bottom:24px;z-index:9999;display:flex;align-items:center;gap:24px;max-width:calc(100vw - 32px);padding:16px 18px;color:#fff;background:#08271f;border-radius:2px;box-shadow:0 12px 35px rgba(0,0,0,.22);font-size:13px;font-weight:700;letter-spacing:.02em;}',

  '.pw-card-cart-toast[hidden]{display:none;}',

  '.pw-card-cart-toast.is-error{background:#8b1e1e;}',

  '.pw-card-cart-toast a{color:#c8ff3d;text-decoration:none;font-size:11px;font-weight:800;white-space:nowrap;}',
  /* PRODUCT IMAGE */

  '.pw-card__media{position:relative;width:100%;height:350px;background:#f7f9f7;overflow:hidden;display:flex;align-items:center;justify-content:center;box-sizing:border-box;}',

  '.pw-card__product-image{display:block;flex:0 0 auto;width:auto;max-width:calc(100% - 24px);height:312px;max-height:calc(100% - 22px);object-fit:contain;object-position:center center;background:transparent;mix-blend-mode:multiply;padding:8px 0;box-sizing:border-box;opacity:0;visibility:hidden;transform:translate3d(var(--pw-product-image-x,0px),var(--pw-product-image-y,0px),0) scale(var(--pw-product-image-scale,1));transform-origin:center center;transition:none;}',

  '.pw-card__product-image[data-pw-image-analysis="complete"],.pw-card__product-image[data-pw-image-analysis="unavailable"]{opacity:1;visibility:visible;}',

  '.pw-card:hover .pw-card__product-image{transform:translate3d(var(--pw-product-image-x,0px),var(--pw-product-image-y,0px),0) scale(var(--pw-product-image-hover-scale,1.04));}',

'.pw-card__brand-logo{position:absolute;top:10px;left:10px;z-index:4;display:block;width:auto;max-width:96px;height:auto;max-height:40px;object-fit:contain;object-position:left center;background:transparent;mix-blend-mode:multiply;padding:0;border:0;border-radius:0;box-shadow:none;pointer-events:none;}',

  '.pw-card__no-image{width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f7f9f7;color:#999;font-size:12px;}',


  /* PRODUCT INFORMATION */

  '.pw-card__body{display:flex;flex:1 1 auto;flex-direction:column;padding:0 16px 16px;text-align:left;background:#f7f9f7;box-sizing:border-box;}',

  '.pw-card__vendor{text-transform:uppercase;font-size:11px;font-weight:850;line-height:1.2;letter-spacing:.055em;color:#14231e;margin:0 0 5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',

  '.pw-card__title{font-size:14px;line-height:1.32;min-height:20px;margin:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;color:#313a35;}',

  '.pw-card__meta{display:flex;flex-wrap:wrap;align-items:center;gap:4px;margin:5px 0 0;color:#6b726d;font-size:12px;line-height:1.3;}',

  '.pw-card__size{color:#424d48;font-weight:800;}',

  '.pw-card__department{display:inline-flex;min-height:18px;align-items:center;padding:2px 7px;border:1px solid transparent;border-radius:999px;font-size:9px;font-weight:800;line-height:1;letter-spacing:.025em;white-space:nowrap;}',

  '.pw-card__department--women{color:#76465b;background:#f8e6ee;border-color:#f1d8e3;}',

  '.pw-card__department--men{color:#315a75;background:#e4eff8;border-color:#d5e5f1;}',

  '.pw-card__department--girls{color:#5f4a82;background:#eee7fa;border-color:#e2d8f3;}',

  '.pw-card__department--boys{color:#2f6655;background:#e2f2ec;border-color:#d2e8df;}',

  '.pw-card__department--baby{color:#775b22;background:#fff3d8;border-color:#f5e6c4;}',

  '.pw-card__meta-divider{color:#9aa19d;}',

  '.pw-card__price{display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:6px 10px;margin-top:auto;padding-top:10px;font-size:13px;line-height:1.2;}',

'.pw-card__original{color:#767f7a;text-decoration:line-through;font-weight:500;}',

'.pw-card__current{font-size:18px;font-weight:800;color:#14231e;}',

'.pw-card__saving{display:inline-flex;min-height:23px;align-items:center;padding:0 10px;border-radius:999px;background:#e5f1df;color:#315d3b;font-size:10px;font-weight:750;line-height:1;white-space:nowrap;}',

  '.pw-card__sold-context{margin:5px 0 0;color:#5f7f08;font-size:9px;font-weight:800;letter-spacing:.08em;line-height:1.2;}',


  /* LOADING / ERRORS */

  '.pw-search__loading{padding:12px 0 18px;color:#555;font-size:13px;}',

  '.pw-search__spinner{display:inline-block;width:14px;height:14px;border:2px solid #ddd;border-top-color:#111;border-radius:50%;animation:pwSpin .7s linear infinite;vertical-align:-2px;margin-right:5px;}',

  '@keyframes pwDirectoryCountLoading{0%{background-position:100% 0}100%{background-position:-100% 0}}',

  '@keyframes pwSpin{to{transform:rotate(360deg)}}',

  '.pw-search__error{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:12px;margin-bottom:15px;border:1px solid #c33;background:#fff4f4;color:#8b0000;font-size:13px;}',

  '.pw-search__error[hidden]{display:none!important;}',

  '.pw-search__retry{appearance:none;flex:0 0 auto;min-height:34px;padding:0 15px;border:1px solid #8b0000;border-radius:999px;background:#fff;color:#8b0000;cursor:pointer;font:inherit;font-size:11px;font-weight:800;}',

  '.pw-search__empty{grid-column:1/-1;padding:50px 10px;text-align:center;color:#666;}',

  '#PrewornWideSearch.is-wardrobe-page-browse .pw-wardrobe-search-power[hidden]{display:none!important;}',

  '#PrewornWideSearch.is-wardrobe-unconfigured .pw-search{display:block;max-width:1040px;margin:0 auto;padding-left:28px;padding-right:28px;}',

  '#PrewornWideSearch.is-wardrobe-unconfigured .pw-search__filters,#PrewornWideSearch.is-wardrobe-unconfigured .pw-quick-filters,#PrewornWideSearch.is-wardrobe-unconfigured .pw-active-filters,#PrewornWideSearch.is-wardrobe-unconfigured .pw-search__toolbar,#PrewornWideSearch.is-wardrobe-unconfigured .pw-search__pagination{display:none!important;}',

  '#PrewornWideSearch.is-wardrobe-unconfigured .pw-search__results{padding-top:18px;}',

  '.pw-search__empty--wardrobe{display:flex;min-height:260px;align-items:center;justify-content:center;flex-direction:column;gap:10px;padding:40px 22px;border:1px solid #d8e1db;border-radius:15px;background:linear-gradient(135deg,#f5f8f5,#fff);color:#56645e;}',

  '.pw-search__empty--wardrobe strong{color:#14231e;font-size:clamp(19px,2vw,26px);line-height:1.15;}',

  '.pw-search__empty--wardrobe span{max-width:580px;font-size:13px;line-height:1.55;}',

  '.pw-search__empty--wardrobe button{min-height:42px;margin-top:8px;padding:0 20px;border:1px solid #123d31;border-radius:999px;background:#123d31;color:#fff;cursor:pointer;font:inherit;font-size:12px;font-weight:850;}',


  /* FILTERS */

  '.pw-filter{border-bottom:1px solid #e5e5e5;}',

  '.pw-filter summary{list-style:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;padding:14px 0;font-size:13px;font-weight:700;color:#26332d;}',

  '.pw-filter summary::-webkit-details-marker{display:none;}',

  '.pw-filter__chevron{transition:transform .18s ease;}',

  '.pw-filter[open] .pw-filter__chevron{transform:rotate(180deg);}',

  '.pw-filter__body{padding:0 0 15px;}',

  '.pw-filter__sizes{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;}',

  '.pw-size{height:36px;border:1px solid #ccc;background:#fff;font-size:12px;cursor:pointer;overflow:hidden;text-overflow:ellipsis;padding:0 4px;display:flex;align-items:center;justify-content:center;gap:4px;}',

  '.pw-size:hover{border-color:#315d3b;}',

  '.pw-size.is-active{background:#315d3b;color:#fff;border-color:#315d3b;}',

  '.pw-size__label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',

  '.pw-size__remove{flex:0 0 auto;font-size:16px;font-weight:800;line-height:1;}',

  '.pw-size.is-disabled,.pw-colour.is-disabled,.pw-filter-value.is-disabled{opacity:.28;cursor:default;}',


  /* COLOUR */

  '.pw-filter__colours{display:flex;flex-wrap:wrap;gap:9px;}',

  '.pw-colour{position:relative;width:31px;height:31px;border:1px solid transparent;background:none;border-radius:50%;padding:3px;cursor:pointer;overflow:visible;}',

  '.pw-colour__dot{display:block;width:23px;height:23px;border-radius:50%;border:1px solid #aaa;box-sizing:border-box;}',

  '.pw-colour.is-active{border-color:#315d3b;}',

  '.pw-colour__remove{position:absolute;right:-5px;top:-6px;display:flex;align-items:center;justify-content:center;width:15px;height:15px;border:1px solid #fff;border-radius:50%;background:#173f34;color:#fff;box-shadow:0 1px 4px rgba(0,0,0,.2);font-size:12px;font-weight:800;line-height:1;}',


  /* STANDARD FILTER VALUES */

  '.pw-filter__list{display:flex;flex-direction:column;}',

  '.pw-filter-value{appearance:none;border:0;background:none;padding:6px 0;display:grid;grid-template-columns:17px 1fr auto;align-items:center;gap:8px;text-align:left;cursor:pointer;font:inherit;font-size:12px;}',

  '.pw-filter-value__box{display:grid;width:15px;height:15px;place-items:center;border:1px solid #aaa;box-sizing:border-box;}',

  '.pw-filter-value.is-active .pw-filter-value__box{background:#315d3b;border-color:#315d3b;box-shadow:inset 0 0 0 3px #fff;}',

  '.pw-filter-value.is-active .pw-filter-value__box::after{content:"✓";color:#fff;font-size:9px;font-weight:950;line-height:1;}',

  '.pw-filter-value__count{color:#888;font-size:11px;}',

  '.pw-filter-value__remove{display:inline-flex;align-items:center;gap:3px;color:#315d3b;font-size:10px;font-weight:800;text-decoration:underline;}',

  '.pw-filter-value__remove i{font-size:14px;font-style:normal;line-height:1;text-decoration:none;}',

  '.pw-filter-value--extra{display:none;}',

  '.pw-filter__more{appearance:none;border:0;background:none;padding:8px 0 0;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;text-align:left;}',


  /* PRICE */

  '.pw-price{display:grid;grid-template-columns:1fr 1fr;gap:8px;}',

  '.pw-price label{font-size:11px;color:#666;}',

  '.pw-price input{display:block;width:100%;height:36px;margin-top:4px;border:1px solid #ccc;padding:0 7px;box-sizing:border-box;}',

  '.pw-price__apply{grid-column:1/-1;height:36px;border:1px solid #315d3b;background:#315d3b;color:#fff;cursor:pointer;}',


  /* PAGINATION */

  '.pw-search__pagination{display:flex;align-items:center;justify-content:center;padding:52px 0 14px;}',

  '.pw-page-shell{position:relative;width:min(100%,780px);padding:20px 24px 17px;border:1px solid #d8dfda;border-radius:18px;background:linear-gradient(135deg,#f7f9f5 0%,#fff 58%,#f2f7ef 100%);box-shadow:0 14px 34px rgba(12,43,33,.08);box-sizing:border-box;text-align:center;overflow:hidden;}',

  '.pw-page-shell:before{content:"";position:absolute;inset:0 0 auto;height:4px;background:linear-gradient(90deg,#baff2a 0 28%,#0a3025 28% 100%);}',

  '.pw-page-kicker{display:block;margin:0 0 13px;color:#496159;font-size:10px;font-weight:800;letter-spacing:.17em;text-transform:uppercase;}',

  '.pw-page-controls{display:flex;align-items:center;justify-content:center;gap:14px;}',

  '.pw-page-numbers{display:flex;align-items:center;justify-content:center;gap:6px;min-width:0;}',

  '.pw-page-button,.pw-page-number{appearance:none;display:inline-flex;align-items:center;justify-content:center;height:44px;border:1px solid #cbd5ce;background:#fff;color:#123128;font:inherit;font-size:12px;font-weight:750;line-height:1;cursor:pointer;box-sizing:border-box;transition:transform .16s ease,border-color .16s ease,background .16s ease,color .16s ease,box-shadow .16s ease;}',

  '.pw-page-button{gap:9px;min-width:118px;padding:0 18px;border-color:#0a3025;border-radius:999px;background:#0a3025;color:#fff;letter-spacing:.03em;}',

  '.pw-page-number{width:42px;padding:0;border-radius:50%;}',

  '.pw-page-button:not(:disabled):hover,.pw-page-number:not(.is-current):not(.is-disabled):hover{transform:translateY(-2px);border-color:#0a3025;box-shadow:0 7px 16px rgba(10,48,37,.13);}',

  '.pw-page-button:focus-visible,.pw-page-number:focus-visible{outline:3px solid rgba(186,255,42,.65);outline-offset:3px;}',

  '.pw-page-number.is-current{border-color:#baff2a;background:#baff2a;color:#08271f;box-shadow:0 6px 14px rgba(141,196,24,.25);}',

  '.pw-page-number.is-disabled{opacity:.32;cursor:default;}',

  '.pw-page-ellipsis{display:inline-flex;align-items:center;justify-content:center;width:24px;height:42px;color:#7b8983;font-size:10px;letter-spacing:.08em;}',

  '.pw-page-button:disabled{border-color:#d8ded9;background:#edf0ed;color:#9ca7a1;box-shadow:none;cursor:default;}',

  '.pw-page-status{display:block;margin-top:12px;color:#65746d;font-size:11px;letter-spacing:.02em;}',

  '.pw-page-status strong{color:#15332a;font-weight:800;}',

  '.pw-search__muted{color:#888;font-size:12px;}',

  '.pw-visually-hidden{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important;}',

  '#PrewornWideSearch.is-loading .pw-search__grid{opacity:.45;pointer-events:none;}',


  /* LARGE / MEDIUM DESKTOP */

  '@media(max-width:1399px){' +
    '.pw-search__grid{grid-template-columns:repeat(4,minmax(0,1fr));}' +
    '.pw-card__media{height:350px;}' +
  '}',


  /* TABLET / SMALL DESKTOP */

  '@media(max-width:1199px){' +
    '.pw-search{grid-template-columns:230px minmax(0,1fr);gap:22px;padding-left:18px;padding-right:18px;}' +
    '.pw-search__grid{grid-template-columns:repeat(3,minmax(0,1fr));}' +
    '.pw-filter__sizes{grid-template-columns:repeat(3,minmax(0,1fr));}' +
    '.pw-card__media{height:315px;}' +
    '.pw-card__product-image{height:280px;}' +
  '}',


  /* MOBILE */

  '@media(max-width:749px){' +
    'html.pw-catalogue-page,body.pw-catalogue-page{max-width:100%;overflow-x:hidden;overflow-x:clip;}' +
    '#PrewornWideSearch{max-width:100vw;overflow:visible!important;}' +
    '.pw-collection-styles{padding:8px 12px 10px;}' +
    '.pw-collection-styles__track{grid-auto-columns:132px;gap:8px;}' +
    '.pw-collection-styles__media{height:106px;}' +
    '.pw-collection-styles__card.is-active:not(.is-all)::after{width:23px;height:23px;top:5px;right:5px;font-size:13px;}' +
    '.pw-collection-styles__arrow--previous{left:-7px;}' +
    '.pw-collection-styles__arrow--next{right:-7px;}' +
    '.pw-search{display:block;max-width:100%;min-width:0;padding:0 14px 35px;overflow:visible;}' +
    '.pw-search__results{width:100%;max-width:100%;min-width:0;padding-top:8px;overflow:visible!important;box-sizing:border-box;}' +
    '.pw-search--colour-page{padding-right:12px;padding-left:12px;}' +
    '.pw-search--colour-page .pw-search__results{padding-top:6px;}' +
    '.pw-search--search-page .pw-search__results{padding-top:4px;}' +
    'body.pw-search-filter-open{overflow:hidden;}' +
    '.pw-search__filters{display:grid;grid-template-rows:64px minmax(0,1fr) auto;position:fixed;inset:0 auto 0 0;z-index:10010;width:100vw;max-width:100vw;min-width:0;height:100vh;height:100dvh;max-height:none;overflow:hidden;overscroll-behavior:contain;border:0;padding:0;margin:0;background:#fff;box-sizing:border-box;visibility:hidden;pointer-events:none;transform:translateX(-100%);transition:transform .24s ease,visibility 0s linear .24s;}' +
    '.pw-search__filters.is-mobile-open{visibility:visible;pointer-events:auto;transform:translateX(0);transition:transform .24s ease;}' +
    '.pw-search__filter-head{position:relative;z-index:3;display:grid;width:100%;max-width:100%;min-width:0;grid-template-columns:38px minmax(0,1fr) auto;min-height:64px;align-items:center;gap:8px;padding:0 16px;border-bottom:1px solid #cfd4d0;background:#fff;box-sizing:border-box;}' +
    '.pw-search__filter-head>strong{min-width:0;overflow:hidden;font-size:14px;font-weight:900;letter-spacing:-.01em;text-overflow:ellipsis;white-space:nowrap;}' +
    '.pw-search__filter-actions{display:flex;min-width:max-content;align-items:center;justify-content:flex-end;}' +
    '.pw-search__clear{display:none;}' +
    '.pw-search__filter-close{display:inline-flex;width:36px;height:36px;border:0;border-radius:50%;background:transparent;font-size:27px;font-weight:450;}' +
    '.pw-search__filter-close:focus-visible,.pw-search__filter-close-text:focus-visible,.pw-search__filter-view:focus-visible,.pw-filter-layer__button:focus-visible{outline:3px solid #baff2a;outline-offset:-3px;}' +
    '.pw-search__filter-close-text{appearance:none;display:inline-flex;min-height:38px;align-items:center;padding:0;border:0;background:transparent;color:#14231e;cursor:pointer;font:inherit;font-size:12px;font-weight:850;}' +
    '.pw-search__filter-scroll{width:100%;max-width:100%;min-width:0;min-height:0;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;padding:0 18px 24px;box-sizing:border-box;scrollbar-width:none;-ms-overflow-style:none;}' +
    '.pw-search__filter-scroll::-webkit-scrollbar,.pw-filter-layer__nav::-webkit-scrollbar,.pw-filter-layer__panes::-webkit-scrollbar,.pw-filter.is-mobile-active .pw-filter__body::-webkit-scrollbar{display:none;}' +
    '.pw-search__filter-footer{position:relative;z-index:3;display:block;width:100%;max-width:100%;min-width:0;padding:12px 16px calc(12px + env(safe-area-inset-bottom));border-top:1px solid #dce1de;background:rgba(255,255,255,.98);box-shadow:0 -8px 24px rgba(11,35,27,.08);box-sizing:border-box;}' +
    '.pw-search__filter-view{appearance:none;display:flex;width:100%;max-width:100%;min-width:0;min-height:52px;align-items:center;justify-content:center;gap:5px;overflow:hidden;padding:0 14px;border:1px solid #07120e;border-radius:7px;background:#07120e;color:#fff;cursor:pointer;box-sizing:border-box;font:inherit;font-size:clamp(11px,3.2vw,13px);font-weight:850;letter-spacing:.035em;text-transform:uppercase;white-space:nowrap;}' +
    '.pw-search__filter-view strong{color:#c7ff3d;font-size:14px;font-weight:950;}' +
    '.pw-exact-colour{width:100%;max-width:100%;min-width:0;margin-top:16px;box-sizing:border-box;}' +
    '#PrewornSearchFiltersBody{width:100%;max-width:100%;min-width:0;padding-top:10px;overflow-x:hidden;box-sizing:border-box;}' +
    '.pw-filter-layer__nav{display:flex;flex-direction:column;}' +
    '.pw-filter-layer__button{appearance:none;display:flex;width:100%;max-width:100%;min-width:0;min-height:54px;align-items:center;justify-content:space-between;padding:0 8px;border:0;border-bottom:1px solid #e1e5e2;background:#fff;color:#14231e;cursor:pointer;box-sizing:border-box;font:inherit;font-size:13px;font-weight:750;text-align:left;}' +
    '.pw-filter-layer__label{min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}' +
    '.pw-filter-layer__selection{display:inline-flex;min-width:22px;height:22px;align-items:center;justify-content:center;margin:0 5px;padding:0 5px;border-radius:999px;background:#baff2a;color:#0b241b;box-sizing:border-box;font-size:10px;font-weight:950;line-height:1;}' +
    '.pw-filter-layer__selection[hidden]{display:none;}' +
    '.pw-filter-layer__button::after{content:"›";color:#718079;font-size:20px;font-weight:500;}' +
    '.pw-filter-layer__button.is-active{background:#f2f4f2;color:#071b14;font-weight:900;}' +
    '.pw-filter-layer__panes{display:none;}' +
    '.pw-search__filters.is-mobile-filter-layered .pw-search__filter-scroll{overflow:hidden;padding:0;}' +
    '.pw-search__filters.is-mobile-filter-layered .pw-exact-colour{display:none;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered{display:grid;height:100%;min-height:0;grid-template-columns:minmax(118px,38%) minmax(0,62%);padding:0;overflow:hidden;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-layer__nav{min-height:0;overflow-y:auto;overscroll-behavior:contain;border-right:1px solid #cfd4d0;background:#fafbfa;scrollbar-width:none;-ms-overflow-style:none;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-layer__button{flex:0 0 auto;padding:0 14px;background:#fafbfa;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-layer__button.is-active{background:#fff;box-shadow:inset 3px 0 0 #baff2a;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-layer__panes{display:block;min-width:0;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:0 16px 20px;background:#fff;box-sizing:border-box;scrollbar-width:none;-ms-overflow-style:none;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter{display:none;border:0;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter.is-mobile-active{display:block;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter>summary{display:none;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter.is-mobile-active>.pw-filter__body{display:block!important;overflow:visible;padding:16px 0 28px;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-layer__hint{display:block;margin:0 0 12px;color:#66746e;font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter__sizes{grid-template-columns:repeat(2,minmax(0,1fr));}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter-value{min-height:43px;padding:7px 0;}' +
    '#PrewornSearchFiltersBody.is-mobile-layered .pw-filter__more{min-height:42px;}' +
    '#PrewornSearchFiltersBody:not(.is-mobile-layered) .pw-filter-layer__panes{display:none;}' +
    '.pw-tag-shades{margin:0 0 14px;padding:13px 12px 10px;border-radius:11px;}' +
    '.pw-tag-shades__head{align-items:flex-start;margin-bottom:10px;}' +
    '.pw-tag-shades__head p{max-width:132px;font-size:9px;}' +
    '.pw-tag-shades__list{margin-right:-12px;padding-right:12px;}' +
    '.pw-tag-shade{min-width:max-content;}' +
    '.pw-search__intro{margin-bottom:13px;padding-bottom:14px;}' +
    '.pw-colour-product-search{grid-template-columns:minmax(0,1fr) auto;padding:7px;gap:6px;border-radius:10px;}' +
    '.pw-search--search-page .pw-colour-product-search{padding:4px;}' +
    '.pw-colour-product-search__submit{min-width:78px;padding:0 13px;}' +
    '#PrewornWideSearch.is-empty-search .pw-search{min-height:48vh;padding:34px 14px 40px;}' +
    '.pw-search__query{font-size:23px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle{min-height:0;align-items:center;flex-direction:row;gap:8px;margin-bottom:9px;padding:7px 8px 7px 10px;border-radius:10px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle::before{width:3px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-leading{width:auto;min-width:0;flex:1;gap:8px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-emblem{flex-basis:34px;width:34px;height:34px;border-radius:9px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-emblem svg{width:18px;height:18px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-copy>strong{font-size:12px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-actions{width:auto;flex:0 0 auto;gap:5px;}' +
    '#PrewornWideSearch.is-wardrobe-page-browse .pw-search--colour-page .pw-wardrobe-search-metrics{flex:0 1 auto;grid-template-columns:repeat(3,minmax(62px,auto));margin:0 2px 0 auto;}' +
    '#PrewornWideSearch.is-wardrobe-page-browse .pw-search--colour-page .pw-wardrobe-search-metrics>span{padding:0 8px;}' +
    '#PrewornWideSearch.is-wardrobe-page-browse .pw-search--colour-page .pw-wardrobe-search-metrics strong{font-size:13px;}' +
    '#PrewornWideSearch.is-wardrobe-page-browse .pw-search--colour-page .pw-wardrobe-search-metrics small{font-size:5.5px!important;letter-spacing:.06em;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-power,.pw-search--colour-page .pw-wardrobe-search-toggle [data-pw-search-wardrobe]{width:auto;min-width:76px;height:34px;padding:0 10px;font-size:8px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-switch{display:none;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-edit,.pw-search--colour-page .pw-wardrobe-search-toggle.is-active .pw-wardrobe-search-edit{width:34px;min-width:34px;height:34px;line-height:34px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-edit::after{height:34px;line-height:34px;}' +
    '.pw-search--colour-page .pw-wardrobe-search-toggle .pw-wardrobe-search-add{position:absolute;right:8px;bottom:-35px;z-index:3;min-width:122px;height:30px;}' +
    '.pw-search--colour-page .pw-quick-filters{margin-bottom:8px;padding-bottom:2px;}' +
    '.pw-search--colour-page .pw-quick-chip{height:32px;padding:0 13px;font-size:11px;}' +
    '.pw-search--colour-page .pw-active-filters{margin-bottom:8px;}' +
    '.pw-search--colour-page .pw-search__toolbar{margin-bottom:10px;}' +
    '.pw-quick-filters{flex-wrap:nowrap;overflow-x:auto;margin-left:-14px;margin-right:-14px;padding:0 14px 4px;scrollbar-width:none;}' +
    '.pw-quick-filters::-webkit-scrollbar{display:none;}' +
    '.pw-search__toolbar-sentinel{display:block;width:100%;height:1px;margin-bottom:-1px;pointer-events:none;}' +
    '.pw-search__toolbar{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));position:sticky;top:0;z-index:1000;gap:0;margin:0 -14px 14px;padding:0;border-top:1px solid #cfd4d0;border-bottom:1px solid #cfd4d0;background:#fff;box-shadow:0 7px 18px rgba(10,31,24,.1);isolation:isolate;}' +
    '.pw-search__result-count{margin:0 0 8px;padding:0 1px;}' +
    '.pw-search__toolbar.is-mobile-dock-source{visibility:hidden;}' +
    '.pw-search__toolbar--dock{position:fixed!important;top:0!important;bottom:auto!important;z-index:10000!important;overflow:hidden;width:auto;max-width:none;margin:0!important;box-sizing:border-box;visibility:hidden!important;opacity:0;pointer-events:none;transform:translate3d(0,-100%,0);}' +
    '.pw-search__toolbar--dock.is-active{visibility:visible!important;opacity:1;pointer-events:auto;transform:translate3d(0,0,0);}' +
    '.pw-search__toolbar-summary{display:contents;}' +
    '.pw-search__result-count #PrewornToolbarCount{display:inline-flex;}' +
    '.pw-search__toolbar-stat-label--desktop{display:none;}' +
    '.pw-search__toolbar-stat-label--mobile{display:inline;}' +
    '.pw-search__filter-toggle{grid-column:2;grid-row:1;display:inline-flex;width:100%;min-width:0;height:58px;align-items:center;justify-content:center;gap:10px;padding:0 12px;border:0;border-left:1px solid #cfd4d0;border-radius:0;background:#fff;box-sizing:border-box;font-size:13px;font-weight:900;letter-spacing:.09em;text-transform:uppercase;}' +
    '.pw-search__filter-icon{width:25px;height:25px;flex-basis:25px;stroke-width:1.6;}' +
    '.pw-search__sort{grid-column:1;grid-row:1;position:relative;width:100%;min-width:0;height:58px;justify-content:center;gap:0;margin:0;background:#fff;box-sizing:border-box;cursor:pointer;}' +
    '.pw-search__sort-desktop{display:none;}' +
    '.pw-search__sort-mobile{display:inline-flex;align-items:center;justify-content:center;gap:10px;font-size:13px;font-weight:900;letter-spacing:.09em;text-transform:uppercase;}' +
    '.pw-search__sort-mobile strong{font:inherit;}' +
    '.pw-search__sort select{position:absolute;inset:0;z-index:2;width:100%;min-width:0;max-width:none;height:100%;padding:0;border:0;opacity:0;cursor:pointer;}' +
    '.pw-search__sort select:disabled{opacity:0;cursor:not-allowed;}' +
    '.pw-search__sort:focus-within,.pw-search__filter-toggle:focus-visible{outline:3px solid rgba(186,255,42,.85);outline-offset:-3px;}' +
    '.pw-search__sort.is-disabled{color:#7b8580;background:#f1f4f2;cursor:not-allowed;}' +
    '.pw-search__sort.is-disabled .pw-search__sort-mobile{opacity:.72;}' +
    '.pw-search--colour-page .pw-search__toolbar{margin-left:-12px;margin-right:-12px;}' +
    '.pw-search__grid{width:100%;max-width:100%;min-width:0;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px 10px;}' +
    '.pw-filter__sizes{grid-template-columns:repeat(5,minmax(0,1fr));}' +
    '.pw-card__media{height:230px;}' +
    '.pw-card__product-image{height:208px;max-height:calc(100% - 12px);}' +
    '.pw-card__brand-logo{top:7px;left:7px;max-width:78px;max-height:34px;padding:0;}' +
    '.pw-card{border-radius:12px;}' +
    '.pw-card__body{padding:0 9px 11px;}' +
    '.pw-card__title{font-size:12px;min-height:20px;}' +
    '.pw-card__vendor{font-size:10px;}' +
    '.pw-card__meta{font-size:11px;}' +
    '.pw-card__price{font-size:13px;}' +
    '.pw-card__current{font-size:16px;}' +
    '.pw-card__discount{width:37px;height:37px;right:7px;top:50px;}' +
    '.pw-card__discount span{font-size:11px;line-height:12px;}' +
    '.pw-card__discount small{font-size:6px;margin-top:1px;}' +
    '.pw-card__wishlist,.pw-card__cart,.pw-card__outfit-start{right:7px;}' +
    '.pw-card__wishlist{top:7px;}' +
    '.pw-card__cart{bottom:7px;}' +
    '.pw-colour-match-chip{left:6px;bottom:6px;max-width:calc(100% - 86px);padding:3px 6px;font-size:8px;}' +
    '.pw-card__wishlist,.pw-card__cart,.pw-card__outfit-start{height:35px;}' +
    '.pw-card__wishlist,.pw-card__cart{width:35px;}' +
    '.pw-card__wishlist svg,.pw-card__cart svg{width:18px;height:18px;}' +
    '.pw-card-cart-toast{right:12px;bottom:12px;left:12px;justify-content:space-between;}' +
    '.pw-search__pagination{padding:35px 0 4px;}' +
    '.pw-page-shell{padding:17px 12px 14px;border-radius:14px;}' +
    '.pw-page-kicker{margin-bottom:11px;}' +
    '.pw-page-controls{gap:7px;}' +
    '.pw-page-numbers{gap:5px;}' +
    '.pw-page-button{min-width:42px;width:42px;height:42px;padding:0;}' +
    '.pw-page-button__label{display:none;}' +
    '.pw-page-number{width:40px;height:40px;}' +
    '.pw-page-number:not(.is-current):not(.pw-page-number--near),.pw-page-ellipsis{display:none;}' +
    '.pw-page-status{margin-top:10px;}' +
  '}'

].join('');

    document.head.appendChild(style);
  }

  /* The homepage uses this exact renderer so product cards, pricing,
     wishlist behaviour and styles cannot drift from browse/search cards. */
  window.PrewornProductCards = {
    brandLogoFilename: function (vendor) {
      var names = brandLogoAssetNames(vendor);
      return names.length ? names[0] : '';
    },
    renderList: function (products) {
      injectStyles();
      var items = Array.isArray(products) ? products : [];
      var wishlistIndex = getWishlistIndex();
      var html = '';

      for (var i = 0; i < items.length; i++) {
        html += productCard(items[i], wishlistIndex, i);
      }

      return html;
    },
    bindWishlist: function (scope) {
      if (scope) bindWishlistButtons(scope);
    },
    bindImages: function (scope) {
      if (scope) bindProductImageNormalisation(scope);
    },
    normaliseImage: function (image, mediaSelector, fillMode) {
      if (!image) return;
      if (mediaSelector) image.setAttribute('data-pw-normalise-media', mediaSelector);
      if (fillMode) image.setAttribute('data-pw-normalise-fill', fillMode);
      if (image._pwProductImageBounds) applyProductImageBounds(image, image._pwProductImageBounds);
      else analyseProductImage(image);
    },
    ensureStyles: injectStyles
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
